/**
 * ==============================================================
 * FLAMERACER CONFIGURATOR — MAIN APP (Shopify version)
 * ==============================================================
 *
 * Reads data from:
 *   - window.FR_* globals (defined in flameracer-data.js)
 *   - window.FR_SHOPIFY_DATA (injected by Liquid snippet)
 *
 * Adds cart integration to Shopify's /cart/add.js endpoint.
 *
 * To bring real GLB files online, uncomment loadRealAssets() at
 * the bottom of this file and ensure GLBs are uploaded to assets/.
 * ==============================================================
 */

import * as THREE from 'three';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { RGBELoader } from 'three/addons/loaders/RGBELoader.js';
import { RectAreaLightUniformsLib } from 'three/addons/lights/RectAreaLightUniformsLib.js';
import { mergeVertices, toCreasedNormals } from 'three/addons/utils/BufferGeometryUtils.js';
import { DecalGeometry } from 'three/addons/geometries/DecalGeometry.js';

// Real carbon-fibre 2/2 twill weave (128×128, tileable) — ripped from a Koenigsegg
// Agera RS GLB and embedded as a data-URI so it works in preview AND the live store
// with no asset-path/CORS plumbing. Sampled TRIPLANAR in the body shader (the body
// mesh has no UVs). Also saved as assets/fr-carbon-weave.png for reference.
const FR_CARBON_TEX_B64 = 'iVBORw0KGgoAAAANSUhEUgAAAIAAAACACAMAAAD04JH5AAAAM1BMVEUeHR4bGhsYGBgVFRUVFBUSFBISEhISERIQEBANDg0NDQ0NDA0KDQoKCgoKCAoICAgIBAjX/KsyAAACfUlEQVR42u2by26EMAxFbx6t1F3//z+rVqpCuuDVZoDEjh06EKRZXQwhMz6gM8G8DyFgQDCAcTDuIwIAzAvG7Q3fw4A5t/jcyHP1R7nH9hYDjrcpd5X1GwMw0xG261ySB2L9HC+53dsBmQNg5zy5+nTLzYCRzqkzEDMXECvrH2fATd9qjMdXMOxcYa4+ydcBmHmw7s8Jfp0pAkCYf/ZD3Mxz9Q+5eZ37dQjH/TzmGI7zXH2awy99ZMNxP495MHs8KKt//A3ETL8V5mwe2MJ+V+OB1+r30twK9XPkztDuDLTiwQoiYj9L8WAeWW0/M3PrfGG/ZvuZywNvQOp3cR5Y0PpdnAfpDDTngRW+v5Pr1xmI5/DAO9Du38I8CMY36ff95wtP7HdpHgTP7HcxHlhmv4vxwLe+/6e5ZV6BGA8883lejAee+zwvxoOsH1DmQd4PKPPAV/Z7NQ9q/UA1D5r7AeoMaPOA7geEeUD2A+I8oPoB6ZzsB6R5IOYH2DyQ8gNsHjyvH5DiAdsPlPIgjh95P1DMg7v4ATYPpP0AmQfSfoDMg+f3A7U8qPYDtTyo9gOVPBj9gIXF10X8AJUHan6gmAdafqCYB9fxA1weiPkBLg/E/ACbB1f1A6U8UPcD2VzbD2Tz6/kB9fUDwjyQ9wNUHlzeDzRbP8DOW/kB3M8PqK8fkMrV/MDt1g+w89Z+AGf7AdzPD6ivH6jN1f3A7dYPUPPT/ADO9gO4nx9QXz/AzX3r+3/3A2ne/cD9/ID6+gHt9wvE8+4Huh/ofqD7ge4Huh/ofqD7gckPLO8To/L5IFef5gsJl/eHpf4PLMy7H+h+4B/4gXWPpF8fjqCT/wASBY72hnButwAAAABJRU5ErkJggg==';

// ============================================================
// 3D SCENE
// ============================================================
class FlameracerScene {
  constructor(container) {
    this.container = container;
    this.partSlots = new Map();
    this.activeParts = new Map();
    this.partFinishes = new Map();
    this.wheelMeshes = [];
    this.targetRotY = -0.5;
    this.currentRotY = -0.5;
    this.isDragging = false;
    this.lastInteractTime = Date.now();
    // Render continuously for the first ~16s (covers GLB load + HDRI + setup),
    // then the on-demand loop takes over and the GPU idles when nothing moves.
    this._renderUntil = (typeof performance !== 'undefined' ? performance.now() : Date.now()) + 16000;
    this._setup();
  }

  _setup() {
    // ============================================================
    // RENDERER — Lambo cinematic preset
    // ============================================================
    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    // Cap device-pixel-ratio: 1.5 on desktop, 1.0 on phones — phones are
    // fill-rate bound on this heavy scene, so 1.0 is a big FPS/heat win with
    // little visible loss on small dense screens.
    this._isMobile = window.matchMedia('(max-width:760px)').matches;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, this._isMobile ? 1.0 : 1.5));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 0.1; // LAB SPEC 2026-06-24 (user "match exactly"): exposure 0.1
    this.renderer.shadowMap.enabled = true;
    // Mobile: cheaper hard-PCF shadows (PCFSoft does many samples/frame). Desktop = soft.
    this.renderer.shadowMap.type = this._isMobile ? THREE.PCFShadowMap : THREE.PCFSoftShadowMap;
    this.container.appendChild(this.renderer.domElement);

    // ============================================================
    // SCENE — HDRI studio environment + vignetted backdrop
    // ============================================================
    this.scene = new THREE.Scene();

    // BLACK backdrop. The car keeps its full studio lighting + HDRI reflections
    // (lights and scene.environment are untouched below) — only the surroundings
    // go black, so the car reads as a hero shot on a black void. The floor fades
    // to the same black via matching fog, so there's no visible horizon line.
    const COVE = '#000000';
    this.scene.background = new THREE.Color(0x000000);
    this.scene.fog = new THREE.Fog(new THREE.Color(COVE), 18, 64);
    this._coveColor = COVE;

    const pmrem = new THREE.PMREMGenerator(this.renderer);
    pmrem.compileEquirectangularShader();

    // Temporary RoomEnvironment until HDRI loads
    this.scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;

    // Load real photo studio HDRI for realistic car paint reflections
    // studio_small_08 from Polyhaven - free CC0 studio environment with CORS enabled
    const _hdriUrl = 'https://dl.polyhaven.org/file/ph-assets/HDRIs/hdr/1k/studio_small_08_1k.hdr';
    new RGBELoader().load(_hdriUrl, (tex) => {
      tex.mapping = THREE.EquirectangularReflectionMapping;
      const envMap = pmrem.fromEquirectangular(tex).texture;
      this.scene.environment = envMap;
      tex.dispose();
      // Keep our custom dark backdrop as visible background (HDRI only used for reflections)
      console.log('[Flameracer] HDRI loaded — real studio reflections active');
    }, undefined, (err) => {
      console.warn('[Flameracer] HDRI failed, using fallback', err);
    });

    window.__frBackdrop = null;

    // ============================================================
    // CAMERA — slightly lower hero angle
    // ============================================================
    this.camera = new THREE.PerspectiveCamera(34, 1, 0.1, 100);
    this.camera.position.set(3.7, 0.92, 3.9);
    this.camera.lookAt(-0.10, -0.04, 0.05);

    // ============================================================
    // LIGHTING — STUDIO SOFTBOX RIG
    // The HDRI environment carries the reflections (the thing that makes
    // car paint read as car paint). Direct lights are kept minimal and
    // SOFT: a low ambient floor, ONE gentle key purely to throw the ground
    // shadow, a touch of floor bounce, and three RectAreaLight "softboxes"
    // that paint clean rectangular highlight bands across the paint — like a
    // real photo studio. Tune live, e.g.:
    //   __frLights.softTop.intensity = 7
    //   __frLights.key.intensity = 0.7
    //   __frRenderer.toneMappingExposure = 1.2
    // ============================================================
    RectAreaLightUniformsLib.init();
    const lights = {};

    // Ambient — kept LOW so the HDRI/softboxes drive the look with rich contrast
    // (high ambient flattens the paint and washes out reflections).
    lights.ambient = new THREE.AmbientLight(0xffffff, 0.0); // LAB SPEC: fill_ambient 0
    this.scene.add(lights.ambient);

    // KEY: soft top light, slightly camera-side. Its main job is the shadow.
    lights.key = new THREE.DirectionalLight(0xffffff, 2.84); // LAB SPEC: key 2.84
    lights.key.position.set(2.5, 8, 3);
    lights.key.castShadow = true;
    lights.key.shadow.mapSize.set(this._isMobile ? 1024 : 2048, this._isMobile ? 1024 : 2048);
    Object.assign(lights.key.shadow.camera, { near: 0.5, far: 24, left: -6, right: 6, top: 6, bottom: -6 });
    lights.key.shadow.bias = -0.0005;
    lights.key.shadow.radius = 6;
    this.scene.add(lights.key);

    // Floor/sky bounce — gentle, just to keep undersides from crushing to black
    lights.fill = new THREE.HemisphereLight(0xffffff, 0x404048, 3.0); // LAB SPEC: skyfill_hemi 3
    this.scene.add(lights.fill);

    // --- SOFTBOXES: the studio look. Big soft rectangles reflected in paint ---
    lights.softTop = new THREE.RectAreaLight(0xffffff, 15.0, 6, 3); // LAB SPEC: softboxScale 3 (5.0×3)
    lights.softTop.position.set(0, 6, 0.5);
    lights.softTop.lookAt(0, -0.4, 0);
    this.scene.add(lights.softTop);

    lights.softLeft = new THREE.RectAreaLight(0xffffff, 9.0, 4, 3); // LAB SPEC: softboxScale 3 (3.0×3)
    lights.softLeft.position.set(-5, 2.5, 3.5);
    lights.softLeft.lookAt(0, 0, 0);
    this.scene.add(lights.softLeft);

    lights.softRight = new THREE.RectAreaLight(0xffffff, 7.2, 4, 3); // LAB SPEC: softboxScale 3 (2.4×3)
    lights.softRight.position.set(5, 2.2, 3.0);
    lights.softRight.lookAt(0, 0, 0);
    this.scene.add(lights.softRight);

    // Kept (off) for back-compat with anything referencing them via __frLights
    lights.rimLeft = new THREE.DirectionalLight(0xffffff, 0);
    lights.rimLeft.position.set(-5, 2, -3);
    this.scene.add(lights.rimLeft);
    lights.rimRight = new THREE.DirectionalLight(0xffffff, 0);
    lights.rimRight.position.set(5, 1.5, -2);
    this.scene.add(lights.rimRight);
    lights.accent = new THREE.PointLight(0xaa1828, 0, 5, 1.8);
    lights.accent.position.set(0, -0.2, 0);
    this.scene.add(lights.accent);
    lights.spot = new THREE.SpotLight(0xffffff, 0, 14, Math.PI / 3, 0.95, 1.5);
    lights.spot.position.set(0, 10, 0);
    lights.spot.target.position.set(0, 0, 0);
    this.scene.add(lights.spot);
    this.scene.add(lights.spot.target);

    this.lights = lights;

    // ============================================================
    // HEXAGON GARAGE LIGHT — toggleable overhead fixture
    // A honeycomb cluster of hex panels mounted near the ceiling, like
    // the modular LED hex lights in a detailing garage. DEFAULT OFF.
    //
    // Two light sources fire when it's on:
    //   • a RectAreaLight — the soft AREA reflection you actually see
    //     streak across the paint (this is what "reflects on the car")
    //   • a PointLight — fills the scene with brightness
    // The visible hex panels are auto-hidden in TOP view so they never
    // cover the car (they sit between the top camera and the car).
    //
    // Toggle from the LIGHT button (left rail) or the console:
    //   __frHexLight.on() · off() · toggle()
    //   __frHexLight.bright(30)        // set overall brightness in one call
    //   __frHexLight.lamp.intensity    // point-light brightness
    //   __frHexLight.area.intensity    // reflected-streak brightness
    // ============================================================
    RectAreaLightUniformsLib.init();

    const hexGroup = new THREE.Group();
    hexGroup.name = 'hexlight';

    const HEX_R = 0.85;   // radius of each hexagon
    const HEX_Y = 6.6;    // height above the floor (well above the car)

    // Build a flat (pointy-top) hexagon Shape of radius r
    const makeHex = (r) => {
      const s = new THREE.Shape();
      for (let i = 0; i < 6; i++) {
        const a = (Math.PI / 3) * i + Math.PI / 6;
        const x = Math.cos(a) * r, y = Math.sin(a) * r;
        i === 0 ? s.moveTo(x, y) : s.lineTo(x, y);
      }
      s.closePath();
      return s;
    };

    const panelGeo = new THREE.ShapeGeometry(makeHex(HEX_R));
    const frameGeo = new THREE.ShapeGeometry(makeHex(HEX_R * 1.08));
    const panelMat = new THREE.MeshBasicMaterial({ color: 0xffffff, side: THREE.DoubleSide });
    const frameMat = new THREE.MeshBasicMaterial({ color: 0x111317, side: THREE.DoubleSide });

    // Honeycomb layout — 1 center + 6 around it (classic hex-light cluster)
    const sx = Math.sqrt(3) * HEX_R;  // horizontal spacing
    const sz = 1.5 * HEX_R;           // depth spacing
    const cells = [
      [0, 0],
      [sx, 0], [-sx, 0],
      [sx / 2, sz], [-sx / 2, sz],
      [sx / 2, -sz], [-sx / 2, -sz],
    ];
    const hexPanels = []; // visible meshes — hidden in TOP view
    cells.forEach(([ox, oz]) => {
      const frame = new THREE.Mesh(frameGeo, frameMat);
      frame.rotation.x = Math.PI / 2;        // lay flat, facing down
      frame.position.set(ox, HEX_Y + 0.03, oz);
      hexGroup.add(frame);
      hexPanels.push(frame);
      const panel = new THREE.Mesh(panelGeo, panelMat);
      panel.rotation.x = Math.PI / 2;
      panel.position.set(ox, HEX_Y, oz);
      hexGroup.add(panel);
      hexPanels.push(panel);
    });

    // AREA reflection — a soft rectangle of light across the cluster
    // footprint. RectAreaLight is the only light that paints a visible
    // reflection band on glossy car paint, so this is what makes the
    // hex light "show" on the body.
    const hexArea = new THREE.RectAreaLight(0xffffff, 0, sx * 3.0, sz * 3.0);
    hexArea.position.set(0, HEX_Y - 0.05, 0);
    hexArea.lookAt(0, -0.4, 0); // aim down at the car
    hexGroup.add(hexArea);

    // Fill brightness — washes the whole scene when on
    const hexLamp = new THREE.PointLight(0xfff4e0, 0, 60, 0); // decay 0 = full reach
    hexLamp.position.set(0, HEX_Y - 0.4, 0);
    hexGroup.add(hexLamp);

    this.scene.add(hexGroup);

    // Default-OFF brightness levels (used when toggled on). Bumped up so
    // the light reads strong out of the box — tune live via the console.
    this.hexAreaIntensity = 10;  // reflected streak strength
    this.hexLampIntensity = 8;   // overall fill brightness

    this.hexLightGroup = hexGroup;
    this.hexLightArea = hexArea;
    this.hexLightLamp = hexLamp;
    this.hexPanels = hexPanels;
    this.hexLightOn = false;
    this._topView = false;
    this._applyHex();

    // ============================================================
    // FLOOR — invisible standard floor kept for back-compat. Combined mode
    // hides this; the real grounding is the ShadowMaterial plane below.
    // ============================================================
    // Radial "cove" texture: a soft darker pool directly under the car (which
    // grounds it and adds drama) fading out to the cove colour, so the floor's
    // outer reaches match the fog + backdrop and the horizon disappears.
    // Dark "black studio" floor: a faint pool directly under the car for a soft
    // reflection + grounding, fading to pure black so the whole surround reads
    // black. Kept dark + rough so the strong studio lights don't wash it grey.
    const floorTex = (() => {
      const S = 1024;
      const cv = document.createElement('canvas'); cv.width = cv.height = S;
      const ctx = cv.getContext('2d');
      // Soft, broad "pool of light" the car sits in — lit dark-grey centre fading
      // smoothly to black at the edges (blends into the backdrop, no harsh horizon).
      const g = ctx.createRadialGradient(S/2, S/2, S*0.02, S/2, S/2, S*0.46);
      g.addColorStop(0.00, '#17171c');
      g.addColorStop(0.40, '#0c0c10');
      g.addColorStop(0.75, '#040406');
      g.addColorStop(1.00, '#000000');
      ctx.fillStyle = g; ctx.fillRect(0, 0, S, S);
      const t = new THREE.CanvasTexture(cv); t.colorSpace = THREE.SRGBColorSpace; return t;
    })();
    const floor = new THREE.Mesh(
      new THREE.PlaneGeometry(120, 120),
      new THREE.MeshStandardMaterial({
        map: floorTex,     // matte dark floor — reflections OFF for now (no HDRI hotspots)
        metalness: 0.1,
        roughness: 0.82,   // high → matte, no mirror of the lighting rig
        envMapIntensity: 0.08,
      })
    );
    floor.visible = true;  // shown for the studio-showroom look
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -0.4;
    floor.receiveShadow = true;
    this.scene.add(floor);
    this.floor = floor;

    // ============================================================
    // CONTACT SHADOW — transparent ShadowMaterial plane. Renders ONLY the
    // soft shadow the key light throws, so the void stays pure black but the
    // car is grounded instead of floating. NOT hidden in combined mode
    // (unlike this.floor), so the grounding survives the car swap.
    // Tune the darkness live:  __frShadowFloor.material.opacity = 0.55
    // ============================================================
    const shadowFloor = new THREE.Mesh(
      new THREE.PlaneGeometry(40, 40),
      new THREE.ShadowMaterial({ opacity: 0.45 })
    );
    shadowFloor.rotation.x = -Math.PI / 2;
    shadowFloor.position.y = -0.399; // a hair above the old floor to avoid z-fighting
    shadowFloor.receiveShadow = true;
    this.scene.add(shadowFloor);
    this.shadowFloor = shadowFloor;
    window.__frShadowFloor = shadowFloor;

    // ============================================================
    // RADIAL GROUND GLOW — moody pool of light under the car
    // ============================================================
    const gradMat = new THREE.ShaderMaterial({
      uniforms: { color: { value: new THREE.Color(0x1a1018) } },
      vertexShader: `varying vec2 vUv; void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
      fragmentShader: `uniform vec3 color; varying vec2 vUv; void main() { float d = distance(vUv, vec2(0.5)) * 2.0; gl_FragColor = vec4(color, (1.0 - smoothstep(0.0, 0.9, d)) * 0.55); }`,
      transparent: true, depthWrite: false,
    });
    const grad = new THREE.Mesh(new THREE.CircleGeometry(7, 64), gradMat);
    grad.rotation.x = -Math.PI / 2;
    grad.position.y = -0.39;
    this.scene.add(grad);
    grad.visible = false;
    this.groundGlow = grad;

    // ============================================================
    // SHOWROOM REFLECTION — subtle planar reflector under the car.
    // Goal: a faint, soft car reflection that bleeds downward/outward and
    // fades to black — "premium showroom floor", NOT a sharp mirror.
    // How the subtlety is achieved:
    //   • low-res reflection texture (512) → naturally soft/blurry + cheap
    //   • dark tint → the bright HDRI rig doesn't throw hotspots
    //   • a single radial overlay that BOTH mutes the reflection (centre
    //     ~55% strength) AND fades it to pure black at the rim.
    // Perf: the reflector only re-renders while the car is moving (on-demand
    // rendering), so idle GPU still sleeps.
    // Live tuning:  __frFloorTune({ strength, tint, size })
    // ============================================================
    this._mirrorY = -0.4; // updated to groundY once the car loads
    // MOBILE (2026-07-30): the reflector re-renders the ENTIRE scene every frame
    // it draws — the single most expensive thing on a phone GPU. Phones get the
    // plain dark floor instead; desktop keeps the showroom mirror.
    if (this._isMobile) { this.mirror = null; } else
    import('three/addons/objects/Reflector.js').then(({ Reflector }) => {
      const R = 5.6; // covers the whole car footprint; the shader fades the rim out
      // Custom reflector shader: a TRUE multiplicative dim (uStrength) + a radial
      // alpha fade, output transparent so the reflection blends faintly over the
      // dark floor and dissolves to nothing at the rim. (THREE's stock Reflector
      // uses an overlay blend that preserves highlights — too bright for a subtle
      // showroom look — and writes alpha 1.0, so a separate mask plane can't dim it.)
      const ShowroomShader = {
        uniforms: {
          color:         { value: new THREE.Color(0x9aa0ad) }, // cool, slightly desaturated tint
          tDiffuse:      { value: null },
          textureMatrix: { value: null },
          uStrength:     { value: 0.28 }, // overall reflection brightness (0..1) — faint, showroom-subtle
          uRadius:       { value: R },
          uFadeStart:    { value: 0.30 }, // radial fraction where the fade begins
        },
        vertexShader: /* glsl */`
          uniform mat4 textureMatrix;
          uniform float uRadius;
          varying vec4 vUv;
          varying float vR;
          void main() {
            vUv = textureMatrix * vec4( position, 1.0 );
            vR = length( position.xy ) / uRadius; // 0 at centre → 1 at rim
            gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
          }
        `,
        fragmentShader: /* glsl */`
          uniform vec3 color;
          uniform sampler2D tDiffuse;
          uniform float uStrength;
          uniform float uFadeStart;
          varying vec4 vUv;
          varying float vR;
          void main() {
            vec4 base = texture2DProj( tDiffuse, vUv );
            vec3 col = base.rgb * color * uStrength;          // real dim
            float fade = 1.0 - smoothstep( uFadeStart, 1.0, vR ); // bleed out to rim
            gl_FragColor = vec4( col, fade );
            #include <tonemapping_fragment>
            #include <colorspace_fragment>
          }
        `,
      };
      const mirror = new Reflector(new THREE.CircleGeometry(R, 96), {
        clipBias: 0.003,
        textureWidth: 512,   // low-res → naturally soft/blurry + cheap
        textureHeight: 512,
        shader: ShowroomShader,
      });
      mirror.material.transparent = true;  // blend faintly over the dark floor below
      mirror.material.depthWrite = false;
      mirror.rotation.x = -Math.PI / 2;
      mirror.position.y = this._mirrorY + 0.001;
      this.scene.add(mirror);
      this.mirror = mirror;
      window.__frMirror = mirror;

      // Live tuning:  __frFloorTune({ strength, tint, size, fadeStart })
      window.__frFloorTune = ({ strength, tint, size, fadeStart } = {}) => {
        const u = mirror.material.uniforms;
        if (typeof strength  === 'number') u.uStrength.value  = strength;
        if (typeof fadeStart === 'number') u.uFadeStart.value = fadeStart;
        if (typeof tint      === 'number') u.color.value.setHex(tint);
        if (typeof size      === 'number') {
          mirror.geometry.dispose();
          mirror.geometry = new THREE.CircleGeometry(size, 96);
          u.uRadius.value = size;
        }
        this.requestRender && this.requestRender(400);
      };
      // The Reflector loads async, so it can arrive AFTER the user has already
      // switched to the light studio — which would drop a dark disc onto the white
      // floor. Adopt the current backdrop on arrival.
      if (this._bgMode === 'white') this._setFloorMode('white');
    }).catch(err => {
      console.warn('[Flameracer] Reflector failed to load', err);
    });

    // ============================================================
    // GARAGE — procedural SDC-style space
    // Dark walls + neon edge strips + ceiling. Built in code, ~50KB.
    // All elements exposed for live console tuning.
    // ============================================================
    const garage = new THREE.Group();
    garage.name = 'garage';

    // Garage dimensions — expanded so corners fall into fog
    const W = 42;   // width (left-right)
    const D = 42;   // depth (front-back)
    const H = 8;    // height (floor to ceiling)
    const yFloor = -0.4;
    const yCeil = yFloor + H;

    // Dark concrete wall material
    const wallMat = new THREE.MeshStandardMaterial({
      color: 0x0c0c10,
      metalness: 0.15,
      roughness: 0.85,
    });

    // Back wall
    const backWall = new THREE.Mesh(new THREE.PlaneGeometry(W, H), wallMat);
    backWall.position.set(0, yFloor + H / 2, -D / 2);
    garage.add(backWall);

    // Front wall (camera-facing, behind viewer mostly)
    const frontWall = new THREE.Mesh(new THREE.PlaneGeometry(W, H), wallMat);
    frontWall.position.set(0, yFloor + H / 2, D / 2);
    frontWall.rotation.y = Math.PI;
    garage.add(frontWall);

    // Left wall
    const leftWall = new THREE.Mesh(new THREE.PlaneGeometry(D, H), wallMat);
    leftWall.position.set(-W / 2, yFloor + H / 2, 0);
    leftWall.rotation.y = Math.PI / 2;
    garage.add(leftWall);

    // Right wall
    const rightWall = new THREE.Mesh(new THREE.PlaneGeometry(D, H), wallMat);
    rightWall.position.set(W / 2, yFloor + H / 2, 0);
    rightWall.rotation.y = -Math.PI / 2;
    garage.add(rightWall);

    // Ceiling
    const ceilMat = new THREE.MeshStandardMaterial({
      color: 0x080809,
      metalness: 0.2,
      roughness: 0.9,
    });
    const ceiling = new THREE.Mesh(new THREE.PlaneGeometry(W, D), ceilMat);
    ceiling.position.set(0, yCeil, 0);
    ceiling.rotation.x = Math.PI / 2;
    garage.add(ceiling);

    // ============================================================
    // NEON EDGE STRIPS — blue glowing trim along wall/floor & wall/ceiling joints
    // ============================================================
    const NEON_COLOR = 0xd6253d;
    const neonMat = new THREE.MeshBasicMaterial({
      color: NEON_COLOR,
      transparent: true,
      opacity: 0.95,
    });

    // Strip geometry helper — thin glowing rectangle
    const makeStrip = (w, h) => new THREE.Mesh(new THREE.PlaneGeometry(w, h), neonMat);

    const stripHeight = 0.08;

    // Floor-line strips (along base of each wall)
    const fbBack = makeStrip(W, stripHeight);
    fbBack.position.set(0, yFloor + 0.02, -D / 2 + 0.01);
    garage.add(fbBack);

    const fbLeft = makeStrip(D, stripHeight);
    fbLeft.position.set(-W / 2 + 0.01, yFloor + 0.02, 0);
    fbLeft.rotation.y = Math.PI / 2;
    garage.add(fbLeft);

    const fbRight = makeStrip(D, stripHeight);
    fbRight.position.set(W / 2 - 0.01, yFloor + 0.02, 0);
    fbRight.rotation.y = -Math.PI / 2;
    garage.add(fbRight);

    // Vertical corner strips (back two corners)
    const cornerLB = makeStrip(stripHeight, H);
    cornerLB.position.set(-W / 2 + 0.02, yFloor + H / 2, -D / 2 + 0.02);
    cornerLB.rotation.y = Math.PI / 4;
    garage.add(cornerLB);

    const cornerRB = makeStrip(stripHeight, H);
    cornerRB.position.set(W / 2 - 0.02, yFloor + H / 2, -D / 2 + 0.02);
    cornerRB.rotation.y = -Math.PI / 4;
    garage.add(cornerRB);

    // Ceiling-line strips (along top of each wall)
    const ctBack = makeStrip(W, stripHeight);
    ctBack.position.set(0, yCeil - 0.02, -D / 2 + 0.01);
    garage.add(ctBack);

    const ctLeft = makeStrip(D, stripHeight);
    ctLeft.position.set(-W / 2 + 0.01, yCeil - 0.02, 0);
    ctLeft.rotation.y = Math.PI / 2;
    garage.add(ctLeft);

    const ctRight = makeStrip(D, stripHeight);
    ctRight.position.set(W / 2 - 0.01, yCeil - 0.02, 0);
    ctRight.rotation.y = -Math.PI / 2;
    garage.add(ctRight);

    // ============================================================
    // NEON POINT LIGHTS — actual illumination from the strips
    // Cool blue glow that washes the walls
    // ============================================================
    const neonLights = [];
    const neonPositions = [
      { x: -W / 2 + 0.5, y: yFloor + 0.3, z: 0 },
      { x:  W / 2 - 0.5, y: yFloor + 0.3, z: 0 },
      { x: 0, y: yFloor + 0.3, z: -D / 2 + 0.5 },
      { x: -W / 2 + 0.5, y: yCeil - 0.3, z: 0 },
      { x:  W / 2 - 0.5, y: yCeil - 0.3, z: 0 },
    ];
    neonPositions.forEach(p => {
      const nl = new THREE.PointLight(NEON_COLOR, 2.5, 14, 1.5);
      nl.position.set(p.x, p.y, p.z);
      garage.add(nl);
      neonLights.push(nl);
    });

    // ============================================================
    // CEILING SPOT LIGHTS — industrial overhead fixtures
    // Small bright cones above the car
    // ============================================================
    const ceilingSpots = [];
    const spotPositions = [
      { x: -3, z:  3 },
      { x:  3, z:  3 },
      { x: -3, z: -3 },
      { x:  3, z: -3 },
    ];
    spotPositions.forEach(p => {
      // Invisible point light only — no visible fixture or bulb disc
      const cl = new THREE.PointLight(0xffe9b0, 0.3, 12, 1.5);
      cl.position.set(p.x, yCeil - 0.3, p.z);
      garage.add(cl);
      ceilingSpots.push(cl);
    });

    garage.visible = false; // Showroom look uses the studio gradient backdrop, not the neon garage
    this.scene.add(garage);
    this.garage = garage;

    // Expose for live tuning
    window.__frGarage = garage;
    window.__frNeonLights = neonLights;
    window.__frCeilingSpots = ceilingSpots;
    window.__frNeonStrips = garage.children.filter(c => c.material === neonMat);

    // ============================================================
    // EXPOSE FOR LIVE CONSOLE TUNING
    // Access via: window.__frScene, window.__frLights, etc.
    // ============================================================
    window.__frScene = this.scene;
    window.__frRenderer = this.renderer;
    window.__frCamera = this.camera;
    window.__frLights = lights;
    window.__frFloor = floor;
    window.__frGroundGlow = grad;
    window.__frHexLight = {
      on:     () => this.setHexLight(true),
      off:    () => this.setHexLight(false),
      toggle: () => this.setHexLight(!this.hexLightOn),
      bright: (lvl) => this.setHexBrightness(lvl), // one-call brightness, e.g. __frHexLight.bright(30)
      group:  this.hexLightGroup,
      area:   this.hexLightArea,   // reflected-streak light — .intensity
      lamp:   this.hexLightLamp,   // fill light — .intensity
    };
    window.__frTHREE = THREE;

    // Loaders
    // DRACO decoder served from the SAME CDN we load three.js from (jsdelivr).
    // The gstatic path can fail/be blocked on some stores, which surfaces as
    // "GLTFLoader: JSON content not found" when a Draco-compressed GLB can't be
    // decompressed. Matching the three@0.160.0 path keeps the decoder reachable.
    const draco = new DRACOLoader();
    draco.setDecoderConfig({ type: 'js' }); // JS fallback = most compatible
    draco.setDecoderPath('https://cdn.jsdelivr.net/npm/three@0.160.0/examples/jsm/libs/draco/');
    this.gltfLoader = new GLTFLoader();
    this.gltfLoader.setDRACOLoader(draco);

    // Materials
    this.bodyMaterial = new THREE.MeshPhysicalMaterial({
      color: '#f5d01f', metalness: 0.0, roughness: 0.30,
      clearcoat: 1.0, clearcoatRoughness: 0.045,
      envMapIntensity: 1.4,
    });
    // CHAMELEON/FLIP support — injected once. setPaint() just toggles the
    // uFlipAmt uniform (0 = normal paint, 1 = view-angle colour shift), so no
    // shader recompiles when switching paints.
    // uFlipMode: 0 = solid paint · 1 = 2-colour chameleon flip · 2 = 4-stop
    // iridescent ramp (Pink Pearl psychedelic, Burnt Titanium, Oil Slick, Burnt
    // Fire Orange). The ramp cycles ~2x across the view angle for rich movement.
    this.bodyMaterial.userData.flipUniforms = {
      uFlipMode: { value: 0 },
      uFlipA: { value: new THREE.Color('#000000') },
      uFlipB: { value: new THREE.Color('#000000') },
      uFlipC: { value: new THREE.Color('#000000') },
      uFlipD: { value: new THREE.Color('#000000') },
    };
    // SPLIT PAINT — uniform-driven two-tone split plane. SAME shader as the Split
    // Lab so authored looks bake straight in. Plane is evaluated in WORLD space; the
    // render loop refreshes uPlaneP/uPlaneN each frame so it stays glued as the car
    // turns. Off (uSplitOn=0) by default → normal single-colour paint.
    this.bodyMaterial.userData.splitUniforms = {
      uSplitOn: { value: 0 },
      uColA:    { value: new THREE.Color('#ffffff') },
      uColB:    { value: new THREE.Color('#000000') },
      uPlaneP:  { value: new THREE.Vector3() },
      uPlaneN:  { value: new THREE.Vector3(1, 0, 0) },
      uBlend:   { value: 0.02 },
      uFlipSplit: { value: 0 },
      uRoughA:  { value: 0.1 },
      uRoughB:  { value: 0.1 },
      // PEARLESCENT per split side — when a side wears a pearl colour, its diffuse
      // shifts toward the grazing colour at glancing angles (fresnel), like real
      // interference paint. 0 = plain solid side. (user 2026-06-29: pearl in the split)
      uPearlA:  { value: 0 },
      uPearlB:  { value: 0 },
      uPearlGrazeA: { value: new THREE.Color('#ffffff') },
      uPearlGrazeB: { value: new THREE.Color('#ffffff') },
      // CENTRELINE CARBON SPLIT — separate from the front/rear two-tone paint split.
      // When on, one lateral half keeps the weave (carbon), the other half is uColB
      // (the selected base paint). Plane normal = the car's lateral (left-right) axis.
      uCarbonSplit: { value: 0 },
      uCarbonP:     { value: new THREE.Vector3() },
      uCarbonN:     { value: new THREE.Vector3(1, 0, 0) },
      // CARBON WEAVE — generated PROCEDURALLY (triplanar, object-space) because the body
      // mesh has NO UVs, so a texture map can't show. uCarbonOn gates it.
      uCarbonOn:    { value: 0 },
      uCarbonMap:   { value: null },                        // the REAL carbon weave texture (set on first apply)
      uCarbonTint:  { value: new THREE.Color('#c8c8d0') },  // raw carbon = light wash over the dark weave; a colour tints it
      uCarbonScale: { value: 118.0 },                       // baked from the Weave Lab
      uCarbonBright:{ value: 4.5 },                         // lift the dark carbon so it doesn't read black under low exposure
      uWeaveContrast:{ value: 0.8 },                        // weave contrast
      uCarbonTinted:{ value: 0 },                           // 1 = vivid candy colour (modulated weave); 0 = raw graphite
      uCarbonBodyDark:{ value: 0.76 },                      // FULL-BODY carbon darken (deep candy look); two-tone paint-shift stays bright
      uCarbonMatte: { value: 0 },                           // 1 = matte weave (raises the per-fragment tow roughness)
      uMirrorOn:    { value: 1 },                           // 1 = book-match the weave at the centreline
      uWeaveDir:    { value: -1 },                          // chevron direction: -1 points one way, +1 the other (tunable)
      uMirrorCenter:{ value: new THREE.Vector3() },         // object-space lateral centre — the centreline seam
      uMirrorAxis:  { value: new THREE.Vector3(1, 0, 0) },  // object-space lateral (left-right) axis
    };
    this.bodyMaterial.onBeforeCompile = (shader) => {
      Object.assign(shader.uniforms, this.bodyMaterial.userData.flipUniforms);
      Object.assign(shader.uniforms, this.bodyMaterial.userData.splitUniforms);
      // world-space position varying for the split plane + OBJECT-space pos/normal for
      // the triplanar carbon weave (the body mesh has no UVs, so we project in 3D).
      shader.vertexShader = 'varying vec3 vWorldPosFR;\nvarying vec3 vObjPosFR;\nvarying vec3 vObjNrmFR;\n' + shader.vertexShader;
      shader.vertexShader = shader.vertexShader.replace(
        '#include <project_vertex>',
        '#include <project_vertex>\n  vWorldPosFR = (modelMatrix * vec4(transformed, 1.0)).xyz;\n  vObjPosFR = position;\n  vObjNrmFR = normalize(normal);'
      );
      shader.fragmentShader =
        'uniform float uFlipMode;\nuniform vec3 uFlipA;\nuniform vec3 uFlipB;\nuniform vec3 uFlipC;\nuniform vec3 uFlipD;\n' +
        'uniform float uSplitOn,uBlend,uFlipSplit,uRoughA,uRoughB,uPearlA,uPearlB,uCarbonSplit,uCarbonOn,uCarbonScale,uCarbonBright,uCarbonTinted,uCarbonBodyDark,uMirrorOn,uWeaveDir,uWeaveContrast,uCarbonMatte;\nuniform vec3 uColA,uColB,uPearlGrazeA,uPearlGrazeB,uPlaneP,uPlaneN,uCarbonP,uCarbonN,uCarbonTint,uMirrorCenter,uMirrorAxis;\nuniform sampler2D uCarbonMap;\nvarying vec3 vWorldPosFR;\nvarying vec3 vObjPosFR;\nvarying vec3 vObjNrmFR;\n' +
        // Procedural 2/2 twill carbon-fibre tow. Returns weave luminance; `gl` = tow
        // highlight (1 at the glossy crest) for the roughness stage.
        'float frCarbonCell(vec2 p, out float gl){\n' +
        '  vec2 id = floor(p); vec2 f = fract(p);\n' +
        '  bool over = mod(id.x + id.y, 4.0) < 2.0;\n' +
        '  float along = over ? (f.x + f.y)*0.5 : (f.x + 1.0 - f.y)*0.5;\n' +
        '  float hi = smoothstep(0.0,0.5,along)*smoothstep(1.0,0.5,along);\n' +
        '  float across = over ? (f.x - f.y) : (f.x + f.y - 1.0);\n' +
        '  float fil = 0.5 + 0.5*cos(across*42.0);\n' +
        '  gl = hi; return mix(0.035, 0.52, hi) * mix(0.8, 1.0, fil);\n' +
        '}\n' +
        shader.fragmentShader;
      // CARBON WEAVE (triplanar, object-space) — drawn BEFORE the split logic so a
      // centreline split mixes real woven carbon on one half and paint on the other.
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <map_fragment>',
        `#include <map_fragment>
        float frCarbonGloss = 0.0, frWeaveLum = 1.0;
        if (uCarbonOn > 0.5) {
          // TRIPLANAR sample of the REAL carbon weave (object-space; body has no UVs)
          vec3 sp = vObjPosFR;
          // BOOK-MATCH — cut the weave at the centreline and flip the far half, so the
          // diagonal tows MIRROR and meet in a chevron down the middle (front→rear).
          if (uMirrorOn > 0.5) {
            vec3 mAx = normalize(uMirrorAxis);
            float dM = dot(sp - uMirrorCenter, mAx);
            sp -= mAx * 2.0 * max(dM, 0.0);
            if (uWeaveDir < 0.0) sp.z = -sp.z;   // flip the chevron to point the other way (toward the front)
          }
          vec3 n = abs(normalize(vObjNrmFR));
          vec3 bw = n / max(n.x + n.y + n.z, 1e-4);
          float sc = uCarbonScale;
          vec3 tx = texture2D(uCarbonMap, sp.yz * sc).rgb * bw.x
                  + texture2D(uCarbonMap, sp.xz * sc).rgb * bw.y
                  + texture2D(uCarbonMap, sp.xy * sc).rgb * bw.z;
          tx = pow(max(tx, 0.0), vec3(2.2)) * uCarbonBright;     // sRGB->linear + lift the dark weave
          tx = max((tx - 0.16) * uWeaveContrast + 0.16, 0.0);    // mild contrast → tows read a touch more (pattern unchanged)
          frWeaveLum = clamp(dot(tx, vec3(0.299, 0.587, 0.114)), 0.0, 3.0);
          frCarbonGloss = smoothstep(0.05, 0.6, frWeaveLum);     // bright tow crests = glossier
          // FULL-BODY carbon is toned a bit darker (deep candy look, like real lacquered
          // carbon); the two-tone paint-shift branch below keeps its brighter look.
          if (uCarbonTinted > 0.5) diffuseColor.rgb = uCarbonTint * (0.35 + 1.6 * frWeaveLum) * uCarbonBodyDark; // VIVID candy carbon
          else diffuseColor.rgb = tx * uCarbonTint * uCarbonBodyDark;   // raw graphite carbon
        }
        float tSplitFR = 0.0;
        if (uSplitOn > 0.5) {
          float sdFR = dot(vWorldPosFR - uPlaneP, normalize(uPlaneN));
          tSplitFR = smoothstep(-uBlend, uBlend, sdFR);
          if (uFlipSplit > 0.5) tSplitFR = 1.0 - tSplitFR;
          // carbon ON → two-tone CARBON (vivid weave tinted per side); else regular two-tone paint
          vec3 splitCol = mix(uColA, uColB, tSplitFR);
          diffuseColor.rgb = (uCarbonOn > 0.5) ? (vec3(0.35 + 1.6 * frWeaveLum) * splitCol) : splitCol;
        }`
      );
      // per-side gloss + carbon tow gloss
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <roughnessmap_fragment>',
        `#include <roughnessmap_fragment>
        if (uSplitOn > 0.5) roughnessFactor = mix(uRoughA, uRoughB, tSplitFR);
        if (uCarbonOn > 0.5) roughnessFactor = (uCarbonMatte > 0.5) ? mix(0.86, 0.62, frCarbonGloss) : mix(0.30, 0.05, frCarbonGloss);`
      );
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <normal_fragment_begin>',
        `#include <normal_fragment_begin>
        if (uFlipMode > 0.5) {
          float fdot = clamp(abs(dot(normalize(vViewPosition), normal)), 0.0, 1.0);
          if (uFlipMode > 1.5) {
            float t = fract(pow(1.0 - fdot, 1.35) * 2.0);   // 2 cycles across the angle
            vec3 c;
            if (t < 0.3333) c = mix(uFlipA, uFlipB, t / 0.3333);
            else if (t < 0.6666) c = mix(uFlipB, uFlipC, (t - 0.3333) / 0.3333);
            else c = mix(uFlipC, uFlipD, (t - 0.6666) / 0.3334);
            diffuseColor.rgb = mix(diffuseColor.rgb, c, 0.9);
          } else {
            float _f = pow(1.0 - fdot, 2.5);
            diffuseColor.rgb = mix(uFlipA, uFlipB, _f);
          }
        }
        // PEARLESCENT split side — shift each side's diffuse toward its grazing colour
        // at glancing angles (interference look). Per-side, gated by uPearlA/uPearlB;
        // skipped while carbon owns the body. tSplitFR (from <map_fragment>) = side mix.
        if (uSplitOn > 0.5 && uCarbonOn < 0.5 && (uPearlA + uPearlB) > 0.5) {
          float pdot = clamp(abs(dot(normalize(vViewPosition), normal)), 0.0, 1.0);
          float pfres = pow(1.0 - pdot, 2.2);
          vec3 graze = mix(uPearlGrazeA, uPearlGrazeB, tSplitFR);
          float pamt = mix(uPearlA, uPearlB, tSplitFR);
          diffuseColor.rgb = mix(diffuseColor.rgb, graze, pfres * pamt * 0.85);
        }`
      );
    };
    this.glassMaterial = new THREE.MeshPhysicalMaterial({
      color: 0x000000, metalness: 0.0, roughness: 0.0, clearcoat: 1.0, clearcoatRoughness: 0.0, transmission: 0.0, ior: 1.5, envMapIntensity: 4.0, // LAB SPEC: opaque gloss-black glass
    });
    this.wheelMaterial = new THREE.MeshPhysicalMaterial({
      color: 0x8a5a2b, metalness: 0.9, roughness: 0.2, clearcoat: 0.5,
    });
    this.calliperMaterial = new THREE.MeshPhysicalMaterial({
      color: 0xc41030, metalness: 1.0, roughness: 0.19, clearcoat: 0.0, ior: 1.32, envMapIntensity: 4.0, // LAB SPEC
    });
    this.centerCapMaterial = new THREE.MeshPhysicalMaterial({
      color: 0xff0000, metalness: 1.0, roughness: 0.0, envMapIntensity: 4.0, // LAB SPEC: mirror-red caps
    });
    // Brake disk / rotor — its OWN material, recolored independently of the
    // rim and caliper via setDiskColor(). Defaults to brushed silver steel.
    this.diskMaterial = new THREE.MeshPhysicalMaterial({
      color: 0x787878, metalness: 1.0, roughness: 0.35, sheen: 1.0, envMapIntensity: 4.0, // LAB SPEC
    });

    // Car
    this.carGroup = new THREE.Group();
    this.carGroup.position.y = -0.4;
    this.scene.add(this.carGroup);
    this.bodyGroup = this._buildPlaceholderBody();
    this.carGroup.add(this.bodyGroup);
    this._buildWheels();
    this._buildCarLights();

    // Interaction
    this._setupInteraction();
    this._setupResize();
    this._animate();
  }

  _buildPlaceholderBody() {
    const group = new THREE.Group();
    const s = new THREE.Shape();
    s.moveTo(-2.0, 0); s.lineTo(-1.9, 0.15); s.lineTo(-1.4, 0.5);
    s.lineTo(-0.4, 0.7); s.lineTo(0.8, 0.55); s.lineTo(1.7, 0.3);
    s.lineTo(2.0, 0.12); s.lineTo(2.0, 0); s.lineTo(-2.0, 0);
    const g = new THREE.ExtrudeGeometry(s, { steps: 1, depth: 1.6, bevelEnabled: true, bevelThickness: 0.1, bevelSize: 0.1, bevelSegments: 6 });
    g.translate(0, 0, -0.8); g.computeVertexNormals();
    const body = new THREE.Mesh(g, this.bodyMaterial);
    body.castShadow = true; body.receiveShadow = true;
    group.add(body);
    const c = new THREE.Shape();
    c.moveTo(-0.7, 0); c.lineTo(-0.4, 0.35); c.lineTo(0.5, 0.35); c.lineTo(0.8, 0);
    const cg = new THREE.ExtrudeGeometry(c, { steps: 1, depth: 1.3, bevelEnabled: true, bevelThickness: 0.05, bevelSize: 0.05, bevelSegments: 4 });
    cg.translate(0, 0.55, -0.65);
    const cabin = new THREE.Mesh(cg, this.glassMaterial); cabin.castShadow = true;
    group.add(cabin);
    return group;
  }

  _buildWheels() {
    // Tire material promoted to the instance so tire prints can be applied to it.
    this.tireMaterial = new THREE.MeshStandardMaterial({ color: 0x080808, metalness: 0.1, roughness: 0.95 });
    this.tireSidewalls = []; // sidewall discs that carry the print texture (both outer faces of every wheel)
    const positions = [{x:1.25,z:0.55},{x:1.25,z:-0.55},{x:-1.25,z:0.55},{x:-1.25,z:-0.55}];
    positions.forEach(pos => {
      const w = new THREE.Group();
      const tg = new THREE.TorusGeometry(0.32, 0.13, 16, 32); tg.rotateY(Math.PI / 2);
      const tm = new THREE.Mesh(tg, this.tireMaterial); tm.castShadow = true; w.add(tm);
      // Sidewall disc on the outer (side-facing, +/-Z) face — the print lands here.
      // Default material is the bare blackwall; setTirePrint swaps in a canvas texture.
      const swGeo = new THREE.RingGeometry(0.30, 0.45, 48);
      [0.131, -0.131].forEach((zoff, i) => {
        const sw = new THREE.Mesh(swGeo, this._bareSidewallMat());
        sw.position.z = zoff;
        if (i === 1) sw.rotation.y = Math.PI; // face outward on the far side
        sw.renderOrder = 2;
        w.add(sw);
        this.tireSidewalls.push(sw);
      });
      const rg = new THREE.CylinderGeometry(0.26, 0.26, 0.16, 24); rg.rotateZ(Math.PI / 2);
      const rim = new THREE.Mesh(rg, this.wheelMaterial); rim.castShadow = true; w.add(rim);
      for (let i = 0; i < 5; i++) {
        const sp = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.42, 0.05), this.wheelMaterial);
        sp.rotation.x = (i / 5) * Math.PI * 2; rim.add(sp);
      }
      const cal = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.18, 0.1), this.calliperMaterial);
      cal.position.set(0, 0, 0.1); cal.visible = false; rim.add(cal); w.userData.calliper = cal;
      const capG = new THREE.CylinderGeometry(0.08, 0.08, 0.18, 16); capG.rotateZ(Math.PI / 2);
      const cap = new THREE.Mesh(capG, this.centerCapMaterial);
      cap.visible = false; rim.add(cap); w.userData.centerCap = cap;
      w.position.set(pos.x, 0.32, pos.z);
      this.carGroup.add(w); this.wheelMeshes.push(w);
    });
  }

  // Plain blackwall sidewall (no print).
  _bareSidewallMat() {
    return new THREE.MeshStandardMaterial({ color: 0x0a0a0a, metalness: 0.1, roughness: 0.95 });
  }

  registerSlots(parts) {
    parts.forEach(p => {
      const slot = new THREE.Group();
      slot.position.set(p.slot.x, p.slot.y, p.slot.z);
      slot.visible = false;
      this.carGroup.add(slot);
      this.partSlots.set(p.id, slot);
    });
  }

  togglePart(partId, enabled) {
    // Combined GLB path: show/hide the real pre-positioned mesh
    if (this._combinedLoaded) { this.toggleCombinedPart(partId, enabled); return; }
    const slot = this.partSlots.get(partId);
    if (!slot) return;
    slot.visible = enabled;
    if (enabled && !this.activeParts.has(partId)) {
      const placeholder = new THREE.Mesh(
        new THREE.BoxGeometry(0.18, 0.06, 0.18),
        new THREE.MeshStandardMaterial({ color: 0xd6253d, metalness: 0.6, roughness: 0.4, emissive: 0xd6253d, emissiveIntensity: 0.15 })
      );
      placeholder.castShadow = true;
      slot.add(placeholder);
      this.activeParts.set(partId, placeholder);
    }
  }

  async loadPartGLB(partId, url) {
    const slot = this.partSlots.get(partId);
    if (!slot) return;
    const gltf = await this.gltfLoader.loadAsync(url);
    const model = gltf.scene;
    model.traverse(c => {
      if (c.isMesh) {
        c.castShadow = true; c.receiveShadow = true;
        const f = this.partFinishes.get(partId);
        if (f) c.material = this._makeFinishMaterial(f);
      }
    });
    const existing = this.activeParts.get(partId);
    if (existing) slot.remove(existing);
    slot.add(model);
    this.activeParts.set(partId, model);
  }

  async loadBodyGLB(url) {
    const gltf = await this.gltfLoader.loadAsync(url);
    const model = gltf.scene;
    model.traverse(c => {
      if (c.isMesh) { c.castShadow = true; c.receiveShadow = true; c.material = this.bodyMaterial; }
    });
    this.carGroup.remove(this.bodyGroup);
    this.bodyGroup = model;
    this.carGroup.add(this.bodyGroup);
  }

  // Load the single combined Car_Parts GLB. It's modeled at ~250-unit scale,
  // so we auto-fit it into the scene's ~2-unit working space, recenter it on
  // the floor, and tag meshes by name:
  //   body  (mclaren*)  → paint material (recolors with the paint picker)
  //   tires (Tire*)     → dark rubber; sidewall is the modeled Text mesh
  //   Text              → kept as the physical wordmark (light metallic)
  //   rims  (Cylinder*) → wheel-finish material (recolors with finish picker)
  // Replaces the placeholder body + placeholder wheels.
  async loadFullCar(url) {
    const gltf = await this.gltfLoader.loadAsync(url);
    const model = gltf.scene;

    // Materials we reuse so the existing pickers keep working
    const tireMat = new THREE.MeshStandardMaterial({ color: 0x0a0a0b, metalness: 1.0, roughness: 0.37, envMapIntensity: 4.0 }); // LAB SPEC 2026-06-24 (user "keep metallic"): metallic tyre
    const textMat = new THREE.MeshStandardMaterial({ color: 0xe8e9ee, emissive: 0x111111, metalness: 0.6, roughness: 0.35 });

    this.realBodyMeshes = [];
    this.realRimMeshes = [];
    this.realTireMeshes = [];
    this.realTextMeshes = [];

    model.traverse(c => {
      if (!c.isMesh) return;
      c.castShadow = true; c.receiveShadow = true;
      const n = (c.name || c.parent?.name || '').toLowerCase();
      if (n.startsWith('mclaren')) {
        c.material = this.bodyMaterial; this.realBodyMeshes.push(c);
      } else if (n.startsWith('tire')) {
        c.material = tireMat; this.realTireMeshes.push(c);
      } else if (n.startsWith('text')) {
        c.material = textMat; this.realTextMeshes.push(c);
      } else if (n.startsWith('cylinder')) {
        c.material = this.wheelMaterial; this.realRimMeshes.push(c);
      } else {
        // cubes / calipers / clutter → caliper material as a neutral default
        c.material = this.calliperMaterial;
      }
    });

    // Auto-fit: scale + recenter so the car sits in the same volume as the
    // placeholder did (≈3.2 units long, sitting on the floor at carGroup y).
    const box = new THREE.Box3().setFromObject(model);
    const size = new THREE.Vector3(); box.getSize(size);
    const center = new THREE.Vector3(); box.getCenter(center);
    const maxDim = Math.max(size.x, size.y, size.z) || 1;
    const targetLength = 3.4;                 // desired longest dimension in scene units
    const k = targetLength / maxDim;

    const fit = new THREE.Group();
    fit.name = 'realcar-fit';
    fit.scale.setScalar(k);
    // recenter on origin, then drop so the bottom rests near the floor
    fit.position.set(-center.x * k, -center.y * k + (size.y * k) / 2 - 0.4, -center.z * k);
    fit.add(model);

    // Orient: model's long axis is Z in local space → rotate so length runs
    // along +X (our FRONT). If it looks sideways we just tweak this one value.
    model.rotation.y = Math.PI / 2;

    // Swap out placeholders
    this.carGroup.remove(this.bodyGroup);
    if (this.wheelGroup) this.carGroup.remove(this.wheelGroup);
    this.bodyGroup = fit;
    this.carGroup.add(fit);

    // Re-apply current selections so the loaded car reflects the build
    if (this._currentPaint) this.setPaint(this._currentPaint);
    if (this.wheelMaterial && this._currentWheelFinish) this.setWheelFinish(this._currentWheelFinish);

    this._realCarLoaded = true;
    return model;
  }

  // ============================================================
  // COMBINED CAR — single merged GLB containing the base car + every
  // accessory part + the wheel set, all already positioned by Pranav in
  // one shared coordinate space. We index every part node by name, match
  // it to its FR_BODY_PARTS id by keyword, and hide all parts at start so
  // the car loads bare. togglePart() then just shows/hides the real,
  // pre-positioned mesh — no slot guessing.
  //
  // Tune live (then tell me the numbers to bake in):
  //   __frPartMove('flr-17', { x:0, y:0.1, z:0, mirror:false, scale:1 })
  // ============================================================
  async loadCombinedCar(url, onProgress) {
    const gltf = await this.gltfLoader.loadAsync(url, onProgress);
    const model = gltf.scene;

    // Index every top-level part node by its name
    this.combinedNodes = new Map();   // name → THREE node
    this.partNodeById = new Map();     // FR_BODY_PARTS id → node (matched below)
    let baseCarNode = null;

    model.traverse(c => {
      if (c.isMesh) {
        c.castShadow = true; c.receiveShadow = true;
        // The GLB ships the body with normals but EVERY add-on part and wheel
        // mesh (~61 of them) has NO vertex normals — so lighting can't be
        // computed and they render pure black. Generate smooth normals for any
        // mesh missing them so parts/wheels light correctly. THIS is the fix for
        // "everything looks black".
        if (c.geometry && !c.geometry.attributes.normal) {
          // These add-on/part meshes ship with NO normals AND are exported with
          // split (un-welded) vertices, so a plain computeVertexNormals() leaves
          // them faceted/"rugged" (visible on the hoods + widebody). Weld the
          // coincident vertices first, then compute normals — that averages
          // across shared faces and gives the smooth CAD surface back.
          try {
            const merged = mergeVertices(c.geometry, 1e-4);
            if (merged) { merged.computeVertexNormals(); c.geometry = merged; }
            else c.geometry.computeVertexNormals();
          } catch (e) { c.geometry.computeVertexNormals(); }
        }
      }
    });

    // Keyword map: FR_BODY_PARTS id → words to look for in the GLB node name.
    // Pranav's names are descriptive (e.g. "6_ Massive Front Splitter – Race X").
    const KW = {
      'flr-01': ['massive front splitter', 'front splitter'],
      'flr-02': ['compact', 'front lip'],
      'flr-03': ['dive plane', 'mini canard'],
      'flr-04': ['headlight cover', 'headlamp', 'vortex headlight'],
      'flr-05': ['under car front splitter', 'underbody', 'under car'],
      'flr-22': ['front window canards', 'window canard'],
      'flr-06': ['roof scoop', 'intake scoop', 'air intake'],
      'flr-07': ['roof aero clips', 'roof fin', 'roof aero'],
      'flr-08': ['front over tire hood vents', 'hood vents', 'hood overpiece'],
      'flr-09': ['sunshade', 'sun shade'],
      'flr-10': ['antenna'],
      'flr-23': ['storage box', 'up storage', 'down storage'],
      'flr-11': ['side skirts', 'side skirt'],
      'flr-12': ['quarter panel', 'door-side vent', 'door side vent'],
      'flr-13': ['widebody', 'wide stance', 'ultra stance'],
      'flr-14': ['back over tire hood vents', 'fender', 'extractor'],
      'flr-24': ['side bulk'],
      'flr-15': ['rear aero', '765lt style rear'],
      'flr-16': ['diffuser – extended', 'extended race', 'diffuser'],
      'flr-17': ['gt wing', 'aggressive gt wing', 'add-on gt wing'],
      'flr-18': ['strut diffuser', 'license plate diffuser'],
      'flr-19': ['rectangular exhaust', 'duo exhaust', 'exhaust tip'],
      'flr-20': ['oval exhaust', 'exhaust cover'],
      'flr-21': ['tail light overlay', 'tail lamp', 'taillight'],
      'flr-25': ['tow hook'],
    };

    const norm = (s) => (s || '').toLowerCase().replace(/[_\-–]/g, ' ').replace(/\s+/g, ' ').trim();

    // ============================================================
    // V2 GROUPED MODEL. Root "Scene Collection" holds named groups:
    //   Body · WHEELS — FULL SET OF 4 · Actual_Wheel · BODY ACCESSORY PARTS · MIRRORED PARTS
    // Treat each group correctly (vs the old flat "every child is a part" scan).
    // ============================================================
    const findGroup = (re) => { let g = null; model.traverse(n => { if (!g && !n.isMesh && re.test(norm(n.name))) g = n; }); return g; };
    const bodyGrp   = findGroup(/^body$/);
    const wheelsGrp = findGroup(/full set of 4|wheels full set/);
    const stockGrp  = findGroup(/^actual wheel$/);
    const accGrp    = findGroup(/body accessory parts/);
    const mirrorGrp = findGroup(/mirrored parts/);

    // ---- BODY: painted panels + glass + separated lights + logo ----
    this._lightMeshes = [];        // { mesh, type:'front'|'rear' } for the glow feature
    this._glassMeshes = []; this._logoMeshes = []; this._bodyPanelMeshes = []; this._spoilerMeshes = [];
    this._areaMeshes = {}; this._areaMats = {}; // per-area trim/glass painting (windshield, doors, mirrors…)
    // Lights = milky frosted translucent plastic (same look as the exhaust); emissive kept at 0 so the glow toggle can light them later
    // OPAQUE milky lens — was transmission:0.5 + transparent:true, but a transmissive
    // material renders in three's separate transmission pass which samples the PRIOR
    // frame, so the headlight visibly LAGGED a frame behind the body when the car
    // rotated. Dropping transmission/transparent renders it in the normal opaque pass
    // (no lag). Still reads as a frosted white lens + still glows via emissive when lit.
    if (!this._headlightMat) this._headlightMat = new THREE.MeshPhysicalMaterial({ color: 0xd6d6d6, metalness: 0.2, roughness: 0.12, transmission: 0.06, transparent: true, clearcoat: 0.25, clearcoatRoughness: 0.04, ior: 1.5, envMapIntensity: 4.0, emissive: 0xfff0d0, emissiveIntensity: 0 }); // LAB SPEC 2026-06-24
    if (!this._taillightMat) this._taillightMat = new THREE.MeshPhysicalMaterial({ color: 0xffffff, metalness: 0, roughness: 0.5, transmission: this._isMobile ? 0 : 0.9, thickness: 0.6, ior: 1.45, attenuationColor: 0xff2030, attenuationDistance: 1.2, transparent: !this._isMobile, opacity: 1, clearcoat: 0.3, clearcoatRoughness: 0.4, emissive: 0xff1424, emissiveIntensity: 0.6 }); // milky lens glowing red (mobile: opaque, no transmission pass)
    if (!this._logoMat)        this._logoMat        = new THREE.MeshStandardMaterial({ color: 0xf2f4f8, metalness: 0.2, roughness: 0.35 }); // WHITE wordmark text
    if (!this._logoEmblemMat)  this._logoEmblemMat  = new THREE.MeshStandardMaterial({ color: 0xff2008, metalness: 0.35, roughness: 0.3, emissive: 0xff2008, emissiveIntensity: 0.18 }); // fire-red flame emblem
    // Gloss-black accent for trim / diffuser / spoiler / mirrors (NOT body-painted)
    if (!this._blackAccentMat) this._blackAccentMat = new THREE.MeshPhysicalMaterial({ color: 0x050506, metalness: 1.0, roughness: 0.53, clearcoat: 1.0, clearcoatRoughness: 0.0, ior: 1.0, envMapIntensity: 4.0 }); // LAB SPEC 2026-06-24
    // Milky frosted translucent plastic for the exhaust
    if (!this._exhaustMat) this._exhaustMat = new THREE.MeshPhysicalMaterial({ color: 0xffffff, metalness: 0, roughness: 0.0, transmission: this._isMobile ? 0 : 0.79, thickness: 0.6, ior: 1.5, attenuationColor: 0xffffff, attenuationDistance: 2.5, transparent: !this._isMobile, opacity: 1, clearcoat: 0.0, clearcoatRoughness: 0.5, envMapIntensity: 4.0, emissive: 0xffffff, emissiveIntensity: 0 }); // LAB SPEC 2026-06-24 (mobile: opaque)
    // Grafted TOP rear lights — own milky-frosted material so their color is
    // independent from the exhaust. Lower transmission than the exhaust so the LED
    // colour reads SOLID + deep (not washed pale) when lit.
    // MILKY DIFFUSED PLASTIC lens (like the exhaust): translucent + soft when OFF,
    // tinted + glowing the LED colour when ON. Low clearcoat/env so the studio HDRI
    // doesn't wash the colour pale.
    if (!this._topLightMat) this._topLightMat = new THREE.MeshPhysicalMaterial({ color: 0xeef1f4, metalness: 0, roughness: 0.5, transmission: this._isMobile ? 0 : 0.45, thickness: 0.5, ior: 1.42, transparent: !this._isMobile, opacity: 1, clearcoat: 0.12, clearcoatRoughness: 0.5, envMapIntensity: 0.25, emissive: 0xffffff, emissiveIntensity: 0 }); // mobile: opaque, no transmission pass
    if (bodyGrp) bodyGrp.traverse(c => {
      if (!c.isMesh) return;
      c.castShadow = true; c.receiveShadow = true;
      const nm = norm(c.name);
      if (/glass/.test(nm))            { c.material = this.glassMaterial; this._glassMeshes.push(c); (this._areaMeshes.windshield = this._areaMeshes.windshield || []).push(c); }
      else if (/front light/.test(nm)) { c.material = this._headlightMat; this._lightMeshes.push({ mesh: c, type: 'front' }); }
      else if (/rear light/.test(nm))  { c.material = this._taillightMat; this._lightMeshes.push({ mesh: c, type: 'rear' }); } // milky RED lower lamps
      else if (/emblem|flameracer.*001/.test(nm)) { c.material = this._logoEmblemMat; this._logoMeshes.push(c); } // fire-red flame emblem (designer's split mesh — "FlameRacer.001")
      else if (/flameracer/.test(nm))  { c.material = this._logoMat; this._logoMeshes.push(c); }       // white wordmark text
      else if (/exhaust/.test(nm))     { c.material = this._exhaustMat; c.castShadow = false; }
      else if (/diffuser|spoiler|mirror|\btrim\b|bumper|\bdoors?\b|\bbuttons?\b|\broof\b/.test(nm)) { c.material = this._blackAccentMat; if (/spoiler/.test(nm)) this._spoilerMeshes.push(c); const _a = /\bdoors?\b/.test(nm) ? 'doors' : /mirror/.test(nm) ? 'mirrors' : /spoiler/.test(nm) ? 'spoiler' : /diffuser/.test(nm) ? 'diffuser' : /bumper/.test(nm) ? 'bumper' : /\broof\b/.test(nm) ? 'roof' : 'trim'; (this._areaMeshes[_a] = this._areaMeshes[_a] || []).push(c); } // fixed gloss-black accents (bucketed by area for per-area painting)
      else { c.material = this.bodyMaterial; this._bodyPanelMeshes.push(c); } // painted body panel
    });
    // SAFETY NET — reclaim a mis-named flame emblem. When the designer splits the
    // logo into wordmark + flame but leaves the flame mis-named (e.g.
    // "_lower_diffuser001"), it falls into the black bucket above. If a SMALL,
    // black-accent mesh sits right on top of the FlameRacer wordmark, it's the
    // emblem → paint it fire-red. Guards (small + black + co-located) keep the
    // real diffuser and the tail lights safe.
    if (bodyGrp && this._logoMeshes.length) {
      bodyGrp.updateMatrixWorld(true);
      const Box = THREE.Box3, V3 = THREE.Vector3;
      const wb = new Box(); this._logoMeshes.forEach(m => wb.expandByObject(m));
      const wc = wb.getCenter(new V3()); const ws = wb.getSize(new V3()).length() || 1e-4;
      bodyGrp.traverse(c => {
        if (!c.isMesh || c.material !== this._blackAccentMat) return;
        const b = new Box().setFromObject(c);
        if (b.getSize(new V3()).length() <= ws * 1.6 && b.getCenter(new V3()).distanceTo(wc) <= ws * 1.3) {
          c.material = this._logoEmblemMat; this._logoMeshes.push(c);
          const si = this._spoilerMeshes.indexOf(c); if (si >= 0) this._spoilerMeshes.splice(si, 1);
          console.log('[FLR] reclaimed mis-named logo emblem:', c.name);
        }
      });
    }
    baseCarNode = bodyGrp || model;  // body group = "base car" for fit/ground/paint/decals

    // ---- WHEELS (per-corner): collect by rim design and by corner (FL/FR/RL/RR) ----
    this.realRimMeshes = []; this.realTireMeshes = []; this._wheelTrackMeshes = [];
    this._wheelCorners = { FL: [], FR: [], RL: [], RR: [] };
    this.wheelRimById = new Map();
    this._wheelCapMeshes = []; this._wheelBrakeMeshes = []; this._wheelCaliperMeshes = [];
    this._wheelTireMeshes = []; this._stockRimMeshes = []; this._stockTireMeshes = [];
    const tireMat = new THREE.MeshStandardMaterial({ color: 0x0a0a0b, metalness: 1.0, roughness: 0.37, envMapIntensity: 4.0 }); // LAB SPEC 2026-06-24 (user "keep metallic"): metallic tyre
    const cornerOf = (nm) => { const m = nm.match(/\b(fl|fr|rl|rr)\b/) || nm.match(/^(fl|fr|rl|rr)/); return m ? m[1].toUpperCase() : null; };
    const rimBuckets = { Ring1: [], Ring2: [], Ring3: [], Ring4: [] };
    if (wheelsGrp) wheelsGrp.traverse(c => {
      if (!c.isMesh) return;
      c.castShadow = true;
      const nm = norm(c.name); const corner = cornerOf(nm);
      if (corner) this._wheelCorners[corner].push(c);
      this._wheelTrackMeshes.push(c);
      if (/tire/.test(nm))          { c.material = tireMat; this.realTireMeshes.push(c); this._wheelTireMeshes.push(c); }
      else if (/caliper/.test(nm))  { c.material = this.calliperMaterial; this._wheelCaliperMeshes.push(c); }
      else if (/break/.test(nm))    { c.material = this.diskMaterial; this._wheelBrakeMeshes.push(c); }
      else if (/cap/.test(nm))      { c.material = this.centerCapMaterial; this._wheelCapMeshes.push(c); }
      else { const rm = nm.match(/ring\s*([1-4])/); if (rm) rimBuckets['Ring' + rm[1]].push(c); c.material = this.wheelMaterial; this.realRimMeshes.push(c); }
    });
    if (stockGrp) stockGrp.traverse(c => {
      if (!c.isMesh) return;
      c.castShadow = true;
      const nm = norm(c.name); const corner = cornerOf(nm);
      if (corner) this._wheelCorners[corner].push(c);
      this._wheelTrackMeshes.push(c);
      if (/tire/.test(nm)) { c.material = tireMat; this._stockTireMeshes.push(c); this.realTireMeshes.push(c); }
      else { c.material = this.wheelMaterial; this._stockRimMeshes.push(c); this.realRimMeshes.push(c); }
    });
    this._rimBuckets = rimBuckets;
    (window.FR_WHEELS || []).forEach(w => {
      if (w.id === 'stock') this.wheelRimById.set('stock', this._stockRimMeshes.concat(this._stockTireMeshes));
      else if (w.rim && rimBuckets[w.rim]) this.wheelRimById.set(w.id, rimBuckets[w.rim]);
    });

    // ---- AERO ADD-ON parts for flr matching (the two part groups only) ----
    const partNodes = [];
    [accGrp, mirrorGrp].forEach(g => { if (g) g.children.forEach(n => partNodes.push(n)); });
    // Storage-box pieces (lid `Storage_box_Up_`, base `Storage_Box_Down`, legs
    // `Storage_box_Stand`) live at the SCENE ROOT, not in the part groups — so the
    // matcher never saw them and they defaulted to the GLB's baked visibility
    // (lid/base ON, stand OFF). Pull them in so flr-23 claims + toggles all three
    // together as ONE part, and so they start hidden like every other add-on.
    model.traverse(n => { if (n.isMesh && /storage/i.test(n.name) && !partNodes.includes(n)) partNodes.push(n); });

    // Match each FR_BODY_PARTS id to the first node whose name contains a keyword.
    // Also link its mirror twin: Pranav exported the other side as a "<name>_001"
    // node, so pair parts (canards, skirts, vents, etc.) have a matching _001.
    // We store the twin and show/recolor both together.
    // id → array of EXTRA nodes (mirror _001 twins and any explicit secondary
    // nodes) that toggle/recolor together with the primary matched node.
    this.partTwinsById = new Map();
    const used = new Set();
    // THREE's GLTFLoader rewrites SPACES in node names to underscores (e.g.
    // "...front splitter_ LOW" → "...front_splitter__LOW_"), while our `glbNode`
    // data stores the human names with spaces and trailing spaces trimmed.
    // Canonicalize both sides — whitespace runs → single "_", strip leading/
    // trailing "_", lowercase — so a declared exact name still matches the
    // loaded node. (Runs of underscores are NOT collapsed, so "4_Carbon" and
    // "4_ Carbon" stay distinct — needed for multi-piece parts like the widebody.)
    const canon = (s) => (s || '').toLowerCase().replace(/\s+/g, '_').replace(/^_+|_+$/g, '');
    const findExact = (name, claimed) => { const c = canon(name); return partNodes.find(n => !used.has(n) && !(claimed && claimed.has(n)) && canon(n.name) === c); };
    const findTwin = (matchNode) => {
      const base = norm(matchNode.name);
      // v2: the mirror twin lives in the MIRRORED PARTS group with a "_MIRRORED"
      // suffix (norm → " mirrored"); v1 used "_001". Match either.
      return partNodes.find(n => n !== matchNode && !used.has(n) && (norm(n.name) === base + ' mirrored' || norm(n.name) === base + ' 001'))
          || partNodes.find(n => n !== matchNode && !used.has(n) && norm(n.name).replace(/ (mirrored|001)$/, '') === base && / (mirrored|001)$/.test(norm(n.name)));
    };
    const claimAsPart = (node) => {
      node.visible = false;
      node.traverse(c => {
        if (!c.isMesh) return;
        // Crease-angle normals — SMOOTH across gently-curved faces, HARD across
        // sharp edges (vents/louvres), so the hood body smooths without the vent
        // detail "melting". Runs ONLY on demand (the first time a part is shown)
        // via _refinePartNormals, NOT during load — doing all 35 here froze the
        // renderer. Here we just assign the material.
        c.material = this._makePartMaterial({ id: 'matte-black' }); // parts default MATTE BLACK (was carbon)
      });
    };
    for (const part of (window.FR_BODY_PARTS || [])) {
      // deletion option: skip only when it has NO mesh of its own. 2026-07-30: Delete
      // Rear Spoiler now carries the designer's flush deck panel via glbNode — match
      // it like any part (starts hidden) and toggleCombinedPart shows it when active.
      if (part.removeSpoiler && !part.glbNode) continue;
      let match = null;
      const extras = []; // secondary nodes shown/recolored with the primary
      // 1) EXACT node name(s) — deterministic, collision-free. `glbNode` may be a
      //    single exact name or an array (multi-piece part, e.g. up+down storage).
      if (part.glbNode) {
        const names = Array.isArray(part.glbNode) ? part.glbNode : [part.glbNode];
        const claimed = new Set(); const nodes = [];
        for (const nm of names) { const node = findExact(nm, claimed); if (node) { claimed.add(node); nodes.push(node); } }
        if (nodes.length) { match = nodes[0]; extras.push(...nodes.slice(1)); }
      }
      // 2) Fallback: keyword match against the node name.
      if (!match) {
        const kws = KW[part.id] || [norm(part.name)];
        for (const n of partNodes) {
          if (used.has(n)) continue;
          const nm = norm(n.name);
          if (nm.endsWith(' 001')) continue; // never match the mirror twin as the primary
          if (kws.some(k => nm.includes(norm(k)))) { match = n; break; }
        }
      }
      if (match) {
        used.add(match);
        this.partNodeById.set(part.id, match);
        claimAsPart(match);
        extras.forEach(n => used.add(n)); // claim explicit secondaries before twin search
        // auto-link the mirror _001 twin so both sides toggle/recolor together
        const twin = findTwin(match);
        if (twin && !extras.includes(twin)) extras.push(twin);
        extras.forEach(n => { used.add(n); claimAsPart(n); });
        if (extras.length) this.partTwinsById.set(part.id, extras);
      }
    }

    // REMOVED PARTS (FLR-19 Duo Rectangular Exhaust, FLR-30 765LT Rear Aero) —
    // taken out of FR_BODY_PARTS, so the matching loop no longer hides their GLB
    // meshes and they were showing by default. Force-hide them by node name.
    {
      const REMOVED = ['15_ Carbon Duo Exhaust Tip – Clip-On Edition', '11_765LT STYLE Rear Aero Pieces_FLR-CBRA001'];
      const normR = s => (s || '').toLowerCase().replace(/[_\s]+/g, ' ').replace(/[–-]/g, '-').trim();
      const want = REMOVED.map(normR);
      let hidn = 0;
      model.traverse(n => { if (n.isMesh && want.some(w => normR(n.name).includes(w) || w.includes(normR(n.name)))) { n.visible = false; hidn++; } });
      if (hidn) console.log('[FLR] force-hid', hidn, 'removed-part mesh(es)');
    }

    // WIDE STANCE (flr-13) — the new model split the widebody into per-corner
    // meshes (4_FL/FR/RL/RR…) and THREE strips the ".001" suffix, so exact glbNode
    // matching only caught some → the rest stayed VISIBLE by default. Robustly
    // claim EVERY /widebody/ mesh for flr-13: hide them all + register as one part
    // so the Wide Stance toggle shows/hides the full set together.
    {
      // HARDENED (2026-08-05): this fallback used to claim EVERY /widebody/ mesh,
      // which double-claimed the front canards (flr-70) before their nodes were
      // renamed — and would swallow any future "widebody"-named node too. Build
      // the set of nodes (and their subtrees) already registered to OTHER parts
      // by the explicit matching loop above, and only sweep up the strays.
      const claimedElsewhere = new Set();
      this.partNodeById.forEach((node, pid) => { if (pid !== 'flr-13' && node) claimedElsewhere.add(node); });
      this.partTwinsById.forEach((twins, pid) => { if (pid !== 'flr-13' && twins) twins.forEach(t => claimedElsewhere.add(t)); });
      const ownedByOther = n => { let p = n; while (p) { if (claimedElsewhere.has(p)) return true; p = p.parent; } return false; };
      const wbNodes = [];
      model.traverse(n => { if (n.isMesh && /widebody/i.test(n.name || '') && !ownedByOther(n)) wbNodes.push(n); });
      if (wbNodes.length) {
        const primary = this.partNodeById.get('flr-13') || wbNodes[0];
        this.partNodeById.set('flr-13', primary);
        wbNodes.forEach(n => { used.add(n); n.visible = false; n.traverse(c => { if (c.isMesh) c.material = this._makePartMaterial({ id: 'matte-black' }); }); }); // matte black (was carbon)
        const extras = wbNodes.filter(n => n !== primary);
        if (extras.length) this.partTwinsById.set('flr-13', extras);
      }
    }

    // Wheels, body materials, glass/lights/logo, and aero parts were all set up
    // in the grouped section above (per-corner wheels, body keeps its CAD normals
    // untouched). Just finish the wheel/tyre state:
    this._trackCx = null; // wide-stance recomputes the track centre on demand
    try { this._setupTireUVs(model); } catch (e) {}
    this._showWheelRim((this._currentWheel && this._currentWheel.id) || 'stock');
    this._capEnabled = !!this._capEnabled;
    this._syncCapToWheel();

    // ---- FIT TO THE BASE CAR ONLY ----
    // The base car and the parts/wheels were exported at DIFFERENT scales, so
    // fitting the whole merged bbox shrinks the car. Instead we compute the
    // fit from the base car's bounds and apply it to the whole group; parts
    // then get per-group corrective scaling (below) to match the car.
    const fitTarget = baseCarNode || model;
    // Orient the car to its final heading BEFORE measuring. The fit below
    // re-centres the car on the carGroup spin axis using this bbox; if we
    // measure first and rotate after, the box centre is in the wrong frame
    // and the car's true centre ends up OFF the spin axis — so the turntable
    // orbits off-centre (the "oval / wobble" rotation the user reported).
    // Rotating first → measuring the rotated bbox → centring on it puts the
    // visual centre exactly on the axis → a clean circular turntable.
    model.rotation.y = Math.PI / 2;
    model.updateWorldMatrix(true, true);
    const box = new THREE.Box3().setFromObject(fitTarget);
    const size = new THREE.Vector3(); box.getSize(size);
    const center = new THREE.Vector3(); box.getCenter(center);
    const maxDim = Math.max(size.x, size.y, size.z) || 1;
    const k = 3.4 / maxDim;
    this._fitScale = k; // world units per local unit — used by setWideStance
    const fit = new THREE.Group();
    fit.name = 'realcar-fit';
    fit.scale.setScalar(k);
    fit.position.set(-center.x * k, -center.y * k + (size.y * k) / 2 - 0.4, -center.z * k);
    fit.add(model);

    // ---- REMOVE OLD PLACEHOLDERS (aggressive) ----
    // The old slot system added empty Groups + red BoxGeometry placeholders to
    // carGroup, and the old wheels are separate meshes. In combined mode we use
    // the real GLB meshes only, so fully REMOVE all of that — not just hide it.
    this.carGroup.remove(this.bodyGroup);
    if (this.wheelGroup) { this.carGroup.remove(this.wheelGroup); this.wheelGroup = null; }
    if (this.wheelMeshes) { this.wheelMeshes.forEach(w => { try { w.parent && w.parent.remove(w); } catch(e){} }); this.wheelMeshes = []; }
    if (this.activeParts) { this.activeParts.forEach(m => { try { m.parent && m.parent.remove(m); } catch(e){} }); this.activeParts.clear(); }
    // fully detach every placeholder slot group from the scene graph
    if (this.partSlots) { this.partSlots.forEach(s => { try { s.parent && s.parent.remove(s); } catch(e){} }); }
    // sweep carGroup for any stray non-fit children (leftover placeholder boxes/wheels)
    [...this.carGroup.children].forEach(ch => {
      if (ch !== fit) { try { this.carGroup.remove(ch); } catch(e){} }
    });
    // BULLETPROOF: walk the ENTIRE scene and remove every BoxGeometry mesh that
    // is NOT inside the real GLB (kills the red caliper + spoke placeholders and
    // any old part placeholder boxes, wherever they were parented).
    const toRemove = [];
    this.scene.traverse(o => {
      if (!o.isMesh || o.geometry?.type !== 'BoxGeometry') return;
      let n = o.parent, inFit = false;
      while (n) { if (n === fit || n === model) { inFit = true; break; } n = n.parent; }
      if (!inFit) toRemove.push(o);
    });
    toRemove.forEach(o => { try { o.parent && o.parent.remove(o); } catch(e){} });
    if (toRemove.length) console.log('[FLAMERACER] removed', toRemove.length, 'placeholder boxes');
    // Showroom reflection stays visible in combined mode (premium studio look);
    // it's re-grounded to the car's actual floor height a few lines below.

    this.bodyGroup = fit;
    this.combinedModel = model;
    this._baseCarNode = baseCarNode;
    this._partNodesArr = partNodes;
    this.carGroup.add(fit);

    // CAR HOME POSITION — tuned by the user in the Camera Tuning Station
    // (?camtune=1). Moving carGroup (not the fit) keeps the turntable centred
    // on the car's own axis, so the spin stays a clean circle. The camera
    // presets below were framed with the car at exactly this position.
    this.carGroup.position.set(-0.24, -0.56, -0.02);

    // GROUND THE CAR: drop the floor + contact shadow to the car's actual lowest
    // point (the wheels/splitter) so it sits ON the floor instead of sinking in.
    this.carGroup.updateWorldMatrix(true, true);
    const carBox = new THREE.Box3().setFromObject(fit);
    const groundY = carBox.min.y;
    const cgx = this.carGroup.position.x, cgz = this.carGroup.position.z;
    this._groundY = groundY;
    if (this.floor)        this.floor.position.y = groundY;
    if (this.shadowFloor)  this.shadowFloor.position.set(cgx, groundY + 0.006, cgz);
    if (window.__frGroundGlow) window.__frGroundGlow.position.set(cgx, groundY + 0.008, cgz);
    // Re-ground the showroom reflection to the real floor height (under the car).
    this._mirrorY = groundY;
    if (this.mirror) this.mirror.position.set(cgx, groundY + 0.001, cgz);

    // Rebuild the car-lights rig — the carGroup sweep above removed the one built
    // in the constructor. Now the real lamp lenses (_headlightMat/_taillightMat)
    // and groundY are available, so the lights land on the real car.
    this._buildCarLights();

    if (this._currentPaint) this.setPaint(this._currentPaint);

    const matched = this.partNodeById.size;
    console.log(`[FLAMERACER] combined car loaded — ${matched}/${(window.FR_BODY_PARTS||[]).length} parts matched by name (fit to base car)`);
    this._combinedLoaded = true;
    this._realCarLoaded = true;
    this.requestRender && this.requestRender(2500); // draw once the (heavy) car has landed
    // Graft on the front trim + real top rear lights (separate GLBs the designer
    // split out / we extracted from v4). Fire-and-forget so the car shows first.
    this._loadGraftParts();
    return model;
  }

  // ── GRAFT PARTS (always-on additions to the base car) ────────────────────
  // Front trim ("under bumper", extracted from the v4 file) + the REAL top rear
  // lights (Actual_top_rear_light_L/R from the new separated-parts drop). Both
  // are in the same GLB-native space as the body, so adding them under
  // combinedModel inherits the exact fit/rotation transform → they drop in
  // aligned, no manual positioning. Front trim = gloss-black accent; top lights
  // = glowing red taillight lens (added to _lightMeshes so they light with the
  // ignition). Run once per car load (loadCombinedCar wipes/rebuilds the graph).
  async _loadGraftParts() {
    if (!this.combinedModel) return;
    const base = (window.FR_CAR_GLB || '').split('?')[0].replace(/[^/]+$/, '');
    const specs = [
      { url: base + 'frpart-under-bumper.glb',   kind: 'trim',  name: 'under_bumper' },
      { url: base + 'frpart-lower-diffuser.glb', kind: 'trim',  name: 'lower_diffuser' },
      { url: base + 'frpart-toplight-l.glb',     kind: 'light', name: 'Actual_top_rear_light_L' },
      { url: base + 'frpart-toplight-r.glb',     kind: 'light', name: 'Actual_top_rear_light_R' },
    ];
    // The current model's _lower_diffuser is the OLD fused honeycomb piece with the
    // rear lights BAKED IN — it z-fights the separately-grafted top lights. Hide it
    // (BEFORE adding grafts, so the new lights-free diffuser below isn't matched);
    // the new gloss-black diffuser + grafted lights replace it cleanly.
    // Match ONLY the plain diffuser, NOT "_lower_diffuser001" (which is actually
    // the mis-named FlameRacer emblem) — anchor at end so the 001 emblem is spared.
    let hidOld = 0;
    this.combinedModel.traverse(o => {
      if (o.isMesh && /lower[\s_]*diffuser$/i.test(o.name || '')) { o.visible = false; o.userData._hiddenOldDiffuser = true; hidOld++; }
    });
    // Trim gets its OWN gloss-black material instance with a polygon offset so it
    // can sit flush against the body shell without z-fighting it.
    if (!this._graftBlackMat) {
      this._graftBlackMat = this._blackAccentMat.clone();
      this._graftBlackMat.polygonOffset = true;
      this._graftBlackMat.polygonOffsetFactor = -1.5;
      this._graftBlackMat.polygonOffsetUnits = -1.5;
    }
    this._graftRoots = this._graftRoots || [];
    for (const s of specs) {
      try {
        const gltf = await new Promise((res, rej) => this.gltfLoader.load(s.url, res, undefined, rej));
        const root = gltf.scene;
        root.name = 'graft-' + s.name;
        root.traverse(o => {
          if (!o.isMesh) return;
          if (s.kind === 'trim') { o.material = this._graftBlackMat; if (s.name === 'under_bumper') { this._areaMeshes = this._areaMeshes || {}; (this._areaMeshes.bumper = this._areaMeshes.bumper || []).push(o); } } // grafted front bumper → its own trim area
          else { o.material = this._topLightMat; } // milky frosted plastic, independently colourable from the exhaust
          o.renderOrder = 2;
          // The under-bumper's "square artifacts" are FLAT per-face normals on a
          // dense mesh (not a draco issue). Weld coincident verts + re-crease the
          // normals (smooth below ~36°, keep real hard edges) → smooth surfaces.
          if (s.name === 'under_bumper' && o.geometry && o.geometry.attributes.position) {
            try { o.geometry = toCreasedNormals(mergeVertices(o.geometry), Math.PI / 5); } catch (e) { try { o.geometry.computeVertexNormals(); } catch (_) {} }
          }
        });
        this.combinedModel.add(root);
        // UNDER-BUMPER PLACEMENT — the under_bumper graft is authored in the same
        // GLB-native space as the body (native rest: pos 0,0,0 / scale 1 / no rot),
        // so it drops in already aligned at the front of the car. We capture that
        // rest transform and apply NEUTRAL offsets by default (see
        // _applyUnderBumperOffset); the window hooks let the user fine-tune the fit
        // live by eye without any baked-in shift fighting the authored position.
        if (s.name === 'under_bumper') {
          this._underBumperRoot = root; // handle for live re-tuning
          this._underBumperBase = root.position.clone(); // GLB-native rest position
          this._underBumperScaleBase = root.scale.x || 1; // GLB-native rest scale
          this._applyUnderBumperOffset();
        }
        this._graftRoots.push(root);
      } catch (e) { console.warn('[FLR graft] failed', s.url, e?.message || e); }
    }
    // HI-RES RIM SWAP — the car bakes DECIMATED rims (Circuit ~4.4k, Vortex
    // ~25k, Nakamishi ~21k verts) vs the designer's standalone originals
    // (~28k / 160k / 131k). Graft the originals per corner (native-aligned like
    // the body) and swap them in for the low-poly car rim ONLY when that wheel
    // is selected (see _showWheelRim). They share this.wheelMaterial, so the
    // finish/colour follows automatically. (Razor is already hi-res in the car.)
    this._hiResRims = {};
    this._hiResMap = { circuit: 'ring1', vortex: 'ring3', nakamishi: 'ring4' };
    // PERF: eager-load only Circuit (~0.4MB — it's the hub-bolt reference and a
    // common rim). Vortex/Nakamishi (~2/1.5MB) load lazily on first selection
    // (_ensureHiResRim) so the initial payload isn't +4.8MB on mobile.
    await this._loadHiResRim('circuit');
    // Apply current rim visibility now that the hi-res meshes exist.
    if (this._currentWheel) this._showWheelRim(this._currentWheel.id);
    // Hub bolts — fill the open hub by default (removed when a cap is added).
    this._buildHubBolts && this._buildHubBolts();
    // If the active wheel is a heavy hi-res design, fetch it now.
    if (this._currentWheel && this._hiResMap[this._currentWheel.id]) this._ensureHiResRim(this._currentWheel.id);

    this.requestRender && this.requestRender(1500);
    console.log('[FLR graft] added ' + this._graftRoots.length + ' parts; hid ' + hidOld + ' old diffuser mesh(es)');
  }

  // UNDER-BUMPER PLACEMENT — the under_bumper graft is authored in the SAME
  // GLB-native space as the body, so its native rest transform (position 0,0,0 /
  // scale 1 / no rotation) already drops it in aligned at the front of the car.
  // Defaults below are therefore NEUTRAL (0 offset, scale 1): the bumper loads at
  // its true authored position. The window hooks exist purely for live nudging if
  // the user wants to fine-tune the fit by eye. Offsets are GLB-NATIVE local units
  // applied ABSOLUTELY from the stored rest base (not cumulative), so re-calling is
  // idempotent. The graft is parented under combinedModel ahead of the realcar-fit
  // scale `k`, so offsets ride the fit-scale automatically.
  //
  // Live tuning (paste in console, then it applies instantly):
  //   window.__frBumperX = 0.01;  window.__frCtl._applyUnderBumperOffset();  // +X = toward front
  //   window.__frBumperY = 0.01;  window.__frCtl._applyUnderBumperOffset();  // +Y = up
  //   window.__frBumperZ = 0.01;  window.__frCtl._applyUnderBumperOffset();  // +Z = lateral
  //   window.__frBumperScale = 1.02; window.__frCtl._applyUnderBumperOffset(); // grow/shrink
  // Back-compat aliases: __frBumperLift → +Y, __frBumperPush → -Z.
  _applyUnderBumperOffset() {
    const root = this._underBumperRoot, base = this._underBumperBase;
    if (!root || !base) return;
    const W = (typeof window !== 'undefined') ? window : {};
    const num = (v, d) => (v != null && isFinite(+v)) ? +v : d;
    // Per-axis absolute offsets (default 0 = native authored position).
    const OX = num(W.__frBumperX, 0);
    // +Y aliases __frBumperLift; +Z = lateral, and __frBumperPush tucks back (−Z).
    const OY = num(W.__frBumperY, num(W.__frBumperLift, 0));
    const OZ = num(W.__frBumperZ, -num(W.__frBumperPush, 0));
    const SC = num(W.__frBumperScale, num(this._underBumperScaleBase, 1));
    root.position.set(base.x + OX, base.y + OY, base.z + OZ);
    root.scale.setScalar(SC);
    this.requestRender && this.requestRender(1000);
  }

  // Show/hide a part by id using the real positioned mesh from the combined GLB.
  toggleCombinedPart(partId, enabled) {
    // Deletion option (e.g. Delete Rear Spoiler): no mesh to add — instead hide
    // the stock spoiler + struts while the option is selected.
    const partDef = (window.FR_BODY_PARTS || []).find(p => p.id === partId);
    if (partDef && partDef.removeSpoiler) {
      (this._spoilerMeshes || []).forEach(m => { m.visible = !enabled; });
      // 2026-07-30: if the deletion option carries a mesh (flush deck panel),
      // fall through so the generic path toggles it with the option.
      if (!this.partNodeById?.get(partId)) return true;
    }
    const node = this.partNodeById?.get(partId);
    if (!node) { console.warn('[FLR] no matched node for', partId); return false; }
    // Show/hide the part INSTANTLY. The crease-normal smoothing used to run here
    // synchronously and froze the press for a long time (worse now that the parts
    // are denser) — it's deferred to idle below so the press is snappy.
    node.visible = !!enabled;
    const extras = this.partTwinsById?.get(partId);
    if (extras) extras.forEach(n => { n.visible = !!enabled; }); // mirror/secondary pieces together
    // NOTE: we deliberately DON'T touch part normals anymore — parts now keep the
    // designer's own smooth normals (exactly like the body, which looks perfect).
    // This also removes the on-toggle lag entirely (no mergeVertices/crease pass).
    this.requestRender && this.requestRender(500);
    // Wide Stance package also pushes all 4 wheels out flush with the flares.
    if (partId === 'flr-13') this.setWideStance(!!enabled);
    return true;
  }

  // Defer crease-normal smoothing to idle, ONE mesh per tick, so toggling a part
  // never blocks the frame (the part appears immediately; it smooths a beat later).
  _scheduleRefine(nodes) {
    const meshes = [];
    nodes.forEach(n => n && n.traverse(c => { if (c.isMesh && !c.userData._creased && c.geometry && c.geometry.attributes.position) meshes.push(c); }));
    if (!meshes.length) return;
    const idle = window.requestIdleCallback || ((f) => setTimeout(f, 24));
    let i = 0;
    const step = () => {
      if (i >= meshes.length) return;
      const c = meshes[i++];
      if (!c.userData._creased) {
        try { const w = mergeVertices(c.geometry, 1e-5) || c.geometry; c.geometry = toCreasedNormals(w, Math.PI / 3.2); } catch (e) {}
        c.userData._creased = true;
        this.requestRender && this.requestRender(120); // draw the newly-smoothed mesh
      }
      idle(step);
    };
    idle(step);
  }

  // Crease-angle normals for ONE part node, on first show, cached. Smooths the
  // broad panel while keeping vent/louvre edges crisp (plain smoothing "melts"
  // them). Runs lazily per part so it never blocks the initial car load.
  _refinePartNormals(node) {
    if (!node) return;
    node.traverse(c => {
      if (!c.isMesh || c.userData._creased || !c.geometry || !c.geometry.attributes.position) return;
      try {
        const welded = mergeVertices(c.geometry, 1e-5) || c.geometry;
        c.geometry = toCreasedNormals(welded, Math.PI / 3.2); // ≈56° crease
      } catch (e) { /* keep original geometry on failure */ }
      c.userData._creased = true;
    });
  }

  // Live position/scale/mirror nudge for a matched part node.
  movePart(partId, o = {}) {
    const node = this.partNodeById?.get(partId);
    if (!node) return console.warn('[FLR] no node for', partId);
    if (o.x !== undefined) node.position.x = o.x;
    if (o.y !== undefined) node.position.y = o.y;
    if (o.z !== undefined) node.position.z = o.z;
    if (o.dx) node.position.x += o.dx;
    if (o.dy) node.position.y += o.dy;
    if (o.dz) node.position.z += o.dz;
    if (o.scale !== undefined) node.scale.setScalar(o.scale);
    if (o.mirror !== undefined) node.scale.z = Math.abs(node.scale.z) * (o.mirror ? -1 : 1);
    if (o.rotY !== undefined) node.rotation.y = o.rotY;
    node.visible = true;
    console.log('[FLR] moved', partId, 'pos', node.position.toArray().map(n=>+n.toFixed(3)));
  }

  setPartFinish(partId, finish) {
    this.partFinishes.set(partId, finish);
    // Combined GLB path: apply the SELECTED finish as a lit, shaded material so
    // parts respond to scene lighting (highlights/shadow) and reflect the studio.
    if (this._combinedLoaded) {
      const mat = this._makePartMaterial(finish);
      const paint = (n) => { if (!n) return; n.traverse(c => { if (c.isMesh) { c.material = mat; c.material.needsUpdate = true; } }); };
      paint(this.partNodeById?.get(partId));
      (this.partTwinsById?.get(partId) || []).forEach(paint);
      // If this part is currently joined to the body split, re-apply it over the new
      // finish (so changing a finish doesn't kick the part out of the split).
      if (this._splitParts && this._splitParts.has(partId)) this.setPartSplit(partId, true);
      return;
    }
    const model = this.activeParts.get(partId);
    if (!model) return;
    const mat = this._makeFinishMaterial(finish);
    model.traverse(c => { if (c.isMesh) c.material = mat; });
  }

  _makeFinishMaterial(f) {
    if (f.gradient) return this._makeGradientMaterial(f.gradient);
    return new THREE.MeshPhysicalMaterial({
      color: new THREE.Color(f.hex),
      metalness: f.metalness ?? 0.7,
      roughness: f.roughness ?? 0.25,
      clearcoat: f.id === 'matte-black' ? 0 : 1,
      clearcoatRoughness: 0.05,
    });
  }

  // Part finish material — LIT and shaded, driven by the studio environment.
  // No emissive lift any more: the scene is properly lit, so parts catch the
  // softbox highlights and respond to light/shadow like real painted/CF pieces.
  // (matte-black is genuinely dark by design — if you ever need it more visible
  // against the body in the UI, lift its color a few values, not via emissive.)
  _makePartMaterial(f) {
    // Translucent finishes. Crystal Blue = tinted glass; Clear Translucent =
    // true PLEXIGLASS / acrylic — fully see-through (transmission 1.0), zero haze
    // (roughness 0), acrylic IOR 1.49, thin + long attenuation so it barely tints.
    if (f && f.translucent) {
      const clear = f.id === 'clear-translucent';
      const cblue = f.id === 'crystal-blue';
      const mat = new THREE.MeshPhysicalMaterial({
        // MAXIMUM clarity: near-WHITE diffuse (a coloured diffuse blocks light and
        // looks opaque) + transmission 1.0 + thin + long attenuation. Tint comes
        // ONLY from attenuationColor (light bending through), so both read as
        // genuinely see-through glass/acrylic. Crystal Blue = clear blue.
        color: new THREE.Color((clear || cblue) ? '#ffffff' : f.hex), metalness: 0.0,
        roughness: (clear || cblue) ? 0.0 : 0.07,
        transmission: (clear || cblue) ? 1.0 : Math.max(f.transmission ?? 0.7, 0.9),
        ior: 1.49,
        thickness: clear ? 0.08 : cblue ? 0.15 : 0.9,
        attenuationDistance: clear ? 14.0 : cblue ? 3.2 : 1.4,
        clearcoat: 1.0, clearcoatRoughness: clear ? 0.02 : 0.03,
        envMapIntensity: clear ? 1.15 : cblue ? 1.25 : 1.7, transparent: true,
      });
      if (cblue && 'attenuationColor' in mat) mat.attenuationColor = new THREE.Color('#4a93ff');
      return mat;
    }
    // Iridescent finishes (Pink Pearl psychedelic, Burnt Titanium, Oil Slick, Burnt Fire Orange).
    if (f && f.iridescent) {
      const mat = new THREE.MeshPhysicalMaterial({
        color: new THREE.Color(f.iridescent[0]), metalness: f.metalness ?? 0.6,
        roughness: 0.16, clearcoat: 1.0, clearcoatRoughness: 0.05, envMapIntensity: 1.6,
      });
      this._applyIridescent(mat, f.iridescent);
      return mat;
    }
    const MAP = {
      'gloss-white':  { color: 0xf4f4f6, metalness: 0.10, roughness: 0.18, clearcoat: 1.0, envMapIntensity: 1.2 },
      'matte-black':  { color: 0x111114, metalness: 0.00, roughness: 0.72, clearcoat: 0.0, envMapIntensity: 4.0 }, // LAB SPEC 2026-06-24 (body parts default)
      'carbon-fiber': { color: 0x14161a, metalness: 0.50, roughness: 0.35, clearcoat: 1.0, envMapIntensity: 1.2 },
      // Metallic standard finishes — full metalness so they read as real metal.
      'gold':         { color: 0xd4af37, metalness: 1.0,  roughness: 0.18, clearcoat: 0.35, envMapIntensity: 1.5 },
      'chrome':       { color: 0xf4f6fa, metalness: 1.0,  roughness: 0.04, clearcoat: 0.0,  envMapIntensity: 2.0 }, // mirror
      'gunmetal':     { color: 0x3a3e44, metalness: 0.8,  roughness: 0.34, clearcoat: 0.5,  envMapIntensity: 1.3 }, // deep dark gray metallic
      'tow-red':      { color: 0xd11026, metalness: 0.20, roughness: 0.25, clearcoat: 1.0, envMapIntensity: 1.2 },
      'tow-black':    { color: 0x111114, metalness: 0.20, roughness: 0.70, clearcoat: 0.2, envMapIntensity: 0.8 },
      'tow-orange':   { color: 0xff6a1a, metalness: 0.20, roughness: 0.30, clearcoat: 1.0, envMapIntensity: 1.2 },
      'green-purple-flip': { color: 0x0a7a4a, metalness: 0.30, roughness: 0.18, clearcoat: 1.0, envMapIntensity: 1.2 },
      'red-black-flip':    { color: 0xc8102e, metalness: 0.30, roughness: 0.18, clearcoat: 1.0, envMapIntensity: 1.2 },
    };
    // Fall back to the finish's own hex when it isn't in MAP — this is what
    // makes the tow-hook colours (FR_TOW_HOOK_COLORS carry only {id,name,hex})
    // actually paint: blue/green/yellow/purple/pink/silver aren't in MAP, so
    // before this they all rendered as the grey default. Anodised-metal look.
    // Fallback honors the finish's own metalness/roughness when supplied (so the
    // bronze body finishes read as real metal); else the anodised-metal default.
    const p = MAP[f?.id] || { color: (f?.hex != null ? new THREE.Color(f.hex).getHex() : 0xdedee4), metalness: f?.metalness ?? 0.25, roughness: f?.roughness ?? 0.26, clearcoat: 1.0, envMapIntensity: 1.2 };
    const mat = new THREE.MeshPhysicalMaterial({
      color: new THREE.Color(f?.flip ? f.flip[0] : p.color),
      metalness: p.metalness,
      roughness: p.roughness,
      clearcoat: p.clearcoat,
      clearcoatRoughness: 0.06,
      envMapIntensity: p.envMapIntensity,
    });
    if (f?.flip) this._applyFlip(mat, f.flip);   // chameleon part finish
    return mat;
  }

  // Add a view-angle colour shift (chameleon/flip) to any material — reused by
  // the flip paints and flip part-finishes. UV-free (fresnel on the normal).
  _applyFlip(mat, colors) {
    const a = new THREE.Color(colors[0]), b = new THREE.Color(colors[1]);
    mat.color.copy(a);
    mat.onBeforeCompile = (shader) => {
      shader.uniforms.uFA = { value: a };
      shader.uniforms.uFB = { value: b };
      shader.fragmentShader = 'uniform vec3 uFA;\nuniform vec3 uFB;\n' + shader.fragmentShader;
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <normal_fragment_begin>',
        '#include <normal_fragment_begin>\n  { float _f = pow(1.0 - clamp(abs(dot(normalize(vViewPosition), normal)), 0.0, 1.0), 2.5); diffuseColor.rgb = mix(uFA, uFB, _f); }'
      );
    };
    mat.needsUpdate = true;
  }

  // 4-stop angle-shifting iridescence (Pink Pearl, Burnt Titanium, Oil Slick,
  // Burnt Fire Orange). Cycles ~2x across the view angle for rich colour movement.
  _applyIridescent(mat, colors) {
    const cols = [0, 1, 2, 3].map(i => new THREE.Color(colors[i] || colors[0]));
    mat.color.copy(cols[0]);
    mat.onBeforeCompile = (shader) => {
      shader.uniforms.uIA = { value: cols[0] }; shader.uniforms.uIB = { value: cols[1] };
      shader.uniforms.uIC = { value: cols[2] }; shader.uniforms.uID = { value: cols[3] };
      shader.fragmentShader = 'uniform vec3 uIA;\nuniform vec3 uIB;\nuniform vec3 uIC;\nuniform vec3 uID;\n' + shader.fragmentShader;
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <normal_fragment_begin>',
        `#include <normal_fragment_begin>
        { float fdot = clamp(abs(dot(normalize(vViewPosition), normal)), 0.0, 1.0);
          float t = fract(pow(1.0 - fdot, 1.35) * 2.0); vec3 ic;
          if (t < 0.3333) ic = mix(uIA, uIB, t / 0.3333);
          else if (t < 0.6666) ic = mix(uIB, uIC, (t - 0.3333) / 0.3333);
          else ic = mix(uIC, uID, (t - 0.6666) / 0.3334);
          diffuseColor.rgb = mix(diffuseColor.rgb, ic, 0.9); }`
      );
    };
    mat.needsUpdate = true;
  }

  _makeGradientMaterial(colors) {
    const cv = document.createElement('canvas'); cv.width = 256; cv.height = 64;
    const ctx = cv.getContext('2d');
    const g = ctx.createLinearGradient(0, 0, 256, 0);
    colors.forEach((c, i) => g.addColorStop(i / (colors.length - 1), c));
    ctx.fillStyle = g; ctx.fillRect(0, 0, 256, 64);
    const tex = new THREE.CanvasTexture(cv); tex.colorSpace = THREE.SRGBColorSpace;
    return new THREE.MeshPhysicalMaterial({ map: tex, metalness: 0.9, roughness: 0.12, clearcoat: 1, clearcoatRoughness: 0.05, envMapIntensity: 1.2 });
  }

  setWheel(w) {
    this._currentWheel = w;
    this.wheelMaterial.color = new THREE.Color(w.color);
    const f = w.finish;
    this.wheelMaterial.metalness = f === 'chrome' ? 1.0 : f === 'pearl' ? 0.85 : f === 'metallic' ? 0.9 : 0.7;
    this.wheelMaterial.roughness = f === 'chrome' ? 0.05 : f === 'pearl' ? 0.15 : f === 'metallic' ? 0.25 : 0.3;
    // Combined GLB: show only this style's rim, hide the rest.
    this._showWheelRim(w.id);
    // Lazily fetch the heavy hi-res rim (Vortex/Nakamishi) on first selection.
    if (this._hiResMap && this._hiResMap[w.id] && this._hiResRims && !this._hiResRims[w.id]) this._ensureHiResRim(w.id);
    // Keep the centre cap blended into the wheel finish unless the buyer turned
    // the Center-Caps feature on (then it keeps its chosen colour).
    this._syncCapToWheel();
    // Legacy placeholder wheels (empty in combined mode) — keep for back-compat.
    const showCal = w.id !== 'stock';
    if (this.wheelMeshes) this.wheelMeshes.forEach(wm => wm.userData.calliper.visible = showCal);
  }

  // Build a flat stock rim: one flat disc filling each wheel face, derived
  // from the tire mesh's four wheel clusters. No spokes, no depth — a plain
  // stock rim. Hidden unless the Stock wheel is selected.
  _buildStockFlatRim(model) {
    const tire = model.children.find(c => c.name === 'Tire');
    if (!tire) return null;
    tire.updateWorldMatrix(true, false);
    const pos = tire.geometry.attributes.position;
    const toModel = new THREE.Matrix4().copy(model.matrixWorld).invert().multiply(tire.matrixWorld);
    const pts = []; const v = new THREE.Vector3();
    for (let i = 0; i < pos.count; i++) { v.fromBufferAttribute(pos, i).applyMatrix4(toModel); pts.push(v.clone()); }
    const box = new THREE.Box3(); pts.forEach(p => box.expandByPoint(p));
    const c = box.getCenter(new THREE.Vector3());
    const groups = {};
    pts.forEach(p => { const k = (p.x > c.x ? 'A' : 'B') + (p.z > c.z ? 'A' : 'B'); (groups[k] = groups[k] || []).push(p); });
    const grp = new THREE.Group(); grp.name = 'stockFlatRim';
    // Clean SATIN material — low metalness + low env reflection so the flat
    // rim reads as a plain stock rim instead of a busy mirror catching the
    // studio lights (that was the "mess" on the stock wheels).
    const stockMat = new THREE.MeshStandardMaterial({
      color: 0x303338, metalness: 0.4, roughness: 0.66, envMapIntensity: 0.25,
      side: THREE.DoubleSide, // visible from both sides so it never looks hollow
    });
    Object.values(groups).forEach(arr => {
      const ctr = arr.reduce((a, p) => a.add(p), new THREE.Vector3()).multiplyScalar(1 / arr.length);
      let r = 0; arr.forEach(p => { const d = Math.hypot(p.x - ctr.x, p.y - ctr.y); if (d > r) r = d; });
      const disc = new THREE.Mesh(new THREE.CircleGeometry(r * 0.80, 48), stockMat);
      disc.position.copy(ctr);
      disc.rotation.y = Math.PI / 2; // face along the wheel spin axis (X)
      disc.castShadow = true; disc.receiveShadow = true;
      grp.add(disc);
    });
    model.add(grp);
    this._stockRimGroup = grp;
    return grp;
  }

  // Fix the "faceted / dented" body reflections. The GLB exports the body with
  // vertices SPLIT at every face (371k verts for 144k tris), so smooth normals
  // can't be averaged across faces → flat shading that reflects every facet.
  // Fix: weld coincident vertices, then recompute smooth normals. Geometry is
  // NOT moved (smoothing the geometry creates lumps — leftover surface waviness
  // is instead hidden via the soft, low-reflection paint in setPaint()).
  _smoothBaseCar(baseCarNode) {
    if (!baseCarNode) return;
    baseCarNode.traverse(mesh => {
      if (!mesh.isMesh || !mesh.geometry) return;
      const geo = mesh.geometry;
      // (1) WELD by position
      geo.computeBoundingBox();
      const size = new THREE.Vector3(); geo.boundingBox.getSize(size);
      const prec = (Math.max(size.x, size.y, size.z) || 1) / 50000;
      const pos = geo.attributes.position;
      const idxArr = geo.index ? geo.index.array : null;
      const map = new Map(); const newPos = []; const remap = new Int32Array(pos.count);
      const q = (val) => Math.round(val / prec);
      for (let i = 0; i < pos.count; i++) {
        const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
        const key = q(x) + '_' + q(y) + '_' + q(z);
        let id = map.get(key);
        if (id === undefined) { id = newPos.length / 3; map.set(key, id); newPos.push(x, y, z); }
        remap[i] = id;
      }
      const newIndex = [];
      if (idxArr) { for (let i = 0; i < idxArr.length; i++) newIndex.push(remap[idxArr[i]]); }
      else { for (let i = 0; i < pos.count; i++) newIndex.push(remap[i]); }
      const ng = new THREE.BufferGeometry();
      ng.setAttribute('position', new THREE.Float32BufferAttribute(newPos, 3));
      ng.setIndex(newIndex);
      // (2) smooth normals over the now-shared vertices — fixes the flat-shaded
      // faceting WITHOUT moving any geometry (so no lumps/artifacts).
      ng.computeVertexNormals();
      mesh.geometry.dispose();
      mesh.geometry = ng;
    });
  }

  // STOPGAP tire densifier: subdivide each triangle of the tire mesh into 4,
  // `passes` times (×4^passes triangles). Adds the surface resolution the
  // low-poly tire lacks so tire-print text maps cleanly. Remove once a real
  // higher-poly tire GLB is supplied.
  _densifyTire(model, passes = 2) {
    const tire = model.children.find(c => c.name === 'Tire');
    if (!tire || !tire.geometry) return;
    let arr = (tire.geometry.index ? tire.geometry.toNonIndexed() : tire.geometry).attributes.position.array;
    const subdiv = (a) => {
      const out = [];
      const get = (i) => [a[i * 3], a[i * 3 + 1], a[i * 3 + 2]];
      const mid = (u, w) => [(u[0] + w[0]) / 2, (u[1] + w[1]) / 2, (u[2] + w[2]) / 2];
      const push = (v) => out.push(v[0], v[1], v[2]);
      const tris = a.length / 9;
      for (let t = 0; t < tris; t++) {
        const v0 = get(t * 3), v1 = get(t * 3 + 1), v2 = get(t * 3 + 2);
        const m01 = mid(v0, v1), m12 = mid(v1, v2), m20 = mid(v2, v0);
        [[v0, m01, m20], [m01, v1, m12], [m20, m12, v2], [m01, m12, m20]].forEach(tri => tri.forEach(push));
      }
      return new Float32Array(out);
    };
    for (let i = 0; i < passes; i++) arr = subdiv(arr);
    const ng = new THREE.BufferGeometry();
    ng.setAttribute('position', new THREE.Float32BufferAttribute(arr, 3));
    ng.computeVertexNormals();
    tire.geometry.dispose();
    tire.geometry = ng;
  }

  // Generate wrap-around UVs on the REAL tire mesh so tire-print text can be
  // painted straight onto the tire surface — no floating ring geometry.
  //   U = angle around each wheel (0..1 → full 360° wrap)
  //   V = normalized radius (0 hub … 1 outer edge) → text band sits on sidewall
  // All four wheels share one mesh, so U/V are computed per wheel cluster.
  // Generate polar-wrap UVs on EVERY per-corner tyre so the sidewall-print strip
  // (U = around the tyre, V = radius) maps correctly. v2 split the wheels into
  // per-corner meshes, so each tyre mesh is processed independently — the old
  // code assumed one merged `Tire` node and no-op'd on v2.
  _setupTireUVs(model) {
    const tires = [...(this._wheelTireMeshes || []), ...(this._stockTireMeshes || [])];
    if (!tires.length) return;
    model.updateWorldMatrix(true, true);
    const inv = new THREE.Matrix4().copy(model.matrixWorld).invert();
    const v = new THREE.Vector3(), nv = new THREE.Vector3();
    // Per-tyre: local-space points + per-vertex normals (model frame) + bounds.
    const data = tires.map(t => {
      const toModel = new THREE.Matrix4().copy(inv).multiply(t.matrixWorld);
      const nMat = new THREE.Matrix3().getNormalMatrix(toModel);
      const geo = t.geometry, pos = geo.attributes.position, nrm = geo.attributes.normal;
      const pts = new Array(pos.count), nor = new Array(pos.count); const box = new THREE.Box3();
      for (let i = 0; i < pos.count; i++) {
        const p = v.fromBufferAttribute(pos, i).applyMatrix4(toModel).clone(); pts[i] = p; box.expandByPoint(p);
        nor[i] = nrm ? nv.fromBufferAttribute(nrm, i).applyMatrix3(nMat).normalize().clone() : null;
      }
      return { geo, pos, pts, nor, ctr: box.getCenter(new THREE.Vector3()), size: box.getSize(new THREE.Vector3()) };
    });
    // Axle = smallest-extent axis (a tyre is thin along the axle, wide across the
    // diameter). Averaged so every tyre agrees on the same spin axis.
    const avg = data.reduce((a, d) => a.add(d.size), new THREE.Vector3()).multiplyScalar(1 / data.length);
    const axle = (avg.x <= avg.y && avg.x <= avg.z) ? 'x' : (avg.y <= avg.z ? 'y' : 'z');
    const [a1, a2] = axle === 'x' ? ['y', 'z'] : axle === 'y' ? ['x', 'z'] : ['x', 'y'];
    const carAxle = data.reduce((s, d) => s + d.ctr[axle], 0) / data.length;
    data.forEach(d => {
      const outerSign = Math.sign(d.ctr[axle] - carAxle) || 1; // outward sidewall faces away from car centre
      let maxR = 1e-5;
      d.pts.forEach(p => { const r = Math.hypot(p[a1] - d.ctr[a1], p[a2] - d.ctr[a2]); if (r > maxR) maxR = r; });
      const uv = new Float32Array(d.pos.count * 2);
      d.pts.forEach((p, i) => {
        // Use the NORMAL to pick ONLY the outward-facing sidewall — excludes the
        // tread/shoulder (radial normals) + inner face. The outer face gets a
        // PLANAR projection (sidewall plane → square canvas, tyre fits [0,1]²), so
        // the ring lettering stays undistorted (no polar fanning). Everything else
        // maps to the dark base corner.
        const facing = d.nor[i] ? d.nor[i][axle] * outerSign : Math.sign(p[axle] - d.ctr[axle]) * outerSign;
        if (facing > 0.55) {
          uv[i * 2]     = 0.5 + outerSign * (p[a1] - d.ctr[a1]) / (2 * maxR);
          uv[i * 2 + 1] = 0.5 + (p[a2] - d.ctr[a2]) / (2 * maxR);
        } else {
          uv[i * 2] = 0.002; uv[i * 2 + 1] = 0.002;
        }
      });
      d.geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
      d.geo.attributes.uv.needsUpdate = true;
    });
  }

  // Build the tire-print texture as a horizontal strip: the wordmark repeats
  // across the full width (= the circumference) and sits in a band at the
  // sidewall radius, dark elsewhere. Mapped via the wrap UVs it reads as
  // lettering running all the way around the tire.
  _makeTireStripTexture(print, colorOverride) {
    // Horizontal strip mapped by the wrap UVs: U (x) = around the tyre, V (y) =
    // radius. ONE bold wordmark band sits near the outer edge of the sidewall and
    // repeats a few times around (like real raised tyre lettering).
    // Tunable live: __frTireTune({ fontPx, vCenter, repeats })
    const o = this._tireTextOpts || (this._tireTextOpts = { fontPx: 150, vCenter: 0.80, repeats: 0 });
    const W = 4096, H = 1024;
    const cv = document.createElement('canvas'); cv.width = W; cv.height = H;
    const ctx = cv.getContext('2d');
    ctx.fillStyle = '#0b0b0d'; ctx.fillRect(0, 0, W, H);   // tyre base colour
    const colors = print.colors || ['#f2f2f2'];
    const main = colorOverride || colors[0] || '#f2f2f2';
    ctx.textBaseline = 'middle'; ctx.textAlign = 'center';
    const fontPx = o.fontPx;
    ctx.font = `900 ${fontPx}px "Arial Black", "JetBrains Mono", monospace`;
    const unit = (print.text || 'FLAMERACER').toUpperCase() + '   •   ';
    const charW = ctx.measureText('M').width || fontPx * 0.6;
    const reps = o.repeats > 0 ? o.repeats : Math.max(2, Math.round(W / (unit.length * charW)));
    const text = unit.repeat(reps);
    const total = text.length;
    // Band centre at vCenter (≈ outer sidewall). CanvasTexture flips Y → y=(1-V)*H.
    const y = (1 - o.vCenter) * H;
    ctx.lineWidth = 7; ctx.strokeStyle = 'rgba(0,0,0,0.6)'; // outline so it reads on any shading
    ctx.fillStyle = main;
    for (let i = 0; i < total; i++) {
      const x = ((i + 0.5) / total) * W;
      ctx.strokeText(text[i], x, y);
      ctx.fillText(text[i], x, y);
    }
    const tex = new THREE.CanvasTexture(cv);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = this.renderer?.capabilities.getMaxAnisotropy?.() || 1;
    tex.needsUpdate = true;
    return tex;
  }

  // Replace the low-poly faceted tire with smooth lathe-revolved tires at the
  // same size + position. 72 segments = round silhouette; the cross-section has
  // rounded shoulders and two subtle INLINE tread grooves. Hides the original.
  _buildSmoothTires(model) {
    const tire = model.children.find(c => c.name === 'Tire');
    if (!tire) return;
    tire.updateWorldMatrix(true, false);
    const pos = tire.geometry.attributes.position;
    const toModel = new THREE.Matrix4().copy(model.matrixWorld).invert().multiply(tire.matrixWorld);
    const pts = []; const v = new THREE.Vector3();
    for (let i = 0; i < pos.count; i++) { pts.push(v.fromBufferAttribute(pos, i).applyMatrix4(toModel).clone()); }
    const box = new THREE.Box3(); pts.forEach(p => box.expandByPoint(p));
    const c = box.getCenter(new THREE.Vector3());
    const groups = {};
    pts.forEach(p => { const k = (p.x > c.x ? 'A' : 'B') + (p.z > c.z ? 'A' : 'B'); (groups[k] = groups[k] || []).push(p); });
    let R = 0, W = 0; const centers = [];
    Object.values(groups).forEach(arr => {
      const ctr = arr.reduce((a, p) => a.add(p), new THREE.Vector3()).multiplyScalar(1 / arr.length);
      let r = 0, hw = 0;
      arr.forEach(p => { const rr = Math.hypot(p.y - ctr.y, p.z - ctr.z); if (rr > r) r = rr; const w = Math.abs(p.x - ctr.x); if (w > hw) hw = w; });
      R = Math.max(R, r); W = Math.max(W, hw); centers.push(ctr.clone());
    });
    const Rin = R * 0.62;
    const V2 = THREE.Vector2;
    const P = [
      [Rin, W], [R * 0.84, W], [R * 0.95, W * 0.78], [R * 0.995, W * 0.5],
      [R, W * 0.34], [R * 0.965, W * 0.27], [R, W * 0.20],     // inline groove 1
      [R, -W * 0.20], [R * 0.965, -W * 0.27], [R, -W * 0.34],  // inline groove 2
      [R * 0.995, -W * 0.5], [R * 0.95, -W * 0.78], [R * 0.84, -W], [Rin, -W],
    ].map(([x, y]) => new V2(x, y));
    const geo = new THREE.LatheGeometry(P, 72);
    geo.computeVertexNormals();
    const mat = this.tireMaterial || new THREE.MeshStandardMaterial({ color: 0x0b0b0d, metalness: 0, roughness: 0.9 });
    const grp = new THREE.Group(); grp.name = 'smoothTires';
    centers.forEach(ctr => {
      const m = new THREE.Mesh(geo, mat);
      m.position.copy(ctr);
      m.rotation.z = Math.PI / 2; // lathe Y-axis → wheel spin axis (X)
      m.castShadow = true; m.receiveShadow = true;
      grp.add(m);
    });
    model.add(grp);
    tire.visible = false; // hide the faceted original
    this._smoothTires = grp;
  }

  // Show only the rim for the given wheel id; hide the other styles.
  // Stock = the flat rim disc with NO brake disk, caliper, designed rim, or
  // centre cap. The cool rims (Ring1..4) keep the brake disk, caliper, and
  // the extended centre cap.
  // Load one hi-res rim design's 4 corners (idempotent). Shared by the eager
  // Circuit load and the lazy Vortex/Nakamishi loads.
  async _loadHiResRim(wheelId) {
    if (this._isMobile) return;             // mobile uses the lighter baked-in rims (skip the hi-res grafts → ~1M fewer triangles)
    if (!this._hiResRims || this._hiResRims[wheelId]) return;
    const ring = this._hiResMap && this._hiResMap[wheelId];
    if (!ring || !this.combinedModel) return;
    const base = (window.FR_CAR_GLB || '').split('?')[0].replace(/[^/]+$/, '');
    const arr = [];
    for (const c of ['fl', 'fr', 'rl', 'rr']) {
      try {
        const gltf = await new Promise((res, rej) => this.gltfLoader.load(base + 'frwheel-' + ring + '-' + c + '.glb', res, undefined, rej));
        const root = gltf.scene; root.name = 'graft-' + ring + '-' + c;
        root.traverse(o => { if (o.isMesh) { o.material = this.wheelMaterial; o.visible = false; o.renderOrder = 1; arr.push(o); } });
        this.combinedModel.add(root);
        (this._graftRoots = this._graftRoots || []).push(root);
        if (this._wheelTrackMeshes) root.traverse(o => { if (o.isMesh) this._wheelTrackMeshes.push(o); });
      } catch (e) { console.warn('[FLR hi-res rim] failed', ring, c, e?.message || e); }
    }
    this._hiResRims[wheelId] = arr;
  }

  // Lazily fetch a hi-res rim on first selection, then swap it in.
  async _ensureHiResRim(wheelId) {
    if (!this._hiResMap || !this._hiResMap[wheelId] || (this._hiResRims && this._hiResRims[wheelId])) return;
    await this._loadHiResRim(wheelId);
    if (this._currentWheel) this._showWheelRim(this._currentWheel.id); // swap to the freshly-loaded hi-res
    this.requestRender && this.requestRender(600);
  }

  _showWheelRim(wheelId) {
    if (!this.wheelRimById) return;
    const isStock = wheelId === 'stock';
    const setVis = (arr, v) => (arr || []).forEach(m => { m.visible = v; });
    // v2: each bucket is an ARRAY of 4 per-corner meshes. Show only the selected
    // design (incl. the 'stock' bucket = Actual_Wheel rim+tyre); hide the rest.
    this.wheelRimById.forEach((meshes, id) => setVis(meshes, id === wheelId));
    // Shared hardware: cool (designed) rims get the configurable tyre + caps +
    // brakes + calipers; stock shows its own Actual rim/tyre and hides those.
    setVis(this._wheelTireMeshes, !isStock);   // configurable tyre group
    // Centre cap shows ONLY when the customer adds it — otherwise the cool rim's
    // clean open hub shows (the cap is its own mesh, FL_cap/etc., so it's hideable).
    setVis(this._wheelCapMeshes, !isStock && !!this._capEnabled);
    setVis(this._wheelBrakeMeshes, !isStock);
    setVis(this._wheelCaliperMeshes, !isStock);
    // Hi-res rim swap: show the grafted hi-res rims for the selected wheel (and
    // hide that wheel's low-poly car rim); hide every other hi-res group.
    if (this._hiResRims) {
      for (const id in this._hiResRims) {
        const active = id === wheelId;
        setVis(this._hiResRims[id], active);
        if (active) setVis(this.wheelRimById.get(id), false);
      }
    }
    this._updateHubBolts && this._updateHubBolts();
    // Re-apply the wheel transform to the now-active rims — ALWAYS (the constant
    // up-raise applies even with no stance) and so it survives wheel swaps + the
    // late-loaded hi-res rim meshes (which start at their un-transformed positions).
    this._refreshWheelPush();
  }

  // WHEEL TRACK PUSH: shove all 4 wheels (tire + rim + cap + brake + caliper)
  // outward along the track. Pure vertex translation in each wheel mesh's LOCAL
  // space — track axis is local X, and every wheel mesh is baked with all 4 wheels,
  // so its X-midpoint is the car centreline. Re-applied from the cached original
  // each call → exactly reversible AND composable. Two independent sources push the
  // wheels and they STACK (_refreshWheelPush sums their fractions): the WIDE STANCE
  // widebody package (flr-13, big push to sit flush with the flares) + the free
  // AGGRESSIVE STANCE selector (a small poke).
  _ensureTrackCache() {
    const meshes = this._wheelTrackMeshes || [];
    if (!meshes.length) return;
    // Cache each mesh's ORIGINAL local pose once (before any push). We move the wheel
    // TRANSFORMS rigidly — NOT geometry verts — so wheels space out WITHOUT growing.
    // (The model is per-corner single-wheel meshes positioned by transform; the old
    // geometry-vertex split fattened each wheel because the geometry is centred.)
    // Per-mesh + lazy so late-loaded hi-res rims cache from their clean pose too.
    meshes.forEach(m => { if (!m.userData._origPos) m.userData._origPos = m.position.clone(); });
    if (this._latHalfLocal == null) {
      // Lateral = each wheel mesh's PARENT-LOCAL X (verified: left wheels sit at +X,
      // right at −X, for tyre/ring/cap alike). This frame is CAR-FIXED (rotates with
      // the turntable), unlike world axes — so the push stays correct at any angle.
      // Half-track (local units) = the typical |x| placement of a wheel.
      let h = 0; meshes.forEach(m => { const ax = Math.abs(m.userData._origPos.x); if (ax > h) h = ax; });
      if (h > 0) this._latHalfLocal = h;
    }
  }
  // Move every wheel OUTWARD along its parent-local X by `frac` of the half-track, PLUS a
  // small constant UP-raise (parent-local Y) — both RIGID TRANSFORM translations, NO
  // geometry edits → wheels space out + lift with NO fattening/growing. Re-applied from
  // the cached original pose → exactly reversible + composable + rotation-proof.
  _applyWheelPush(frac) {
    this._ensureTrackCache();
    if (this._latHalfLocal == null) return;
    const dOut = frac * this._latHalfLocal;
    const dUp = (this._wheelRaiseFrac != null ? this._wheelRaiseFrac : 0.04) * this._latHalfLocal;
    (this._wheelTrackMeshes || []).forEach(m => {
      const op = m.userData._origPos; if (!op) return;
      const sign = op.x > 1e-5 ? 1 : (op.x < -1e-5 ? -1 : 0); // left(+X)/right(−X) → outward
      m.position.set(op.x + sign * dOut, op.y + dUp, op.z);
    });
  }
  // Sum the two stance sources + re-apply (keeps the result correct as either toggles
  // and as wheels are swapped/hi-res-loaded). The constant up-raise rides along.
  _refreshWheelPush() {
    // NOTE: fractions are now % of the WORLD half-track (real spacing), so they're much
    // smaller than the old geometry-scale numbers. Tune on a real device.
    const wb = this._wideStanceOn ? (this._wideStancePush != null ? this._wideStancePush : 0.07) : 0;
    const ag = this._aggrStanceOn ? (this._aggrStancePush != null ? this._aggrStancePush : 0.03) : 0;
    this._applyWheelPush(wb + ag);
  }
  // Wide Stance widebody package (flr-13). Tune live with __frWideStance(0.07).
  setWideStance(on) { this._wideStanceOn = !!on; this._refreshWheelPush(); }
  // Free "Aggressive Stance" selector — a small extra outward poke for a meaner look.
  // Tune live with __frAggrStance(0.05).
  setAggressiveStance(on) { this._aggrStanceOn = !!on; this._refreshWheelPush(); }

  // Recolor the rim to a chosen wheel finish (from FR_WHEEL_FINISHES).
  // Overrides the wheel's native color/finish set by setWheel().
  // Apply a finish object's full look — base colour + metalness/roughness PLUS the
  // rare EFFECTS (translucent transmission, iridescent colour-shift, pearl sheen) —
  // to ANY MeshPhysicalMaterial. Shared by the wheel rim, the calipers and the brake
  // disks so all three offer the exact same finish set. Solid finishes reset every
  // effect prop so toggling back from a translucent/iridescent pick renders cleanly.
  // A small carbon-weave texture for WHEEL parts (rims/calipers/disks/caps). Unlike
  // the body (no UVs → triplanar shader), the rim meshes are normal UV'd meshes, so a
  // plain .map works when UVs exist. Built once + cached, tiled via RepeatWrapping.
  _wheelCarbonTexture() {
    if (this._wheelCarbonTex) return this._wheelCarbonTex;
    const tex = new THREE.TextureLoader().load(
      'data:image/png;base64,' + FR_CARBON_TEX_B64,
      () => { this.requestRender && this.requestRender(400); }
    );
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    tex.repeat.set(6, 6);
    tex.anisotropy = 16;
    this._wheelCarbonTex = tex;
    return tex;
  }

  _applyFinishToMaterial(m, finish) {
    if (!m || !finish) return;
    // CARBON FIBRE (+$50): give the part a real carbon-weave look. If the geometry has
    // UVs, drop the weave texture in as a .map; otherwise fall back to a convincing dark
    // carbon material (low metal / mid rough + a subtle clearcoat). Either way: dark hex.
    if (finish.carbon) {
      m.color = new THREE.Color(finish.hex);
      m.metalness = finish.metalness ?? 0.2;
      m.roughness = finish.roughness ?? 0.5;
      m.transmission = 0; m.thickness = 0; m.transparent = false;
      if ('iridescence' in m) m.iridescence = 0.0;
      // Does ANY mesh wearing this material have UVs? (rims share one material.)
      const wearers = [
        ...(this.realRimMeshes || []), ...(this._wheelCaliperMeshes || []),
        ...(this._wheelBrakeMeshes || []), ...(this._wheelCapMeshes || []),
      ].filter(c => c && c.material === m);
      const hasUV = wearers.some(c => c.geometry && c.geometry.attributes && c.geometry.attributes.uv);
      if (hasUV) {
        m.map = this._wheelCarbonTexture();
        m.color = new THREE.Color('#ffffff'); // let the weave read true; tint stays subtle
        m.clearcoat = 0.6; m.clearcoatRoughness = 0.18;
      } else {
        if (m.map) { m.map = null; }           // dark carbon-look fallback (no weave)
        m.clearcoat = 0.5; m.clearcoatRoughness = 0.2;
      }
      m.needsUpdate = true;
      return;
    }
    if (m.map) { m.map = null; m.needsUpdate = true; } // leaving carbon → drop the weave
    m.color = new THREE.Color(finish.hex);
    m.metalness = finish.metalness ?? 0.85;
    m.roughness = finish.roughness ?? 0.2;
    const wfClear = finish.id === 'wf-clear-translucent'; // true plexiglass
    const wfCblue = finish.id === 'wf-crystal-blue';       // fully see-through blue
    m.transmission = finish.translucent ? ((wfClear || wfCblue) ? 1.0 : Math.max(finish.transmission ?? 0.6, 0.9)) : 0;
    if (finish.translucent) {
      if (wfClear || wfCblue) m.color = new THREE.Color('#ffffff'); // near-white diffuse = max clarity (tint via attenuation)
      m.ior = 1.49;
      m.thickness = wfClear ? 0.08 : wfCblue ? 0.15 : 0.9;
      m.attenuationDistance = wfClear ? 14.0 : wfCblue ? 3.2 : 1.4;
      m.roughness = wfClear ? 0.0 : wfCblue ? 0.0 : 0.07;
      if ('attenuationColor' in m) m.attenuationColor = new THREE.Color(wfCblue ? '#4a93ff' : '#ffffff');
    } else {
      m.thickness = 0;
      if ('attenuationColor' in m) m.attenuationColor = new THREE.Color('#ffffff');
    }
    if ('iridescence' in m) {
      if (finish.iridescent)   { m.iridescence = 1.0; m.iridescenceIOR = 1.6; m.iridescenceThicknessRange = [120, 520]; }
      else if (finish.pearl)   { m.iridescence = 0.6; m.iridescenceIOR = 1.3; }
      else                     { m.iridescence = 0.0; }
    }
    if (finish.pearl || finish.iridescent || finish.translucent) { m.clearcoat = 1.0; m.clearcoatRoughness = 0.06; }
    else { m.clearcoat = 0.5; m.clearcoatRoughness = 0.2; }
    m.transparent = !!finish.translucent;
    m.needsUpdate = true;
  }

  setWheelFinish(finish) {
    if (!finish) return;
    this._currentWheelFinish = finish;
    this._applyFinishToMaterial(this.wheelMaterial, finish);
    this._syncCapToWheel(); // cap follows the rim finish unless caps are enabled
  }

  // Calipers + brake disks reuse the SAME finish set as the rim, so they get every
  // colour AND every rare effect (chrome / translucent / iridescent / pearl).
  setCaliperFinish(finish) { this._applyFinishToMaterial(this.calliperMaterial, finish); }
  setDiskFinish(finish)    { this._applyFinishToMaterial(this.diskMaterial, finish); }

  // Keep the centre-cap material matching the current wheel finish, so the cap
  // reads as part of the rim when the Center-Caps add-on is OFF. When it's ON,
  // setCenterCapColor has painted it a chosen colour and we leave it alone.
  _syncCapToWheel() {
    if (this._capEnabled) return;
    // Blend the cap into the rim — match the rim's full finish (incl. rare effects).
    if (this._currentWheelFinish) { this._applyFinishToMaterial(this.centerCapMaterial, this._currentWheelFinish); return; }
    this.centerCapMaterial.color.copy(this.wheelMaterial.color);
    this.centerCapMaterial.metalness = this.wheelMaterial.metalness ?? 0.7;
    this.centerCapMaterial.roughness = this.wheelMaterial.roughness ?? 0.2;
    this.centerCapMaterial.needsUpdate = true;
  }

  // Apply a tire print (from FR_TIRE_PRINTS) directly onto the tire surface
  // via the wrap UVs from _setupTireUVs — the wordmark runs all the way around
  // the tire. Passing 'none' (or empty text) restores the bare blackwall.
  setTirePrint(print, colorOverride) {
    // A "print" is either a wordmark (has .text) OR a pinstripe (kind:'pinstripe').
    this._tirePrint = (print && (print.text || print.kind === 'pinstripe')) ? print : null;
    this._tirePrintOverride = colorOverride || null;
    this._applySidewall();
  }

  // Sidewall pinstripe — a bold coloured ring, independent of the wordmark.
  // Pass a colour object {hex} / hex string, or null to remove it.
  // Deprecated: pinstripes are now tire-print entries (setTirePrint). Kept as a
  // no-op so any older saved-build / call path doesn't throw.
  setTirePinstripe() { this._tirePinstripe = null; }

  // Build + apply the sidewall texture from whatever sidewall features are
  // active (wordmark print and/or pinstripe). Blackwall only when BOTH are off.
  _applySidewall() {
    const tires = [...(this.realTireMeshes || []), ...(this._stockTireMeshes || [])];
    if (!tires.length) return;
    // The per-corner tyres SHARE a material, so operate on unique materials to
    // avoid disposing the freshly-assigned texture on the 2nd..Nth tyre.
    const mats = [...new Set(tires.map(t => t.material))];
    const print = this._tirePrint;
    const hasContent = !!print && (print.text || print.kind === 'pinstripe');
    if (!hasContent) {
      mats.forEach(m => {
        if (m.map) { m.map.dispose(); m.map = null; }
        m.color = new THREE.Color(0x0c0c0e); // bare blackwall
        m.needsUpdate = true;
      });
      return;
    }
    const tex = this._makeSidewallTexture(print, this._tirePrintOverride);
    mats.forEach(m => {
      if (m.map && m.map !== tex) m.map.dispose();
      m.map = tex;
      m.color = new THREE.Color(0xffffff); // let the texture show true
      m.needsUpdate = true;
    });
    this.requestRender && this.requestRender(400);
  }

  // Tire SURFACE finish — Gloss (wet, low roughness) vs Matte (flat rubber, the
  // default). Sets roughness on every tyre material; the sidewall texture/colour
  // path (above) leaves roughness alone, so the finish persists.
  setTireFinish(finish) {
    this._tireFinish = (finish === 'gloss') ? 'gloss' : 'matte';
    const tires = [...(this.realTireMeshes || []), ...(this._stockTireMeshes || [])];
    if (!tires.length) return;
    const gloss = this._tireFinish === 'gloss';
    [...new Set(tires.map(t => t.material))].forEach(m => {
      m.roughness = gloss ? 0.34 : 0.92;
      m.metalness = gloss ? 0.15 : 0.08;
      m.envMapIntensity = gloss ? 1.2 : 0.6;
      m.needsUpdate = true;
    });
    this.requestRender && this.requestRender(400);
  }

  // CLEAR TRANSLUCENT LID — swap the car's `roof` mesh to clear plexiglass so the
  // interior ("vapor chamber") shows through; restore the painted roof when off.
  setRoofTranslucent(on) {
    this._clearRoof = !!on;
    if (!this._roofMesh && this.combinedModel) {
      this.combinedModel.traverse(n => { if (n.isMesh && /^roof$/i.test((n.name || '').replace(/[_\s]+/g, ' ').trim())) this._roofMesh = n; });
    }
    const r = this._roofMesh;
    if (!r) { console.warn('[FLR] no roof mesh to make translucent'); return; }
    if (on) {
      if (!r.userData._origMat) r.userData._origMat = r.material;
      if (!this._clearRoofMat) this._clearRoofMat = this._makePartMaterial({ id: 'clear-translucent', translucent: true, transmission: 1.0 });
      r.material = this._clearRoofMat; r.visible = true;
    } else if (r.userData._origMat) {
      r.material = r.userData._origMat;
    }
    r.material.needsUpdate = true;
    this.requestRender && this.requestRender(400);
  }

  // Build a transparent canvas with the print text curved around the ring,
  // colored from print.colors ([0]=main letters, [1]=accent, [2]=divider).
  // colorOverride: when set (Signature recolor), the whole wordmark renders in
  // that single solid color — no rainbow, no neon glow.
  _makeSidewallTexture(print, colorOverride) {
    // Opaque tyre-surface canvas: dark base + the wordmark drawn as a curved RING
    // (each glyph rotated tangentially). Mapped onto the planar-projected sidewall
    // it reads as clean, undistorted raised lettering near the outer edge.
    // Tunable live:  __frTireTune({ fontPx, ringFrac, repeats })
    const o = this._tireTextOpts || (this._tireTextOpts = { fontPx: 56, ringFrac: 0.84, repeats: 0 });
    const S = 1024;
    const cv = document.createElement('canvas'); cv.width = cv.height = S;
    const ctx = cv.getContext('2d');
    ctx.fillStyle = '#0b0b0d'; ctx.fillRect(0, 0, S, S);   // tyre base colour
    const cx = S / 2, cy = S / 2;
    // colorOverride may be a colour OBJECT {hex, metallic}, a hex string, or null.
    const ov = colorOverride && typeof colorOverride === 'object' ? colorOverride
             : (colorOverride ? { hex: colorOverride } : null);

    // ---- PINSTRIPE print: 1 or 2 clean coloured rings, NO glow. Colour comes from
    // the shared Print/Stripe colour picker (override), else white. Mutually
    // exclusive with a wordmark (only one sidewall treatment is ever selected).
    if (print && print.kind === 'pinstripe') {
      const ph = (ov && (ov.metallic ? (ov.metallic === 'gold' ? '#d9bb55' : '#cfd2d8') : ov.hex)) || '#f2f2f2';
      ctx.lineCap = 'round';
      const rings = print.stripes === 2 ? [[0.945, 13], [0.865, 13]] : [[0.905, 17]];
      rings.forEach(([fr, w]) => {
        const rr = (S / 2) * fr;
        ctx.setLineDash([]); ctx.lineCap = 'round';
        ctx.beginPath(); ctx.arc(cx, cy, rr, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(0,0,0,0.5)'; ctx.lineWidth = w + 5; ctx.stroke(); // recessed seat (not a glow)
        // Dotted variant: dash the coloured ring into evenly-spaced dots.
        if (print.dotted) { ctx.setLineDash([w * 1.5, w * 1.7]); ctx.lineCap = 'round'; }
        ctx.beginPath(); ctx.arc(cx, cy, rr, 0, Math.PI * 2);
        ctx.strokeStyle = ph; ctx.lineWidth = w; ctx.stroke();
        ctx.setLineDash([]);
      });
      const tps = new THREE.CanvasTexture(cv);
      tps.colorSpace = THREE.SRGBColorSpace;
      tps.anisotropy = this.renderer?.capabilities.getMaxAnisotropy?.() || 1;
      tps.needsUpdate = true;
      return tps;
    }

    // The rest (wordmark) only runs when there's a print with text.
    if (!print || !print.text) {
      const t0 = new THREE.CanvasTexture(cv);
      t0.colorSpace = THREE.SRGBColorSpace;
      t0.anisotropy = this.renderer?.capabilities.getMaxAnisotropy?.() || 1;
      t0.needsUpdate = true;
      return t0;
    }
    const colors = print.colors || ['#f2f2f2'];
    const main = (ov && ov.hex) || colors[0] || '#f2f2f2';
    const accent = colors[1] || main;
    const divider = colors[2] || accent;
    const radius = (S / 2) * o.ringFrac;
    const fontPx = o.fontPx;
    ctx.font = `900 ${fontPx}px "Arial Black", "JetBrains Mono", monospace`;
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    // Brushed-metal gradient (in glyph-local space) for the gold / aluminium finishes.
    const metalFill = (type) => {
      const g = ctx.createLinearGradient(0, -fontPx * 0.62, 0, fontPx * 0.62);
      if (type === 'gold') { g.addColorStop(0, '#fbf0bf'); g.addColorStop(0.42, '#e7ca52'); g.addColorStop(0.5, '#9a7b25'); g.addColorStop(0.58, '#e7ca52'); g.addColorStop(1, '#caa334'); }
      else { g.addColorStop(0, '#f6f8fc'); g.addColorStop(0.42, '#d2d5db'); g.addColorStop(0.5, '#83868f'); g.addColorStop(0.58, '#d2d5db'); g.addColorStop(1, '#b6bac2'); }
      return g;
    };

    // Twin pinstripes bracketing the lettering (dual prints, default colours only).
    if (colors.length > 1 && !ov) {
      ctx.strokeStyle = divider; ctx.lineWidth = 3;
      const fr = (fontPx / S) * 1.25;
      [o.ringFrac + fr, o.ringFrac - fr].forEach(r => { ctx.beginPath(); ctx.arc(cx, cy, (S / 2) * r, 0, Math.PI * 2); ctx.stroke(); });
    }

    const unit = (print.text || 'FLAMERACER').toUpperCase() + '   •   ';
    const charW = ctx.measureText('M').width || fontPx * 0.6;
    const circ = 2 * Math.PI * radius;
    const reps = o.repeats > 0 ? o.repeats : Math.max(1, Math.round(circ / (unit.length * charW)));
    const text = unit.repeat(reps);
    const total = text.length;
    const step = (Math.PI * 2) / total;
    const start = -Math.PI / 2;
    const chromeHexes = ['#e8e9ee', '#d9dade', '#c0c0c0', '#cfd2d8'];
    const isChrome = (hex) => chromeHexes.includes((hex || '').toLowerCase());

    for (let i = 0; i < total; i++) {
      const ch = text[i];
      const a = start + step * i;
      ctx.save();
      ctx.translate(cx + Math.cos(a) * radius, cy + Math.sin(a) * radius);
      ctx.rotate(a + Math.PI / 2);                       // glyph stands radially, base inward
      // Default colours use the dual accent on every 4th glyph; an override paints
      // the whole wordmark one colour (or brushed metal).
      const fill = ov ? main : ((colors.length > 1 && i % 4 === 0) ? accent : main);
      ctx.lineWidth = Math.max(3, fontPx * 0.10); ctx.strokeStyle = 'rgba(0,0,0,0.6)'; // outline
      ctx.strokeText(ch, 0, 0);
      ctx.fillStyle = (ov && ov.metallic) ? metalFill(ov.metallic) : (isChrome(fill) ? '#e0e3e9' : fill);
      ctx.fillText(ch, 0, 0);
      ctx.restore();
    }
    const tex = new THREE.CanvasTexture(cv);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = this.renderer?.capabilities.getMaxAnisotropy?.() || 1;
    tex.needsUpdate = true;
    return tex;
  }

  // Toggle the overhead hexagon garage light on/off.
  setHexLight(on) {
    this.hexLightOn = !!on;
    this._applyHex();
  }

  // Hide the visible hex panels while in TOP view (they sit between the
  // top-down camera and the car). The lights keep firing.
  setTopView(active) {
    this._topView = !!active;
    this._applyHex();
  }

  // One-call brightness for the whole fixture (area + fill).
  setHexBrightness(level) {
    this.hexAreaIntensity = level;
    this.hexLampIntensity = level * 0.8;
    this._applyHex();
  }

  // Apply current on/off + top-view state to every hex element.
  _applyHex() {
    const on = this.hexLightOn;
    this.hexLightArea.intensity = on ? this.hexAreaIntensity : 0;
    this.hexLightLamp.intensity = on ? this.hexLampIntensity : 0;
    this.hexPanels.forEach(m => { m.visible = on && !this._topView; });
  }

  // Solid-hex helper for the custom colour-wheel + legacy callers (resets effects).
  setCaliperColor(hex) { this._applyFinishToMaterial(this.calliperMaterial, { hex, metalness: 0.6, roughness: 0.3 }); }

  // Recolor the brake disk/rotor independently — accepts a wheel-finish object OR a
  // plain {hex,metalness,roughness}, so the disk shares the rim's full finish set.
  setDiskColor(disk) { if (disk) this._applyFinishToMaterial(this.diskMaterial, disk); }

  // Center-Caps feature. The real centre cap is the GLB `cap` node (always
  // present — on stock wheels it's the flat rim face, on the designed rims it's
  // the hub cap). Enabling paints it a chosen gloss colour; disabling blends it
  // back into the current wheel finish. Never hides it (it's structural).
  setCenterCapColor(finishOrHex, enabled) {
    this._capEnabled = !!enabled;
    if (enabled) {
      // Accept a full wheel-finish object (colour + rare effects) OR a plain hex.
      const finish = (finishOrHex && typeof finishOrHex === 'object')
        ? finishOrHex
        : { hex: finishOrHex, metalness: 0.7, roughness: 0.18 };
      this._applyFinishToMaterial(this.centerCapMaterial, finish);
    } else {
      this._syncCapToWheel(); // blend back into the rim finish
    }
    // Show/hide the REAL per-corner centre caps with the selection (only on a cool
    // rim — stock has no cap). No cap selected ⇒ the rim's open hub shows.
    const onCool = !!enabled && this._currentWheel && this._currentWheel.id !== 'stock';
    (this._wheelCapMeshes || []).forEach(m => { m.visible = !!onCool; });
    // Legacy placeholder wheels (empty array in combined mode) — back-compat.
    if (this.wheelMeshes) this.wheelMeshes.forEach(w => { if (w.userData && w.userData.centerCap) w.userData.centerCap.visible = enabled; });
    this.requestRender && this.requestRender(300);
    this._updateHubBolts && this._updateHubBolts(); // bolts are the inverse of caps
  }

  // ---- HUB BOLT / SCREW ----
  // A procedural hex bolt that fills the open hub of a cool rim BY DEFAULT, and
  // is removed when the customer adds a centre cap (inverse of _wheelCapMeshes).
  // Placement is auto-detected from each centre-cap mesh's world bbox (centre +
  // hub-axis = the cap's thinnest dimension), then fine-tunable live via
  // window.__frBoltTune({axial,scale}) / the ?camtune Hub-bolt section.
  // Regular cross-head (Phillips) screw — MUST match _glb_work/hub_viewer.html
  // makeBolt() exactly so a placement confirmed in the viewer ships identically.
  // R = head radius.
  _makeBolt(R) {
    if (!this._boltMat) {
      this._boltMat = new THREE.MeshStandardMaterial({ color: 0x0c0c0e, metalness: 0.75, roughness: 0.38, envMapIntensity: 1.1 });  // screw body = black
      this._boltSocketMat = new THREE.MeshStandardMaterial({ color: 0x1e1e22, metalness: 0.55, roughness: 0.6 });                 // the X cross = near-black dark grey (barely visible)
    }
    const steel = this._boltMat, dark = this._boltSocketMat, g = new THREE.Group();
    // domed pan head (lathe profile)
    const prof = [[0.001,0],[R,0.04*R],[R,0.16*R],[0.95*R,0.26*R],[0.78*R,0.36*R],[0.5*R,0.44*R],[0.22*R,0.48*R],[0.001,0.49*R]]
      .map(p => new THREE.Vector2(p[0], p[1]));
    g.add(new THREE.Mesh(new THREE.LatheGeometry(prof, 48), steel));
    // recessed Phillips cross
    const cy = 0.41 * R;
    const sx = new THREE.Mesh(new THREE.BoxGeometry(1.12 * R, 0.22 * R, 0.16 * R), dark); sx.position.y = cy; g.add(sx);
    const sz = new THREE.Mesh(new THREE.BoxGeometry(0.16 * R, 0.22 * R, 1.12 * R), dark); sz.position.y = cy; g.add(sz);
    // threaded shaft into the hub
    const shaftR = 0.4 * R, shaftLen = 1.5 * R;
    const core = new THREE.Mesh(new THREE.CylinderGeometry(shaftR, shaftR * 0.62, shaftLen, 20), steel);
    core.position.y = -shaftLen * 0.5; g.add(core);
    const pts = [], turns = 6, seg = 18;
    for (let i = 0; i <= turns * seg; i++) { const t = i / seg, a = t * Math.PI * 2, f = t / turns, rr = shaftR * (1 - 0.42 * f); pts.push(new THREE.Vector3(Math.cos(a) * rr, -shaftLen * f, Math.sin(a) * rr)); }
    g.add(new THREE.Mesh(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), turns * seg, 0.07 * R, 5, false), steel));
    const tip = new THREE.Mesh(new THREE.ConeGeometry(shaftR * 0.6, 0.42 * R, 16), steel); tip.rotation.x = Math.PI; tip.position.y = -shaftLen - 0.18 * R; g.add(tip);
    const wrap = new THREE.Group(); wrap.add(g);
    return wrap;
  }

  // Place a hex screw in each hub. Placement params are NORMALIZED to the hub
  // (rim) radius — the exact values the hub viewer exports — so they transfer at
  // any scale and across all 4 corners. Built with carGroup rotation ZEROED so
  // each bolt's local transform is correct and it spins with the car.
  _buildHubBolts() {
    if (!this.carGroup) return;
    (this._hubBolts || []).forEach(b => { try { b.parent && b.parent.remove(b); } catch (e) {} });
    this._hubBolts = [];
    // Defaults BAKED from the Hub Bolt Placer (user-confirmed 2026-06-17).
    this._boltDepth = this._boltDepth ?? 0.24;  // along hub axis, in hub-radius units
    this._boltScale = this._boltScale ?? 0.10;  // head radius / hub radius
    this._boltSpin  = this._boltSpin  ?? 0.0;   // rotation about hub axis (rad)
    this._boltU = this._boltU ?? 0.0;           // in-plane nudge (hub-radius units)
    this._boltV = this._boltV ?? 0.0;
    // Reference rims = the Circuit hi-res corners (the SAME geometry the viewer
    // measured); fall back to the centre-cap meshes if rims aren't loaded.
    const refs = (this._hiResRims && this._hiResRims.circuit && this._hiResRims.circuit.length)
      ? this._hiResRims.circuit : (this._wheelCapMeshes || []);
    if (!refs.length) return;
    const V = THREE.Vector3;
    const savedRot = this.carGroup.rotation.y;
    this.carGroup.rotation.y = 0; this.carGroup.updateWorldMatrix(true, true);
    const cgWorld = this.carGroup.getWorldPosition(new V());
    refs.forEach(ref => {
      ref.updateWorldMatrix(true, false);
      const box = new THREE.Box3().setFromObject(ref);
      if (box.isEmpty()) return;
      const c = box.getCenter(new V()), s = box.getSize(new V());
      const dims = [['x', s.x], ['y', s.y], ['z', s.z]].sort((a, b) => a[1] - b[1]);
      const axisKey = dims[0][0];
      const hubR = Math.max(dims[1][1], dims[2][1]) * 0.5;
      const axis = new V(axisKey === 'x' ? 1 : 0, axisKey === 'y' ? 1 : 0, axisKey === 'z' ? 1 : 0);
      if ((c[axisKey] - cgWorld[axisKey]) < 0) axis.negate();
      // in-plane basis — same fixed rule as the viewer
      let u, v;
      if (axisKey === 'z') { u = new V(1, 0, 0); v = new V(0, 1, 0); }
      else if (axisKey === 'x') { u = new V(0, 1, 0); v = new V(0, 0, 1); }
      else { u = new V(0, 0, 1); v = new V(1, 0, 0); }
      const bolt = this._makeBolt(hubR * this._boltScale);
      bolt.quaternion.setFromUnitVectors(new V(0, 1, 0), axis);
      bolt.rotateY(this._boltSpin);
      const worldPos = c.clone()
        .addScaledVector(axis, this._boltDepth * hubR)
        .addScaledVector(u, this._boltU * hubR)
        .addScaledVector(v, this._boltV * hubR);
      bolt.position.copy(this.carGroup.worldToLocal(worldPos));
      this.carGroup.add(bolt);
      this._hubBolts.push(bolt);
    });
    this.carGroup.rotation.y = savedRot; this.carGroup.updateWorldMatrix(true, true);
    this._updateHubBolts();
  }

  _updateHubBolts() {
    // Show the hub screw on ALL wheels (incl. stock, per user) whenever no centre
    // cap is fitted; the cap replaces it.
    const show = !!this._currentWheel && !this._capEnabled;
    (this._hubBolts || []).forEach(b => { b.visible = show; });
    this.requestRender && this.requestRender(200);
  }

  // Live tuner / bake — accepts the hub-viewer's normalized params.
  __boltTune(o = {}) {
    if (o.depth != null || o.axial != null) this._boltDepth = (o.depth != null ? o.depth : o.axial);
    if (o.boltDepth != null) this._boltDepth = o.boltDepth;
    if (o.scale != null) this._boltScale = o.scale;
    if (o.boltScale != null) this._boltScale = o.boltScale;
    if (o.spin != null) this._boltSpin = o.spin;
    if (o.boltSpin != null) this._boltSpin = o.boltSpin;
    if (o.u != null || o.boltU != null) this._boltU = (o.boltU != null ? o.boltU : o.u);
    if (o.v != null || o.boltV != null) this._boltV = (o.boltV != null ? o.boltV : o.v);
    this._buildHubBolts();
    return { depth: this._boltDepth, scale: this._boltScale, spin: this._boltSpin, u: this._boltU, v: this._boltV };
  }

  // ---- LICENSE PLATE MOUNTS + GRAPHIC ----
  // The GLB has a front number-plate node (flr-27) and a rear plate-bracket node
  // (flr-26). They stay hidden until a license plate is chosen in the Plates
  // step, then show per the mount option (both / front / rear). A textured plane
  // carrying the chosen plate design is overlaid on each shown bracket.
  // Call with (null) to clear everything.
  setLicensePlate(plate, mount) {
    this.requestRender && this.requestRender(800); // live text edits update the 3D without a UI re-render
    this._currentPlate = plate; this._currentPlateMount = mount;
    ['flr-27', 'flr-26'].forEach(id => this.toggleCombinedPart(id, false));
    this._removePlateGraphics();
    if (!plate) return;
    const mode = (mount && mount.id) || 'both';
    let front = mode === 'both' || mode === 'front';
    let rear  = mode === 'both' || mode === 'rear';
    // The big rear diffusers (flr-16 / flr-18) occupy the rear bracket — when one
    // is on, the plate can only go FRONT (we never drop the diffuser).
    const diffuserOn = ['flr-16', 'flr-18'].some(id => { const n = this.partNodeById && this.partNodeById.get(id); return n && n.visible; });
    if (diffuserOn) { front = true; rear = false; }
    this._plateFrontForced = diffuserOn; // UI reads this to show the notice + disable rear
    // Place the plate graphic with the car momentarily UN-rotated, then restore —
    // so the plane lands on the bracket correctly at ANY car angle (it used to be
    // computed in world space at the current rotation, so it only sat flat when the
    // car faced forward). attach() then glues it to the car so it rotates along.
    const savedRot = this.carGroup.rotation.y;
    this.carGroup.rotation.y = 0; this.carGroup.updateWorldMatrix(true, true);
    if (front) { this.toggleCombinedPart('flr-27', true); this._addPlateGraphic('front', plate); }
    if (rear)  { this.toggleCombinedPart('flr-26', true); this._addPlateGraphic('rear', plate); }
    this.carGroup.rotation.y = savedRot; this.carGroup.updateWorldMatrix(true, true);
    if (this._bracketColor) this.setBracketColor(this._bracketColor); // re-tint freshly-shown brackets
  }

  // Recolour the visible plate BRACKETS (GLB nodes flr-27 front / flr-26 rear).
  // `hex` null/undefined → restore the original carbon material. Clones the
  // material per mesh so shared materials elsewhere are never affected.
  setBracketColor(hex) {
    this._bracketColor = hex || null;
    ['flr-27', 'flr-26'].forEach(id => {
      const node = this.partNodeById && this.partNodeById.get(id);
      if (!node) return;
      node.traverse(o => {
        if (!o.isMesh || !o.material) return;
        if (!o.userData._origBracketMat) o.userData._origBracketMat = o.material;
        if (!hex) { o.material = o.userData._origBracketMat; return; }
        if (!o.userData._tintBracketMat) o.userData._tintBracketMat = o.userData._origBracketMat.clone();
        const m = o.userData._tintBracketMat;
        m.color.set(hex); m.map = null; m.metalness = 0.65; m.roughness = 0.32;
        if ('emissive' in m) m.emissive.set(0x000000);
        m.needsUpdate = true; o.material = m;
      });
    });
    this.requestRender && this.requestRender(400);
  }

  _removePlateGraphics() {
    (this._plateGraphics || []).forEach(m => {
      try { if (m.parent) m.parent.remove(m); if (m.material.map) m.material.map.dispose(); m.material.dispose(); m.geometry.dispose(); } catch (e) {}
    });
    this._plateGraphics = [];
  }

  // Overlay the plate design image on the bracket. Positioned from the bracket's
  // world bounding box; nudge sizes/offsets here (or via __frPlateTune) to taste.
  _addPlateGraphic(side, plate) {
    const id = side === 'front' ? 'flr-27' : 'flr-26';
    const node = this.partNodeById && this.partNodeById.get(id);
    if (!node || !plate || !(plate.image || plate.custom || plate.rendered)) return;
    node.updateWorldMatrix(true, true);
    const box = new THREE.Box3().setFromObject(node);
    if (box.isEmpty()) return;
    const c = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    const t = this._plateTune || {};
    const isFront = side === 'front';
    // The plane faces ±X, so its width spans Z and height spans Y — size it to
    // the bracket footprint so the design sits ON the plate, not floating.
    const w = Math.max(0.1, size.z) * (t.wScale || 0.86) * (t.scale || 1);
    const h = Math.max(0.05, size.y) * (t.hScale || 0.82) * (t.scale || 1);
    const tex = this._makePlateTexture(plate);
    // DoubleSide + sit just proud of the bracket's OUTER face so the black
    // bracket can't occlude it from any angle.
    const mat = new THREE.MeshBasicMaterial({ map: tex, transparent: true, toneMapped: false, side: THREE.DoubleSide });
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(w, h), mat);
    const gap = (t.gap != null ? t.gap : 0.006);
    mesh.position.set(
      isFront ? box.max.x + gap : box.min.x - gap,
      c.y + (t.dy || 0),
      c.z + (t.dz || 0)
    );
    mesh.rotation.y = isFront ? Math.PI / 2 : -Math.PI / 2;
    mesh.renderOrder = 4;
    mesh.userData.platePlane = side;
    // Set the WORLD transform above, then reparent onto the car so the plate
    // stays glued to the bracket when the car rotates/orbits (attach() keeps the
    // world transform). Previously added to the scene root → it "ghosted" off to
    // the side as the car turned.
    mesh.updateMatrixWorld(true);
    this.carGroup.attach(mesh);
    (this._plateGraphics = this._plateGraphics || []).push(mesh);
  }

  // Build the plate texture: the design image (or custom text) composited with
  // the chosen finish (brushed aluminium = default, holographic = rainbow
  // overlay, 24K gold = gold overlay). Returns a CanvasTexture that updates
  // itself once the design image loads. crossOrigin so the CDN image can be
  // drawn to canvas; on a CORS failure it falls back to a plain fill.
  // Small filled star (used by EU/USA region accents).
  _star(ctx, cx, cy, outer, inner, spikes) {
    ctx.beginPath();
    for (let i = 0; i < spikes * 2; i++) { const r = i % 2 === 0 ? outer : inner; const a = (i / (spikes * 2)) * Math.PI * 2 - Math.PI / 2; const x = cx + Math.cos(a) * r, y = cy + Math.sin(a) * r; i ? ctx.lineTo(x, y) : ctx.moveTo(x, y); }
    ctx.closePath(); ctx.fill();
  }

  // Region accent overlay for the custom plate — bold, country-vibe accents.
  _drawPlateRegion(ctx, W, H, plate) {
    const r = plate.region;
    if (!r || r === 'none') return;
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    // ---- EU-family + UK: navy left strip + star ring (or union cross) + code ----
    const EU = { eu: 1, germany: 1, france: 1, italy: 1, spain: 1 };
    const EU_CODE = { germany: 'D', france: 'F', italy: 'I', spain: 'E' };
    if (EU[r] || r === 'uk') {
      const sw = 64;
      ctx.fillStyle = r === 'uk' ? '#0b2a6b' : '#003399'; ctx.fillRect(14, 14, sw, H - 28);
      if (r === 'uk') {
        ctx.strokeStyle = 'rgba(255,255,255,0.85)'; ctx.lineWidth = 4;
        ctx.beginPath(); ctx.moveTo(14, 42); ctx.lineTo(14 + sw, 42); ctx.moveTo(14 + sw / 2, 16); ctx.lineTo(14 + sw / 2, 70); ctx.stroke();
      } else {
        ctx.fillStyle = '#ffcc00'; const sx = 14 + sw / 2, sy = 14 + 50;
        for (let i = 0; i < 12; i++) { const a = i / 12 * Math.PI * 2 - Math.PI / 2; this._star(ctx, sx + Math.cos(a) * 21, sy + Math.sin(a) * 21, 4.3, 1.9, 5); }
      }
      ctx.fillStyle = '#fff'; ctx.font = '800 30px Arial';
      ctx.fillText(r === 'uk' ? 'GB' : (EU_CODE[r] || ((plate.miniText || 'EU').slice(0, 2)).toUpperCase()), 14 + sw / 2, H - 46);
      return;
    }
    if (r === 'usa') {
      ctx.save(); ctx.globalAlpha = 0.12; ctx.fillStyle = '#ffffff'; this._star(ctx, W / 2, H / 2, 110, 48, 5); ctx.restore();
      const tab = (x, t, c) => { ctx.fillStyle = c; ctx.beginPath(); (ctx.roundRect ? ctx.roundRect(x, 20, 62, 36, 6) : ctx.rect(x, 20, 62, 36)); ctx.fill(); ctx.fillStyle = '#111'; ctx.font = '800 19px Arial'; ctx.fillText(t, x + 31, 38); };
      tab(W - 150, 'MAY', '#ffd34d'); tab(W - 82, '26', '#7fe6a0'); return;
    }
    if (r === 'california') {
      ctx.fillStyle = '#c8102e'; ctx.font = 'italic 800 36px "Brush Script MT","Segoe Script",cursive'; ctx.textBaseline = 'top'; ctx.fillText('California', W / 2, 16); return;
    }
    if (r === 'japan') {
      ctx.fillStyle = 'rgba(13,120,70,0.95)'; ctx.font = '800 22px Arial'; ctx.textBaseline = 'top';
      ctx.fillText(((plate.miniText || 'TOKYO 330').slice(0, 12)).toUpperCase(), W / 2, 18); return;
    }
    if (r === 'dubai') {
      const sw = 60, g = ctx.createLinearGradient(14, 14, 14 + sw, 14); g.addColorStop(0, '#d8b24a'); g.addColorStop(1, '#8a6a1f'); ctx.fillStyle = g; ctx.fillRect(14, 14, sw, H - 28);
      ctx.fillStyle = '#1c1206'; ctx.font = '800 24px Arial'; ctx.fillText('DXB', 14 + sw / 2, 44);
      ctx.fillStyle = 'rgba(255,238,190,0.95)'; ctx.beginPath(); ctx.arc(14 + sw / 2, H - 58, 17, 0, Math.PI * 2); ctx.fill(); return;
    }
    if (r === 'miami') {
      ctx.fillStyle = 'rgba(255,225,150,0.95)'; ctx.beginPath(); ctx.arc(W - 72, H - 64, 34, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = '#0a2a1a'; ctx.lineWidth = 6; ctx.lineCap = 'round';
      ctx.beginPath(); ctx.moveTo(W - 62, H - 16); ctx.quadraticCurveTo(W - 66, H - 64, W - 50, H - 92); ctx.stroke();
      for (const a of [-1.0, -0.45, 0.1, 0.65]) { ctx.beginPath(); ctx.moveTo(W - 50, H - 92); ctx.quadraticCurveTo(W - 50 + Math.cos(a) * 34, H - 100 + Math.sin(a) * 12, W - 50 + Math.cos(a) * 58, H - 90 + Math.sin(a) * 28); ctx.stroke(); }
      return;
    }
  }

  // Frame material + corner bolts for the custom plate.
  _drawPlateFrame(ctx, W, H, plate) {
    const fr = plate.frame || 'thin'; const pad = 14;
    if (fr === 'none') return;
    if (fr === 'thin') { ctx.strokeStyle = 'rgba(0,0,0,0.5)'; ctx.lineWidth = 12; ctx.strokeRect(pad, pad, W - pad * 2, H - pad * 2); return; }
    let c0, c1, c2;
    if (fr === 'chrome') { c0 = '#f4f6f9'; c1 = '#8b9099'; c2 = '#cfd4da'; }
    else if (fr === 'carbon') { c0 = '#2a2c32'; c1 = '#0c0d10'; c2 = '#1a1c21'; }
    else { c0 = '#43454a'; c1 = '#0c0c0e'; c2 = '#26282c'; }
    const fw = 17; const g = ctx.createLinearGradient(0, 0, 0, H); g.addColorStop(0, c0); g.addColorStop(0.5, c1); g.addColorStop(1, c2);
    ctx.fillStyle = g;
    ctx.fillRect(pad, pad, W - pad * 2, fw);
    ctx.fillRect(pad, H - pad - fw, W - pad * 2, fw);
    ctx.fillRect(pad, pad, fw, H - pad * 2);
    ctx.fillRect(W - pad - fw, pad, fw, H - pad * 2);
    ctx.strokeStyle = 'rgba(255,255,255,0.16)'; ctx.lineWidth = 1; ctx.strokeRect(pad + fw, pad + fw, W - (pad + fw) * 2, H - (pad + fw) * 2);
    const bolt = (x, y) => { const bg = ctx.createRadialGradient(x - 2, y - 2, 1, x, y, 7); bg.addColorStop(0, '#e8e9ee'); bg.addColorStop(0.6, '#9a9ea6'); bg.addColorStop(1, '#5a5e66'); ctx.fillStyle = bg; ctx.beginPath(); ctx.arc(x, y, 6.5, 0, Math.PI * 2); ctx.fill(); ctx.strokeStyle = 'rgba(0,0,0,0.5)'; ctx.lineWidth = 1.4; ctx.beginPath(); ctx.moveTo(x - 3.5, y); ctx.lineTo(x + 3.5, y); ctx.stroke(); };
    const b = pad + fw / 2; bolt(b, b); bolt(W - b, b); bolt(b, H - b); bolt(W - b, H - b);
  }

  // Small deterministic "1 of 1" serial along the bottom of the plate.
  _drawPlateSerial(ctx, W, H, plate) {
    const s = [plate.text, plate.bgStyle, plate.bgColor, plate.textColor, plate.font, plate.emoji, plate.region].join('|');
    let hh = 0; for (let i = 0; i < s.length; i++) hh = (hh * 31 + s.charCodeAt(i)) >>> 0;
    const code = hh.toString(36).toUpperCase().slice(0, 4).padStart(4, '0');
    const label = `1 OF 1 · #FR-${code}`;
    ctx.textAlign = 'center'; ctx.textBaseline = 'bottom'; ctx.font = '700 13px "JetBrains Mono", monospace';
    ctx.fillStyle = 'rgba(0,0,0,0.5)'; ctx.fillText(label, W / 2 + 0.5, H - 19.5);
    ctx.fillStyle = 'rgba(255,255,255,0.6)'; ctx.fillText(label, W / 2, H - 20);
  }

  // Premium gold "Flameracer" autograph in the corner (the Signature option).
  _drawPlateSignature(ctx, W, H) {
    ctx.save();
    ctx.font = 'italic 700 24px "Brush Script MT","Segoe Script","Snell Roundhand",cursive';
    ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
    const g = ctx.createLinearGradient(28, 0, 175, 0); g.addColorStop(0, '#fbe6a0'); g.addColorStop(0.5, '#d4af37'); g.addColorStop(1, '#a8842a');
    ctx.fillStyle = g; ctx.shadowColor = 'rgba(0,0,0,0.45)'; ctx.shadowBlur = 3; ctx.shadowOffsetY = 1;
    ctx.fillText('Flameracer', 30, H - 24);
    ctx.restore();
  }

  // Draw the custom-plate BACKGROUND — a solid colour or a drawn style (carbon,
  // city skyline, neon, galaxy, aurora, circuit, chrome…). All canvas-drawn, so
  // there are no image assets to upload.
  _drawPlateBg(ctx, W, H, plate) {
    const base = plate.bgColor || '#f2f2f2';
    const style = plate.bgStyle || 'solid';
    const shade = (hex, f) => { const n = parseInt(hex.slice(1).padEnd(6, '0').slice(0, 6), 16); const cl = v => Math.max(0, Math.min(255, Math.round(v))); const r = cl(((n >> 16) & 255) * (1 + f)), g = cl(((n >> 8) & 255) * (1 + f)), b = cl((n & 255) * (1 + f)); return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1); };
    const lumOf = hex => { const n = parseInt(hex.slice(1).padEnd(6, '0').slice(0, 6), 16); return (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255; };
    const fill = c => { ctx.fillStyle = c; ctx.fillRect(0, 0, W, H); };
    // Seeded RNG so star / trace patterns stay STABLE across redraws (no jitter when
    // you change an unrelated option). Seed varies by style so each looks distinct.
    let _s = 0x9e3779b9 ^ style.length; for (let i = 0; i < style.length; i++) _s = Math.imul(_s ^ style.charCodeAt(i), 0x85ebca6b) >>> 0;
    const rnd = () => { _s = (_s + 0x6D2B79F5) | 0; let t = Math.imul(_s ^ (_s >>> 15), 1 | _s); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
    switch (style) {
      case 'gradient': { const g = ctx.createLinearGradient(0, 0, W, H); g.addColorStop(0, shade(base, 0.18)); g.addColorStop(1, shade(base, -0.5)); fill(g); break; }
      case 'stripes': { fill(base); ctx.fillStyle = lumOf(base) > 0.5 ? 'rgba(0,0,0,0.78)' : 'rgba(255,255,255,0.85)'; const sw = 26, gap = 12, cx = W / 2; ctx.fillRect(cx - sw - gap / 2, 0, sw, H); ctx.fillRect(cx + gap / 2, 0, sw, H); break; }
      case 'carbon': { fill('#15171b'); const s = 14; for (let y = 0; y < H; y += s) { for (let x = 0; x < W; x += s) { ctx.fillStyle = (((x / s + y / s) & 1) === 0) ? '#21242b' : '#16181c'; ctx.fillRect(x, y, s, s); } } const g = ctx.createLinearGradient(0, 0, 0, H); g.addColorStop(0, 'rgba(255,255,255,0.10)'); g.addColorStop(0.5, 'rgba(255,255,255,0)'); g.addColorStop(1, 'rgba(0,0,0,0.20)'); ctx.fillStyle = g; ctx.fillRect(0, 0, W, H); break; }
      case 'galaxy': { const g = ctx.createLinearGradient(0, 0, W, H); g.addColorStop(0, '#05060f'); g.addColorStop(0.5, '#1a1040'); g.addColorStop(1, '#3b0a52'); fill(g); const neb = ctx.createRadialGradient(W * 0.7, H * 0.35, 8, W * 0.7, H * 0.35, 190); neb.addColorStop(0, 'rgba(130,70,230,0.5)'); neb.addColorStop(1, 'rgba(130,70,230,0)'); ctx.fillStyle = neb; ctx.fillRect(0, 0, W, H); ctx.fillStyle = '#fff'; for (let i = 0; i < 150; i++) { ctx.globalAlpha = 0.3 + rnd() * 0.7; ctx.beginPath(); ctx.arc(rnd() * W, rnd() * H, rnd() * 1.4, 0, Math.PI * 2); ctx.fill(); } ctx.globalAlpha = 1; break; }
      case 'aurora': { const g = ctx.createLinearGradient(0, 0, 0, H); g.addColorStop(0, '#020a18'); g.addColorStop(1, '#06121f'); fill(g); [['rgba(40,255,160,0.35)', 0.42], ['rgba(60,180,255,0.30)', 0.52], ['rgba(180,80,255,0.25)', 0.62]].forEach(([col, yf]) => { ctx.fillStyle = col; ctx.beginPath(); ctx.moveTo(0, H * yf); for (let x = 0; x <= W; x += 16) ctx.lineTo(x, H * yf + Math.sin(x / 55) * 26 - (x / W) * 30); ctx.lineTo(W, H); ctx.lineTo(0, H); ctx.closePath(); ctx.fill(); }); ctx.fillStyle = 'rgba(255,255,255,0.8)'; for (let i = 0; i < 50; i++) ctx.fillRect(rnd() * W, rnd() * H * 0.4, 1.4, 1.4); break; }
      case 'circuit': { fill('#06120c'); ctx.strokeStyle = 'rgba(60,255,140,0.5)'; ctx.lineWidth = 2; ctx.lineCap = 'round'; for (let i = 0; i < 22; i++) { let x = rnd() * W, yy = rnd() * H; ctx.beginPath(); ctx.moveTo(x, yy); for (let s = 0; s < 3; s++) { x += (rnd() * 60 + 20) * (rnd() < 0.5 ? 1 : -1); ctx.lineTo(x, yy); yy += (rnd() * 40 - 20); ctx.lineTo(x, yy); } ctx.stroke(); } ctx.fillStyle = 'rgba(80,255,160,0.85)'; for (let i = 0; i < 40; i++) { ctx.beginPath(); ctx.arc(rnd() * W, rnd() * H, 2.4, 0, Math.PI * 2); ctx.fill(); } break; }
      case 'sunset': { const g = ctx.createLinearGradient(0, 0, 0, H); g.addColorStop(0, '#2b0b52'); g.addColorStop(0.45, '#c0297a'); g.addColorStop(0.72, '#ff7a3d'); g.addColorStop(1, '#ffd36b'); fill(g); ctx.fillStyle = 'rgba(255,240,180,0.92)'; ctx.beginPath(); ctx.arc(W / 2, H * 0.66, 54, 0, Math.PI * 2); ctx.fill(); break; }
      case 'skyline': { const g = ctx.createLinearGradient(0, 0, 0, H); g.addColorStop(0, '#0b1f3a'); g.addColorStop(0.55, '#1d4e74'); g.addColorStop(1, '#e89a4a'); fill(g); const seed = [60, 110, 40, 150, 80, 130, 55, 170, 95, 120, 70, 140, 50, 160, 90, 100]; let x = 0, si = 0; const baseY = H; while (x < W) { const v = seed[si % seed.length]; const bw = 24 + (v % 30); const bh = Math.min(70 + v, H * 0.62); const top = baseY - bh; ctx.fillStyle = '#0a0e17'; ctx.fillRect(x, top, bw, bh); ctx.fillStyle = 'rgba(255,214,120,0.5)'; for (let wy = top + 8; wy < baseY - 8; wy += 14) { for (let wx = x + 5; wx < x + bw - 5; wx += 12) { if (((wx + wy) & 3) === 0) ctx.fillRect(wx, wy, 4, 6); } } x += bw + 4; si++; } break; }
      case 'neon': { fill('#0a0a12'); const g = ctx.createLinearGradient(0, 0, 0, H * 0.5); g.addColorStop(0, '#1b1140'); g.addColorStop(1, '#0a0a12'); ctx.fillStyle = g; ctx.fillRect(0, 0, W, H * 0.5); ctx.strokeStyle = 'rgba(255,40,160,0.5)'; ctx.lineWidth = 2; for (let i = 1; i <= 9; i++) { const y = H * 0.5 + (i * i) * 1.7; if (y > H) break; ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); } for (let i = -6; i <= 6; i++) { ctx.beginPath(); ctx.moveTo(W / 2, H * 0.5); ctx.lineTo(W / 2 + i * 72, H); ctx.stroke(); } break; }
      // ── TASK 2: TACTICAL + CYBERPUNK family (near-black canvas styles) ──
      case 'cyber': { fill('#05060b'); const g = ctx.createRadialGradient(W / 2, H / 2, 8, W / 2, H / 2, W * 0.62); g.addColorStop(0, 'rgba(0,60,80,0.35)'); g.addColorStop(1, 'rgba(5,6,11,0)'); ctx.fillStyle = g; ctx.fillRect(0, 0, W, H); const cell = 26; ctx.lineWidth = 1; ctx.strokeStyle = 'rgba(0,240,255,0.35)'; for (let x = 0; x <= W; x += cell) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke(); } ctx.strokeStyle = 'rgba(255,0,200,0.28)'; for (let y = 0; y <= H; y += cell) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); } ctx.fillStyle = 'rgba(0,240,255,0.9)'; for (let i = 0; i < 22; i++) { ctx.globalAlpha = 0.4 + rnd() * 0.6; ctx.fillRect(Math.round(rnd() * W / cell) * cell - 2, Math.round(rnd() * H / cell) * cell - 2, 4, 4); } ctx.globalAlpha = 1; const glow = ctx.createLinearGradient(0, H, 0, H * 0.55); glow.addColorStop(0, 'rgba(255,0,200,0.30)'); glow.addColorStop(1, 'rgba(255,0,200,0)'); ctx.fillStyle = glow; ctx.fillRect(0, H * 0.55, W, H * 0.45); break; }
      case 'tactical': { fill('#14160f'); ctx.save(); ctx.globalAlpha = 0.5; const s = 16; for (let y = 0; y < H; y += s) for (let x = 0; x < W; x += s) { ctx.fillStyle = (((x / s + y / s) & 1) === 0) ? '#1b1e15' : '#14160f'; ctx.fillRect(x, y, s, s); } ctx.restore(); ctx.fillStyle = 'rgba(74,92,46,0.9)'; const bar = Math.round(H * 0.16); ctx.fillRect(0, Math.round(H * 0.12), W, bar); ctx.fillRect(0, Math.round(H * 0.72), W, bar); ctx.fillStyle = 'rgba(0,0,0,0.55)'; for (let x = 24; x < W; x += 44) { ctx.fillRect(x, Math.round(H * 0.12), 12, bar); ctx.fillRect(x, Math.round(H * 0.72), 12, bar); } ctx.strokeStyle = 'rgba(150,170,110,0.35)'; ctx.lineWidth = 3; ctx.strokeRect(8, 8, W - 16, H - 16); break; }
      case 'bloodtech': { fill('#0a0304'); const s = Math.round(H * 0.16); ctx.strokeStyle = 'rgba(220,30,50,0.4)'; ctx.lineWidth = 1.4; for (let row = 0, y = 0; y < H + s; y += s * 0.75, row++) { for (let x = (row & 1 ? s * 0.9 : 0) - s; x < W + s; x += s * 1.8) { ctx.beginPath(); for (let k = 0; k < 6; k++) { const a = Math.PI / 3 * k - Math.PI / 6; const px = x + s * 0.9 * Math.cos(a), py = y + s * 0.9 * Math.sin(a); k ? ctx.lineTo(px, py) : ctx.moveTo(px, py); } ctx.closePath(); ctx.stroke(); } } const g = ctx.createLinearGradient(0, 0, W, H); g.addColorStop(0, 'rgba(150,10,20,0.25)'); g.addColorStop(0.5, 'rgba(0,0,0,0)'); g.addColorStop(1, 'rgba(150,10,20,0.25)'); ctx.fillStyle = g; ctx.fillRect(0, 0, W, H); ctx.fillStyle = 'rgba(255,40,60,0.85)'; for (let i = 0; i < 16; i++) { ctx.globalAlpha = 0.3 + rnd() * 0.6; ctx.fillRect(rnd() * W, rnd() * H, 2, 2); } ctx.globalAlpha = 1; break; }
      case 'noir': { fill('#0c0c0e'); ctx.save(); ctx.globalAlpha = 0.9; ctx.fillStyle = 'rgba(255,255,255,0.06)'; for (let y = 0; y < H; y += 4) ctx.fillRect(0, y, W, 2); ctx.restore(); const g = ctx.createRadialGradient(W / 2, H / 2, 10, W / 2, H / 2, W * 0.6); g.addColorStop(0, 'rgba(255,255,255,0.10)'); g.addColorStop(1, 'rgba(0,0,0,0.35)'); ctx.fillStyle = g; ctx.fillRect(0, 0, W, H); ctx.strokeStyle = 'rgba(255,255,255,0.5)'; ctx.lineWidth = 2; ctx.strokeRect(10, 10, W - 20, H - 20); break; }
      case 'solid':
      default: fill(base);
    }
  }

  // Compose the whole CUSTOM plate (bg + region + sheen + frame + text + serial +
  // finish) onto a 2D context. Single source of truth — used by BOTH the 3D plate
  // texture and the live 2D preview in the editor, so they always match.
  _paintCustomPlate(ctx, W, H, plate) {
    this._drawPlateBg(ctx, W, H, plate);
    this._drawPlateRegion(ctx, W, H, plate); // EU strip / USA marks / Miami palm
    const sh = ctx.createLinearGradient(0, 0, 0, H);
    sh.addColorStop(0, 'rgba(255,255,255,0.18)'); sh.addColorStop(0.5, 'rgba(255,255,255,0)'); sh.addColorStop(1, 'rgba(0,0,0,0.14)');
    ctx.fillStyle = sh; ctx.fillRect(0, 0, W, H);
    this._drawPlateFrame(ctx, W, H, plate); // frame material + corner bolts
    const fontFamily = plate.font || '"Arial Black", Arial';
    const txtColor = plate.textColor || '#111114';
    const tLum = (h => { const n = parseInt((h || '#111').slice(1).padEnd(6, '0').slice(0, 6), 16); return (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255; })(txtColor);
    const outline = tLum > 0.5 ? 'rgba(0,0,0,0.72)' : 'rgba(255,255,255,0.72)';
    const lettering = plate.lettering || 'flat';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.lineJoin = 'round';
    const drawTxt = (text, x, y, fs2) => {
      if (lettering === 'raised') {
        ctx.fillStyle = 'rgba(0,0,0,0.55)'; ctx.fillText(text, x + 3, y + 3);
        ctx.fillStyle = 'rgba(255,255,255,0.5)'; ctx.fillText(text, x - 1.5, y - 1.5);
        ctx.fillStyle = txtColor; ctx.fillText(text, x, y);
      } else if (lettering === 'stamped') {
        ctx.fillStyle = 'rgba(255,255,255,0.45)'; ctx.fillText(text, x + 1.5, y + 1.5);
        ctx.fillStyle = 'rgba(0,0,0,0.6)'; ctx.fillText(text, x - 2, y - 2);
        ctx.fillStyle = txtColor; ctx.fillText(text, x, y);
      } else {
        ctx.lineWidth = Math.max(4, fs2 * 0.12); ctx.strokeStyle = outline; ctx.strokeText(text, x, y);
        ctx.fillStyle = txtColor; ctx.fillText(text, x, y);
      }
    };
    const emoji = plate.emoji || '';
    const flank = emoji ? 86 : 0;
    const mini = (plate.miniText || '').slice(0, 16).toUpperCase();
    const txt = (plate.text || 'CUSTOM').slice(0, 8).toUpperCase();
    const mainY = mini ? H / 2 - 22 : H / 2 + 6;
    let fs = mini ? 120 : 150; ctx.font = `900 ${fs}px ${fontFamily}`;
    while (ctx.measureText(txt).width > W - 70 - flank * 2 && fs > 24) { fs -= 6; ctx.font = `900 ${fs}px ${fontFamily}`; }
    drawTxt(txt, W / 2, mainY, fs);
    if (emoji) {
      ctx.font = `${Math.round(fs * 0.7)}px "Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif`;
      ctx.fillText(emoji, W * 0.12, mainY); ctx.fillText(emoji, W * 0.88, mainY);
    }
    if (mini) {
      let ms = 46; ctx.font = `700 ${ms}px ${fontFamily}`;
      while (ctx.measureText(mini).width > W - 90 && ms > 14) { ms -= 3; ctx.font = `700 ${ms}px ${fontFamily}`; }
      drawTxt(mini, W / 2, H * 0.8, ms);
    }
    if (plate.serial !== false) this._drawPlateSerial(ctx, W, H, plate);
    if (plate.signature) this._drawPlateSignature(ctx, W, H);
    // finish overlay (holographic / gold tint)
    const fin = plate.finish || {};
    if (fin.gradient) {
      ctx.globalCompositeOperation = 'overlay'; const g = ctx.createLinearGradient(0, 0, W, H); fin.gradient.forEach((c, i) => g.addColorStop(i / Math.max(1, fin.gradient.length - 1), c)); ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
      ctx.globalCompositeOperation = 'soft-light'; ctx.fillStyle = g; ctx.fillRect(0, 0, W, H); ctx.globalCompositeOperation = 'source-over';
    } else if (/gold/i.test(fin.name || '') || fin.id === 'brushed-gold') {
      ctx.globalCompositeOperation = 'overlay'; ctx.fillStyle = 'rgba(212,175,55,0.7)'; ctx.fillRect(0, 0, W, H); ctx.globalCompositeOperation = 'source-over';
    }
  }

  _makePlateTexture(plate) {
    const W = 512, H = 256;
    const cv = document.createElement('canvas'); cv.width = W; cv.height = H;
    const ctx = cv.getContext('2d');
    const tex = new THREE.CanvasTexture(cv); tex.colorSpace = THREE.SRGBColorSpace;
    const fin = plate.finish || {};
    const overlay = () => {
      if (fin.gradient) { // holographic
        ctx.globalCompositeOperation = 'overlay';
        const g = ctx.createLinearGradient(0, 0, W, H);
        fin.gradient.forEach((c, i) => g.addColorStop(i / Math.max(1, fin.gradient.length - 1), c));
        ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
        ctx.globalCompositeOperation = 'soft-light';
        ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
        ctx.globalCompositeOperation = 'source-over';
      } else if (/gold/i.test(fin.name || '') || fin.id === 'brushed-gold') {
        ctx.globalCompositeOperation = 'overlay';
        ctx.fillStyle = 'rgba(212,175,55,0.7)'; ctx.fillRect(0, 0, W, H);
        ctx.globalCompositeOperation = 'source-over';
      } // brushed aluminium → no tint (the plate's own look)
    };
    if (plate.custom || plate.rendered) {
      this._paintCustomPlate(ctx, W, H, plate);
      tex.needsUpdate = true;
    } else {
      ctx.fillStyle = '#d8d9dd'; ctx.fillRect(0, 0, W, H); // base while loading
      // Try the CDN url first (live store), then the THEME ASSETS base (same place
      // the decal PNGs live — works on live AND in the dev harness), then relative.
      const cand = [(window.FR_IMG ? window.FR_IMG(plate.image) : null), this._decalAssetBase() + plate.image, plate.image, './' + plate.image].filter(Boolean);
      let ci = 0;
      const tryNext = () => {
        if (ci >= cand.length) { ctx.fillStyle = '#cfd0d4'; ctx.fillRect(0, 0, W, H); overlay(); tex.needsUpdate = true; return; }
        const img = new Image(); img.crossOrigin = 'anonymous';
        img.onload = () => { try { ctx.clearRect(0, 0, W, H); ctx.drawImage(img, 0, 0, W, H); overlay(); tex.needsUpdate = true; } catch (e) { ci++; tryNext(); } };
        img.onerror = () => { ci++; tryNext(); };
        img.src = cand[ci];
      };
      tryNext();
    }
    return tex;
  }

  // ════════════════════ CUSTOM RACING PERMIT RENDERER ════════════════════
  // A motorsport CREDENTIAL (not a license plate): header band + stamped STATUS +
  // official seal + permit number + security accents (checker / holo / barcode), in
  // distinct card LAYOUTS. Single source of truth for the editor preview, the 3D
  // decal texture, and the print PNG. Design fields:
  //   layout: 'authorization' | 'pitpass' | 'license'
  //   title, status, cls (class/grade), holder, authority, season, number(optional)
  //   accent (#hex), base ('carbon'|'noir'|'navy'|'blood'|'steel'|'security')
  //   stamp / holo / barcode / checker (booleans)
  _accentInk(hex) {
    const n = parseInt((hex || '#f5d000').slice(1).padEnd(6, '0').slice(0, 6), 16);
    const lum = (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
    return lum > 0.62 ? '#0a0a0c' : '#ffffff';
  }
  _permitNo(d) {
    if (d.number) return d.number;
    const s = [d.title, d.status, d.cls, d.authority, d.layout, d.season].join('|');
    let h = 0; for (let i = 0; i < s.length; i++) h = (Math.imul(h, 31) + s.charCodeAt(i)) >>> 0;
    const L = String.fromCharCode(65 + (h % 26)) + String.fromCharCode(65 + ((h >> 8) % 26));
    return `${L}-${d.season || '2026'}-${String(h % 9000 + 1000)}`;
  }
  _permitBase(ctx, W, H, d) {
    const base = d.base || 'carbon';
    const fill = c => { ctx.fillStyle = c; ctx.fillRect(0, 0, W, H); };
    let _s = 0x9e3779b9 ^ base.length; for (let i = 0; i < base.length; i++) _s = Math.imul(_s ^ base.charCodeAt(i), 0x85ebca6b) >>> 0;
    const rnd = () => { _s = (_s + 0x6D2B79F5) | 0; let t = Math.imul(_s ^ (_s >>> 15), 1 | _s); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
    switch (base) {
      case 'noir': { const g = ctx.createRadialGradient(W / 2, H * 0.42, 16, W / 2, H / 2, W * 0.72); g.addColorStop(0, '#202127'); g.addColorStop(1, '#0a0a0c'); fill(g); break; }
      case 'navy': { const g = ctx.createLinearGradient(0, 0, 0, H); g.addColorStop(0, '#0a1730'); g.addColorStop(0.5, '#14315e'); g.addColorStop(1, '#0a1730'); fill(g); break; }
      case 'blood': { const g = ctx.createLinearGradient(0, 0, W, H); g.addColorStop(0, '#1a0406'); g.addColorStop(0.5, '#6a121b'); g.addColorStop(1, '#16050a'); fill(g); break; }
      case 'steel': { const g = ctx.createLinearGradient(0, 0, 0, H); g.addColorStop(0, '#3c4048'); g.addColorStop(0.48, '#9aa0ab'); g.addColorStop(0.52, '#7b818c'); g.addColorStop(1, '#2a2d33'); fill(g); ctx.strokeStyle = 'rgba(255,255,255,0.05)'; for (let i = 0; i < 320; i++) { const x = rnd() * W; ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke(); } break; }
      case 'security': { fill('#0a1024'); ctx.strokeStyle = 'rgba(120,160,255,0.12)'; ctx.lineWidth = 1; for (let k = 0; k < 7; k++) { ctx.beginPath(); for (let a = 0; a <= Math.PI * 2 + 0.03; a += 0.03) { const rr = 60 + 26 * Math.sin(a * (5 + k)) + 12 * Math.cos(a * (2 + k)); ctx.lineTo(W / 2 + Math.cos(a) * rr * 2.5, H / 2 + Math.sin(a) * rr); } ctx.stroke(); } break; }
      // ── TASK 2: TACTICAL + CYBERPUNK family ──
      case 'cyber': { fill('#05060b'); const rg = ctx.createRadialGradient(W / 2, H / 2, 8, W / 2, H / 2, W * 0.6); rg.addColorStop(0, 'rgba(0,60,80,0.4)'); rg.addColorStop(1, 'rgba(5,6,11,0)'); ctx.fillStyle = rg; ctx.fillRect(0, 0, W, H); const cell = 22; ctx.lineWidth = 1; ctx.strokeStyle = 'rgba(0,240,255,0.32)'; for (let x = 0; x <= W; x += cell) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke(); } ctx.strokeStyle = 'rgba(255,0,200,0.26)'; for (let y = 0; y <= H; y += cell) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); } ctx.fillStyle = 'rgba(0,240,255,0.9)'; for (let i = 0; i < 20; i++) { ctx.globalAlpha = 0.4 + rnd() * 0.6; ctx.fillRect(Math.round(rnd() * W / cell) * cell - 2, Math.round(rnd() * H / cell) * cell - 2, 4, 4); } ctx.globalAlpha = 1; break; }
      case 'tactical': { fill('#14160f'); const s2 = 14; for (let y = 0; y < H; y += s2) for (let x = 0; x < W; x += s2) { ctx.fillStyle = (((x / s2 + y / s2) & 1) === 0) ? '#1b1e15' : '#14160f'; ctx.fillRect(x, y, s2, s2); } ctx.fillStyle = 'rgba(74,92,46,0.85)'; ctx.fillRect(0, Math.round(H * 0.08), W, Math.round(H * 0.1)); ctx.fillStyle = 'rgba(0,0,0,0.5)'; for (let x = 20; x < W; x += 40) ctx.fillRect(x, Math.round(H * 0.08), 10, Math.round(H * 0.1)); break; }
      case 'bloodtech': { fill('#0a0304'); const s2 = Math.round(H * 0.2); ctx.strokeStyle = 'rgba(220,30,50,0.38)'; ctx.lineWidth = 1.4; for (let row = 0, y = 0; y < H + s2; y += s2 * 0.75, row++) { for (let x = (row & 1 ? s2 * 0.9 : 0) - s2; x < W + s2; x += s2 * 1.8) { ctx.beginPath(); for (let k = 0; k < 6; k++) { const a = Math.PI / 3 * k - Math.PI / 6; const px = x + s2 * 0.9 * Math.cos(a), py = y + s2 * 0.9 * Math.sin(a); k ? ctx.lineTo(px, py) : ctx.moveTo(px, py); } ctx.closePath(); ctx.stroke(); } } const g2 = ctx.createLinearGradient(0, 0, W, H); g2.addColorStop(0, 'rgba(150,10,20,0.25)'); g2.addColorStop(0.5, 'rgba(0,0,0,0)'); g2.addColorStop(1, 'rgba(150,10,20,0.25)'); ctx.fillStyle = g2; ctx.fillRect(0, 0, W, H); break; }
      case 'carbon': default: { fill('#15171b'); const s = 12; for (let y = 0; y < H; y += s) for (let x = 0; x < W; x += s) { ctx.fillStyle = (((x / s + y / s) & 1) === 0) ? '#20232a' : '#16181c'; ctx.fillRect(x, y, s, s); } const g = ctx.createLinearGradient(0, 0, 0, H); g.addColorStop(0, 'rgba(255,255,255,0.08)'); g.addColorStop(0.5, 'rgba(255,255,255,0)'); g.addColorStop(1, 'rgba(0,0,0,0.22)'); ctx.fillStyle = g; ctx.fillRect(0, 0, W, H); break; }
    }
    ctx.strokeStyle = 'rgba(255,255,255,0.12)'; ctx.lineWidth = 2; ctx.strokeRect(6, 6, W - 12, H - 12);
  }
  _permitSeal(ctx, cx, cy, r, ring, ink) {
    ctx.save(); ctx.translate(cx, cy);
    ctx.strokeStyle = ring; ctx.lineWidth = 3; ctx.beginPath(); ctx.arc(0, 0, r, 0, Math.PI * 2); ctx.stroke();
    ctx.lineWidth = 1.4; ctx.beginPath(); ctx.arc(0, 0, r - 7, 0, Math.PI * 2); ctx.stroke();
    for (let i = 0; i < 28; i++) { const a = i / 28 * Math.PI * 2; ctx.beginPath(); ctx.moveTo(Math.cos(a) * (r - 7), Math.sin(a) * (r - 7)); ctx.lineTo(Math.cos(a) * (r - 3), Math.sin(a) * (r - 3)); ctx.stroke(); }
    ctx.fillStyle = ring; if (this._star) this._star(ctx, 0, 0, r * 0.4, r * 0.17, 5);
    ctx.restore();
  }
  _permitChecker(ctx, x, y, w, h, sq) {
    ctx.save(); ctx.beginPath(); ctx.rect(x, y, w, h); ctx.clip();
    const cols = Math.ceil(w / sq), rows = Math.ceil(h / sq);
    for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) { ctx.fillStyle = ((r + c) & 1) ? '#ffffff' : '#0a0a0c'; ctx.fillRect(x + c * sq, y + r * sq, sq, sq); }
    ctx.restore();
  }
  _permitHolo(ctx, x, y, w, h) {
    const g = ctx.createLinearGradient(x, y, x + w, y);
    ['#ff3b9a', '#ffd23b', '#3bff9a', '#3bd0ff', '#b06bff', '#ff3b9a'].forEach((c, i, a) => g.addColorStop(i / (a.length - 1), c));
    ctx.save(); ctx.globalAlpha = 0.55; ctx.fillStyle = g; ctx.fillRect(x, y, w, h);
    ctx.globalAlpha = 0.22; ctx.fillStyle = '#fff'; for (let i = 0; i < w; i += 3) ctx.fillRect(x + i, y, 1, h); ctx.restore();
    ctx.strokeStyle = 'rgba(255,255,255,0.35)'; ctx.lineWidth = 1; ctx.strokeRect(x + 0.5, y + 0.5, w - 1, h - 1);
  }
  _permitBarcode(ctx, x, y, w, h, seed) {
    let s = (seed >>> 0) || 98765; const rnd = () => { s = (Math.imul(s, 1103515245) + 12345) >>> 0; return s / 4294967296; };
    ctx.fillStyle = '#fff'; ctx.fillRect(x - 3, y - 3, w + 6, h + 6);
    let cx = x; while (cx < x + w) { const bw = 1 + Math.floor(rnd() * 4); if (rnd() < 0.55) { ctx.fillStyle = '#0a0a0c'; ctx.fillRect(cx, y, bw, h); } cx += bw + 1; }
  }
  _paintCustomPermit(ctx, W, H, d) {
    d = d || {};
    const acc = d.accent || '#f5d000', ink = this._accentInk(acc), layout = d.layout || 'authorization';
    const BEBAS = '"Bebas Neue", Impact, sans-serif', MONO = '"JetBrains Mono", monospace', BLACK = '"Arial Black", Arial';
    const fit = (txt, weight, px, maxW, fam) => { fam = fam || BLACK; let f = px; ctx.font = `${weight} ${f}px ${fam}`; while (ctx.measureText(txt).width > maxW && f > 8) { f -= 1; ctx.font = `${weight} ${f}px ${fam}`; } return f; };
    const title = (d.title || 'RACING PERMIT').toUpperCase(), status = (d.status || 'APPROVED').toUpperCase();
    const authority = (d.authority || 'FLAMERACER MOTORSPORT').toUpperCase(), season = (d.season || '2026'), no = this._permitNo(d);
    let bseed = 0; for (let i = 0; i < no.length; i++) bseed = (Math.imul(bseed, 31) + no.charCodeAt(i)) >>> 0;
    this._permitBase(ctx, W, H, d);
    const badge = (text, x, y, fill, tint, px) => { ctx.font = `800 ${px}px ${BEBAS}`; const w = ctx.measureText(text).width + 18; ctx.fillStyle = fill; ctx.fillRect(x, y, w, px + 10); ctx.fillStyle = tint; ctx.textAlign = 'left'; ctx.textBaseline = 'middle'; ctx.fillText(text, x + 9, y + (px + 10) / 2 + 1); ctx.textBaseline = 'alphabetic'; return w; };

    if (layout === 'pitpass') {
      const railW = Math.round(W * 0.34);
      ctx.fillStyle = acc; ctx.fillRect(8, 8, railW, H - 16);
      ctx.fillStyle = 'rgba(0,0,0,0.32)'; ctx.beginPath(); ctx.arc(8 + railW / 2, 34, 13, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#0a0a0c'; ctx.beginPath(); ctx.arc(8 + railW / 2, 34, 7, 0, Math.PI * 2); ctx.fill();
      ctx.save(); ctx.translate(8 + railW * 0.46, H - 30); ctx.rotate(-Math.PI / 2);
      ctx.fillStyle = ink; ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
      const tf = fit(title, '800', 38, H - 120, BEBAS); ctx.font = `800 ${tf}px ${BEBAS}`; ctx.fillText(title, 0, 0); ctx.restore();
      if (d.stamp !== false) this._permitSeal(ctx, 8 + railW / 2, H - 52, 26, ink, ink);
      const rx = railW + 24;
      ctx.textAlign = 'left'; ctx.fillStyle = 'rgba(255,255,255,0.6)'; ctx.font = `700 13px ${MONO}`; ctx.fillText('SEASON ' + season, rx, 40);
      const sf = fit(status, '900', 58, W - rx - 26, BLACK); ctx.font = `900 ${sf}px ${BLACK}`; ctx.fillStyle = '#fff'; ctx.textBaseline = 'alphabetic'; ctx.fillText(status, rx, 46 + sf * 0.86);
      let by = 132; if (d.cls) { badge('CLASS ' + d.cls, rx, by, acc, ink, 17); by += 36; }
      if (d.holder) { ctx.fillStyle = 'rgba(255,255,255,0.92)'; ctx.font = `700 16px ${BEBAS}`; ctx.textAlign = 'left'; ctx.fillText('HOLDER · ' + d.holder.toUpperCase(), rx, by + 4); by += 26; }
      ctx.fillStyle = 'rgba(255,255,255,0.55)'; ctx.font = `600 11px ${MONO}`; ctx.fillText('No. ' + no, rx, H - 24);
      if (d.barcode !== false) this._permitBarcode(ctx, W - 134, H - 40, 110, 22, bseed);
      if (d.holo) this._permitHolo(ctx, 8, 8, railW, 10);
    } else if (layout === 'license') {
      ctx.fillStyle = acc; ctx.fillRect(14, 14, W - 28, 6);
      ctx.fillStyle = 'rgba(255,255,255,0.95)'; ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
      const tf = fit(title, '800', 30, W - 200, BEBAS); ctx.font = `800 ${tf}px ${BEBAS}`; ctx.fillText(title, 24, 56);
      if (d.holo) this._permitHolo(ctx, W - 168, 30, 142, 16);
      const gx = 78, gy = 138;
      ctx.strokeStyle = acc; ctx.lineWidth = 4; ctx.beginPath(); ctx.arc(gx, gy, 46, 0, Math.PI * 2); ctx.stroke();
      ctx.fillStyle = acc; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      const gf = fit((d.cls || 'A'), '900', 52, 76, BLACK); ctx.font = `900 ${gf}px ${BLACK}`; ctx.fillText((d.cls || 'A'), gx, gy + 2);
      ctx.fillStyle = 'rgba(255,255,255,0.55)'; ctx.font = `700 11px ${MONO}`; ctx.fillText('GRADE', gx, gy + 64);
      const cx2 = 150; ctx.textAlign = 'left';
      ctx.fillStyle = '#fff'; const sf = fit(status, '900', 46, W - cx2 - 24, BLACK); ctx.font = `900 ${sf}px ${BLACK}`; ctx.textBaseline = 'alphabetic'; ctx.fillText(status, cx2, 110);
      ctx.fillStyle = 'rgba(255,255,255,0.85)'; ctx.font = `600 13px ${MONO}`;
      ctx.fillText('HOLDER  ' + (d.holder ? d.holder.toUpperCase() : '____________'), cx2, 138);
      ctx.fillText('SEASON  ' + season + '          No. ' + no, cx2, 160);
      ctx.fillStyle = 'rgba(255,255,255,0.45)'; ctx.font = `600 10px ${MONO}`; ctx.fillText(authority, cx2, 182);
      ctx.save(); ctx.font = 'italic 700 26px "Brush Script MT","Segoe Script",cursive'; const g = ctx.createLinearGradient(cx2, 0, cx2 + 150, 0); g.addColorStop(0, '#fbe6a0'); g.addColorStop(1, '#c89a30'); ctx.fillStyle = g; ctx.fillText('Flameracer', cx2, H - 22); ctx.restore();
      if (d.stamp !== false) this._permitSeal(ctx, W - 56, H - 54, 32, acc, acc);
      if (d.barcode !== false) this._permitBarcode(ctx, W - 150, 56, 124, 16, bseed);
    } else { // ---- authorization (default) ----
      ctx.fillStyle = acc; ctx.fillRect(14, 14, W - 28, 48);
      ctx.fillStyle = ink; ctx.textBaseline = 'middle'; ctx.textAlign = 'left';
      const tf = fit(title, '800', 30, W - 150, BEBAS); ctx.font = `800 ${tf}px ${BEBAS}`; ctx.fillText(title, 28, 39);
      ctx.textAlign = 'right'; ctx.font = `700 15px ${MONO}`; ctx.fillText('· ' + season, W - 28, 40);
      if (d.checker !== false) this._permitChecker(ctx, 14, 62, W - 28, 10, 10);
      ctx.save(); ctx.translate(W / 2, H * 0.56); ctx.rotate(-0.12);
      const sf = fit(status, '900', 96, W - 100, BLACK); ctx.font = `900 ${sf}px ${BLACK}`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.lineWidth = Math.max(5, sf * 0.09); ctx.strokeStyle = acc; ctx.strokeText(status, 0, 0);
      ctx.fillStyle = 'rgba(255,255,255,0.94)'; ctx.fillText(status, 0, 0); ctx.restore();
      if (d.stamp !== false) this._permitSeal(ctx, W - 60, 100, 30, acc, acc);
      ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
      ctx.fillStyle = 'rgba(255,255,255,0.9)'; ctx.font = `700 15px ${MONO}`; ctx.fillText('No. ' + no, 24, H - 42);
      ctx.fillStyle = 'rgba(255,255,255,0.5)'; ctx.font = `600 11px ${MONO}`; ctx.fillText(authority, 24, H - 24);
      if (d.cls) badge('CLASS ' + d.cls, 24, 80, acc, ink, 17);
      if (d.barcode !== false) this._permitBarcode(ctx, W - 150, H - 44, 124, 22, bseed);
      if (d.holo) this._permitHolo(ctx, 14, H - 14, W - 28, 6);
    }
  }

  // Build a CanvasTexture for a CUSTOM racing permit from the credential renderer.
  // The metallic decal finish (alu / holo / gold) is applied SEPARATELY by
  // _makeDecalMaterial as a laminate, so we DON'T bake a finish overlay here.
  // 512×256 → a clean 2:1 permit card; the decal projector reads the 0.5 aspect.
  _permitCanvasTexture(design) {
    const W = 512, H = 256;
    const cv = document.createElement('canvas'); cv.width = W; cv.height = H;
    try { this._paintCustomPermit(cv.getContext('2d'), W, H, design || {}); } catch (e) {}
    const tex = new THREE.CanvasTexture(cv);
    tex.colorSpace = THREE.SRGBColorSpace; tex.anisotropy = 4; tex.needsUpdate = true;
    return tex;
  }

  // ════════════════════ CUSTOM SUN BANNER RENDERER ════════════════════
  // Wide retro-JDM banner (windshield sun strip / side stripe). Many Japanese
  // backgrounds + flanking motifs. Single source for the editor preview, the 3D
  // decal texture and the print PNG. Rendered 1024×288 (~3.5:1 wide strip).
  // Fields: text, sub, bg, motif, accent, textColor, font, outline, italic.
  _paintCustomBanner(ctx, W, H, d) {
    d = d || {};
    const acc = d.accent || '#ff2052', txtCol = d.textColor || '#ffffff';
    const bg = d.bg || 'synthwave', motif = d.motif || 'none', font = d.font || '"Bebas Neue", Impact, sans-serif';
    const main = (d.text || 'TOUGE').toUpperCase(), sub = (d.sub || '');
    let _s = 0x9e3779b9 ^ bg.length; for (let i = 0; i < bg.length; i++) _s = Math.imul(_s ^ bg.charCodeAt(i), 0x85ebca6b) >>> 0;
    const rnd = () => { _s = (_s + 0x6D2B79F5) | 0; let t = Math.imul(_s ^ (_s >>> 15), 1 | _s); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
    const fill = c => { ctx.fillStyle = c; ctx.fillRect(0, 0, W, H); };
    const grid = (horizon, col) => { ctx.strokeStyle = col; ctx.lineWidth = 2; const vp = W / 2; for (let i = -12; i <= 12; i++) { ctx.beginPath(); ctx.moveTo(vp + i * (W * 0.042), horizon); ctx.lineTo(vp + i * (W * 0.16), H); ctx.stroke(); } for (let i = 1; i <= 16; i++) { const y = horizon + (i * i) * (H * 0.006); if (y > H) break; ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); } };
    const sunDisc = (cx, cy, r, c0, c1, scan) => { const sg = ctx.createLinearGradient(cx, cy - r, cx, cy + r); sg.addColorStop(0, c0); sg.addColorStop(1, c1); ctx.save(); ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.clip(); ctx.fillStyle = sg; ctx.fillRect(cx - r, cy - r, r * 2, r * 2); if (scan) { ctx.fillStyle = scan; for (let y = cy; y < cy + r; y += 11) ctx.fillRect(cx - r, y, r * 2, 2 + (y - cy) / r * 7); } ctx.restore(); };
    switch (bg) {
      case 'synthwave': { const g = ctx.createLinearGradient(0, 0, 0, H); g.addColorStop(0, '#1a0b2e'); g.addColorStop(0.55, '#3b0a52'); g.addColorStop(0.56, '#0a0518'); g.addColorStop(1, '#160826'); fill(g); sunDisc(W / 2, H * 0.4, H * 0.32, '#ffd23b', '#ff1f8c', '#0a0518'); grid(H * 0.56, 'rgba(255,40,160,0.5)'); break; }
      case 'vapor': { const g = ctx.createLinearGradient(0, 0, W, H); g.addColorStop(0, '#2a1a5e'); g.addColorStop(0.5, '#c84fb0'); g.addColorStop(1, '#1fb6d8'); fill(g); sunDisc(W / 2, H * 0.4, H * 0.3, '#fff3a0', '#ff6ab5', null); grid(H * 0.58, 'rgba(255,255,255,0.32)'); break; }
      case 'grid': { fill('#06060f'); const gl = ctx.createLinearGradient(0, H * 0.3, 0, H * 0.56); gl.addColorStop(0, 'rgba(22,200,232,0.12)'); gl.addColorStop(1, 'rgba(22,200,232,0)'); ctx.fillStyle = gl; ctx.fillRect(0, H * 0.3, W, H * 0.3); grid(H * 0.56, 'rgba(22,200,232,0.6)'); break; }
      case 'risingsun': { fill('#f4ece0'); ctx.save(); ctx.translate(W / 2, H * 0.52); for (let i = 0; i < 24; i += 2) { const a = i / 24 * Math.PI * 2; ctx.fillStyle = acc; ctx.beginPath(); ctx.moveTo(0, 0); ctx.arc(0, 0, W, a, a + Math.PI / 24); ctx.closePath(); ctx.fill(); } ctx.restore(); ctx.fillStyle = acc; ctx.beginPath(); ctx.arc(W / 2, H * 0.52, H * 0.3, 0, Math.PI * 2); ctx.fill(); break; }
      case 'sakura': { const g = ctx.createLinearGradient(0, 0, 0, H); g.addColorStop(0, '#3a1030'); g.addColorStop(1, '#a02a6e'); fill(g); for (let i = 0; i < 44; i++) { const x = rnd() * W, y = rnd() * H, s = 2 + rnd() * 5; ctx.save(); ctx.translate(x, y); ctx.rotate(rnd() * 6.28); ctx.beginPath(); for (let p = 0; p < 5; p++) { const a = p / 5 * 6.28; ctx.lineTo(Math.cos(a) * s, Math.sin(a) * s * 1.4); } ctx.closePath(); ctx.globalAlpha = 0.4 + rnd() * 0.5; ctx.fillStyle = 'rgba(255,200,225,0.95)'; ctx.fill(); ctx.restore(); } ctx.globalAlpha = 1; break; }
      case 'wave': { const g = ctx.createLinearGradient(0, 0, 0, H); g.addColorStop(0, '#06243f'); g.addColorStop(1, '#0e4a78'); fill(g); ctx.fillStyle = 'rgba(255,255,255,0.9)'; ctx.beginPath(); ctx.moveTo(0, H); for (let x = 0; x <= W; x += 12) ctx.lineTo(x, H * 0.7 + Math.sin(x / 60) * 20 - (x / W) * 30); ctx.lineTo(W, H); ctx.closePath(); ctx.fill(); ctx.fillStyle = 'rgba(31,156,255,0.6)'; ctx.beginPath(); ctx.moveTo(0, H); for (let x = 0; x <= W; x += 12) ctx.lineTo(x, H * 0.78 + Math.sin(x / 45 + 1) * 16); ctx.lineTo(W, H); ctx.closePath(); ctx.fill(); break; }
      case 'city': { const g = ctx.createLinearGradient(0, 0, 0, H); g.addColorStop(0, '#0b0420'); g.addColorStop(1, '#2a0a3e'); fill(g); sunDisc(W / 2, H * 0.36, H * 0.22, '#ff7a3d', '#ff1f8c', null); let x = 0; const seed = [40, 90, 30, 120, 60, 100, 45, 130, 70, 95, 55, 110]; let si = 0; while (x < W) { const v = seed[si % seed.length]; const bw = 30 + (v % 36); const bh = 30 + v * 0.8; ctx.fillStyle = '#06030f'; ctx.fillRect(x, H - bh, bw, bh); ctx.fillStyle = 'rgba(255,40,160,0.4)'; for (let wy = H - bh + 6; wy < H - 6; wy += 12) for (let wx = x + 5; wx < x + bw - 5; wx += 11) if (((wx + wy) & 3) === 0) ctx.fillRect(wx, wy, 3, 5); x += bw + 5; si++; } break; }
      case 'mountains': { const g = ctx.createLinearGradient(0, 0, 0, H); g.addColorStop(0, '#2b0b52'); g.addColorStop(0.5, '#c0297a'); g.addColorStop(1, '#ff7a3d'); fill(g); ctx.fillStyle = 'rgba(255,240,180,0.95)'; ctx.beginPath(); ctx.arc(W / 2, H * 0.42, H * 0.2, 0, Math.PI * 2); ctx.fill(); ctx.fillStyle = '#160826'; ctx.beginPath(); ctx.moveTo(0, H); ctx.lineTo(W * 0.5, H * 0.32); ctx.lineTo(W, H); ctx.closePath(); ctx.fill(); break; }
      case 'noir': { const g = ctx.createRadialGradient(W / 2, H / 2, 10, W / 2, H / 2, W * 0.6); g.addColorStop(0, '#1a1015'); g.addColorStop(1, '#08080a'); fill(g); ctx.save(); ctx.globalAlpha = 0.13; fill(acc); ctx.restore(); break; }
      // ── TASK 2: TACTICAL + CYBERPUNK family ──
      case 'cyber': { fill('#05060b'); const rg = ctx.createRadialGradient(W / 2, H / 2, 10, W / 2, H / 2, W * 0.55); rg.addColorStop(0, 'rgba(0,70,90,0.4)'); rg.addColorStop(1, 'rgba(5,6,11,0)'); ctx.fillStyle = rg; ctx.fillRect(0, 0, W, H); ctx.strokeStyle = 'rgba(0,240,255,0.5)'; grid(H * 0.56, 'rgba(0,240,255,0.5)'); ctx.strokeStyle = 'rgba(255,0,200,0.30)'; ctx.lineWidth = 2; for (let i = 1; i <= 10; i++) { const y = H * 0.56 + (i * i) * (H * 0.005); if (y > H) break; ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); } ctx.fillStyle = 'rgba(0,240,255,0.85)'; for (let i = 0; i < 26; i++) { ctx.globalAlpha = 0.4 + rnd() * 0.6; ctx.fillRect(rnd() * W, rnd() * H * 0.5, 3, 3); } ctx.globalAlpha = 1; break; }
      case 'tactical': { fill('#14160f'); const s2 = 18; ctx.save(); ctx.globalAlpha = 0.5; for (let y = 0; y < H; y += s2) for (let x = 0; x < W; x += s2) { ctx.fillStyle = (((x / s2 + y / s2) & 1) === 0) ? '#1b1e15' : '#14160f'; ctx.fillRect(x, y, s2, s2); } ctx.restore(); ctx.fillStyle = 'rgba(74,92,46,0.9)'; const bar = Math.round(H * 0.14); ctx.fillRect(0, Math.round(H * 0.1), W, bar); ctx.fillRect(0, Math.round(H * 0.76), W, bar); ctx.fillStyle = 'rgba(0,0,0,0.5)'; for (let x = 30; x < W; x += 54) { ctx.fillRect(x, Math.round(H * 0.1), 14, bar); ctx.fillRect(x, Math.round(H * 0.76), 14, bar); } break; }
      case 'bloodtech': { fill('#0a0304'); const s2 = Math.round(H * 0.22); ctx.strokeStyle = 'rgba(220,30,50,0.4)'; ctx.lineWidth = 1.6; for (let row = 0, y = 0; y < H + s2; y += s2 * 0.75, row++) { for (let x = (row & 1 ? s2 * 0.9 : 0) - s2; x < W + s2; x += s2 * 1.8) { ctx.beginPath(); for (let k = 0; k < 6; k++) { const a = Math.PI / 3 * k - Math.PI / 6; const px = x + s2 * 0.9 * Math.cos(a), py = y + s2 * 0.9 * Math.sin(a); k ? ctx.lineTo(px, py) : ctx.moveTo(px, py); } ctx.closePath(); ctx.stroke(); } } const g2 = ctx.createLinearGradient(0, 0, W, H); g2.addColorStop(0, 'rgba(150,10,20,0.28)'); g2.addColorStop(0.5, 'rgba(0,0,0,0)'); g2.addColorStop(1, 'rgba(150,10,20,0.28)'); ctx.fillStyle = g2; ctx.fillRect(0, 0, W, H); ctx.fillStyle = 'rgba(255,40,60,0.8)'; for (let i = 0; i < 18; i++) { ctx.globalAlpha = 0.3 + rnd() * 0.6; ctx.fillRect(rnd() * W, rnd() * H, 2, 2); } ctx.globalAlpha = 1; break; }
      case 'solid': default: { const g = ctx.createLinearGradient(0, 0, 0, H); g.addColorStop(0, '#0c0c10'); g.addColorStop(1, acc); ctx.save(); ctx.globalAlpha = 0.85; fill(g); ctx.restore(); break; }
    }
    const emojiFlank = (e, size) => { ctx.save(); ctx.globalAlpha = 0.92; ctx.font = `${size}px "Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif`; ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.fillText(e, W * 0.1, H * 0.5); ctx.translate(W * 0.9, H * 0.5); ctx.scale(-1, 1); ctx.fillText(e, 0, 0); ctx.restore(); };
    if (motif === 'dragon') emojiFlank('🐉', H * 0.55);
    else if (motif === 'oni') emojiFlank('👹', H * 0.5);
    else if (motif === 'sakura') emojiFlank('🌸', H * 0.42);
    else if (motif === 'torii') emojiFlank('⛩️', H * 0.5);
    else if (motif === 'sun') { ctx.save(); ctx.globalAlpha = 0.55; ctx.strokeStyle = acc; ctx.lineWidth = 4; for (const cx2 of [W * 0.11, W * 0.89]) for (let i = 0; i < 12; i++) { const a = i / 12 * Math.PI * 2; ctx.beginPath(); ctx.moveTo(cx2 + Math.cos(a) * 18, H * 0.5 + Math.sin(a) * 18); ctx.lineTo(cx2 + Math.cos(a) * 36, H * 0.5 + Math.sin(a) * 36); ctx.stroke(); } ctx.restore(); }
    const cx = W / 2;
    ctx.save();
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.lineJoin = 'round';
    let fs = H * 0.5; ctx.font = `900 ${fs}px ${font}`;
    const maxW = W * (motif === 'none' ? 0.86 : 0.66);
    while (ctx.measureText(main).width > maxW && fs > 16) { fs -= 2; ctx.font = `900 ${fs}px ${font}`; }
    const ty = sub ? H * 0.42 : H * 0.5;
    if (d.italic) { ctx.translate(cx, ty); ctx.transform(1, 0, -0.2, 1, 0, 0); ctx.translate(-cx, -ty); }
    if (d.outline !== false) { ctx.lineWidth = Math.max(4, fs * 0.1); ctx.strokeStyle = '#0a0a0c'; ctx.strokeText(main, cx, ty); }
    ctx.fillStyle = acc; ctx.fillText(main, cx + fs * 0.03, ty + fs * 0.05);
    ctx.fillStyle = txtCol; ctx.fillText(main, cx, ty);
    ctx.restore();
    if (sub) { ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; let ss = H * 0.2; ctx.font = `700 ${ss}px ${font}`; const subMax = W * 0.6; while (ctx.measureText(sub).width > subMax && ss > 10) { ss -= 1; ctx.font = `700 ${ss}px ${font}`; } ctx.fillStyle = acc; ctx.fillRect(cx - subMax * 0.34, H * 0.71, subMax * 0.68, Math.max(2, H * 0.012)); ctx.fillStyle = txtCol; ctx.fillText(sub, cx, H * 0.84); }
  }
  _bannerCanvasTexture(design) {
    const W = 1024, H = 288;
    const cv = document.createElement('canvas'); cv.width = W; cv.height = H;
    try { this._paintCustomBanner(cv.getContext('2d'), W, H, design || {}); } catch (e) {}
    const tex = new THREE.CanvasTexture(cv);
    tex.colorSpace = THREE.SRGBColorSpace; tex.anisotropy = 4; tex.needsUpdate = true;
    return tex;
  }

  // ════════════════════ REAR WING DECAL RENDERER ════════════════════
  // A long thin HORIZONTAL text band for the wing's rear face (see
  // FR_WING_DECAL_POS in the data file). Transparent bg — the sticker is the
  // TEXT+graphic, not a filled box. Single source for the editor preview, the 3D
  // decal texture and the print PNG. Rendered wide (1024×160 ≈ 6.4:1) to suit the
  // thin bar aspect. Fields: text, style ('cyber'|'stencil'|'blood'), color.
  // Cyberpunk / tactical to match the brand; kept tasteful + legible at the thin
  // aspect (single bold line, neon/stencil edge, subtle side ticks).
  _paintWingDecal(ctx, W, H, d) {
    d = d || {};
    ctx.clearRect(0, 0, W, H); // transparent background (sticker = the art, not a box)
    const STYLES = window.FR_WING_DECAL_STYLES || [];
    const def = STYLES.find(s => s.id === (d.style || 'cyber')) || STYLES[0] || {};
    const kind = def.kind || 'text';
    const raw = (d.text != null ? d.text : 'FLAMERACER');
    const main = String(raw || '').toUpperCase().slice(0, 18);
    const hasText = main.length > 0;
    const col = d.color || def.color || '#16f5d0';
    const glow = d.color || def.glow || col;
    const font = def.font || '"Bebas Neue", Impact, sans-serif';
    const track = def.track != null ? def.track : 0.12;
    const cx = W / 2, cy = H * 0.52;

    // ── shared helpers ──────────────────────────────────────────────────
    const hex2rgb = (h) => { const n = parseInt(String(h).slice(1), 16); return [(n >> 16) & 255, (n >> 8) & 255, n & 255]; };
    const rgba = (h, a) => { try { const [r, g, b] = hex2rgb(h); return `rgba(${r},${g},${b},${a})`; } catch (e) { return h; } };
    const roundRect = (x, y, w, h, r) => { ctx.beginPath(); ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r); ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath(); };
    // auto-fit tracked font; leaves ctx.font set for measurement
    const fitFont = (text, maxW, weight) => {
      weight = weight || 900; let fs = H * 0.62; ctx.font = `${weight} ${fs}px ${font}`;
      const meas = () => { let w = 0; for (const ch of text) w += ctx.measureText(ch).width + fs * track; return w - fs * track; };
      while (meas() > maxW && fs > 12) { fs -= 2; ctx.font = `${weight} ${fs}px ${font}`; }
      return { fs, total: meas() };
    };
    const eachChar = (text, fs, total, cb) => { let x = cx - total / 2; for (const ch of text) { const cw = ctx.measureText(ch).width; cb(ch, x, cw); x += cw + fs * track; } };
    // neon/blood glow text (chromatic ghost + glow + dark keyline)
    const neonText = (text, maxW, color, glowC, ghost) => {
      const { fs, total } = fitFont(text, maxW, 900);
      ctx.save(); ctx.textAlign = 'left'; ctx.textBaseline = 'middle'; ctx.lineJoin = 'round';
      if (ghost) { ctx.globalAlpha = 0.5; ctx.fillStyle = ghost; eachChar(text, fs, total, (ch, x) => ctx.fillText(ch, x - fs * 0.03, cy)); ctx.globalAlpha = 1; }
      ctx.shadowColor = glowC || color; ctx.shadowBlur = Math.max(8, fs * 0.4);
      ctx.fillStyle = color; eachChar(text, fs, total, (ch, x) => ctx.fillText(ch, x, cy));
      ctx.shadowBlur = 0; ctx.lineWidth = Math.max(2, fs * 0.05); ctx.strokeStyle = 'rgba(0,0,0,0.6)';
      eachChar(text, fs, total, (ch, x) => ctx.strokeText(ch, x, cy));
      ctx.restore(); return { fs, total };
    };
    // tactical stencil text (dark keyline + punched cut-lines)
    const stencilText = (text, maxW, color) => {
      const { fs, total } = fitFont(text, maxW, 900);
      ctx.save(); ctx.textAlign = 'left'; ctx.textBaseline = 'middle'; ctx.lineJoin = 'round';
      ctx.lineWidth = Math.max(4, fs * 0.12); ctx.strokeStyle = 'rgba(0,0,0,0.85)';
      eachChar(text, fs, total, (ch, x) => ctx.strokeText(ch, x, cy));
      ctx.fillStyle = color; eachChar(text, fs, total, (ch, x) => ctx.fillText(ch, x, cy));
      ctx.globalCompositeOperation = 'destination-out'; ctx.fillStyle = '#000';
      for (const yy of [cy - fs * 0.26, cy + fs * 0.02, cy + fs * 0.3]) ctx.fillRect(cx - total / 2 - 10, yy, total + 20, Math.max(2, fs * 0.045));
      ctx.globalCompositeOperation = 'source-over'; ctx.restore(); return { fs, total };
    };
    const sideTicks = (color) => {
      ctx.save(); ctx.strokeStyle = color; ctx.globalAlpha = 0.85; ctx.lineWidth = Math.max(2, H * 0.03);
      for (const sgn of [-1, 1]) { const ex = cx + sgn * W * 0.44;
        ctx.beginPath(); ctx.moveTo(ex, H * 0.28); ctx.lineTo(ex, H * 0.72); ctx.stroke();
        ctx.globalAlpha = 0.5; ctx.beginPath(); ctx.moveTo(ex - sgn * W * 0.02, H * 0.5); ctx.lineTo(ex - sgn * W * 0.06, H * 0.5); ctx.stroke(); ctx.globalAlpha = 0.85; }
      ctx.restore();
    };
    // half-width of the clear centre zone reserved for the text
    const gapOf = (text) => { if (!text) return 0; const { total } = fitFont(text, W * 0.52, 900); return total / 2 + W * 0.035; };
    const outsideGap = (x, g) => Math.abs(x - cx) > g;

    // ── TEXT-FORWARD kinds (original look) ──────────────────────────────
    if (kind === 'text') {
      sideTicks(col);
      if (def.stencil) stencilText(hasText ? main : 'FLAMERACER', W * 0.78, col);
      else neonText(hasText ? main : 'FLAMERACER', W * 0.78, col, glow, def.ghost);
      return;
    }

    // ── GRAPHICAL DESIGNS ───────────────────────────────────────────────
    const g = gapOf(hasText ? main : '');
    try {
      if (kind === 'triangles') {
        const n = 26, step = W / n, base = H * 0.82, th = H * 0.5;
        for (let i = 0; i < n; i++) {
          const x = i * step + step / 2, fade = outsideGap(x, g) ? 0.9 : 0.12, up = i % 2 === 0;
          ctx.globalAlpha = fade;
          ctx.beginPath();
          if (up) { ctx.moveTo(x - step * 0.42, base); ctx.lineTo(x, base - th); ctx.lineTo(x + step * 0.42, base); }
          else { ctx.moveTo(x - step * 0.42, H * 0.18); ctx.lineTo(x, H * 0.18 + th); ctx.lineTo(x + step * 0.42, H * 0.18); }
          ctx.closePath();
          if (i % 3 === 0) { ctx.fillStyle = col; ctx.shadowColor = col; ctx.shadowBlur = 10; ctx.fill(); ctx.shadowBlur = 0; }
          else { ctx.lineWidth = Math.max(2, H * 0.02); ctx.strokeStyle = rgba(col, 0.85); ctx.stroke(); }
        }
        ctx.globalAlpha = 1;
        if (hasText) neonText(main, W * 0.5, col, glow, null);

      } else if (kind === 'chevron') {
        const cnt = 22, step = W / cnt, cw = step * 0.55;
        ctx.lineWidth = Math.max(3, H * 0.055); ctx.strokeStyle = rgba(col, 0.9); ctx.lineCap = 'round'; ctx.lineJoin = 'round';
        for (let i = 0; i < cnt; i++) { const x = i * step + step / 2; if (!outsideGap(x, g)) continue; const dir = x < cx ? 1 : -1;
          ctx.beginPath(); ctx.moveTo(x - dir * cw / 2, H * 0.30); ctx.lineTo(x + dir * cw / 2, H * 0.5); ctx.lineTo(x - dir * cw / 2, H * 0.70); ctx.stroke(); }
        if (hasText) stencilText(main, W * 0.5, col);

      } else if (kind === 'hazard') {
        const drawBand = (y, bh) => { ctx.save(); ctx.beginPath(); ctx.rect(0, y, W, bh); ctx.clip();
          for (let x = -bh; x < W + bh; x += 88) { ctx.fillStyle = rgba(col, 0.95); ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x + 44, y); ctx.lineTo(x + 44 - bh, y + bh); ctx.lineTo(x - bh, y + bh); ctx.closePath(); ctx.fill(); }
          ctx.restore(); };
        drawBand(H * 0.06, H * 0.22); drawBand(H * 0.72, H * 0.22);
        if (hasText) stencilText(main, W * 0.7, col);

      } else if (kind === 'arrows') {
        const cnt = 15, step = W / cnt, aw = step * 0.5, ah = H * 0.4;
        ctx.fillStyle = rgba(col, 0.9);
        for (let i = 0; i < cnt; i++) { const x = i * step + step * 0.25; if (!outsideGap(x + aw / 2, g)) continue; const dir = x < cx ? 1 : -1;
          const bx = dir > 0 ? x : x + aw; ctx.beginPath(); ctx.moveTo(bx, cy - ah / 2); ctx.lineTo(bx + dir * aw, cy); ctx.lineTo(bx, cy + ah / 2); ctx.closePath(); ctx.fill(); }
        if (hasText) stencilText(main, W * 0.44, col);

      } else if (kind === 'crosshair') {
        ctx.strokeStyle = rgba(col, 0.9); ctx.lineWidth = Math.max(2, H * 0.02); ctx.lineCap = 'round';
        const m = H * 0.14, bl = H * 0.30;
        for (const [x, y, sx, sy] of [[m, m, 1, 1], [W - m, m, -1, 1], [m, H - m, 1, -1], [W - m, H - m, -1, -1]]) { ctx.beginPath(); ctx.moveTo(x + sx * bl, y); ctx.lineTo(x, y); ctx.lineTo(x, y + sy * bl); ctx.stroke(); }
        ctx.globalAlpha = 0.85; const r = H * 0.34;
        ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.stroke();
        for (const a of [0, Math.PI / 2, Math.PI, Math.PI * 1.5]) { ctx.beginPath(); ctx.moveTo(cx + Math.cos(a) * r * 0.72, cy + Math.sin(a) * r * 0.72); ctx.lineTo(cx + Math.cos(a) * r * 1.15, cy + Math.sin(a) * r * 1.15); ctx.stroke(); }
        ctx.globalAlpha = 1;
        if (hasText) neonText(main, W * 0.44, col, glow, null);

      } else if (kind === 'circuit') {
        ctx.strokeStyle = rgba(col, 0.8); ctx.fillStyle = rgba(col, 0.95); ctx.lineWidth = Math.max(2, H * 0.018); ctx.lineCap = 'round';
        const rows = [H * 0.24, H * 0.5, H * 0.76];
        for (let r = 0; r < rows.length; r++) { const y = rows[r]; ctx.beginPath(); ctx.moveTo(0, y);
          for (let x = 0; x < W; x += 70) { const jog = (((x / 70 + r) % 2) ? 1 : -1) * H * 0.12; ctx.lineTo(x + 40, y); ctx.lineTo(x + 40, y + jog); ctx.lineTo(x + 58, y + jog); ctx.lineTo(x + 58, y); } ctx.stroke(); }
        for (let i = 0; i < 26; i++) { const x = i * (W / 26) + 12, y = rows[i % 3]; if (!outsideGap(x, g)) continue; ctx.beginPath(); ctx.arc(x, y, H * 0.03, 0, Math.PI * 2); ctx.fill(); }
        if (hasText) neonText(main, W * 0.5, col, glow, null);

      } else if (kind === 'barcode') {
        let x = W * 0.05, seed = 7;
        for (; x < W * 0.95;) { seed = (seed * 1103515245 + 12345) & 0x7fffffff; const bw = 2 + (seed % 8), gp = 2 + ((seed >> 4) % 6);
          ctx.globalAlpha = 0.92; ctx.fillStyle = rgba(col, 1); ctx.fillRect(x, H * 0.10, bw, H * 0.48); x += bw + gp; }
        ctx.globalAlpha = 1;
        if (hasText) { ctx.save(); ctx.fillStyle = col; ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.font = `700 ${H * 0.22}px ${font}`; ctx.fillText(main, cx, H * 0.80); ctx.restore(); }

      } else if (kind === 'grid') {
        ctx.strokeStyle = rgba(col, 0.7); ctx.lineWidth = Math.max(1, H * 0.012); const hz = H * 0.48;
        for (let i = 1; i <= 6; i++) { const y = hz + (i * i) * (H * 0.52 / 36); if (y > H) break; ctx.globalAlpha = 0.72 - i * 0.08; ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); }
        ctx.globalAlpha = 0.55;
        for (let i = -10; i <= 10; i++) { ctx.beginPath(); ctx.moveTo(cx + i * (W * 0.05), hz); ctx.lineTo(cx + i * (W * 0.14), H); ctx.stroke(); }
        ctx.globalAlpha = 1;
        if (hasText) neonText(main, W * 0.6, col, glow, def.ghost || null);

      } else if (kind === 'radiation') {
        ctx.strokeStyle = rgba(col, 0.85); ctx.lineWidth = Math.max(2, H * 0.025); ctx.setLineDash([H * 0.14, H * 0.09]);
        roundRect(H * 0.08, H * 0.10, W - H * 0.16, H * 0.80, 10); ctx.stroke(); ctx.setLineDash([]);
        const tre = (tx) => { ctx.save(); ctx.translate(tx, cy); ctx.fillStyle = col; ctx.beginPath(); ctx.arc(0, 0, H * 0.06, 0, Math.PI * 2); ctx.fill();
          for (let k = 0; k < 3; k++) { ctx.beginPath(); ctx.moveTo(0, 0); ctx.arc(0, 0, H * 0.30, (k * 120 - 30) * Math.PI / 180, (k * 120 + 30) * Math.PI / 180); ctx.closePath(); ctx.fill(); } ctx.restore(); };
        tre(W * 0.10); tre(W * 0.90);
        if (hasText) stencilText(main, W * 0.55, col);

      } else if (kind === 'skull') {
        const skull = (sx) => { ctx.save(); ctx.translate(sx, cy); ctx.fillStyle = col; ctx.shadowColor = col; ctx.shadowBlur = 8;
          ctx.beginPath(); ctx.arc(0, -H * 0.05, H * 0.22, Math.PI, 0); ctx.lineTo(H * 0.16, H * 0.12); ctx.lineTo(-H * 0.16, H * 0.12); ctx.closePath(); ctx.fill();
          ctx.fillRect(-H * 0.12, H * 0.10, H * 0.24, H * 0.10); ctx.shadowBlur = 0;
          ctx.globalCompositeOperation = 'destination-out'; ctx.beginPath(); ctx.arc(-H * 0.08, -H * 0.04, H * 0.055, 0, Math.PI * 2); ctx.arc(H * 0.08, -H * 0.04, H * 0.055, 0, Math.PI * 2); ctx.fill();
          ctx.beginPath(); ctx.moveTo(0, H * 0.0); ctx.lineTo(-H * 0.03, H * 0.07); ctx.lineTo(H * 0.03, H * 0.07); ctx.closePath(); ctx.fill(); ctx.globalCompositeOperation = 'source-over'; ctx.restore(); };
        skull(W * 0.11); skull(W * 0.89);
        if (hasText) stencilText(main, W * 0.55, col);

      } else if (kind === 'katana') {
        const blade = (bx, dir) => { ctx.save(); ctx.translate(bx, cy); ctx.rotate(dir * -0.5); ctx.fillStyle = rgba(col, 0.95); ctx.shadowColor = col; ctx.shadowBlur = 10;
          ctx.fillRect(-H * 0.42, -H * 0.03, H * 0.84, H * 0.06); ctx.shadowBlur = 0; ctx.fillStyle = 'rgba(18,18,22,0.95)'; ctx.fillRect(H * 0.28, -H * 0.055, H * 0.16, H * 0.11); ctx.restore(); };
        blade(W * 0.12, 1); blade(W * 0.88, -1);
        if (hasText) neonText(main, W * 0.5, col, glow, '#ffffff');

      } else if (kind === 'camo') {
        // coyote/OD blotch camo band + stencil text
        const blobs = [[0.10, 0.35, 0.9], [0.22, 0.7, 0.7], [0.05, 0.75, 0.6], [0.9, 0.4, 0.9], [0.8, 0.72, 0.7], [0.95, 0.72, 0.6], [0.5, 0.15, 0.7], [0.5, 0.9, 0.7]];
        const tones = [col, rgba(col, 0.55), '#3b3a2a', '#5c4a30'];
        blobs.forEach((b, i) => { ctx.save(); ctx.globalAlpha = 0.85; ctx.fillStyle = tones[i % tones.length]; ctx.beginPath();
          const bx = b[0] * W, by = b[1] * H, rr = b[2] * H * 0.4; for (let a = 0; a < Math.PI * 2; a += Math.PI / 5) { const rad = rr * (0.6 + 0.4 * Math.abs(Math.sin(a * 3 + i))); const px = bx + Math.cos(a) * rad, py = by + Math.sin(a) * rad * 0.7; a === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py); } ctx.closePath(); ctx.fill(); ctx.restore(); });
        if (hasText) stencilText(main, W * 0.6, '#e8e5d6');

      } else if (kind === 'stars') {
        const star = (sx, sy, R) => { ctx.beginPath(); for (let k = 0; k < 5; k++) { const a1 = (-90 + k * 72) * Math.PI / 180, a2 = (-90 + k * 72 + 36) * Math.PI / 180; const ox = sx + Math.cos(a1) * R, oy = sy + Math.sin(a1) * R, ix = sx + Math.cos(a2) * R * 0.42, iy = sy + Math.sin(a2) * R * 0.42; k === 0 ? ctx.moveTo(ox, oy) : ctx.lineTo(ox, oy); ctx.lineTo(ix, iy); } ctx.closePath(); };
        ctx.fillStyle = col; ctx.shadowColor = col; ctx.shadowBlur = 6;
        const flank = [W * 0.09, W * 0.15, W * 0.85, W * 0.91];
        flank.forEach((fx, i) => { if (i === 1 || i === 2) { star(fx, cy, H * 0.13); ctx.globalAlpha = 0.8; ctx.fill(); ctx.globalAlpha = 1; } else { star(fx, cy, H * 0.20); ctx.fill(); } });
        ctx.shadowBlur = 0;
        if (hasText) stencilText(main, W * 0.5, col);

      } else {
        // unknown kind → safe text fallback
        sideTicks(col); neonText(hasText ? main : 'FLAMERACER', W * 0.78, col, glow, def.ghost);
      }
    } catch (e) {
      // never let a design throw — fall back to plain text
      ctx.clearRect(0, 0, W, H); neonText(hasText ? main : 'FLAMERACER', W * 0.78, col, glow, null);
    }
  }
  _wingCanvasTexture(design) {
    const W = 1024, H = 160;
    const cv = document.createElement('canvas'); cv.width = W; cv.height = H;
    try { this._paintWingDecal(cv.getContext('2d'), W, H, design || {}); } catch (e) {}
    const tex = new THREE.CanvasTexture(cv);
    tex.colorSpace = THREE.SRGBColorSpace; tex.anisotropy = 4; tex.needsUpdate = true;
    return tex;
  }

  // ════════ DOOR NUMBER BADGE (lower-door authored slot) ════════
  // "A number plate for the door" — WIDE racing door plate (canvas 1108×288, the
  // exact footprint the owner approved in the Sticker Lab / aspect 0.26): number
  // box on the left, name + sub stacked on the right, rivets, style materials.
  // d = {number, name, sub, style, accent} — styles in FR_DOOR_BADGE_STYLES.
  _paintDoorBadge(ctx, W, H, d) {
    d = d || {};
    const num = String(d.number != null && d.number !== '' ? d.number : '07').toUpperCase().slice(0, 3);
    const name = String(d.name || 'FLAMERACER').toUpperCase().slice(0, 16);
    const sub = String(d.sub || '').toUpperCase().slice(0, 20);
    const style = d.style || 'classic';
    const PAL = {
      classic:  { bg: '#e8e6e0', panel: '#f4f2ec', ink: '#111114', band: '#d11a2a', bandInk: '#ffffff', stencil: false, rivets: '#9aa0a8' },
      olive:    { bg: '#2e341d', panel: '#4b5320', ink: '#e8e5d6', band: '#3a4224', bandInk: '#c7cba0', stencil: true,  rivets: '#1f2312', camo: ['#556b2f', '#3b452a', '#5c5a3c'] },
      desert:   { bg: '#6b573d', panel: '#8a6b47', ink: '#2a2119', band: '#75593a', bandInk: '#efe3cf', stencil: true,  rivets: '#4a3b28', camo: ['#a08256', '#7d6547', '#93764f'] },
      blackops: { bg: '#0a0a0c', panel: '#141418', ink: '#e8e9ee', band: '#7a0e12', bandInk: '#e8e9ee', stencil: true,  rivets: '#26262c' },
      hazard:   { bg: '#141414', panel: '#f5d000', ink: '#101010', band: '#101010', bandInk: '#f5d000', stencil: false, rivets: '#3a3a3a', hazard: true },
      carbon:   { bg: '#101014', panel: '#1a1a1f', ink: '#f5d000', band: '#0c0c0f', bandInk: '#e8e9ee', stencil: false, rivets: '#33343c', carbon: true },
      neon:     { bg: '#060012', panel: '#0d0224', ink: '#16f5d0', band: '#120436', bandInk: '#16f5d0', stencil: false, rivets: '#221a44', glow: true, grid: true },
      glitch:   { bg: '#0d0117', panel: '#1a0430', ink: '#ff1f8c', band: '#240640', bandInk: '#ff1f8c', stencil: false, rivets: '#301050', glow: true, glitch: true },
    };
    const p = PAL[style] || PAL.classic;
    const ink = d.accent || p.ink;
    const rr = (x, y, w, h, r) => { ctx.beginPath(); ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r); ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath(); };
    ctx.clearRect(0, 0, W, H);
    // outer badge plate (wide)
    rr(W * 0.008, H * 0.03, W * 0.984, H * 0.94, H * 0.16); ctx.fillStyle = p.bg; ctx.fill();
    ctx.lineWidth = H * 0.035; ctx.strokeStyle = 'rgba(0,0,0,0.55)'; ctx.stroke();
    // inner panel
    rr(W * 0.02, H * 0.1, W * 0.96, H * 0.8, H * 0.12); ctx.fillStyle = p.panel; ctx.fill();
    ctx.save(); rr(W * 0.02, H * 0.1, W * 0.96, H * 0.8, H * 0.12); ctx.clip();
    if (p.camo) { // military blotches along the strip
      const blobs = [[0.12, 0.3, 0.5], [0.32, 0.75, 0.55], [0.52, 0.2, 0.5], [0.72, 0.72, 0.55], [0.9, 0.3, 0.45], [0.42, 0.5, 0.4]];
      blobs.forEach((bl, i) => { ctx.fillStyle = p.camo[i % p.camo.length]; ctx.globalAlpha = 0.5; ctx.beginPath();
        for (let a = 0; a < Math.PI * 2; a += Math.PI / 5) { const rad = bl[2] * H * (0.6 + 0.4 * Math.abs(Math.sin(a * 3 + i))); const px = bl[0] * W + Math.cos(a) * rad * 1.6, py = bl[1] * H + Math.sin(a) * rad * 0.7; a === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py); }
        ctx.closePath(); ctx.fill(); });
      ctx.globalAlpha = 1;
    }
    if (p.carbon) { const s = H / 9; for (let y = 0; y < H; y += s) for (let x = 0; x < W; x += s) { ctx.fillStyle = (((x / s + y / s) | 0) % 2 === 0) ? '#22242b' : '#17181d'; ctx.fillRect(x, y, s, s); } }
    if (p.grid) { ctx.strokeStyle = 'rgba(22,245,208,0.16)'; ctx.lineWidth = 2;
      for (let i = 1; i < 6; i++) { ctx.beginPath(); ctx.moveTo(0, H * (i / 6)); ctx.lineTo(W, H * (i / 6)); ctx.stroke(); }
      for (let i = 1; i < 24; i++) { ctx.beginPath(); ctx.moveTo(W * (i / 24), 0); ctx.lineTo(W * (i / 24), H); ctx.stroke(); } }
    if (p.hazard) { ctx.save(); ctx.globalAlpha = 0.95; ctx.fillStyle = '#101010';
      for (let x = -H; x < W + H; x += H * 0.5) {
        ctx.beginPath(); ctx.moveTo(x, H * 0.1); ctx.lineTo(x + H * 0.25, H * 0.1); ctx.lineTo(x + H * 0.25 - H * 0.08, H * 0.19); ctx.lineTo(x - H * 0.08, H * 0.19); ctx.closePath(); ctx.fill();
        ctx.beginPath(); ctx.moveTo(x, H * 0.81); ctx.lineTo(x + H * 0.25, H * 0.81); ctx.lineTo(x + H * 0.25 - H * 0.08, H * 0.9); ctx.lineTo(x - H * 0.08, H * 0.9); ctx.closePath(); ctx.fill();
      } ctx.restore(); }
    ctx.restore();
    // LEFT number box — contrasting plate with the huge race number
    const nbX = W * 0.035, nbY = H * 0.17, nbW = W * 0.185, nbH = H * 0.66;
    rr(nbX, nbY, nbW, nbH, H * 0.08); ctx.fillStyle = p.band; ctx.fill();
    ctx.lineWidth = H * 0.02; ctx.strokeStyle = 'rgba(0,0,0,0.35)'; ctx.stroke();
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    let numFs = nbH * (num.length >= 3 ? 0.52 : 0.72);
    ctx.font = `900 ${numFs}px "Arial Black", Arial`;
    while (ctx.measureText(num).width > nbW * 0.86 && numFs > 10) { numFs -= 2; ctx.font = `900 ${numFs}px "Arial Black", Arial`; }
    const numX = nbX + nbW / 2, numY = nbY + nbH * 0.54;
    if (p.glow) { ctx.shadowColor = ink; ctx.shadowBlur = H * 0.09; }
    ctx.fillStyle = ink; ctx.fillText(num, numX, numY); ctx.shadowBlur = 0;
    if (p.stencil) { ctx.save(); ctx.globalCompositeOperation = 'destination-out'; ctx.fillStyle = '#000';
      for (const fy of [-0.2, 0.04, 0.28]) ctx.fillRect(nbX + nbW * 0.08, numY + numFs * fy, nbW * 0.84, Math.max(3, numFs * 0.05));
      ctx.restore(); }
    if (p.glitch) { ctx.save(); ctx.globalAlpha = 0.5; ctx.fillStyle = '#16f5d0'; ctx.font = `900 ${numFs}px "Arial Black", Arial`;
      ctx.fillText(num, numX + W * 0.004, numY - H * 0.02); ctx.restore(); }
    // divider
    ctx.strokeStyle = 'rgba(0,0,0,0.3)'; ctx.lineWidth = H * 0.015;
    ctx.beginPath(); ctx.moveTo(W * 0.245, H * 0.2); ctx.lineTo(W * 0.245, H * 0.8); ctx.stroke();
    // RIGHT: name (big) over sub (band)
    const txX = W * 0.6, txW = W * 0.66;
    let nameFs = H * (sub ? 0.34 : 0.42);
    ctx.font = `800 ${nameFs}px "Barlow Condensed", "Arial Narrow", Arial`;
    while (ctx.measureText(name).width > txW && nameFs > 12) { nameFs -= 2; ctx.font = `800 ${nameFs}px "Barlow Condensed", "Arial Narrow", Arial`; }
    if (p.glow) { ctx.shadowColor = ink; ctx.shadowBlur = H * 0.05; }
    ctx.fillStyle = ink; ctx.fillText(name, txX, H * (sub ? 0.38 : 0.5)); ctx.shadowBlur = 0;
    if (sub) {
      const sbW = Math.min(txW, Math.max(W * 0.3, ctx.measureText(sub).width * 0.5 + W * 0.06));
      rr(txX - sbW / 2, H * 0.6, sbW, H * 0.2, H * 0.05); ctx.fillStyle = p.band; ctx.fill();
      let subFs = H * 0.11; ctx.font = `700 ${subFs}px "JetBrains Mono", monospace`;
      while (ctx.measureText(sub).width > sbW * 0.9 && subFs > 8) { subFs -= 1; ctx.font = `700 ${subFs}px "JetBrains Mono", monospace`; }
      ctx.fillStyle = p.bandInk; ctx.fillText(sub, txX, H * 0.705);
    }
    // corner rivets
    for (const [fx, fy] of [[0.025, 0.14], [0.975, 0.14], [0.025, 0.86], [0.975, 0.86]]) {
      ctx.fillStyle = p.rivets;
      ctx.beginPath(); ctx.arc(W * fx, H * fy, H * 0.045, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = 'rgba(255,255,255,0.35)';
      ctx.beginPath(); ctx.arc(W * fx - H * 0.012, H * fy - H * 0.012, H * 0.016, 0, Math.PI * 2); ctx.fill();
    }
  }
  _doorBadgeTexture(design) {
    // 1108×288 = the stand-in art's native ratio (0.26) the owner approved in the lab
    const W = 1108, H = 288, cv = document.createElement('canvas'); cv.width = W; cv.height = H;
    try { this._paintDoorBadge(cv.getContext('2d'), W, H, design || {}); } catch (e) {}
    const tex = new THREE.CanvasTexture(cv);
    tex.colorSpace = THREE.SRGBColorSpace; tex.anisotropy = 4; tex.needsUpdate = true;
    return tex;
  }

  // ════════ FUTURISTIC PANEL (roof-panel + rear-deck authored slots) ════════
  // Angular HUD "tech plate": notched frame, corner brackets, custom TEXT + NUMBER,
  // pattern band. d = {text, number, pattern, color} — patterns in FR_PANEL_PATTERNS.
  _paintPanelSticker(ctx, W, H, d) {
    d = d || {};
    const text = String(d.text || 'FLAMERACER').toUpperCase().slice(0, 14);
    const num = String(d.number != null ? d.number : '07').toUpperCase().slice(0, 3);
    const col = d.color || '#16f5d0';
    const pattern = d.pattern || 'triangles';
    const hex2 = (h, a) => { try { const n = parseInt(h.slice(1), 16); return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`; } catch (e) { return h; } };
    ctx.clearRect(0, 0, W, H);
    // notched-corner frame (angular octagon)
    const nx = W * 0.045, m = W * 0.015;
    const frame = () => { ctx.beginPath(); ctx.moveTo(m + nx, m); ctx.lineTo(W - m - nx, m); ctx.lineTo(W - m, m + nx); ctx.lineTo(W - m, H - m - nx); ctx.lineTo(W - m - nx, H - m); ctx.lineTo(m + nx, H - m); ctx.lineTo(m, H - m - nx); ctx.lineTo(m, m + nx); ctx.closePath(); };
    frame(); ctx.fillStyle = 'rgba(7,8,12,0.88)'; ctx.fill();
    ctx.lineWidth = Math.max(3, W * 0.006); ctx.strokeStyle = col; ctx.stroke();
    frame(); ctx.save(); ctx.clip();
    // inner hairline
    ctx.globalAlpha = 0.35; ctx.lineWidth = 1.5; ctx.strokeStyle = col;
    ctx.strokeRect(m * 3, m * 3, W - m * 6, H - m * 6); ctx.globalAlpha = 1;
    // top-left system label
    ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
    ctx.font = `700 ${H * 0.075}px "JetBrains Mono", monospace`;
    ctx.fillStyle = hex2(col, 0.85); ctx.fillText('FR // SYSTEMS', W * 0.06, H * 0.14);
    // top-right number box
    const nbW = W * 0.16, nbH = H * 0.2, nbX = W * 0.79, nbY = H * 0.055;
    ctx.fillStyle = col; ctx.fillRect(nbX, nbY, nbW, nbH);
    ctx.font = `900 ${nbH * 0.72}px "Arial Black", Arial`; ctx.textAlign = 'center';
    ctx.fillStyle = '#07080c'; ctx.fillText(num, nbX + nbW / 2, nbY + nbH * 0.55);
    // main text
    ctx.font = `900 ${H * 0.3}px "Bebas Neue", Impact, sans-serif`;
    let fs = H * 0.3; while (ctx.measureText(text).width > W * 0.78 && fs > 20) { fs -= 4; ctx.font = `900 ${fs}px "Bebas Neue", Impact, sans-serif`; }
    ctx.shadowColor = col; ctx.shadowBlur = fs * 0.25;
    ctx.fillStyle = col; ctx.fillText(text, W / 2, H * 0.47);
    ctx.shadowBlur = 0;
    // pattern band
    const bandY = H * 0.66, bandH = H * 0.22, bx0 = W * 0.06, bx1 = W * 0.94;
    ctx.save(); ctx.beginPath(); ctx.rect(bx0, bandY, bx1 - bx0, bandH); ctx.clip();
    ctx.strokeStyle = hex2(col, 0.9); ctx.fillStyle = hex2(col, 0.9); ctx.lineWidth = Math.max(2, H * 0.012);
    if (pattern === 'triangles') {
      const n = 16, step = (bx1 - bx0) / n;
      for (let i = 0; i < n; i++) { const x = bx0 + i * step + step / 2; ctx.beginPath(); ctx.moveTo(x - step * 0.4, bandY + bandH * 0.9); ctx.lineTo(x, bandY + bandH * 0.1); ctx.lineTo(x + step * 0.4, bandY + bandH * 0.9); ctx.closePath(); if (i % 3 === 0) ctx.fill(); else ctx.stroke(); }
    } else if (pattern === 'hazard') {
      for (let x = bx0 - bandH; x < bx1 + bandH; x += bandH * 1.2) { ctx.beginPath(); ctx.moveTo(x, bandY); ctx.lineTo(x + bandH * 0.6, bandY); ctx.lineTo(x + bandH * 0.6 - bandH, bandY + bandH); ctx.lineTo(x - bandH, bandY + bandH); ctx.closePath(); ctx.fill(); }
    } else if (pattern === 'circuit') {
      const y = bandY + bandH / 2;
      ctx.beginPath(); ctx.moveTo(bx0, y);
      for (let x = bx0; x < bx1; x += 56) { const jog = ((x / 56) % 2 ? 1 : -1) * bandH * 0.3; ctx.lineTo(x + 30, y); ctx.lineTo(x + 30, y + jog); ctx.lineTo(x + 46, y + jog); ctx.lineTo(x + 46, y); }
      ctx.stroke();
      for (let x = bx0 + 12; x < bx1; x += 56) { ctx.beginPath(); ctx.arc(x, y, bandH * 0.09, 0, Math.PI * 2); ctx.fill(); }
    } else if (pattern === 'grid') {
      for (let i = 0; i <= 8; i++) { const y = bandY + bandH * (i / 8); ctx.globalAlpha = 0.75 - i * 0.06; ctx.beginPath(); ctx.moveTo(bx0, y); ctx.lineTo(bx1, y); ctx.stroke(); }
      ctx.globalAlpha = 0.55;
      for (let i = 0; i <= 20; i++) { const x = bx0 + (bx1 - bx0) * (i / 20); ctx.beginPath(); ctx.moveTo(x, bandY); ctx.lineTo(x, bandY + bandH); ctx.stroke(); }
      ctx.globalAlpha = 1;
    } else if (pattern === 'chevron') {
      const n = 14, step = (bx1 - bx0) / n; ctx.lineCap = 'round'; ctx.lineWidth = Math.max(3, bandH * 0.14);
      for (let i = 0; i < n; i++) { const x = bx0 + i * step + step / 2; ctx.beginPath(); ctx.moveTo(x - step * 0.28, bandY + bandH * 0.12); ctx.lineTo(x + step * 0.28, bandY + bandH * 0.5); ctx.lineTo(x - step * 0.28, bandY + bandH * 0.88); ctx.stroke(); }
    } else if (pattern === 'barcode') {
      let x = bx0, seed = 5;
      while (x < bx1) { seed = (seed * 1103515245 + 12345) & 0x7fffffff; const bw = 3 + (seed % 10), gp = 3 + ((seed >> 4) % 7); ctx.fillRect(x, bandY + bandH * 0.08, bw, bandH * 0.84); x += bw + gp; }
    }
    ctx.restore();
    // bottom-right data tag
    ctx.font = `600 ${H * 0.055}px "JetBrains Mono", monospace`; ctx.textAlign = 'right';
    ctx.fillStyle = hex2(col, 0.6); ctx.fillText(`UNIT-${num} · V2.6`, W * 0.94, H * 0.945);
    // corner brackets
    ctx.restore();
    ctx.lineWidth = Math.max(3, W * 0.007); ctx.strokeStyle = col; ctx.lineCap = 'square';
    const bl = W * 0.05;
    for (const [x, y, sx, sy] of [[m * 2.4, m * 2.4, 1, 1], [W - m * 2.4, m * 2.4, -1, 1], [m * 2.4, H - m * 2.4, 1, -1], [W - m * 2.4, H - m * 2.4, -1, -1]]) {
      ctx.beginPath(); ctx.moveTo(x + sx * bl, y); ctx.lineTo(x, y); ctx.lineTo(x, y + sy * bl); ctx.stroke();
    }
  }
  _panelStickerTexture(design, aspect) {
    // canvas ratio MUST match the slot's approved aspect (roof 0.709 / deck 0.565),
    // otherwise the art is drawn at one ratio and projected at another (stretched).
    const W = 1024, H = Math.round(W * (aspect || 0.5));
    const cv = document.createElement('canvas'); cv.width = W; cv.height = H;
    try { this._paintPanelSticker(cv.getContext('2d'), W, H, design || {}); } catch (e) {}
    const tex = new THREE.CanvasTexture(cv);
    tex.colorSpace = THREE.SRGBColorSpace; tex.anisotropy = 4; tex.needsUpdate = true;
    return tex;
  }

  // ---- PART THUMBNAILS ----
  // Render an individual part node to a transparent PNG data-URL so the Body
  // step can show an actual picture of each part (cached per id). Uses the main
  // renderer + an offscreen render target and a small dedicated lighting rig.
  makePartThumbnail(partId, size = 220) {
    try {
      const node = this.partNodeById && this.partNodeById.get(partId);
      if (!node || !this.renderer) return null;
      if (!this._thumbCache) this._thumbCache = new Map();
      if (this._thumbCache.has(partId)) return this._thumbCache.get(partId);

      if (!this._thumbScene) {
        const s = new THREE.Scene();
        s.environment = this.scene.environment || null;
        s.add(new THREE.AmbientLight(0xffffff, 1.0));
        const d1 = new THREE.DirectionalLight(0xffffff, 1.7); d1.position.set(2, 3, 2.5); s.add(d1);
        const d2 = new THREE.DirectionalLight(0xffffff, 0.6); d2.position.set(-2.5, 1, -2); s.add(d2);
        this._thumbScene = s;
        this._thumbCam = new THREE.PerspectiveCamera(32, 1, 0.001, 5000);
        this._thumbRT = new THREE.WebGLRenderTarget(size, size, { samples: 4 });
        this._thumbMat = new THREE.MeshStandardMaterial({ color: 0x8c9098, metalness: 0.45, roughness: 0.4 });
      }
      const cam = this._thumbCam, rt = this._thumbRT, s = this._thumbScene;

      node.updateWorldMatrix(true, false);
      const clone = node.clone(true);
      clone.visible = true;
      clone.applyMatrix4(node.matrixWorld); // bake real world transform/scale
      clone.traverse(c => { if (c.isMesh) { c.material = this._thumbMat; c.visible = true; } });
      s.add(clone);

      const box = new THREE.Box3().setFromObject(clone);
      if (box.isEmpty()) { s.remove(clone); return null; }
      const sph = box.getBoundingSphere(new THREE.Sphere());
      const r = sph.radius || 1;
      const dist = (r / Math.sin((cam.fov * Math.PI / 180) / 2)) * 1.2;
      const dir = new THREE.Vector3(1, 0.5, 1).normalize();
      cam.position.copy(sph.center).addScaledVector(dir, dist);
      cam.near = Math.max(0.001, dist - r * 2); cam.far = dist + r * 4;
      cam.lookAt(sph.center); cam.updateProjectionMatrix();

      const prevTarget = this.renderer.getRenderTarget();
      const prevAlpha = this.renderer.getClearAlpha();
      this.renderer.setClearAlpha(0);
      this.renderer.setRenderTarget(rt);
      this.renderer.clear();
      this.renderer.render(s, cam);
      this.renderer.setRenderTarget(prevTarget);
      this.renderer.setClearAlpha(prevAlpha);

      const buf = new Uint8Array(size * size * 4);
      this.renderer.readRenderTargetPixels(rt, 0, 0, size, size, buf);
      const cv = document.createElement('canvas'); cv.width = cv.height = size;
      const ctx = cv.getContext('2d');
      const img = ctx.createImageData(size, size);
      for (let y = 0; y < size; y++) {
        const sy = size - 1 - y;
        for (let x = 0; x < size; x++) {
          const si = (sy * size + x) * 4, di = (y * size + x) * 4;
          img.data[di] = buf[si]; img.data[di + 1] = buf[si + 1]; img.data[di + 2] = buf[si + 2]; img.data[di + 3] = buf[si + 3];
        }
      }
      ctx.putImageData(img, 0, 0);
      s.remove(clone);
      const url = cv.toDataURL('image/png');
      this._thumbCache.set(partId, url);
      return url;
    } catch (e) { return null; }
  }

  // Render a clean isolated 3-D picture of ONE corner of a wheel's rim (same safe
  // clone-and-render approach as makePartThumbnail) so the wheel picker shows the
  // real rim design as a product shot, not a flat 2-D icon. Cached per wheel id.
  makeWheelThumbnail(wid, size = 260) {
    try {
      if (!this.renderer || !this._combinedLoaded) return null;
      const w = (typeof wid === 'string') ? (window.FR_WHEELS || []).find(x => x.id === wid) : wid;
      if (!w) return null;
      if (!this._wheelThumb3D) this._wheelThumb3D = new Map();
      if (this._wheelThumb3D.has(w.id)) return this._wheelThumb3D.get(w.id);

      // Gather rim meshes: prefer a hi-res per-corner group if it's already loaded;
      // otherwise use the low-poly rim baked in the car (kicking off the hi-res load
      // for the live view). Either way the rim DESIGN reads in the thumbnail.
      let rimMeshes = [];
      if (this._hiResRims && this._hiResRims[w.id] && this._hiResRims[w.id].length) {
        this._hiResRims[w.id].forEach(g => g.traverse(c => { if (c.isMesh) rimMeshes.push(c); }));
      } else {
        if (this._hiResMap && this._hiResMap[w.id]) this._ensureHiResRim && this._ensureHiResRim(w.id);
        if (this.wheelRimById && this.wheelRimById.get(w.id)) rimMeshes = this.wheelRimById.get(w.id).filter(m => m && m.isMesh);
      }
      if (!rimMeshes.length) return null;

      this.combinedModel && this.combinedModel.updateWorldMatrix(true, true);
      const centerOf = m => new THREE.Box3().setFromObject(m).getCenter(new THREE.Vector3());

      // Isolate ONE corner of the RIM (left-front) so the thumb shows a single wheel.
      let info = rimMeshes.map(m => ({ m, c: centerOf(m) }));
      const xs = info.map(o => o.c.x); const midX = (Math.min(...xs) + Math.max(...xs)) / 2;
      let corner = info.filter(o => o.c.x <= midX);
      if (!corner.length) corner = info;
      const zs = corner.map(o => o.c.z); const midZ = (Math.min(...zs) + Math.max(...zs)) / 2;
      const front = corner.filter(o => o.c.z >= midZ);
      if (front.length) corner = front;
      const cornerMeshes = corner.map(o => o.m);
      // corner centre + radius (to pick the matching tire for THIS wheel only)
      const cc = new THREE.Vector3(); cornerMeshes.forEach(m => cc.add(centerOf(m))); cc.multiplyScalar(1 / cornerMeshes.length);
      let rad = 0.01; cornerMeshes.forEach(m => { rad = Math.max(rad, new THREE.Box3().setFromObject(m).getSize(new THREE.Vector3()).length()); });
      const tireAll = ((this._wheelTireMeshes && this._wheelTireMeshes.length) ? this._wheelTireMeshes : (this.realTireMeshes || [])).filter(m => m && m.isMesh);
      const tireCorner = tireAll.filter(m => centerOf(m).distanceTo(cc) < rad * 0.9); // per-corner tire only (skips a combined 4-tire mesh)

      if (!this._wThumbScene) {
        const s = new THREE.Scene(); s.environment = this.scene.environment || null;
        s.add(new THREE.AmbientLight(0xffffff, 1.05));
        const d1 = new THREE.DirectionalLight(0xffffff, 2.6); d1.position.set(-1.2, 2.4, 3); s.add(d1);
        const d2 = new THREE.DirectionalLight(0xffffff, 1.1); d2.position.set(2, 0.6, -1.4); s.add(d2);
        this._wThumbScene = s;
        this._wThumbCam = new THREE.PerspectiveCamera(28, 1, 0.001, 5000);
        this._wThumbRT = new THREE.WebGLRenderTarget(size, size, { samples: 4 });
        // MATTE BLACK rim — lifted a touch + more metal so the spokes read bright,
        // still matte black, not silver. Matte rubber tyre.
        this._wThumbRimMat = new THREE.MeshStandardMaterial({ color: 0x242428, metalness: 0.45, roughness: 0.48, envMapIntensity: 1.25 });
        this._wThumbTireMat = new THREE.MeshStandardMaterial({ color: 0x111113, metalness: 0.0, roughness: 0.92, envMapIntensity: 0.4 });
      }
      const s = this._wThumbScene, cam = this._wThumbCam, rt = this._wThumbRT;
      const group = new THREE.Group();
      const addClone = (m, mat) => { m.updateWorldMatrix(true, false); const cl = m.clone(); cl.applyMatrix4(m.matrixWorld); cl.material = mat; cl.visible = true; group.add(cl); };
      cornerMeshes.forEach(m => addClone(m, this._wThumbRimMat));
      tireCorner.forEach(m => addClone(m, this._wThumbTireMat));
      s.add(group);

      const box = new THREE.Box3().setFromObject(group);
      if (box.isEmpty()) { s.remove(group); return null; }
      const sph = box.getBoundingSphere(new THREE.Sphere());
      const r = sph.radius || 1;
      const dist = (r / Math.sin((cam.fov * Math.PI / 180) / 2)) * 0.92; // tighter → wheel fills more of the frame
      // FORWARD-FACING: a wheel disc is THINNEST along its spin axis, which is also
      // its face normal. Use the smallest bbox dimension as that axis, point it
      // OUTBOARD (away from the car centre), and look straight down it → the spoke
      // face faces the camera. Tiny Y tilt so it isn't dead-flat.
      const sz = box.getSize(new THREE.Vector3());
      let axis;
      if (sz.x <= sz.y && sz.x <= sz.z) axis = new THREE.Vector3(1, 0, 0);
      else if (sz.z <= sz.x && sz.z <= sz.y) axis = new THREE.Vector3(0, 0, 1);
      else axis = new THREE.Vector3(0, 1, 0);
      const carC = new THREE.Box3().setFromObject(this.combinedModel || this.bodyGroup).getCenter(new THREE.Vector3());
      if (axis.dot(new THREE.Vector3().subVectors(carC, sph.center)) > 0) axis.negate(); // face the OUTBOARD side
      const dir = axis.clone(); dir.y += 0.16; dir.normalize();
      cam.position.copy(sph.center).addScaledVector(dir, dist);
      cam.near = Math.max(0.001, dist - r * 2); cam.far = dist + r * 4;
      cam.lookAt(sph.center); cam.updateProjectionMatrix();
      const url = this._renderRTtoURL(s, cam, rt, size);
      s.remove(group);
      if (url) this._wheelThumb3D.set(w.id, url);
      return url;
    } catch (e) { return null; }
  }

  // Render a glossy paint sphere for the given paint (cached) → data-URL, so the
  // paint picker shows the ACTUAL paint (colour + gloss + reflections) instead
  // of a stock photo.
  makePaintSwatch(paint, size = 200) {
    try {
      if (!this.renderer || !paint) return null;
      if (!this._swatchCache) this._swatchCache = new Map();
      if (this._swatchCache.has(paint.id)) return this._swatchCache.get(paint.id);
      if (!this._swatchScene) {
        const s = new THREE.Scene(); s.environment = this.scene.environment || null;
        // Lighting mirrors the MAIN scene (HDRI-dominant + weak fills) so a swatch
        // is a faithful mini-preview of the car — not a brighter, more-saturated
        // sphere. Without this, high-luminance paints (yellow) look deep/orange on
        // the card but HDRI-washed pale on the car.
        s.add(new THREE.AmbientLight(0xffffff, 0.28));
        s.add(new THREE.HemisphereLight(0xffffff, 0x101018, 0.2));
        const d = new THREE.DirectionalLight(0xffffff, 0.5); d.position.set(2.5, 3, 3); s.add(d);
        const d2 = new THREE.DirectionalLight(0xffffff, 0.2); d2.position.set(-2, -1, 1); s.add(d2);
        this._swatchScene = s;
        this._swatchCam = new THREE.PerspectiveCamera(28, 1, 0.01, 100); this._swatchCam.position.set(0, 0, 3.3); this._swatchCam.lookAt(0, 0, 0);
        this._swatchRT = new THREE.WebGLRenderTarget(size, size, { samples: 4 });
        this._swatchMesh = new THREE.Mesh(new THREE.SphereGeometry(1, 48, 48), new THREE.MeshPhysicalMaterial());
        s.add(this._swatchMesh);
      }
      const m = this._swatchMesh.material;
      m.color = new THREE.Color(paint.flip ? paint.flip[0] : paint.hex);
      m.metalness = paint.metalness ?? 0.0; m.roughness = paint.roughness ?? 0.3; m.clearcoat = paint.clearcoat ?? 0.9; m.clearcoatRoughness = paint.clearcoatRoughness ?? 0.08; m.envMapIntensity = paint.envMapIntensity ?? 1.4; // match the car body finish EXACTLY (incl. per-paint reflection)
      m.needsUpdate = true;
      const url = this._renderRTtoURL(this._swatchScene, this._swatchCam, this._swatchRT, size);
      if (url) this._swatchCache.set(paint.id, url);
      return url;
    } catch (e) { return null; }
  }

  // Render the ACTUAL car wearing a config's parts (white paint) to a thumbnail.
  // Mutates the live car, renders the scene to an offscreen target, then restores
  // everything in the same synchronous tick (no visible flicker). Cached per id.
  makeConfigThumbnail(cfg, size = 240) {
    try {
      if (!this.renderer || !this._combinedLoaded || !this.bodyGroup) return null;
      if (!this._cfgCache) this._cfgCache = new Map();
      if (this._cfgCache.has(cfg.id)) return this._cfgCache.get(cfg.id);

      // --- save current visual state ---
      const savedVis = new Map();
      this.partNodeById.forEach((node, id) => savedVis.set(id, node.visible));
      const savedTwins = new Map();
      if (this.partTwinsById) this.partTwinsById.forEach((arr, id) => savedTwins.set(id, arr.map(n => n.visible)));
      const savedPaint = this._currentPaint;
      const savedWide = this._wideStanceOn;
      const hidden = [];
      (this._plateGraphics || []).concat(this._decalMeshes || []).forEach(m => { if (m.visible) { m.visible = false; hidden.push(m); } });

      // --- apply config (visibility only — don't touch part materials) ---
      this._skipRefine = true; // don't run crease-normals during tiny thumbnails
      this.partNodeById.forEach((node, id) => this.toggleCombinedPart(id, false));
      const white = (window.FR_PAINTS || []).find(p => p.id === 'ghost-white') || { hex: '#f1ebda', roughness: 0.32 };
      this.setPaint(white);
      (cfg.parts || []).forEach(id => { if (window.FR_BODY_PARTS.find(x => x.id === id)) this.toggleCombinedPart(id, true); });

      // --- frame + render the main scene (car on black floor) ---
      if (!this._cfgCam) this._cfgCam = new THREE.PerspectiveCamera(30, 1, 0.05, 500);
      if (!this._cfgRT) this._cfgRT = new THREE.WebGLRenderTarget(size, size, { samples: 4 });
      this.bodyGroup.updateWorldMatrix(true, true);
      const box = new THREE.Box3().setFromObject(this.bodyGroup);
      const sph = box.getBoundingSphere(new THREE.Sphere());
      const dist = (sph.radius / Math.sin((this._cfgCam.fov * Math.PI / 180) / 2)) * 1.02;
      const dir = new THREE.Vector3(1, 0.4, 1).normalize();
      this._cfgCam.position.copy(sph.center).addScaledVector(dir, dist);
      this._cfgCam.lookAt(sph.center); this._cfgCam.updateProjectionMatrix();
      const url = this._renderRTtoURL(this.scene, this._cfgCam, this._cfgRT, size);

      // --- restore ---
      this.partNodeById.forEach((node, id) => { node.visible = savedVis.get(id); });
      if (this.partTwinsById) this.partTwinsById.forEach((arr, id) => { const sv = savedTwins.get(id); if (sv) arr.forEach((n, i) => n.visible = sv[i]); });
      if (savedPaint) this.setPaint(savedPaint);
      this.setWideStance(!!savedWide);
      hidden.forEach(m => m.visible = true);
      this._skipRefine = false;

      if (url) this._cfgCache.set(cfg.id, url);
      return url;
    } catch (e) { this._skipRefine = false; return null; }
  }

  // Shared helper: render a scene+camera to a target and return a transparent PNG.
  _renderRTtoURL(scene, cam, rt, size) {
    const prevTarget = this.renderer.getRenderTarget();
    const prevAlpha = this.renderer.getClearAlpha();
    this.renderer.setClearAlpha(0);
    this.renderer.setRenderTarget(rt);
    this.renderer.clear();
    this.renderer.render(scene, cam);
    this.renderer.setRenderTarget(prevTarget);
    this.renderer.setClearAlpha(prevAlpha);
    const buf = new Uint8Array(size * size * 4);
    this.renderer.readRenderTargetPixels(rt, 0, 0, size, size, buf);
    const cv = document.createElement('canvas'); cv.width = cv.height = size;
    const ctx = cv.getContext('2d');
    const img = ctx.createImageData(size, size);
    for (let y = 0; y < size; y++) {
      const sy = size - 1 - y;
      for (let x = 0; x < size; x++) {
        const si = (sy * size + x) * 4, di = (y * size + x) * 4;
        img.data[di] = buf[si]; img.data[di + 1] = buf[si + 1]; img.data[di + 2] = buf[si + 2]; img.data[di + 3] = buf[si + 3];
      }
    }
    ctx.putImageData(img, 0, 0);
    return cv.toDataURL('image/png');
  }

  // Snapshot the CURRENT live build to a small PNG (for the Garage), framed 3/4 at
  // the canonical hero angle so thumbs are consistent. Uses the offscreen RT path so
  // the visible canvas is never disturbed.
  captureBuildThumbnail(size = 220) {
    try {
      if (!this.renderer || !this._combinedLoaded || !this.carGroup) return '';
      if (!this._gThumbCam) this._gThumbCam = new THREE.PerspectiveCamera(32, 1, 0.05, 500);
      if (!this._gThumbRT) this._gThumbRT = new THREE.WebGLRenderTarget(size, size, { samples: 4 });
      const savedRot = this.carGroup.rotation.y;
      this.carGroup.rotation.y = -0.5; this.carGroup.updateWorldMatrix(true, true);
      const box = new THREE.Box3().setFromObject(this.carGroup);
      const sph = box.getBoundingSphere(new THREE.Sphere());
      const dist = (sph.radius / Math.sin((this._gThumbCam.fov * Math.PI / 180) / 2)) * 1.05;
      this._gThumbCam.position.copy(sph.center).addScaledVector(new THREE.Vector3(1, 0.42, 1).normalize(), dist);
      this._gThumbCam.lookAt(sph.center); this._gThumbCam.updateProjectionMatrix();
      const url = this._renderRTtoURL(this.scene, this._gThumbCam, this._gThumbRT, size);
      this.carGroup.rotation.y = savedRot; this.carGroup.updateWorldMatrix(true, true);
      this.requestRender(200);
      return url || '';
    } catch (e) { return ''; }
  }

  // ──────────────── 360° SPIN GIF EXPORT ────────────────
  _ensureGifLib() {
    if (window.GIF) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/gif.js@0.2.0/dist/gif.js';
      s.onload = resolve; s.onerror = reject; document.head.appendChild(s);
    });
  }
  // gif.js spawns a Worker from a script URL — a cross-origin CDN URL is blocked, so
  // fetch the worker source and run it from a same-origin blob URL.
  async _gifWorkerUrl() {
    if (this._gifWorkerBlobUrl) return this._gifWorkerBlobUrl;
    const txt = await fetch('https://cdn.jsdelivr.net/npm/gif.js@0.2.0/dist/gif.worker.js').then(r => r.text());
    this._gifWorkerBlobUrl = URL.createObjectURL(new Blob([txt], { type: 'application/javascript' }));
    return this._gifWorkerBlobUrl;
  }
  async exportSpinGif(onProgress) {
    if (!this.renderer || !this._combinedLoaded || !this.carGroup) return null;
    await this._ensureGifLib();
    const workerScript = await this._gifWorkerUrl();
    const W = 480, H = 270, FRAMES = 36;
    if (!this._gifRT) this._gifRT = new THREE.WebGLRenderTarget(W, H, { samples: 4 });
    const cam = this.camera, savedAspect = cam.aspect, savedRot = this.carGroup.rotation.y;
    cam.aspect = W / H; cam.updateProjectionMatrix();
    const gif = new window.GIF({ workers: 2, quality: 8, width: W, height: H, workerScript });
    const cv = document.createElement('canvas'); cv.width = W; cv.height = H; const ctx = cv.getContext('2d');
    const buf = new Uint8Array(W * H * 4);
    for (let i = 0; i < FRAMES; i++) {
      this.carGroup.rotation.y = savedRot + (i / FRAMES) * Math.PI * 2;
      this.carGroup.updateWorldMatrix(true, true);
      this.renderer.setRenderTarget(this._gifRT); this.renderer.clear(); this.renderer.render(this.scene, cam); this.renderer.setRenderTarget(null);
      this.renderer.readRenderTargetPixels(this._gifRT, 0, 0, W, H, buf);
      const img = ctx.createImageData(W, H);
      for (let y = 0; y < H; y++) { const sy = H - 1 - y; for (let x = 0; x < W; x++) { const si = (sy * W + x) * 4, di = (y * W + x) * 4; img.data[di] = buf[si]; img.data[di + 1] = buf[si + 1]; img.data[di + 2] = buf[si + 2]; img.data[di + 3] = 255; } }
      ctx.putImageData(img, 0, 0);
      gif.addFrame(ctx, { copy: true, delay: 55 });
      if (onProgress) onProgress((i + 1) / FRAMES * 0.55);
    }
    cam.aspect = savedAspect; cam.updateProjectionMatrix();
    this.carGroup.rotation.y = savedRot; this.carGroup.updateWorldMatrix(true, true);
    this.requestRender(400);
    return new Promise((resolve, reject) => {
      gif.on('progress', p => onProgress && onProgress(0.55 + p * 0.45));
      gif.on('finished', blob => resolve(URL.createObjectURL(blob)));
      try { gif.render(); } catch (e) { reject(e); }
    });
  }

  // ──────────────── AR — "view in my space" (WebXR) ────────────────
  async arSupported() {
    try { return !!(navigator.xr && await navigator.xr.isSessionSupported('immersive-ar')); }
    catch (e) { return false; }
  }
  async enterAR() {
    if (!(await this.arSupported()) || !this.combinedModel) return false;
    const { ARButton } = await import('three/addons/webxr/ARButton.js').catch(() => ({}));
    const renderer = this.renderer;
    renderer.xr.enabled = true;
    // Build a compact AR model: clone the car body group, scale to a ~0.3m desk object.
    const arModel = (this.carGroup || this.combinedModel).clone(true);
    arModel.visible = false;
    const arScene = new THREE.Scene();
    arScene.add(new THREE.HemisphereLight(0xffffff, 0x223344, 1.4));
    const dl = new THREE.DirectionalLight(0xffffff, 1.4); dl.position.set(1, 2, 1); arScene.add(dl);
    arScene.add(arModel);
    // reticle for hit-test placement
    const reticle = new THREE.Mesh(new THREE.RingGeometry(0.05, 0.07, 32).rotateX(-Math.PI / 2), new THREE.MeshBasicMaterial({ color: 0xf5d000 }));
    reticle.matrixAutoUpdate = false; reticle.visible = false; arScene.add(reticle);
    const session = await navigator.xr.requestSession('immersive-ar', { requiredFeatures: ['hit-test'] }).catch(() => null);
    if (!session) return false;
    renderer.xr.setReferenceSpaceType('local');
    await renderer.xr.setSession(session);
    let hitTestSource = null, placed = false;
    const viewerSpace = await session.requestReferenceSpace('viewer');
    session.requestHitTestSource({ space: viewerSpace }).then(s => hitTestSource = s);
    session.addEventListener('select', () => {
      if (reticle.visible && !placed) {
        arModel.position.setFromMatrixPosition(reticle.matrix);
        const k = 0.30 / Math.max(0.001, new THREE.Box3().setFromObject(arModel).getSize(new THREE.Vector3()).length());
        arModel.scale.setScalar(k); arModel.visible = true; placed = true;
      } else if (placed) { arModel.rotation.y += Math.PI / 6; }
    });
    const refSpace = renderer.xr.getReferenceSpace();
    renderer.setAnimationLoop((t, frame) => {
      if (frame && hitTestSource) {
        const hits = frame.getHitTestResults(hitTestSource);
        if (hits.length && !placed) { const pose = hits[0].getPose(refSpace); reticle.visible = true; reticle.matrix.fromArray(pose.transform.matrix); }
        else if (!placed) reticle.visible = false;
      }
      renderer.render(arScene, renderer.xr.getCamera());
    });
    session.addEventListener('end', () => {
      renderer.setAnimationLoop(null); renderer.xr.enabled = false;
      try { arModel.traverse(o => { o.geometry && o.geometry.dispose && o.geometry.dispose(); }); } catch (e) {}
      this.requestRender(600);
    });
    return true;
  }

  // ---- DECALS (placeholders) ----
  // Stickers/decals didn't render at all before. This projects a placeholder
  // decal (a finish-tinted badge / emblem / shape) onto the BODY surface for
  // each selected sticker, at a stable scattered position (raycast from a
  // hashed direction so each decal lands consistently but they spread around
  // the car). Parented to the car so they stay stuck. Finishes (brushed /
  // holographic / gold) change the look. Call with the stickers array.
  _hash(str) { let h = 0; for (let i = 0; i < (str || '').length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0; return h; }

  _removeDecals() {
    (this._decalMeshes || []).forEach(m => {
      try { if (m.parent) m.parent.remove(m); if (m.material.map) m.material.map.dispose(); m.material.dispose(); m.geometry.dispose(); } catch (e) {}
    });
    this._decalMeshes = [];
  }

  // ── CONFORMING DECALS (DecalGeometry) ────────────────────────────────────
  // Ported from the standalone decal-placer (_glb_work/viewer.html). Saved slot
  // points/normals live in GLB-NATIVE space (the viewer loads the GLB with an
  // identity root transform). Here the same GLB is rotated +90°, scaled by
  // this._fitScale, and parented under carGroup — so we map native → configurator
  // world via combinedModel.matrixWorld, then project a real DecalGeometry onto
  // the live panel mesh so the art wraps the curved bodywork like vinyl.

  // Build the decal orientation basis from a surface normal + spin (deg).
  // EXACT copy of the viewer's orient() so placements match 1:1.
  _decalOrient(n, rotDeg) {
    const z = n.clone().normalize();
    let up = Math.abs(z.y) > 0.8 ? new THREE.Vector3(0, 0, -1) : new THREE.Vector3(0, 1, 0);
    let y = up.clone().sub(z.clone().multiplyScalar(up.dot(z)));
    if (y.lengthSq() < 1e-6) y = new THREE.Vector3(1, 0, 0).sub(z.clone().multiplyScalar(z.x));
    y.normalize();
    let x = new THREE.Vector3().crossVectors(y, z).normalize();
    const r = (rotDeg || 0) * Math.PI / 180, cr = Math.cos(r), sr = Math.sin(r);
    const xr = x.clone().multiplyScalar(cr).add(y.clone().multiplyScalar(sr));
    const yr = y.clone().multiplyScalar(cr).sub(x.clone().multiplyScalar(sr));
    return new THREE.Euler().setFromRotationMatrix(new THREE.Matrix4().makeBasis(xr, yr, z));
  }

  // Find the live panel mesh a slot projects onto (by GLB node name), falling
  // back to the largest body mesh if the named panel isn't found.
  _findDecalTarget(name) {
    const model = this.combinedModel;
    if (!model) return null;
    let exact = null, variant = null, biggest = null, maxV = 0;
    // Tolerate a stale side-suffix in slot.panel (e.g. 'doors_L' when the GLB mesh is
    // just 'doors'): if the exact name isn't present, a name with the trailing _L/_R
    // stripped is a far better target than the whole-body fallback — which projects
    // nothing at the slot's location (empty DecalGeometry → an INVISIBLE decal).
    const stripped = name && name.replace(/_(L|R|LEFT|RIGHT)$/i, '');
    model.traverse(o => {
      if (!o.isMesh) return;
      if (o.name === name) exact = o;
      else if (!variant && stripped && stripped !== name && o.name === stripped) variant = o;
      const v = o.geometry?.attributes?.position?.count || 0;
      if (v > maxV) { maxV = v; biggest = o; }
    });
    return exact || variant || biggest;
  }

  // Map a native slot {point,normal,size} → configurator world space.
  _decalNativeToWorld(slot) {
    const model = this.combinedModel;
    model.updateWorldMatrix(true, true);
    const pos = model.localToWorld(new THREE.Vector3(slot.point[0], slot.point[1], slot.point[2]));
    const normal = new THREE.Vector3(slot.normal[0], slot.normal[1], slot.normal[2])
      .transformDirection(model.matrixWorld).normalize();
    const sizeWorld = slot.size * (this._fitScale || 1);
    return { pos, normal, sizeWorld };
  }

  // PERF: DecalGeometry iterates EVERY triangle of the target. Projecting onto
  // the 242k-vert Body_1 cost ~1s/decal. So we first clip the target to just the
  // triangles near the decal and project against that proxy (~hundreds of tris →
  // a few ms). Returns a lightweight Mesh that shares the target's world matrix
  // (so DecalGeometry projects in the same world frame), or the target itself if
  // it's already small. Cached local triangle soup lives on target.userData.
  _clipMeshRegion(target, worldCenter, clipRadius) {
    const geo = target.geometry;
    const posAttr = geo.attributes.position;
    if (posAttr.count <= 8000) return target; // small panel — project directly
    let cache = target.userData._decalTriCache;
    if (!cache) {
      const idx = geo.index;
      const triCount = idx ? idx.count / 3 : posAttr.count / 3;
      const nrmAttr = geo.attributes.normal;
      const P = new Float32Array(triCount * 9);
      const N = nrmAttr ? new Float32Array(triCount * 9) : null;
      const gi = idx ? (i) => idx.getX(i) : (i) => i;
      for (let t = 0; t < triCount; t++) {
        for (let k = 0; k < 3; k++) {
          const vi = gi(t * 3 + k), o = t * 9 + k * 3;
          P[o] = posAttr.getX(vi); P[o + 1] = posAttr.getY(vi); P[o + 2] = posAttr.getZ(vi);
          if (N) { N[o] = nrmAttr.getX(vi); N[o + 1] = nrmAttr.getY(vi); N[o + 2] = nrmAttr.getZ(vi); }
        }
      }
      cache = target.userData._decalTriCache = { P, N, triCount };
    }
    const { P, N, triCount } = cache;
    const inv = new THREE.Matrix4().copy(target.matrixWorld).invert();
    const lc = worldCenter.clone().applyMatrix4(inv);
    const ws = target.getWorldScale(new THREE.Vector3()).x || 1;
    const lr = (clipRadius / ws); const lr2 = lr * lr;
    const op = [], on = N ? [] : null;
    for (let t = 0; t < triCount; t++) {
      const b = t * 9;
      // accept if any vertex (or centroid) is within lr of the decal center
      let keep = false;
      for (let k = 0; k < 3; k++) {
        const o = b + k * 3, dx = P[o] - lc.x, dy = P[o + 1] - lc.y, dz = P[o + 2] - lc.z;
        if (dx * dx + dy * dy + dz * dz <= lr2) { keep = true; break; }
      }
      if (!keep) {
        const cx = (P[b] + P[b + 3] + P[b + 6]) / 3 - lc.x, cy = (P[b + 1] + P[b + 4] + P[b + 7]) / 3 - lc.y, cz = (P[b + 2] + P[b + 5] + P[b + 8]) / 3 - lc.z;
        if (cx * cx + cy * cy + cz * cz > lr2) continue;
      }
      for (let k = 0; k < 9; k++) { op.push(P[b + k]); if (on) on.push(N[b + k]); }
    }
    const pg = new THREE.BufferGeometry();
    pg.setAttribute('position', new THREE.Float32BufferAttribute(op, 3));
    if (on && on.length) pg.setAttribute('normal', new THREE.Float32BufferAttribute(on, 3));
    else pg.computeVertexNormals();
    const proxy = new THREE.Mesh(pg);
    proxy.matrixAutoUpdate = false; proxy.matrixWorldAutoUpdate = false;
    proxy.matrixWorld.copy(target.matrixWorld);
    proxy._clippedTris = op.length / 9;
    return proxy;
  }

  // ONE-SIDED projection filter: keep only triangles whose face normal points the
  // same way as the decal's outward normal. Thin double-skin panels (the WING is two
  // stacked surfaces) otherwise catch the projection on BOTH skins → the sticker
  // clips through and reads mirrored on the far side. Used when slot.oneSided.
  _filterFacing(mesh, worldNormal) {
    const geo = mesh.geometry, posAttr = geo.attributes.position, idx = geo.index;
    const triCount = idx ? idx.count / 3 : posAttr.count / 3;
    const nm = new THREE.Matrix3().getNormalMatrix(mesh.matrixWorld);
    const gi = idx ? (i) => idx.getX(i) : (i) => i;
    const a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3();
    const ab = new THREE.Vector3(), ac = new THREE.Vector3(), fn = new THREE.Vector3();
    const nrmAttr = geo.attributes.normal;
    const op = [], on = nrmAttr ? [] : null;
    for (let t = 0; t < triCount; t++) {
      const i0 = gi(t * 3), i1 = gi(t * 3 + 1), i2 = gi(t * 3 + 2);
      a.fromBufferAttribute(posAttr, i0); b.fromBufferAttribute(posAttr, i1); c.fromBufferAttribute(posAttr, i2);
      ab.subVectors(b, a); ac.subVectors(c, a); fn.crossVectors(ab, ac).applyMatrix3(nm).normalize();
      if (fn.dot(worldNormal) <= 0.15) continue; // faces away (or edge-on) from the projector → drop
      for (const vi of [i0, i1, i2]) {
        op.push(posAttr.getX(vi), posAttr.getY(vi), posAttr.getZ(vi));
        if (on) on.push(nrmAttr.getX(vi), nrmAttr.getY(vi), nrmAttr.getZ(vi));
      }
    }
    const pg = new THREE.BufferGeometry();
    pg.setAttribute('position', new THREE.Float32BufferAttribute(op, 3));
    if (on && on.length) pg.setAttribute('normal', new THREE.Float32BufferAttribute(on, 3));
    else pg.computeVertexNormals();
    const proxy = new THREE.Mesh(pg);
    proxy.matrixAutoUpdate = false; proxy.matrixWorldAutoUpdate = false;
    proxy.matrixWorld.copy(mesh.matrixWorld);
    proxy._clippedTris = op.length / 9;
    return proxy;
  }

  // Build ONE conforming decal mesh from a saved slot. `material` is supplied by
  // the caller (debug fill for the spike; finish-driven material in production).
  // `aspect` = texture height/width (1 for square debug). Returns the mesh,
  // already attached to carGroup so it rotates/sticks with the car.
  _buildConformingDecal(slot, material, aspect = 1) {
    const target = this._findDecalTarget(slot.panel);
    if (!target) { console.warn('[DECAL] no target for', slot.panel); return null; }
    // Zero the car spin so the projection is computed in a canonical frame, then
    // restore — identical trick to setLicensePlate(), keeps placement stable at
    // any viewing angle.
    const savedRot = this.carGroup.rotation.y;
    this.carGroup.rotation.y = 0;
    this.carGroup.updateWorldMatrix(true, true);
    target.updateWorldMatrix(true, true);

    const { pos, normal: worldNrm, sizeWorld } = this._decalNativeToWorld(slot);
    // ORIENTATION — build it in the car's NATIVE frame exactly like the viewer
    // (orient() uses world-up as the in-plane "up" reference), THEN rotate it into
    // world by the car's world rotation. Building it directly in world space used
    // world-up against a car that's rotated 90° vs the viewer, which spun flat/top
    // decals 90° sideways. Native-then-rotate reproduces the viewer on every panel.
    const nN = new THREE.Vector3(slot.normal[0], slot.normal[1], slot.normal[2]);
    const qNative = new THREE.Quaternion().setFromEuler(this._decalOrient(nN, slot.rotDeg));
    const qModel = this.combinedModel.getWorldQuaternion(new THREE.Quaternion());
    const orient = new THREE.Euler().setFromQuaternion(qModel.multiply(qNative));
    // Projector depth matches the viewer's (size*0.35) so the decal wraps the
    // curved panel by the SAME amount — a thicker box would over-wrap on fenders.
    // A slot may pass an explicit `depth` (world units) to force a SHALLOW box so
    // the projection doesn't bleed through a thin panel (e.g. the wing decal, which
    // must stay on the rear face only). Defaults to the standard size*0.35.
    const depthWorld = (slot.depth != null) ? this._decalNativeToWorld({ ...slot, size: slot.depth }).sizeWorld : sizeWorld * 0.35;
    const sizeVec = new THREE.Vector3(sizeWorld, sizeWorld * aspect, depthWorld);
    const t0 = performance.now();
    let projTarget = this._clipMeshRegion(target, pos, sizeWorld * 1.3);
    if (slot.oneSided) projTarget = this._filterFacing(projTarget, worldNrm);
    const geo = new DecalGeometry(projTarget, pos, orient, sizeVec);
    const ms = (performance.now() - t0).toFixed(1);
    const mesh = new THREE.Mesh(geo, material);
    mesh.renderOrder = 6;
    this.scene.add(mesh);
    mesh.updateMatrixWorld(true);
    this.carGroup.attach(mesh);

    this.carGroup.rotation.y = savedRot;
    this.carGroup.updateWorldMatrix(true, true);
    mesh._decalSlot = slot;
    if (window.__frDecalDebug) console.log(`[DECAL] ${slot.panel} tris=${projTarget._clippedTris ?? 'full'} proj=${ms}ms verts=${geo.attributes.position.count}`);
    return mesh;
  }

  // Decal art lives in the (flat) theme assets folder as frdecal-<pack>-<NN>.png.
  // We derive the CDN base from the already-injected GLB asset URL so we don't
  // need a second Liquid injection.
  _decalAssetBase() {
    if (this._decalBase) return this._decalBase;
    const g = window.FR_CAR_GLB || '';
    this._decalBase = g.split('?')[0].replace(/[^/]+$/, '');
    return this._decalBase;
  }

  // Load (and cache) a decal texture. Returns a Promise<THREE.Texture>.
  _decalTexture(file) {
    this._decalTexCache = this._decalTexCache || {};
    if (this._decalTexCache[file]) return this._decalTexCache[file];
    const url = this._decalAssetBase() + file;
    const p = new Promise((resolve) => {
      new THREE.TextureLoader().load(url, (t) => {
        t.colorSpace = THREE.SRGBColorSpace;
        t.anisotropy = 4;
        resolve(t);
      }, undefined, () => { console.warn('[DECAL] texture failed', url); resolve(null); });
    });
    this._decalTexCache[file] = p;
    return p;
  }

  // Procedural BRUSHED-METAL roughness map — fine vertical streaks so the decal
  // catches the studio HDRI like real brushed-vinyl. Cached + reused by the alu
  // and gold finishes (gold just tints the albedo).
  _decalBrushTex() {
    if (this._brushTex) return this._brushTex;
    const S = 256, cv = document.createElement('canvas'); cv.width = cv.height = S;
    const c = cv.getContext('2d');
    c.fillStyle = '#7a7a7a'; c.fillRect(0, 0, S, S);
    for (let i = 0; i < 900; i++) {
      const x = Math.floor(Math.random() * S), g = 90 + Math.floor(Math.random() * 90);
      c.strokeStyle = `rgba(${g},${g},${g},0.5)`; c.lineWidth = Math.random() < 0.5 ? 1 : 2;
      c.beginPath(); c.moveTo(x, 0); c.lineTo(x + (Math.random() * 6 - 3), S); c.stroke();
    }
    const t = new THREE.CanvasTexture(cv);
    t.wrapS = t.wrapT = THREE.RepeatWrapping; t.repeat.set(3, 3);
    this._brushTex = t; return t;
  }

  // Material for a placed decal, keyed by finish. The decal PNG supplies the art
  // (map + emissiveMap so it reads in shadow); the finish is the metallic vinyl
  // laminate over it: STANDARD brushed aluminum (silver brushed-metal sheen),
  // SUPER HOLOGRAPHIC (angle-shift rainbow iridescence), BRUSHED GOLD.
  _makeDecalMaterial(finishId, tex) {
    const mat = new THREE.MeshStandardMaterial({
      map: tex, emissive: 0xffffff, emissiveMap: tex, emissiveIntensity: 0.22,
      transparent: true, alphaTest: 0.04, roughness: 0.32, metalness: 0.85,
      envMapIntensity: 1.25,
      depthWrite: false, polygonOffset: true, polygonOffsetFactor: -6, polygonOffsetUnits: -6,
    });
    this._applyDecalFinish(mat, finishId);
    return mat;
  }

  // Apply one of the 3 metallic decal finishes to a decal material.
  _applyDecalFinish(mat, finishId) {
    // reset state so re-applying a finish is clean
    mat.roughnessMap = null; mat.color.set(0xffffff); mat.onBeforeCompile = () => {};
    mat._holo = false;
    if (finishId === 'brushed-gold') {
      mat.metalness = 0.95; mat.roughness = 0.3; mat.envMapIntensity = 1.35;
      mat.color.set(0xffd66a); mat.roughnessMap = this._decalBrushTex();
    } else if (finishId === 'holographic') {
      // super holographic: keep art, add a fresnel-driven rainbow that shifts
      // with view angle (iridescent foil look).
      mat.metalness = 0.55; mat.roughness = 0.14; mat.envMapIntensity = 1.5;
      mat._holo = true;
      mat.onBeforeCompile = (shader) => {
        shader.fragmentShader = shader.fragmentShader.replace(
          '#include <emissivemap_fragment>',
          `#include <emissivemap_fragment>
           {
             vec3 hN = normalize(vNormal);
             vec3 hV = normalize(vViewPosition);
             float hFres = pow(1.0 - abs(dot(hN, hV)), 1.1);
             // base coverage so the foil shimmers across the whole face, not only edges
             float hAmt = 0.35 + 0.65 * hFres;
             float hHue = hFres * 2.2 + vViewPosition.y * 0.85 + vViewPosition.x * 0.4;
             vec3 hRainbow = 0.5 + 0.5 * cos(6.28318 * (hHue + vec3(0.0, 0.33, 0.67)));
             totalEmissiveRadiance += hRainbow * hAmt * 1.6 * diffuseColor.a;
           }`
        );
      };
    } else {
      // brushed aluminum (standard / free)
      mat.metalness = 0.9; mat.roughness = 0.3; mat.envMapIntensity = 1.3;
      mat.color.set(0xeef0f4); mat.roughnessMap = this._decalBrushTex();
    }
    mat.needsUpdate = true;
  }

  // ── PUBLIC: render a set of conforming decals ────────────────────────────
  // `placements` = array of slot objects (from FR_DECAL_PACKS[pack].slots),
  // each optionally carrying its own `finish`. Loads textures, then projects
  // each as a conforming DecalGeometry. Replaces any currently shown decals.
  async applyConformingDecals(placements, finishId) {
    // Generation guard: rapid re-applies (e.g. clicking permit options fast) overlap
    // because this is async. Tag each run; if a newer run started while we awaited
    // textures, bail so only the latest call builds meshes (no duplicates).
    const gen = (this._decalGen = (this._decalGen || 0) + 1);
    this._removeDecals();
    if (!placements || !placements.length || !this.combinedModel) return;
    // preload all textures first so projection runs in one tight pass. A CUSTOM
    // permit carries a `customDesign` instead of a file → build its texture from the
    // plate engine on the fly (sync) rather than loading a PNG from the CDN.
    const texes = await Promise.all(placements.map(p => p.customDesign
      ? Promise.resolve(
          p.customKind === 'banner' ? this._bannerCanvasTexture(p.customDesign)
          : p.customKind === 'wing' ? this._wingCanvasTexture(p.customDesign)
          : p.customKind === 'doorbadge' ? this._doorBadgeTexture(p.customDesign)
          : p.customKind === 'panel' ? this._panelStickerTexture(p.customDesign, p.aspect)
          : this._permitCanvasTexture(p.customDesign))
      : this._decalTexture(p.file)));
    if (gen !== this._decalGen) { texes.forEach(t => { try { t && t.dispose && t.dispose(); } catch (e) {} }); return; }
    placements.forEach((slot, i) => {
      const tex = texes[i];
      if (!tex) return;
      // Aspect = decal height/width. A slot may pin an explicit `aspect` (e.g. the
      // wing decal's thin bar) so it doesn't depend on the canvas dimensions.
      const aspect = (slot.aspect != null) ? slot.aspect
        : (tex.image && tex.image.width) ? (tex.image.height / tex.image.width) : 1;
      const mat = this._makeDecalMaterial(slot.finish || finishId, tex);
      const mesh = this._buildConformingDecal(slot, mat, aspect);
      if (mesh) (this._decalMeshes = this._decalMeshes || []).push(mesh);
    });
    this.requestRender(400);
  }

  // Convenience: render an ENTIRE pack (all slots) with one finish — used for
  // preview/QA and as the basis for the per-slot customer flow.
  async applyDecalPack(packId, finishId) {
    const pack = (window.FR_DECAL_PACKS || {})[packId];
    if (!pack) { console.warn('[DECAL] unknown pack', packId); return; }
    await this.applyConformingDecals(pack.slots.map(s => ({ ...s })), finishId || 'brushed-aluminum');
    if (window.__frDecalDebug) console.log(`[DECAL] pack ${packId}: ${(this._decalMeshes || []).length} decals`);
  }

  // Render the customer's BUILD: `decals` = placed pack designs
  // [{ packId, page, mirror, mirrored }], `permits` = placed racing permits
  // [{ id }]. Expands each into actual slot placements (primary + mirror page
  // when toggled) and projects them all with the one global finish.
  async applyBuildDecals(decals, permits, finishId, banners, wingDecal, authored) {
    const packs = window.FR_DECAL_PACKS || {};
    const allPermits = window.FR_RACING_PERMITS || [];
    const out = [];
    (decals || []).forEach(d => {
      const pack = packs[d.packId]; if (!pack) return;
      const slot = pack.slots.find(s => s.page === d.page);
      if (slot) out.push({ ...slot, finish: d.finish });
      if (d.mirrored && d.mirror) {
        const ms = pack.slots.find(s => s.page === d.mirror);
        if (ms) out.push({ ...ms, finish: d.finish });
      }
    });
    const positions = window.FR_PERMIT_POSITIONS || [];
    (permits || []).forEach(p => {
      if (p.custom) {
        // Custom permit → reuse the chosen POSITION's known-good slot, but project a
        // canvas texture built from the customer's design instead of a PNG file.
        const pos = positions[p.position] || positions[0];
        if (pos) out.push({ ...pos.slot, customDesign: p.design || {}, finish: p.finish });
        return;
      }
      const def = allPermits.find(x => x.id === p.id);
      if (def) out.push({ ...def.slot, file: def.file, finish: p.finish });
    });
    // Custom sun banners — windshield / mirrored side stripe (canvas texture).
    const bPositions = window.FR_BANNER_POSITIONS || [];
    (banners || []).forEach(b => {
      const pos = bPositions[b.position] || bPositions[0];
      if (!pos) return;
      out.push({ ...pos.slot, customKind: 'banner', customDesign: b.design || {}, finish: b.finish });
      if (b.mirror && pos.mirrorSlot) out.push({ ...pos.mirrorSlot, customKind: 'banner', customDesign: b.design || {}, finish: b.finish });
    });
    // Rear wing decal — a single authored placement (canvas texture) on the wing's
    // rear face. Same conforming-decal path as banners; carries its own design.
    if (wingDecal && wingDecal.on && window.FR_WING_DECAL_POS) {
      const wp = window.FR_WING_DECAL_POS;
      out.push({ ...wp, aspect: (wp.aspect != null ? wp.aspect : wp.aspectH), customKind: 'wing',
        customDesign: { style: wingDecal.style, text: wingDecal.text, color: wingDecal.color },
        finish: wingDecal.finish });
    }
    // FR Signature authored stickers (Sticker Lab placements): door number badge
    // (mirrored pair) + roof/deck futuristic panels. Slots from FR_AUTHORED_STICKER_SLOTS.
    if (authored && window.FR_AUTHORED_STICKER_SLOTS) {
      const slotOf = id => (window.FR_AUTHORED_STICKER_SLOTS || []).find(s => s.id === id);
      const db = authored.doorBadge, ds = slotOf('lower-door');
      if (db && db.on && ds) {
        const design = { number: db.number, name: db.name, sub: db.sub, style: db.style, accent: db.accent };
        out.push({ ...ds.slot, customKind: 'doorbadge', customDesign: design, finish: db.finish });
        if (ds.mirrorSlot) out.push({ ...ds.mirrorSlot, customKind: 'doorbadge', customDesign: design, finish: db.finish });
      }
      for (const [key, slotId] of [['roofPanel', 'roof-panel'], ['deckPanel', 'rear-deck']]) {
        const pn = authored[key], ps = slotOf(slotId);
        if (pn && pn.on && ps) {
          out.push({ ...ps.slot, customKind: 'panel',
            customDesign: { text: pn.text, number: pn.number, pattern: pn.pattern, color: pn.color },
            finish: pn.finish });
        }
      }
    }
    await this.applyConformingDecals(out, finishId || 'brushed-aluminum');
  }

  setDecals(stickers) {
    this._removeDecals();
    const body = this._baseCarNode;
    if (!body || !stickers || !stickers.length) return;
    body.updateWorldMatrix(true, true);
    const ray = new THREE.Raycaster();
    const zones = window.FR_STICKER_ZONES || [];
    stickers.forEach(s => {
      const zone = zones.find(z => z.id === s.zone) || zones[0];
      const view = zone && zone.view; if (!view) return;
      const camPos = new THREE.Vector3(view.pos[0], view.pos[1], view.pos[2]);
      const look0 = new THREE.Vector3(view.look[0], view.look[1], view.look[2]);
      // Per-sticker jitter so multiple decals in one zone scatter across the panel.
      const h = this._hash(s.id);
      const jx = ((h % 7) - 3) * 0.10, jy = (((h >> 3) % 5) - 2) * 0.10, jz = (((h >> 6) % 7) - 3) * 0.10;
      const target = look0.clone().add(new THREE.Vector3(jx, jy, jz));
      const dir = target.clone().sub(camPos).normalize(); // camera → body
      ray.set(camPos, dir);
      const hits = ray.intersectObject(body, true);
      let pos, normal;
      if (hits.length) {
        pos = hits[0].point;
        normal = hits[0].face ? hits[0].face.normal.clone().transformDirection(hits[0].object.matrixWorld).normalize() : dir.clone().negate();
      } else {
        // fallback: float the decal just in front of the panel anchor
        pos = target; normal = camPos.clone().sub(target).normalize();
      }
      const tex = this._makeDecalTexture(s, s.finish);
      const w = 0.32;
      const mat = new THREE.MeshStandardMaterial({ map: tex, transparent: true, metalness: 0.4, roughness: 0.42, depthTest: true, polygonOffset: true, polygonOffsetFactor: -4, polygonOffsetUnits: -4 });
      const mesh = new THREE.Mesh(new THREE.PlaneGeometry(w, w), mat);
      mesh.position.copy(pos).addScaledVector(normal, 0.012);
      mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), normal);
      mesh.renderOrder = 5;
      this.scene.add(mesh);
      mesh.updateMatrixWorld(true);
      this.carGroup.attach(mesh); // stick to the car
      (this._decalMeshes = this._decalMeshes || []).push(mesh);
    });
  }

  // Draw one metallic decal design to a canvas. The SAME drawing feeds both the
  // card thumbnail (withBg=true → dark brushed plate behind it) and the decal
  // actually placed on the car (withBg=false → transparent), so the preview the
  // customer taps matches what lands on the bodywork. 10 distinct archetypes,
  // chosen deterministically by the sticker id hash → 56 decals all look unique.
  _drawDecalCanvas(s, finish, withBg) {
    const S = 256;
    const cv = document.createElement('canvas'); cv.width = cv.height = S;
    const ctx = cv.getContext('2d');
    const fin = finish || {};
    // Metal fill for the design itself.
    let fill;
    if (fin.gradient) { // holographic
      const g = ctx.createLinearGradient(0, 0, S, S);
      fin.gradient.forEach((c, i) => g.addColorStop(i / Math.max(1, fin.gradient.length - 1), c));
      fill = g;
    } else if (/gold/i.test(fin.name || '') || fin.id === 'brushed-gold') {
      const g = ctx.createLinearGradient(0, 0, 0, S); g.addColorStop(0, '#f7e08a'); g.addColorStop(0.5, '#d4af37'); g.addColorStop(1, '#a8842a'); fill = g;
    } else { // brushed aluminum (default)
      const g = ctx.createLinearGradient(0, 0, 0, S); g.addColorStop(0, '#f2f4f8'); g.addColorStop(0.45, '#c2c6cc'); g.addColorStop(1, '#83868c'); fill = g;
    }
    if (withBg) { // dark plate so the metal design reads on the card
      const bg = ctx.createRadialGradient(S / 2, S * 0.4, S * 0.08, S / 2, S / 2, S * 0.72);
      bg.addColorStop(0, '#26272c'); bg.addColorStop(1, '#0c0c0f');
      ctx.fillStyle = bg; ctx.fillRect(0, 0, S, S);
    }
    const h = this._hash(s.id);
    const kind = h % 10;
    const R = S * 0.40;
    ctx.save(); ctx.translate(S / 2, S / 2);
    ctx.fillStyle = fill; ctx.strokeStyle = fill; ctx.lineJoin = 'round'; ctx.lineCap = 'round';
    ctx.shadowColor = 'rgba(0,0,0,0.45)'; ctx.shadowBlur = withBg ? 6 : 8;
    const poly = (pts) => { ctx.beginPath(); pts.forEach((p, i) => i ? ctx.lineTo(p[0], p[1]) : ctx.moveTo(p[0], p[1])); ctx.closePath(); ctx.fill(); };
    const star = (spikes, outer, inner) => { ctx.beginPath(); for (let i = 0; i < spikes * 2; i++) { const r = i % 2 === 0 ? outer : inner; const a = (i / (spikes * 2)) * Math.PI * 2 - Math.PI / 2; ctx.lineTo(Math.cos(a) * r, Math.sin(a) * r); } ctx.closePath(); ctx.fill(); };
    const hexPath = (rad) => { ctx.beginPath(); for (let i = 0; i < 6; i++) { const a = Math.PI / 6 + i / 6 * Math.PI * 2; const x = Math.cos(a) * rad, y = Math.sin(a) * rad; i ? ctx.lineTo(x, y) : ctx.moveTo(x, y); } ctx.closePath(); };
    if (kind === 0) {                       // 5-point star
      star(5, R, R * 0.42);
    } else if (kind === 1) {                // lightning bolt
      poly([[-R * 0.18, -R], [R * 0.36, -R * 0.12], [R * 0.04, -R * 0.12], [R * 0.30, R], [-R * 0.40, R * 0.04], [-R * 0.04, R * 0.04]]);
    } else if (kind === 2) {                // flame
      ctx.beginPath(); ctx.moveTo(0, -R);
      ctx.bezierCurveTo(R * 0.72, -R * 0.3, R * 0.46, R * 0.6, 0, R);
      ctx.bezierCurveTo(-R * 0.46, R * 0.6, -R * 0.72, -R * 0.3, 0, -R);
      ctx.fill();
    } else if (kind === 3) {                // ring + center star
      ctx.lineWidth = R * 0.17; ctx.beginPath(); ctx.arc(0, 0, R * 0.86, 0, Math.PI * 2); ctx.stroke();
      star(5, R * 0.48, R * 0.2);
    } else if (kind === 4) {                // shield crest
      poly([[0, -R], [R * 0.8, -R * 0.55], [R * 0.8, R * 0.25], [0, R], [-R * 0.8, R * 0.25], [-R * 0.8, -R * 0.55]]);
    } else if (kind === 5) {                // triple chevrons
      ctx.lineWidth = R * 0.20;
      for (let k = 0; k < 3; k++) { const y = -R * 0.5 + k * R * 0.5; ctx.beginPath(); ctx.moveTo(-R * 0.6, y + R * 0.25); ctx.lineTo(0, y - R * 0.15); ctx.lineTo(R * 0.6, y + R * 0.25); ctx.stroke(); }
    } else if (kind === 6) {                // diamond
      poly([[0, -R], [R * 0.66, 0], [0, R], [-R * 0.66, 0]]);
    } else if (kind === 7) {                // hex badge (ring + inner hex)
      ctx.lineWidth = R * 0.15; hexPath(R); ctx.stroke(); hexPath(R * 0.42); ctx.fill();
    } else if (kind === 8) {                // dual speed wings
      ctx.lineWidth = R * 0.18;
      for (const sgn of [-1, 1]) {
        ctx.beginPath(); ctx.moveTo(sgn * R * 0.1, -R * 0.05); ctx.quadraticCurveTo(sgn * R * 0.7, -R * 0.45, sgn * R, -R * 0.05); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(sgn * R * 0.1, R * 0.28); ctx.quadraticCurveTo(sgn * R * 0.62, -R * 0.02, sgn * R * 0.86, R * 0.28); ctx.stroke();
      }
    } else {                                // text tag badge
      const tags = ['FR', 'APEX', 'TURBO', 'NITRO', 'GT-R', 'RS', 'BOOST', 'REDLINE', 'DRIFT', 'V8', 'PRO', 'RACE'];
      const tag = tags[h % tags.length];
      if (ctx.roundRect) { ctx.beginPath(); ctx.roundRect(-R * 1.05, -R * 0.48, R * 2.1, R * 0.96, 14); ctx.fill(); }
      else ctx.fillRect(-R * 1.05, -R * 0.48, R * 2.1, R * 0.96);
      ctx.shadowBlur = 0; ctx.fillStyle = withBg ? '#15161a' : '#111114';
      ctx.font = '800 56px Arial'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(tag, 0, 3);
    }
    ctx.restore();
    return cv;
  }

  _makeDecalTexture(s, finish) {
    const cv = this._drawDecalCanvas(s, finish, false);
    const tex = new THREE.CanvasTexture(cv); tex.colorSpace = THREE.SRGBColorSpace; tex.needsUpdate = true; return tex;
  }

  // Cached data-URL preview for the decal cards (dark plate + the metal design).
  decalThumbURL(sticker) {
    this._decalThumbCache = this._decalThumbCache || {};
    if (this._decalThumbCache[sticker.id]) return this._decalThumbCache[sticker.id];
    const cv = this._drawDecalCanvas(sticker, (window.FR_DECAL_FINISHES || [])[0], true);
    const url = cv.toDataURL('image/png');
    this._decalThumbCache[sticker.id] = url;
    return url;
  }

  // Procedural rim icon for the wheel-set tiles. The real rim colours are mostly
  // near-black gunmetal, so a flat colour swatch makes them indistinguishable —
  // here each wheel gets a distinct SPOKE pattern (keyed by id) drawn in its own
  // metal, so customers can actually tell them apart. Sits inside the round tile
  // frame (whose dark border reads as the tyre), so no tyre ring is drawn here.
  _drawWheelIcon(w) {
    const S = 160, cv = document.createElement('canvas'); cv.width = cv.height = S;
    const ctx = cv.getContext('2d');
    const hex = '#' + (w.color || 0x3a3d42).toString(16).padStart(6, '0');
    const light = shade(hex, 55), base = hex, dark = shade(hex, -45);
    const cx = S / 2, cy = S / 2, TAU = Math.PI * 2;
    // Rim barrel — metallic radial gradient (off-centre highlight = 3D sheen).
    const rg = ctx.createRadialGradient(cx - S * 0.12, cy - S * 0.14, S * 0.05, cx, cy, S * 0.5);
    rg.addColorStop(0, light); rg.addColorStop(0.55, base); rg.addColorStop(1, dark);
    ctx.fillStyle = rg; ctx.beginPath(); ctx.arc(cx, cy, S * 0.49, 0, TAU); ctx.fill();
    // Dark face between the spokes so the spokes read.
    ctx.fillStyle = '#0d0d10'; ctx.beginPath(); ctx.arc(cx, cy, S * 0.40, 0, TAU); ctx.fill();
    const spec = ({
      stock:     { n: 5,  style: 'flat' },
      circuit:   { n: 10, style: 'mesh' },
      split:     { n: 5,  style: 'split' },
      vortex:    { n: 6,  style: 'turbine' },
      nakamishi: { n: 5,  style: 'five' },
    })[w.id] || { n: 5, style: 'flat' };
    ctx.save(); ctx.translate(cx, cy);
    const sg = ctx.createLinearGradient(0, -S * 0.4, 0, S * 0.05);
    sg.addColorStop(0, light); sg.addColorStop(1, base);
    ctx.fillStyle = sg; ctx.strokeStyle = dark; ctx.lineWidth = 1; ctx.lineJoin = 'round';
    const rOut = S * 0.42, rIn = S * 0.12;
    const spoke = (ang, hw) => { ctx.save(); ctx.rotate(ang); ctx.beginPath(); ctx.moveTo(-hw * 0.55, -rIn); ctx.lineTo(-hw, -rOut); ctx.lineTo(hw, -rOut); ctx.lineTo(hw * 0.55, -rIn); ctx.closePath(); ctx.fill(); ctx.stroke(); ctx.restore(); };
    if (spec.style === 'turbine') {
      for (let i = 0; i < spec.n; i++) { ctx.save(); ctx.rotate(i / spec.n * TAU); ctx.beginPath(); ctx.moveTo(0, -rIn); ctx.quadraticCurveTo(S * 0.15, -rOut * 0.72, S * 0.045, -rOut); ctx.quadraticCurveTo(-S * 0.03, -rOut * 0.82, 0, -rIn); ctx.fill(); ctx.restore(); }
    } else if (spec.style === 'split') {
      for (let i = 0; i < spec.n; i++) { const a = i / spec.n * TAU; spoke(a - 0.14, S * 0.024); spoke(a + 0.14, S * 0.024); }
    } else if (spec.style === 'mesh') {
      for (let i = 0; i < spec.n; i++) spoke(i / spec.n * TAU, S * 0.018);
    } else if (spec.style === 'five') {
      for (let i = 0; i < spec.n; i++) spoke(i / spec.n * TAU, S * 0.052);
    } else { // flat
      for (let i = 0; i < spec.n; i++) spoke(i / spec.n * TAU, S * 0.062);
    }
    ctx.restore();
    // Centre hub + cap.
    ctx.fillStyle = base; ctx.beginPath(); ctx.arc(cx, cy, S * 0.115, 0, TAU); ctx.fill();
    ctx.strokeStyle = dark; ctx.lineWidth = 2; ctx.stroke();
    ctx.fillStyle = light; ctx.beginPath(); ctx.arc(cx, cy, S * 0.045, 0, TAU); ctx.fill();
    // Rim-lip specular arc.
    ctx.strokeStyle = 'rgba(255,255,255,0.28)'; ctx.lineWidth = 2.5;
    ctx.beginPath(); ctx.arc(cx, cy, S * 0.475, -2.3, -0.7); ctx.stroke();
    return cv;
  }

  wheelThumbURL(w) {
    this._wheelThumbCache = this._wheelThumbCache || {};
    if (this._wheelThumbCache[w.id]) return this._wheelThumbCache[w.id];
    const url = this._drawWheelIcon(w).toDataURL('image/png');
    this._wheelThumbCache[w.id] = url;
    return url;
  }

  setPaint(paint) {
    this.requestRender && this.requestRender(500);
    this._currentPaint = paint;
    const m = this.bodyMaterial;
    if (!this._carbonOn) { if (m.map) m.map = null; const _su = m.userData.splitUniforms; if (_su && _su.uCarbonOn) _su.uCarbonOn.value = 0; }   // clear any leftover carbon weave when a normal paint is applied
    // Premium automotive paint: dielectric base (metalness 0, never chrome) + a
    // tight clearcoat for the deep wet shine. Per-paint roughness controls gloss
    // (flat finishes higher, wet finishes lower).
    const baseHex = paint.iridescent ? paint.iridescent[0] : (paint.flip ? paint.flip[0] : paint.hex);
    m.color = new THREE.Color(baseHex);
    m.metalness = paint.metalness ?? 0.0;               // >0 = metalflake / metal (gold, chrome, gunmetal)
    m.roughness = paint.roughness ?? 0.30;
    m.clearcoat = paint.clearcoat ?? 1.0;               // gloss layer strength
    m.clearcoatRoughness = paint.clearcoatRoughness ?? 0.045; // low = wet mirror, high = satin/matte
    m.envMapIntensity = paint.envMapIntensity ?? 1.4;
    // Translucent finishes (Crystal Blue diffused plastic, Clear Translucent) —
    // reset to 0 for every opaque paint so it never carries over.
    m.transmission = paint.transmission ?? 0.0;
    m.ior = paint.ior ?? 1.5;
    m.sheen = paint.sheen ?? 0.0;          // per-paint sheen (lab spec) — sheen 0 for paints that don't set it
    m.thickness = paint.thickness ?? 0.0;
    // CHAMELEON / IRIDESCENT — shift colour by view angle (fresnel) in the shader,
    // gated by uFlipMode (0 off · 1 two-colour flip · 2 four-stop iridescent ramp).
    const fu = m.userData.flipUniforms;
    if (fu) {
      if (paint.iridescent) {
        fu.uFlipMode.value = 2;
        const c = paint.iridescent;
        fu.uFlipA.value.set(c[0]); fu.uFlipB.value.set(c[1] || c[0]); fu.uFlipC.value.set(c[2] || c[0]); fu.uFlipD.value.set(c[3] || c[0]);
      } else if (paint.flip) {
        fu.uFlipMode.value = 1;
        fu.uFlipA.value.set(paint.flip[0]); fu.uFlipB.value.set(paint.flip[1]);
      } else {
        fu.uFlipMode.value = 0;
      }
    }
    // SPLIT: side A always tracks the selected base paint colour. While a split is
    // active, suppress the flip so it reads as two clean solid colours, and match the
    // far side's gloss to the paint.
    const su = m.userData.splitUniforms;
    if (su) {
      su.uRoughA.value = m.roughness;
      if (this._bodySplit && this._bodySplit.on) { su.uRoughB.value = m.roughness; if (fu) fu.uFlipMode.value = 0; } // split owns the two colours
      else su.uColA.value.set(baseHex);
    }
    m.needsUpdate = true;
  }

  // ===== SPLIT PAINT (body two-tone) ====================================
  // Reference frame for the split plane, computed ONCE from the body panels in the
  // car's LOCAL space so it stays correct as the car turns / is scaled.
  _computeBodySplitLocal() {
    const meshes = (this._bodyPanelMeshes || []).concat(this.realBodyMeshes || []).filter(o => o && o.isMesh && o.geometry);
    if (!meshes.length || !this.carGroup) { this._bodySplitLocal = null; return; }
    this.carGroup.updateWorldMatrix(true, true);
    const inv = this.carGroup.matrixWorld.clone().invert();
    const box = new THREE.Box3(), tmp = new THREE.Box3(), mm = new THREE.Matrix4();
    for (const mesh of meshes) {
      if (!mesh.geometry.boundingBox) mesh.geometry.computeBoundingBox();
      mm.multiplyMatrices(inv, mesh.matrixWorld);
      tmp.copy(mesh.geometry.boundingBox).applyMatrix4(mm);
      box.union(tmp);
    }
    if (box.isEmpty()) { this._bodySplitLocal = null; return; }
    const size = box.getSize(new THREE.Vector3());
    this._bodySplitLocal = {
      center: box.getCenter(new THREE.Vector3()),
      axisA: (size.x >= size.z) ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(0, 0, 1),
      extentA: (size.x >= size.z ? size.x : size.z) * 0.5,
    };
  }

  // Push current split params into the body uniforms; called on change AND every
  // frame while active (so the world-space plane tracks the car's spin).
  _updateBodySplitUniforms() {
    const u = this.bodyMaterial.userData.splitUniforms; if (!u) return;
    const s = this._bodySplit;
    if (!s || !s.on) { u.uSplitOn.value = 0; return; }
    if (!this._bodySplitLocal) this._computeBodySplitLocal();
    const L = this._bodySplitLocal; if (!L) { u.uSplitOn.value = 0; return; }
    u.uSplitOn.value = 1;
    u.uColA.value.set(s.colorA || '#ffffff');   // split is a combo of TWO chosen colours
    u.uColB.value.set(s.colorB || '#0a0a0c');
    // Per-side pearlescence (grazing-angle colour shift) when a side wears a pearl colour.
    u.uPearlA.value = s.pearlA ? 1 : 0;
    u.uPearlB.value = s.pearlB ? 1 : 0;
    u.uPearlGrazeA.value.set(s.grazeA || s.colorA || '#ffffff');
    u.uPearlGrazeB.value.set(s.grazeB || s.colorB || '#ffffff');
    u.uFlipSplit.value = s.flip ? 1 : 0;
    const tilt = (s.tilt || 0) * Math.PI / 180;
    const nLocal = L.axisA.clone().multiplyScalar(Math.cos(tilt)).add(new THREE.Vector3(0, 1, 0).multiplyScalar(Math.sin(tilt))).normalize();
    const pLocal = L.center.clone().add(nLocal.clone().multiplyScalar((s.pos || 0) * L.extentA));
    this.carGroup.updateWorldMatrix(true, false);
    u.uPlaneP.value.copy(pLocal.applyMatrix4(this.carGroup.matrixWorld));
    u.uPlaneN.value.copy(nLocal.transformDirection(this.carGroup.matrixWorld).normalize());
    const scale = this.carGroup.getWorldScale(new THREE.Vector3()).x || 1;
    u.uBlend.value = Math.max(0.0006, (s.blend || 0) * L.extentA * 0.8 * scale);
  }

  // Public: set/merge body split state. {on,color,pos,tilt,blend,flip}
  setBodySplit(params) {
    this._bodySplit = Object.assign(this._bodySplit || { on: false, colorA: null, colorB: '#0a0a0c', parts: [], pos: 0, tilt: 14, blend: 0.12, flip: false }, params || {});
    const fu = this.bodyMaterial.userData.flipUniforms;
    const su = this.bodyMaterial.userData.splitUniforms;
    if (this._bodySplit.on) {
      // default side A to the current base paint the first time it's enabled
      if (!this._bodySplit.colorA && this._currentPaint) this._bodySplit.colorA = this._currentPaint.flip ? this._currentPaint.flip[0] : this._currentPaint.hex;
      if (!this._bodySplitLocal || (params && params.on === true)) this._computeBodySplitLocal();
      if (fu) fu.uFlipMode.value = 0;             // two clean solids while a split is active
      this._updateBodySplitUniforms();
    } else {
      if (su) su.uSplitOn.value = 0;
      if (this._splitParts) [...this._splitParts].forEach(id => this.setPartSplit(id, false)); // restore any parts that were in the split
      if (this._splitAreas) [...this._splitAreas].forEach(a => this.setAreaSplit(a, false)); // restore any trim areas that were in the split
      if (this._splitWheels) [...this._splitWheels].forEach(w => this.setWheelPartSplit(w, false)); // restore any wheel parts that were in the split
      if (this._currentPaint) this.setPaint(this._currentPaint);  // restore the paint's flip finish
    }
    this.requestRender && this.requestRender(500);
  }

  // Make a body PART share the split paint (painted by where it sits relative to the
  // plane — front parts take side A, rear parts side B, a part across the line shows
  // both). It borrows the body material (same colours + plane), so it stays in sync.
  // on=false restores the part's previous finish material.
  setPartSplit(partId, on) {
    if (!this._splitParts) this._splitParts = new Set();
    const nodes = [this.partNodeById && this.partNodeById.get(partId), ...((this.partTwinsById && this.partTwinsById.get(partId)) || [])].filter(Boolean);
    if (!nodes.length) return;
    if (on) {
      nodes.forEach(node => node.traverse(c => {
        if (!c.isMesh) return;
        if (c.material !== this.bodyMaterial) c.userData._preSplitMat = c.material; // remember the finish to restore
        c.material = this.bodyMaterial;
      }));
      this._splitParts.add(partId);
    } else {
      nodes.forEach(node => node.traverse(c => {
        if (!c.isMesh) return;
        if (c.userData._preSplitMat) { c.material = c.userData._preSplitMat; delete c.userData._preSplitMat; }
      }));
      this._splitParts.delete(partId);
    }
    this.requestRender && this.requestRender(400);
  }

  // Fold a WHEEL COMPONENT (rim / caliper / disk / centre cap) into the body's two-tone
  // paint split / carbon — a mirror of setPartSplit, but for wheels. on=true → those
  // meshes borrow the bodyMaterial (so they follow the live split/carbon, painted by
  // where they sit on the car). on=false → restore the finish material they had before.
  setWheelPartSplit(which, on) {
    if (!this._splitWheels) this._splitWheels = new Set();
    const groups = {
      rim:     this.realRimMeshes,
      caliper: this._wheelCaliperMeshes,
      disk:    this._wheelBrakeMeshes,
      cap:     this._wheelCapMeshes,
    };
    const meshes = (groups[which] || []).filter(Boolean);
    if (!meshes.length) return;
    if (on) {
      meshes.forEach(c => {
        if (!c.isMesh) return;
        if (c.material !== this.bodyMaterial) c.userData._preWheelSplitMat = c.material; // remember the finish to restore
        c.material = this.bodyMaterial;
      });
      this._splitWheels.add(which);
    } else {
      meshes.forEach(c => {
        if (!c.isMesh) return;
        if (c.userData._preWheelSplitMat) { c.material = c.userData._preWheelSplitMat; delete c.userData._preWheelSplitMat; }
      });
      this._splitWheels.delete(which);
    }
    this.requestRender && this.requestRender(400);
  }

  // Recolour the gloss-black trim/accent group (null = restore factory black).
  // Also recolours the grafted front-bumper/diffuser material so "All Trim" covers them.
  setTrimColor(hex) {
    const c = hex || 0x050506;
    if (this._blackAccentMat) { this._blackAccentMat.color.set(c); this._blackAccentMat.needsUpdate = true; }
    if (this._graftBlackMat) { this._graftBlackMat.color.set(c); this._graftBlackMat.needsUpdate = true; }
    this.requestRender && this.requestRender(400);
  }

  // Which trim/glass areas this car actually has (for the UI to show only real ones).
  trimAreas() { return Object.keys(this._areaMeshes || {}).filter(a => (this._areaMeshes[a] || []).length); }

  // Paint a SPECIFIC trim/glass area its own colour (windshield, doors, mirrors,
  // spoiler, diffuser, bumper, roof) — or 'all' for the whole trim. hex null = restore.
  setAreaColor(area, hex) {
    if (area === 'all') {
      // make 'all' authoritative: drop every per-area override (back to each mesh's
      // original material), then colour the shared trim + grafted-bumper materials.
      Object.keys(this._areaMeshes || {}).forEach(a => { if (a !== 'windshield') (this._areaMeshes[a] || []).forEach(m => { if (m.userData._areaOrigMat) m.material = m.userData._areaOrigMat; }); });
      this.setTrimColor(hex);
      return;
    }
    this._applyAreaColor(area, hex);
  }
  _applyAreaColor(area, hex) {
    const meshes = (this._areaMeshes && this._areaMeshes[area]) || [];
    if (!meshes.length) return;
    if (!hex) {
      meshes.forEach(m => { if (m.userData._areaOrigMat) m.material = m.userData._areaOrigMat; }); // restore each mesh's original (graft / glass / accent)
    } else {
      let mat = (this._areaMats = this._areaMats || {})[area];
      // polygonOffset keeps the grafted bumper from z-fighting when recoloured.
      if (!mat) { mat = new THREE.MeshPhysicalMaterial({ metalness: 0.2, roughness: 0.25, clearcoat: 1.0, clearcoatRoughness: 0.06, envMapIntensity: 1.6, polygonOffset: true, polygonOffsetFactor: -1.5, polygonOffsetUnits: -1.5 }); this._areaMats[area] = mat; }
      mat.color.set(hex); mat.needsUpdate = true;
      meshes.forEach(m => { if (!m.userData._areaOrigMat) m.userData._areaOrigMat = m.material; m.material = mat; });
    }
    this.requestRender && this.requestRender(400);
  }

  // Fold a TRIM AREA into the body's two-tone paint split (mirror of setPartSplit).
  // on=true → the area's meshes borrow the bodyMaterial (so they show the live split /
  // two-tone carbon, painted by where they sit on the car). on=false → restore the
  // material they had before (a solid trim colour if one is set, else their factory mat).
  // 'all' = every trim bucket (excluding windshield glass).
  setAreaSplit(area, on) {
    if (!this._splitAreas) this._splitAreas = new Set();
    if (area === 'all') {
      Object.keys(this._areaMeshes || {}).forEach(a => { if (a !== 'windshield') this.setAreaSplit(a, on); });
      return;
    }
    const meshes = (this._areaMeshes && this._areaMeshes[area]) || [];
    if (!meshes.length) return;
    if (on) {
      meshes.forEach(m => {
        if (m.material !== this.bodyMaterial) m.userData._preAreaSplitMat = m.material; // remember the finish to restore
        m.material = this.bodyMaterial;
      });
      this._splitAreas.add(area);
    } else {
      meshes.forEach(m => {
        if (m.userData._preAreaSplitMat) { m.material = m.userData._preAreaSplitMat; delete m.userData._preAreaSplitMat; }
        else if (m.userData._areaOrigMat) { m.material = m.userData._areaOrigMat; }
      });
      this._splitAreas.delete(area);
    }
    this.requestRender && this.requestRender(400);
  }

  // ATELIER CUSTOM COLOUR — paint the whole body a 1-of-1 colour. opts: {satin, pearl, flip}.
  // Builds a synthetic paint and runs it through setPaint (so gloss/satin/pearl all work).
  setCustomColor(hex, opts = {}) {
    const p = { id: 'atelier-custom', name: 'Atelier Custom', hex };
    if (opts.metallic) {
      // Chrome / gold / metallic: high metalness + low roughness = a mirror-like, true
      // metal body. Reflections (not a clearcoat) carry the look, so push envMap up and
      // drop the clearcoat haze. metalness/roughness come from the swatch data.
      p.metalness = (opts.metalness != null ? opts.metalness : 0.95);
      p.roughness = (opts.roughness != null ? opts.roughness : 0.12);
      p.clearcoat = 0; p.clearcoatRoughness = 0;
      p.envMapIntensity = (window.__frCustomMetalEnv ?? 1.6);
    }
    else if (opts.satin) { p.roughness = 0.55; p.clearcoat = 0.06; p.clearcoatRoughness = 0.5; p.envMapIntensity = 0.85; }
    // Gloss: NO GLOW. Toned-down reflections (lower envMap + softer/rougher clearcoat)
    // so the body reads as the TRUE picked colour (matches the swatch) instead of a
    // hot, glossy "glowing" reflection. Tunable live via window.__frCustomEnv.
    else { p.roughness = 0.20; p.clearcoat = 0.7; p.clearcoatRoughness = 0.14; p.envMapIntensity = (window.__frCustomEnv ?? 0.9); }
    if (opts.pearl) { p.metalness = 0.45; const f = opts.flip || [hex, this._pearlGraze(hex)]; p.flip = f; p.gradient = f; }
    this.setPaint(p);
  }

  // ===== CARBON-FIBRE WEAVE (Atelier · +$199) ===========================
  // Film carbon "hydro-dripped" onto the main body: a fine 2/2 twill weave drawn
  // procedurally, applied as the body map. Tintable (raw carbon, or any colour
  // washed over the weave) and can carry a colour-shift. Built once + cached.
  _carbonWeaveTexture() {
    if (this._carbonTex) return this._carbonTex;
    // REAL carbon-fibre weave (ripped from a Koenigsegg Agera RS GLB), embedded as a
    // data-URI + sampled TRIPLANAR in the body shader (the body mesh has no UVs).
    const tex = new THREE.TextureLoader().load(
      'data:image/png;base64,' + FR_CARBON_TEX_B64,
      () => { this.requestRender && this.requestRender(400); }   // re-render once the image decodes
    );
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    tex.anisotropy = 16;
    this._carbonTex = tex;
    return tex;
  }

  // Live-tune the weave scale (tow density across the body) and re-apply:
  //   window.__frCarbonScale = 14; __frCtl.refreshCarbon();
  refreshCarbon() {
    this._carbonTex = null;                    // force a rebuild at the new scale
    if (this._carbonOn) this.setCarbonWeave(Object.assign({ on: true }, this._carbonOpts || {}));
  }

  // Apply / remove the carbon weave on the body. opts: { on, tint(hex|null), shift(bool), split(bool) }.
  // split = a straight LENGTHWISE centreline cut (front→rear) dividing LEFT vs RIGHT:
  //   one half = exposed glossy carbon weave, the other half = the selected base paint.
  setCarbonWeave(opts = {}) {
    this.requestRender && this.requestRender(500);
    const m = this.bodyMaterial, fu = m.userData.flipUniforms, su = m.userData.splitUniforms;
    if (!opts.on) {                            // remove → restore the selected paint
      this._carbonOn = false; this._carbonOpts = null; this._carbonSplitOn = false;
      if (su) { su.uCarbonSplit.value = 0; su.uCarbonOn.value = 0; }
      m.map = null; m.needsUpdate = true;
      if (this._currentPaint) this.setPaint(this._currentPaint);
      return;
    }
    this._carbonOn = true; this._carbonOpts = Object.assign({}, opts);
    // The REAL carbon weave texture is sampled TRIPLANAR in the body shader (object-
    // space) because Body_1 has NO UVs — a plain .map can't show. Drive it via uniforms.
    m.map = null;
    m.color = new THREE.Color('#ffffff');               // shader writes diffuse from the weave; keep base neutral
    if (su) {
      su.uCarbonOn.value = 1;
      su.uCarbonMap.value = this._carbonWeaveTexture();  // the real Koenigsegg carbon weave
      su.uCarbonTint.value.set(opts.tint || '#c8c8d0'); // raw = light wash over the weave; a colour tints it
      su.uCarbonTinted.value = opts.tint ? 1 : 0;        // vivid candy modulation when a colour is chosen
      su.uCarbonScale.value = (window.__frCarbonScale ?? 118);   // baked from the Weave Lab
      su.uCarbonBright.value = (window.__frCarbonBright ?? 4.5);
      su.uWeaveContrast.value = (window.__frCarbonContrast ?? 0.8);
      su.uCarbonBodyDark.value = (window.__frCarbonBodyDark ?? 0.76); // full-body darken (two-tone stays bright)
      su.uCarbonMatte.value = opts.matte ? 1 : 0;                  // matte vs glossy weave (per-fragment roughness)
      // Book-matched centreline split mirrors the weave about the body's object-space
      // lateral centre (cached once from the body geometry; X is the width axis).
      if (!this._carbonObjCtr) {
        const bm = (this._bodyPanelMeshes || [])[0];
        if (bm && bm.geometry) { if (!bm.geometry.boundingBox) bm.geometry.computeBoundingBox(); this._carbonObjCtr = bm.geometry.boundingBox.getCenter(new THREE.Vector3()); }
      }
      if (this._carbonObjCtr) su.uMirrorCenter.value.copy(this._carbonObjCtr);
      const _ax = (window.__frCarbonMirrorAxis || 'x');   // 'x'=left/right centreline (default), tunable to 'z'/'y'
      su.uMirrorAxis.value.set(_ax === 'x' ? 1 : 0, _ax === 'y' ? 1 : 0, _ax === 'z' ? 1 : 0);
      su.uMirrorOn.value = (window.__frCarbonMirror != null ? window.__frCarbonMirror : (opts.mirror === false ? 0 : 1)); // book-match unless customer picks Normal weave
      su.uWeaveDir.value = (window.__frCarbonV ?? -1);   // chevron direction
      m.needsUpdate = true;                              // a new sampler uniform → recompile once
    }
    m.metalness = 0.30; m.transmission = 0; m.sheen = 0;
    if (opts.matte) {
      // MATTE carbon — no wet clearcoat, dialed-down reflections; reads as dry raw weave.
      m.roughness = 0.85; m.clearcoat = 0.0; m.clearcoatRoughness = 0.7;
      m.envMapIntensity = (window.__frCarbonEnv ?? 1.1) * 0.4;
    } else {
      // GLOSSY MIRRORED CARBON (default) — true mirror clearcoat (baked Weave-Lab values:
      // ccRough 0, env 1.1). Per-fragment roughness comes from the weave tows.
      m.roughness = 0.30; m.clearcoat = 1.0; m.clearcoatRoughness = (window.__frCarbonCcRough ?? 0.0);
      m.envMapIntensity = (window.__frCarbonEnv ?? 1.1);
    }
    if (fu) {
      if (opts.shift) {                        // colour-shift carbon — iridescent ramp around the tint
        const base = new THREE.Color(opts.tint || '#2a2f3a'), hsl = {}; base.getHSL(hsl);
        const at = d => '#' + new THREE.Color().setHSL((hsl.h + d) % 1, Math.min(1, hsl.s + 0.25), Math.min(0.6, hsl.l + 0.16)).getHexString();
        fu.uFlipMode.value = 2;
        fu.uFlipA.value.set(at(0)); fu.uFlipB.value.set(at(0.12)); fu.uFlipC.value.set(at(0.28)); fu.uFlipD.value.set(at(0.5));
      } else fu.uFlipMode.value = 0;
    }
    // CENTRELINE CARBON SPLIT — a separate uniform-gated branch (does NOT touch the
    // front/rear two-tone paint split). Plane normal = the car's LATERAL (left-right)
    // axis, so the seam runs straight front→rear down the middle.
    this._carbonSplitOn = !!opts.split;
    if (su) {
      if (opts.split) {
        su.uCarbonSplit.value = 1;
        // the paint half shows the currently selected base paint colour
        const paintHex = (this._currentPaint && (this._currentPaint.flip ? this._currentPaint.flip[0] : this._currentPaint.hex)) || '#0a0a0c';
        su.uColB.value.set(paintHex);
        this._updateCarbonSplitUniforms();
      } else {
        su.uCarbonSplit.value = 0;
      }
    }
    m.needsUpdate = true;
  }

  // Reference frame for the CENTRELINE carbon split — the plane normal is the car's
  // LATERAL (width) axis, i.e. the SMALLER of X/Z (Z is length on this car). Computed
  // once in local space from the same body panels the two-tone split uses.
  _computeCarbonSplitLocal() {
    const meshes = (this._bodyPanelMeshes || []).concat(this.realBodyMeshes || []).filter(o => o && o.isMesh && o.geometry);
    if (!meshes.length || !this.carGroup) { this._carbonSplitLocal = null; return; }
    this.carGroup.updateWorldMatrix(true, true);
    const inv = this.carGroup.matrixWorld.clone().invert();
    const box = new THREE.Box3(), tmp = new THREE.Box3(), mm = new THREE.Matrix4();
    for (const mesh of meshes) {
      if (!mesh.geometry.boundingBox) mesh.geometry.computeBoundingBox();
      mm.multiplyMatrices(inv, mesh.matrixWorld);
      tmp.copy(mesh.geometry.boundingBox).applyMatrix4(mm);
      box.union(tmp);
    }
    if (box.isEmpty()) { this._carbonSplitLocal = null; return; }
    const size = box.getSize(new THREE.Vector3());
    this._carbonSplitLocal = {
      center: box.getCenter(new THREE.Vector3()),
      // LATERAL axis = the SMALLER of width(X)/length(Z) → the left-right axis,
      // so the cut runs front-to-rear down the centreline.
      axisA: (size.x < size.z) ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(0, 0, 1),
    };
  }

  // Push the centreline carbon-split plane into the uniforms (world space); called on
  // change AND every frame while active so the seam stays glued as the car turns.
  _updateCarbonSplitUniforms() {
    const u = this.bodyMaterial.userData.splitUniforms; if (!u) return;
    if (!this._carbonSplitOn) { u.uCarbonSplit.value = 0; return; }
    if (!this._carbonSplitLocal) this._computeCarbonSplitLocal();
    const L = this._carbonSplitLocal; if (!L) { u.uCarbonSplit.value = 0; return; }
    u.uCarbonSplit.value = 1;
    const nLocal = L.axisA.clone().normalize();
    const pLocal = L.center.clone();          // plane through the dead centre
    this.carGroup.updateWorldMatrix(true, false);
    u.uCarbonP.value.copy(pLocal.applyMatrix4(this.carGroup.matrixWorld));
    u.uCarbonN.value.copy(nLocal.transformDirection(this.carGroup.matrixWorld).normalize());
  }

  // A subtle, realistic pearl graze tone from a base colour (lighten + slight hue drift).
  _pearlGraze(hex) {
    const c = new THREE.Color(hex), hsl = {}; c.getHSL(hsl);
    return '#' + new THREE.Color().setHSL((hsl.h + 0.08) % 1, Math.min(1, hsl.s * 0.9 + 0.1), Math.min(0.86, hsl.l + 0.3)).getHexString();
  }

  _setupInteraction() {
    const el = this.renderer.domElement;
    let sx = 0, sr = 0;
    // While CUSTOMIZING you can drag/scroll to nudge the car, but only ±30° around the
    // current view's angle (`_baseRotY`, set by each preset/focus) — so you can peek at
    // a wheel/caliper/decal/body panel from a slightly different angle without spinning
    // away from it. The 360 view is unclamped (free spin). Pressing a preset resets the
    // base angle and animateCameraTo eases there smoothly.
    const CLAMP = Math.PI / 6; // 30°
    const clamp = (v) => {
      if (this._fullscreen) return v;                 // 360 = free
      const b = this._baseRotY || 0;
      // WHEEL-FINISH VIEW — ASYMMETRIC clamp (TASK 1).
      // Bug: on the wheel-finish focus view the car could rotate too far to the RIGHT.
      // Desired: rotate LEFT freely (up to a range) but NOT further RIGHT than the
      // base/entry angle of the view. `_wheelFinishClamp` is set true only while that
      // view is active (see focusWheelView('wheels')); every other view stays symmetric.
      // Which rotation direction is "right" (and how far the free side may travel) is
      // tunable at runtime via window hooks so it can be dialed in without a rebuild:
      //   window.__frWheelClampRightSign : +1 (default) = clamp the +Δ (drag-right) side
      //                                    to the base; free side is -Δ. Set to -1 to
      //                                    flip if the model's rotation reads inverted.
      //   window.__frWheelClampFreeRange : radians the free (left) side may travel
      //                                    (default = CLAMP, i.e. 30°).
      if (this._wheelFinishClamp) {
        const rightSign = (typeof window !== 'undefined' && window.__frWheelClampRightSign != null)
          ? window.__frWheelClampRightSign : -1;  // 2026-06-30: default FLIPPED per user (clamp the OTHER side)
        const freeRange = (typeof window !== 'undefined' && window.__frWheelClampFreeRange != null)
          ? window.__frWheelClampFreeRange : CLAMP;
        // Right side is clamped hard at the base angle; left side gets freeRange.
        // With rightSign = +1: hi (max) = base (no going right), lo = base - freeRange.
        // With rightSign = -1: lo (min) = base (no going right), hi = base + freeRange.
        const lo = rightSign >= 0 ? (b - freeRange) : b;
        const hi = rightSign >= 0 ? b : (b + freeRange);
        return Math.max(lo, Math.min(hi, v));
      }
      return Math.max(b - CLAMP, Math.min(b + CLAMP, v));
    };
    el.addEventListener('mousedown', e => { this.isDragging = true; sx = e.clientX; sr = this.targetRotY; this.requestRender && this.requestRender(220); });
    // TOUCH: 1 finger = rotate (turntable). 2 fingers in MOBILE 360 = pinch-zoom (dolly
    // the fixed camera in/out) — mobile has no OrbitControls, so we do it by hand.
    let pinching = false, pinchGap0 = 0, pinchDist0 = 0;
    const gap = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
    const mob360 = () => this._fullscreen && this._mob360Center && !(this._fsControls && this._fsControls.enabled);
    el.addEventListener('touchstart', e => {
      if (e.touches.length >= 2 && mob360()) {
        pinching = true; this.isDragging = false;
        pinchGap0 = gap(e.touches);
        pinchDist0 = this.camera.position.distanceTo(this._mob360Center);
      } else if (e.touches.length === 1) {
        this.isDragging = true; sx = e.touches[0].clientX; sr = this.targetRotY;
      }
    }, { passive: false });
    window.addEventListener('mousemove', e => {
      if (!this.isDragging) return;
      this.targetRotY = clamp(sr + (e.clientX - sx) * 0.008);
      // Keep the on-demand render loop ALIVE for the whole drag so the rotation eases
      // every frame instead of start-stopping between mouse moves (that read as a jaggy
      // micro-stutter). requestRender also stamps lastInteractTime. (2026-06-30 fix)
      this.requestRender && this.requestRender(220);
    });
    window.addEventListener('touchmove', e => {
      if (pinching && e.touches.length >= 2 && this._mob360Center) {
        if (e.cancelable) e.preventDefault();                    // stop the browser page-pinch
        const ratio = pinchGap0 / Math.max(gap(e.touches), 1);   // spread fingers → gap up → ratio<1 → move closer
        let d = pinchDist0 * ratio;
        d = Math.max(this._mob360MinDist || 1, Math.min(this._mob360MaxDist || 50, d));
        const dir = this.camera.position.clone().sub(this._mob360Center).normalize();
        this.camera.position.copy(this._mob360Center).addScaledVector(dir, d);
        this.camera.lookAt(this._mob360Center);
        if (this.camera.userData) this.camera.userData._lookAt = this._mob360Center.clone();
        this.lastInteractTime = Date.now();
        this.requestRender && this.requestRender(300);
        return;
      }
      if (!this.isDragging) return;
      this.targetRotY = clamp(sr + (e.touches[0].clientX - sx) * 0.008);
      this.requestRender && this.requestRender(220);   // keep rendering during the drag → smooth (no stutter)
    }, { passive: false });
    window.addEventListener('mouseup', () => { this.isDragging = false; });
    window.addEventListener('touchend', e => { this.isDragging = false; if (!e.touches || e.touches.length < 2) pinching = false; });
    // TAP-TO-ZOOM — double-click / double-tap the painted BODY to zoom in on the
    // carbon/paint finish, again to zoom back out. Only the body responds.
    el.addEventListener('dblclick', e => { if (e.cancelable) e.preventDefault(); this._tapZoom(e.clientX, e.clientY); });
    let _ltTap = 0, _ltX = 0, _ltY = 0;
    el.addEventListener('touchend', e => {
      const t = e.changedTouches && e.changedTouches[0]; if (!t || (e.touches && e.touches.length)) return;
      const tn = Date.now();
      if (tn - _ltTap < 320 && Math.hypot(t.clientX - _ltX, t.clientY - _ltY) < 30) { if (e.cancelable) e.preventDefault(); this._tapZoom(t.clientX, t.clientY); _ltTap = 0; }
      else { _ltTap = tn; _ltX = t.clientX; _ltY = t.clientY; }
    });
    // (Scroll-to-rotate removed — it preventDefault()'d the wheel over the canvas,
    // which made page/panel scrolling feel laggy/janky. The car no longer spins on
    // scroll in the build view; use the 360 view to orbit. OrbitControls still owns
    // the wheel for zoom in 360 — it adds its own listener.)
  }

  // Double-tap / double-click the BODY → zoom in on the carbon/paint finish; again →
  // zoom back out. Raycasts ONLY the painted body meshes (wheels/parts are ignored).
  _tapZoom(clientX, clientY) {
    if (this._fullscreen) return;
    if (this._detailZoom) {                              // already zoomed → restore the pre-zoom view
      this._detailZoom = false;
      this._presetView = !!this._preZoomWasPreset;       // restore whether the idle spin was allowed
      const v = this._preZoomView;
      if (v && typeof animateCameraTo === 'function') animateCameraTo(v.pos, v.look, undefined, 620);
      return;
    }
    const rect = this.renderer.domElement.getBoundingClientRect();
    const ndc = new THREE.Vector2(((clientX - rect.left) / rect.width) * 2 - 1, -((clientY - rect.top) / rect.height) * 2 + 1);
    this._ray = this._ray || new THREE.Raycaster();
    this._ray.setFromCamera(ndc, this.camera);
    const targets = (this._bodyPanelMeshes || []).concat(this.realBodyMeshes || []).filter(m => m && m.isMesh && m.visible);
    const hits = this._ray.intersectObjects(targets, false);
    if (!hits.length) return;                            // only the carbon/paint body zooms
    const p = hits[0].point;
    const lk = this.camera.userData && this.camera.userData._lookAt;
    this._preZoomView = { pos: this.camera.position.toArray(), look: lk ? [lk.x, lk.y, lk.z] : [p.x, p.y, p.z] };
    this._preZoomWasPreset = (this._presetView !== false);
    const dir = this.camera.position.clone().sub(p).normalize();
    const pos = p.clone().addScaledVector(dir, 0.95);    // close-up distance (world units)
    this._detailZoom = true;
    this._presetView = false;                            // inspecting → no idle spin
    if (typeof animateCameraTo === 'function') animateCameraTo([pos.x, pos.y, pos.z], [p.x, p.y, p.z], undefined, 720);
  }

  _setupResize() {
    const resize = () => {
      const w = this.container.clientWidth, h = this.container.clientHeight;
      if (!w || !h) return;                              // hidden / zero size → skip (avoids a blank canvas)
      this.renderer.setSize(w, h);
      this.camera.aspect = w / h; this.camera.updateProjectionMatrix();
      // setSize() CLEARS the framebuffer; the loop renders on-demand, so without this
      // the car vanishes after a resize/scale until you interact. Redraw immediately.
      this.requestRender && this.requestRender(400);
    };
    this._resize = resize;
    window.addEventListener('resize', resize);
    // The container can resize WITHOUT a window-resize event (panel layout, browser
    // zoom/scale, tab drag, devtools) — watch it directly so the car stays framed + drawn.
    if (typeof ResizeObserver !== 'undefined') {
      this._containerRO = new ResizeObserver(() => resize());
      this._containerRO.observe(this.container);
    }
    // Returning to a backgrounded tab can leave a cleared buffer → redraw on show.
    document.addEventListener('visibilitychange', () => { if (!document.hidden) this.requestRender && this.requestRender(500); });
    // Some browsers drop the WebGL context on tab-switch / GPU pressure → recover gracefully.
    const canvas = this.renderer.domElement;
    canvas.addEventListener('webglcontextlost', e => { e.preventDefault(); }, false);
    canvas.addEventListener('webglcontextrestored', () => { this.requestRender && this.requestRender(900); }, false);
    resize();
  }

  // ============================================================
  // FULLSCREEN SHOWCASE — expand the viewer to the whole window. Desktop gets
  // free 360 OrbitControls; phones keep the drag-turntable (no free orbit) but
  // get the big canvas + lighting controls. The lighting dock already lives on
  // the stage, so it comes along automatically.
  // ============================================================
  setFullscreen(on) {
    on = !!on;
    // Capture the EXACT view we're opening 360 from (camera pose + car rotation), so
    // exiting restores it precisely — no dragged-around "memory" carrying back into
    // the build screens. Only on a real OFF→ON transition.
    if (on && !this._fullscreen) {
      const L = this.camera.userData._lookAt;
      this._preFsView = {
        pos: this.camera.position.toArray(),
        look: L ? [L.x, L.y, L.z] : null,
        rotY: this.carGroup ? this.carGroup.rotation.y : 0,
      };
    }
    this._fullscreen = on;
    this.container.classList.toggle('fr-fullscreen', on);
    document.documentElement.classList.toggle('fr-fs-active', on);
    // Pull the view rail + lighting dock INTO the fullscreen container (the rail
    // normally lives in .fr-viewer, and on mobile the dock lives in .fr-panel),
    // so both stay usable in fullscreen. Restore them on exit.
    const movers = [document.querySelector('.fr-view-controls'), document.querySelector('[data-lighting-dock]')];
    movers.forEach(elm => {
      if (!elm) return;
      if (on) { if (elm.parentElement !== this.container) { elm.__frPrevParent = elm.parentElement; this.container.appendChild(elm); } }
      else if (elm.__frPrevParent) { elm.__frPrevParent.appendChild(elm); elm.__frPrevParent = null; }
    });
    // resize after layout settles
    requestAnimationFrame(() => { this._resize && this._resize(); this.requestRender && this.requestRender(500); });
    // Backdrop switcher (Studio / White) rides the fullscreen stage as a second,
    // roomier way to reach the SAME backdrop state the stage toggle drives.
    // 2026-08-06 owner: 360 no longer FORCES the white room. The dark studio is
    // the default everywhere and is byte-identical to what it always was; white
    // is opt-in via the "Studio" toggle on the stage (or this switcher), and
    // whatever the user picked simply persists across entering/leaving 360.
    if (on) {
      this._mount360BgSwitcher();
      this._set360SwitchActive(this._bgMode || 'studio');
    } else {
      this._unmount360BgSwitcher();
    }
    // Mini animated hint: "drag to orbit" fades in when you open 360, then auto-hides.
    if (on) this._show360Hint(); else this._hide360Hint();
    const isMobile = window.matchMedia('(max-width:760px)').matches;
    if (on && !isMobile) {
      const enable = () => {
        // Lock the orbit target to the VISIBLE car centre, and size the zoom range
        // to the car so zoom-out never flies off into the fog/void and zoom-in can't
        // punch through it. maxDistance is also kept >= the entry distance so opening
        // 360 never SNAPS the camera inward (that snap read as the "zoom glitch").
        const sph = this._visibleCarSphere();
        if (sph) this._fsControls.target.copy(sph.center);
        const entryDist = this.camera.position.distanceTo(this._fsControls.target);
        const r = sph ? sph.radius : 2;
        this._fsControls.minDistance = Math.max(1.4, r * 1.05);
        this._fsControls.maxDistance = Math.max(entryDist * 1.45, r * 4.2);
        this._fsControls.maxPolarAngle = (window.__frFsMaxPolar ?? Math.PI * 0.46); // keep the mirror in view (re-applied for live tuning)
        this._fsControls.enabled = true; this._fsControls.update(); this._startFsLoop();
      };
      if (!this._fsControls) {
        import('three/addons/controls/OrbitControls.js').then(({ OrbitControls }) => {
          this._fsControls = new OrbitControls(this.camera, this.renderer.domElement);
          this._fsControls.enableDamping = true; this._fsControls.dampingFactor = 0.085;
          // Don't let the camera drop below the horizon (no belly view) — AND stop a
          // touch short of the exact horizon so you're always looking SLIGHTLY down at
          // the floor, otherwise the mirror goes edge-on and the reflection vanishes.
          // Tunable: window.__frFsMaxPolar (radians). Re-applied per entry in enable().
          this._fsControls.maxPolarAngle = (window.__frFsMaxPolar ?? Math.PI * 0.46);
          // FINER scroll-zoom: the default speed (1.0) made one notch jump from
          // maxed-out to too-close. Slow it right down so you ease in gradually.
          this._fsControls.zoomSpeed = (window.__frFsZoomSpeed ?? 0.5);
          // zoomToCursor drifted the orbit target off the car whenever the cursor was
          // over empty background — zooming out got "stuck" and you couldn't zoom back
          // in. Zoom toward the locked car-centre target instead (stable, no drift).
          this._fsControls.zoomToCursor = false;
          // min/maxDistance are set per-entry in enable() (sized to the car).
          enable();
        });
      } else { enable(); }
    } else if (on && isMobile) {
      // MOBILE 360: no free orbit — pull the fixed camera back so the WHOLE car
      // fits the portrait viewport (otherwise the wide car gets cropped/cramped).
      // The user spins the turntable (drag) to see every side; the frame stays put.
      // The fullscreen layout takes a beat to reach full height, so wait until the
      // container is actually portrait before framing (else _resize reads the old
      // landscape size and the fit is wrong). Retry across a few frames.
      if (this._fsControls) this._fsControls.enabled = false;
      this._stopFsLoop();
      const frame = () => { if (this._fullscreen && this.container.clientHeight > this.container.clientWidth) this._frameCarForFullscreen(); };
      frame();
      if (typeof ResizeObserver !== 'undefined') {
        if (this._mobRO) this._mobRO.disconnect();
        this._mobRO = new ResizeObserver(frame);
        this._mobRO.observe(this.container);
        setTimeout(() => { if (this._mobRO) { this._mobRO.disconnect(); this._mobRO = null; } }, 1600);
      }
      setTimeout(frame, 120); setTimeout(frame, 380);
    } else {
      // EXIT (any device): drop orbit + smoothly restore the EXACT view we entered
      // 360 from (camera pose + car rotation), so the dragged-around angle never
      // carries back into the build screens. Falls back to hero if nothing captured.
      if (this._fsControls) this._fsControls.enabled = false;
      this._stopFsLoop();
      if (this._mobRO) { this._mobRO.disconnect(); this._mobRO = null; }
      const v = this._preFsView;
      if (v && typeof animateCameraTo === 'function') animateCameraTo(v.pos, v.look || [0, -0.1, 0], v.rotY, 600);
      else if (typeof setView === 'function') setView('hero');
      this.requestRender && this.requestRender(900);
    }
  }

  // Pull the fixed (mobile) 360 camera back so the WHOLE car fits the portrait
  // viewport. Fits the car's bounding SPHERE (rotation-invariant, so it stays
  // framed as the turntable spins) to whichever FOV is tighter — in portrait that's
  // the horizontal one. Keeps the hero 3/4 direction; smooth move, no rotation change.
  _frameCarForFullscreen() {
    if (!this.carGroup) return;
    this._resize && this._resize();   // ensure camera.aspect = the fullscreen size first
    // Box of ONLY the visible car. Optional body parts (widebody kits, wings,
    // splitters…) live in carGroup as HIDDEN meshes; THREE.Box3.setFromObject would
    // include them and balloon the box (~r 3.4 vs 1.9) → camera pulled way too far →
    // car tiny. Union visible meshes only.
    const box = new THREE.Box3(), tmp = new THREE.Box3();
    this.carGroup.updateWorldMatrix(true, true);
    const effVisible = (o) => { let n = o; while (n && n !== this.carGroup.parent) { if (!n.visible) return false; n = n.parent; } return true; };
    this.carGroup.traverse(o => {
      if (o.isMesh && o.geometry && effVisible(o)) {
        if (!o.geometry.boundingBox) o.geometry.computeBoundingBox();
        tmp.copy(o.geometry.boundingBox).applyMatrix4(o.matrixWorld);
        box.union(tmp);
      }
    });
    if (box.isEmpty()) return;
    const sph = box.getBoundingSphere(new THREE.Sphere());
    const cam = this.camera;
    const vFov = cam.fov * Math.PI / 180;
    const hFov = 2 * Math.atan(Math.tan(vFov / 2) * cam.aspect);
    const fitFov = Math.min(vFov, hFov);                 // portrait → horizontal is tighter
    const dist = (sph.radius / Math.sin(fitFov / 2)) * 1.12;  // +12% breathing room
    const h = viewPresets.hero;
    const dir = new THREE.Vector3(h.pos[0] - h.look[0], h.pos[1] - h.look[1], h.pos[2] - h.look[2]).normalize();
    const pos = sph.center.clone().add(dir.multiplyScalar(dist));
    animateCameraTo([pos.x, pos.y, pos.z], [sph.center.x, sph.center.y, sph.center.z], undefined, 560);
    // Remember the orbit centre + zoom range so two-finger PINCH can dolly this fixed
    // mobile-360 camera (mobile has no OrbitControls).
    this._mob360Center = sph.center.clone();
    this._mob360MinDist = sph.radius * 1.12;
    this._mob360MaxDist = dist * 2.2;
  }
  _startFsLoop() {
    if (this._fsRAF) return;
    const loop = () => {
      this._fsRAF = requestAnimationFrame(loop);
      if (this._fsControls && this._fsControls.enabled) {
        this._fsControls.update();   // damping easing → smooth orbit + zoom
        // Freeze the drag-turntable so canvas drags orbit the camera, not spin the car.
        this.targetRotY = this.currentRotY;
        // Render EVERY frame while orbiting so the damped zoom/orbit eases smoothly
        // (the on-demand loop only drew intermittently → steppy "glitchy" zoom).
        this.renderer.render(this.scene, this.camera);
      }
    };
    loop();
  }
  _stopFsLoop() { if (this._fsRAF) cancelAnimationFrame(this._fsRAF); this._fsRAF = null; }

  // Bounding sphere of only the VISIBLE car meshes. carGroup also holds optional
  // body parts as HIDDEN meshes; Box3.setFromObject would include them and balloon
  // the sphere (~r3.4 vs 1.9), throwing off framing + the 360 zoom range.
  _visibleCarSphere() {
    if (!this.carGroup) return null;
    const box = new THREE.Box3(), tmp = new THREE.Box3();
    this.carGroup.updateWorldMatrix(true, true);
    const effVisible = (o) => { let n = o; while (n && n !== this.carGroup.parent) { if (!n.visible) return false; n = n.parent; } return true; };
    this.carGroup.traverse(o => {
      if (o.isMesh && o.geometry && effVisible(o)) {
        if (!o.geometry.boundingBox) o.geometry.computeBoundingBox();
        tmp.copy(o.geometry.boundingBox).applyMatrix4(o.matrixWorld);
        box.union(tmp);
      }
    });
    if (box.isEmpty()) { const b2 = new THREE.Box3().setFromObject(this.carGroup); return b2.isEmpty() ? null : b2.getBoundingSphere(new THREE.Sphere()); }
    return box.getBoundingSphere(new THREE.Sphere());
  }

  // ============================================================
  // 360-MODE BACKGROUNDS — two scene environments the user can load ON DEMAND
  // (never in the background): a realistic GARAGE (Poly Haven HDRI, same CDN the
  // studio reflections already use) and a full WHITE studio room (procedural, so
  // it's instant + truly white). Each shows a loading screen while it prepares,
  // then becomes both the visible background AND the reflection environment.
  // Lives only in the fullscreen 360 viewer; exiting reverts to the studio look.
  // ============================================================
  _show360Loading(text) {
    let el = this._loading360;
    if (!el) {
      el = document.createElement('div');
      el.className = 'fr-360-loading';
      el.innerHTML = '<div class="fr-360-spin"></div><div class="fr-360-loading-text"></div>';
      this._loading360 = el;
    }
    el.querySelector('.fr-360-loading-text').textContent = text || 'Loading…';
    if (el.parentElement !== this.container) this.container.appendChild(el);
    // force reflow so the .show transition runs even right after append
    el.offsetHeight; el.classList.add('show');
  }
  _hide360Loading() { if (this._loading360) this._loading360.classList.remove('show'); }

  // Mini animated guide shown when the 360 view opens: "drag to orbit". Fades/slides
  // in, the arrow nudges, then it auto-hides after a few seconds (or on first drag).
  _show360Hint() {
    let el = this._hint360;
    if (!el) {
      el = document.createElement('div');
      el.className = 'fr-360-hint';
      const mobile = window.matchMedia('(max-width:760px)').matches;
      el.innerHTML = '<span class="fr-360-hint-ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12h18"/><path d="M7 8l-4 4 4 4"/><path d="M17 8l4 4-4 4"/></svg></span><span class="fr-360-hint-text">' + (mobile ? 'Drag to spin' : 'Drag to orbit · scroll to zoom') + '</span>';
      this._hint360 = el;
    }
    if (el.parentElement !== this.container) this.container.appendChild(el);
    el.classList.remove('hide');
    // restart the entrance animation each time the 360 view opens
    el.style.animation = 'none'; el.offsetHeight; el.style.animation = '';
    clearTimeout(this._hint360Timer);
    this._hint360Timer = setTimeout(() => { if (this._hint360) this._hint360.classList.add('hide'); }, 4200);
    this.renderer.domElement.addEventListener('pointerdown', () => { if (this._hint360) this._hint360.classList.add('hide'); }, { once: true });
  }
  _hide360Hint() { clearTimeout(this._hint360Timer); if (this._hint360) this._hint360.classList.add('hide'); }

  // ============================================================
  // LIGHT STUDIO — the whole art direction for the light backdrop in ONE place.
  // Every value below is light-mode-scoped; dark mode never reads any of it and
  // is restored from captured originals, so it stays byte-identical.
  //
  // Live-tunable from the console (same idiom as __frFloorTune / __frCarTweak):
  //   __frLightStudio.floorCentre = '#ffffff';
  //   __frCtl.setView360Env('white');   // re-apply
  //
  // WHY THE FIRST ATTEMPT READ CHEAP (owner: "white wall above a dark muddy
  // gradient with a hard line"). Three separate things were painting the floor
  // region dark, none of them the floor's albedo:
  //   1. FOG. scene.fog is black from 18→64 units, and the floor plane is 120
  //      units across, so everything past ~18u faded to BLACK — the dark band —
  //      and the horizon where it met the white backdrop was the hard line.
  //      Fixed by tinting the fog to the wall colour in light mode: the fog then
  //      BECOMES the soft curved wall-to-floor falloff a real cyc has.
  //   2. TONE MAPPING. The floor is a lit MeshStandardMaterial and the renderer
  //      runs ACES at exposure 0.1, so a white albedo still resolved to mud. The
  //      light floor is therefore drawn as EMISSIVE with toneMapped=false: its
  //      pixels are exactly the colours below, immune to exposure and lighting.
  //   3. THE REFLECTOR. The mirror disc sits above the floor and is OPAQUE in its
  //      centre (alpha 1 inside uFadeStart), and its shader tone-maps a second
  //      time — invisible on a black floor, a grey blob on a white one. It is
  //      hidden in light mode by default (see mirror.on).
  // ============================================================
  static LIGHT_STUDIO = {
    // Cyc wall (the equirect backdrop). Barely-off-white up top → brightest at
    // the horizon, so the space reads lit rather than like a flat fill.
    /* 2026-08-06 owner pass: walls toned down a step so the Ghost White car
       keeps its silhouette instead of washing into the cyc. */
    wallTop:     '#e7e7e5',
    wallHorizon: '#f0f0ee',   // ALSO the fog colour and the CSS letterbox colour —
                              // these three must match or a horizon seam appears.
    // Floor. A soft pool of light under the car easing out to the wall value;
    // the gentle darkening further out is the vignette, and fog lifts it back to
    // wallHorizon by the time it reaches the horizon, so there is no visible edge.
    floorCentre: '#fcfcfb',
    floorNear:   '#f8f8f6',
    floorMid:    '#f3f3f1',
    floorFar:    '#ededeb',
    floorRim:    '#e8e8e6',
    // Grounding. The light floor has no dark pool of its own, so the contact
    // shadow is the only thing holding the car down — it carries more weight here.
    shadowBoost: 1.55,
    shadowMax:   0.9,
    // Car exposure multiplier for this mode only (1 = identical to dark mode).
    exposureMul: 0.93,
    // CAR REFLECTION. Off by default. The reflector's output brightness can't be
    // predicted from source — its shader tone-maps a render target that was
    // already tone-mapped — and a mis-tuned OPAQUE disc is precisely the "grey
    // blob under the car" being complained about. Flip on and tune once someone
    // can actually see it. strength has to fight exposure 0.1 so it starts high,
    // not at the dark studio's 0.28; fadeStart 0 keeps the disc partially
    // transparent everywhere (the stock 0.30 makes its centre fully opaque, which
    // is what turns a bad guess into a hard-edged blob). All three restore to the
    // captured originals on exit.
    /* 2026-08-06: shipped ON at 0.5 after a second live tuning round at the
       close hero angle — values 2..26 saturate to a near-identical strong
       mirror there (double-tone-map), which read as a "doubled car". 0.5 is
       the first point that reads as a soft showroom sheen instead. */
    mirror: { on: true, strength: 0.5, tint: 0xffffff, fadeStart: 0.0 },
  };

  // Procedural seamless CYC backdrop (equirect gradient). v=0 is the ceiling,
  // v≈0.5 the horizon (what sits directly behind the car), v=1 straight down.
  //
  // Why nothing here is #ffffff: this texture is tagged sRGB, and three.js skips
  // tone mapping on sRGB backgrounds — whatever hex is here is literally the pixel
  // value on screen. At pure white the backdrop clips and the default Ghost White
  // car (which IS tone-mapped, and lands just under white) loses its silhouette
  // into it. Staying a few percent below clipping is what keeps the car readable.
  _makeWhiteRoomTex() {
    const LS = FlameracerScene.LIGHT_STUDIO;
    const w = 1024, h = 512, cv = document.createElement('canvas'); cv.width = w; cv.height = h;
    const c = cv.getContext('2d');
    const g = c.createLinearGradient(0, 0, 0, h);
    g.addColorStop(0.00, LS.wallTop);
    g.addColorStop(0.38, LS.floorMid);
    g.addColorStop(0.50, LS.wallHorizon);   // brightest band, right behind the car
    g.addColorStop(0.64, LS.floorNear);
    g.addColorStop(1.00, LS.wallTop);
    c.fillStyle = g; c.fillRect(0, 0, w, h);
    const t = new THREE.CanvasTexture(cv);
    t.mapping = THREE.EquirectangularReflectionMapping;
    t.colorSpace = THREE.SRGBColorSpace;
    return t;
  }

  // Radial floor gradient for the light studio. Sized to what's actually ON SCREEN:
  // the plane is 120 units across with 0..1 UVs, so UV radius 0.5 is 60 units and
  // the visible floor around the car is only the first ~10% of that. All the shaping
  // therefore lives in the low stops — spreading it evenly would look like a flat fill.
  _makeLightFloorTex() {
    const LS = FlameracerScene.LIGHT_STUDIO;
    const S = 1024, cv = document.createElement('canvas'); cv.width = cv.height = S;
    const c = cv.getContext('2d');
    const g = c.createRadialGradient(S / 2, S / 2, 0, S / 2, S / 2, S * 0.5);
    g.addColorStop(0.00, LS.floorCentre);   // 0u   — under the car
    g.addColorStop(0.10, LS.floorNear);     // ~6u
    g.addColorStop(0.20, LS.floorMid);      // ~12u — matches the wall
    g.addColorStop(0.34, LS.floorFar);      // ~20u — vignette; fog takes over here
    g.addColorStop(1.00, LS.floorRim);      // 60u  — past the horizon, never seen bare
    c.fillStyle = g; c.fillRect(0, 0, S, S);
    const t = new THREE.CanvasTexture(cv);
    t.colorSpace = THREE.SRGBColorSpace;
    return t;
  }

  // Load an equirectangular .hdr as a BACKGROUND-only texture (no PMREM env map).
  // Used for the Desk backdrop: we keep the soft studio env for reflections so the
  // car never goes over-glossy from the room's harsh light sources.
  _loadEquirectBg(url) {
    return new Promise((resolve, reject) => {
      new RGBELoader().load(url, (tex) => { tex.mapping = THREE.EquirectangularReflectionMapping; resolve(tex); }, undefined, reject);
    });
  }

  // Recolour the GROUND per backdrop. In light mode this is the whole cyc illusion,
  // so it does four things — shadow, fog, reflector, floor material — and every one
  // of them captures its original first and restores it exactly on the way out.
  _setFloorMode(mode) {
    const LS = FlameracerScene.LIGHT_STUDIO;
    const light = (mode === 'white');

    // 1. CONTACT SHADOW. The dark studio grounds the car twice: the shadow AND the
    // dark pool the floor texture paints under it. The light studio has no pool, so
    // the ShadowMaterial plane is the only thing holding the car down — at studio
    // opacity it floats. This is also what keeps the car readable against the cyc.
    if (this.shadowFloor && this.shadowFloor.material) {
      if (this._shadowOrigOpacity == null) this._shadowOrigOpacity = this.shadowFloor.material.opacity;
      this.shadowFloor.material.opacity = light
        ? Math.min(LS.shadowMax, this._shadowOrigOpacity * LS.shadowBoost)
        : this._shadowOrigOpacity;
    }

    // 2. FOG — the single biggest cause of the "dark muddy gradient". It's black
    // from 18→64 units and the floor plane is 120 units across, so in light mode
    // everything past ~18u was fading to black and meeting the white backdrop at a
    // hard horizon line. Tinted to the wall colour it does the opposite job: it
    // becomes the soft curved floor-to-wall falloff, and the horizon dissolves.
    if (this.scene.fog) {
      if (!this._fogOrigColor) this._fogOrigColor = this.scene.fog.color.clone();
      if (light) this.scene.fog.color.set(LS.wallHorizon);
      else this.scene.fog.color.copy(this._fogOrigColor);
    }

    // 3. REFLECTOR. Opaque in its centre and tone-mapped a second time by its own
    // shader, so on a light floor it reads as a dark disc — the "grey blob". Hidden
    // in light mode unless LIGHT_STUDIO.mirror.on is flipped on and tuned.
    if (this.mirror) {
      const u = this.mirror.material && this.mirror.material.uniforms;
      if (this._mirrorOrig == null) {
        this._mirrorOrig = {
          visible: this.mirror.visible,
          strength: u ? u.uStrength.value : null,
          fadeStart: u ? u.uFadeStart.value : null,
          tint: u ? u.color.value.clone() : null,
        };
      }
      if (light) {
        this.mirror.visible = !!LS.mirror.on;
        if (u && LS.mirror.on) {
          u.uStrength.value = LS.mirror.strength;
          u.uFadeStart.value = LS.mirror.fadeStart;
          u.color.value.setHex(LS.mirror.tint);
        }
      } else {
        this.mirror.visible = this._mirrorOrig.visible;
        if (u && this._mirrorOrig.strength != null) {
          u.uStrength.value = this._mirrorOrig.strength;
          u.uFadeStart.value = this._mirrorOrig.fadeStart;
          u.color.value.copy(this._mirrorOrig.tint);
        }
      }
    }

    if (!this.floor) return;
    const m = this.floor.material;
    // Extra originals the older capture block didn't need. Grabbed lazily here, so
    // this is self-contained no matter which mode is entered first.
    if (!this._floorOrigEx) {
      this._floorOrigEx = {
        emissive: m.emissive ? m.emissive.clone() : null,
        emissiveMap: m.emissiveMap,
        emissiveIntensity: m.emissiveIntensity,
        toneMapped: m.toneMapped,
        metalness: m.metalness,
      };
    }

    if (light) {
      // 4. FLOOR drawn as EMISSIVE with tone mapping OFF. The renderer runs ACES at
      // exposure 0.1, so a lit white albedo resolves to mud no matter what colour it
      // is set to — the previous attempt set it to #ffffff and it still came out
      // grey. Emissive + toneMapped:false means the floor's pixels ARE the texture's
      // colours, so it can be matched to the backdrop exactly. Fog still applies
      // (that's deliberate — it's the falloff), and the contact shadow is a separate
      // mesh on top, so grounding survives.
      if (!this._lightFloorTex) this._lightFloorTex = this._makeLightFloorTex();
      m.map = null;
      m.color.set('#000000');          // kill the lit diffuse term entirely
      if (m.emissive) m.emissive.set('#ffffff');
      m.emissiveMap = this._lightFloorTex;
      m.emissiveIntensity = 1;
      m.toneMapped = false;
      m.metalness = 0;
      m.roughness = 1;
    } else if (mode === 'dark') {
      m.map = null; m.color.set('#070709'); m.roughness = 0.9;
      this._restoreFloorEx(m);
    } else { // studio / desk
      m.map = this._floorOrigMap;
      if (this._floorOrigColor) m.color.copy(this._floorOrigColor);
      if (this._floorOrigRough != null) m.roughness = this._floorOrigRough;
      this._restoreFloorEx(m);
    }
    m.needsUpdate = true;
  }

  // Put the emissive/tone-mapping overrides back exactly as they were found.
  _restoreFloorEx(m) {
    const o = this._floorOrigEx;
    if (!o) return;
    if (m.emissive && o.emissive) m.emissive.copy(o.emissive);
    m.emissiveMap = o.emissiveMap;
    m.emissiveIntensity = o.emissiveIntensity;
    m.toneMapped = o.toneMapped;
    m.metalness = o.metalness;
  }

  // 360 backdrops: studio (default) · desk (realistic room) · white (white room +
  // white floor) · dark (very dark room). Photographic backdrops keep the soft
  // studio env for reflections (so the car isn't glossy); dark also drops exposure.
  setView360Env(mode) {
    this._bgMode = mode || 'studio';
    // Light-mode flag for every overlay riding the viewer (hint, switcher, exit
    // button, Studio toggle, view rail): white backdrop → dark-on-light chrome.
    // NOT gated on fullscreen any more — the white room is reachable whenever the
    // viewer is interactive, so its chrome has to follow it out of 360 too.
    // Stamped on BOTH the scene container and the .fr-viewer wrapper because the
    // view rail is a SIBLING of the container until fullscreen adopts it, so a
    // container-only flag would miss it in the normal build layout.
    const light = this._bgMode === 'white';
    this.container.classList.toggle('fr-360-light', light);
    const viewer = this.container.closest && this.container.closest('.fr-viewer');
    if (viewer) viewer.classList.toggle('fr-360-light', light);
    // Keep both entry points to this state showing the same thing.
    this._set360SwitchActive(this._bgMode);
    if (this._syncStudioToggle) this._syncStudioToggle();
    if (!this._envDefaultsCaptured) {
      this._defaultBg = this.scene.background;
      this._defaultEnv = this.scene.environment;
      this._defaultExposure = this.renderer.toneMappingExposure;
      if (this.floor) { this._floorOrigMap = this.floor.material.map; this._floorOrigColor = this.floor.material.color.clone(); this._floorOrigRough = this.floor.material.roughness; }
      this._envDefaultsCaptured = true;
    }
    const apply = (bg, env, exposure) => {
      if (bg !== undefined) this.scene.background = bg;
      if (env !== undefined) this.scene.environment = env;
      this.renderer.toneMappingExposure = (exposure != null) ? exposure : this._defaultExposure;
      this.requestRender && this.requestRender(1400);
    };
    if (this._bgMode === 'studio') { this._setFloorMode('studio'); apply(this._defaultBg, this._defaultEnv, this._defaultExposure); this._hide360Loading(); return; }
    if (this._bgMode === 'white') {
      if (!this._whiteBgTex) this._whiteBgTex = this._makeWhiteRoomTex();
      this._setFloorMode('white');
      // ENV MAP IS DELIBERATELY UNCHANGED (_defaultEnv): the paint keeps the exact
      // studio reflections it has in the dark room — only what you SEE behind the
      // car changes. Exposure defaults to 1:1 with dark mode for the same reason a
      // pure-white backdrop was rejected: lifting it blows the highlights off the
      // Ghost White car and it loses its edges against the cyc. LIGHT_STUDIO
      // .exposureMul is the one knob if the car needs to be lifted off the wall.
      apply(this._whiteBgTex, this._defaultEnv, this._defaultExposure * (FlameracerScene.LIGHT_STUDIO.exposureMul || 1));
      // Procedural texture — it's ready the instant it's built, so no loading gate
      // (a 280ms "Loading white room…" flash was fine inside 360 but reads as a
      // glitch now that the toggle fires from the normal build view).
      this._hide360Loading();
      return;
    }
    if (this._bgMode === 'dark') {
      if (!this._darkBg) this._darkBg = new THREE.Color('#060608');
      this._setFloorMode('dark');
      // very dark room: near-black bg, faint studio reflections, exposure dropped hard
      apply(this._darkBg, this._defaultEnv, (this._defaultExposure || 1.25) * 0.4);
      this._show360Loading('Loading dark room…');
      setTimeout(() => { if (this._bgMode === 'dark') this._hide360Loading(); }, 220);
      return;
    }
    if (this._bgMode === 'desk') {
      this._setFloorMode('studio');   // keep the grounded studio floor under the car
      if (this._deskBg) { apply(this._deskBg, this._defaultEnv, this._defaultExposure); this._hide360Loading(); return; }
      this._show360Loading('Loading desk setup…');
      this._loadEquirectBg('https://dl.polyhaven.org/file/ph-assets/HDRIs/hdr/2k/lythwood_lounge_2k.hdr')
        .then((tex) => {
          this._deskBg = tex;
          // BACKGROUND ONLY — soft studio env stays for reflections (not over-glossy).
          if (this._bgMode === 'desk') apply(tex, this._defaultEnv, this._defaultExposure);
          this._hide360Loading();
        })
        .catch(() => { this._show360Loading('Desk failed to load'); setTimeout(() => this._hide360Loading(), 1400); });
      return;
    }
  }

  _mount360BgSwitcher() {
    let bar = this._bgSwitcher;
    if (!bar) {
      bar = document.createElement('div');
      bar.className = 'fr-bg-switch';
      const modes = [['studio', 'Studio'], ['white', 'White']];
      bar.innerHTML = '<span class="fr-bg-switch-label">Backdrop</span>' + modes.map(([id, label], i) => `<button data-bg="${id}" class="${i === 0 ? 'active' : ''}">${label}</button>`).join('');
      bar.addEventListener('click', (e) => {
        const b = e.target.closest('[data-bg]'); if (!b) return;
        bar.querySelectorAll('button').forEach(x => x.classList.remove('active'));
        b.classList.add('active');
        this.setView360Env(b.dataset.bg);
      });
      this._bgSwitcher = bar;
    }
    if (bar.parentElement !== this.container) this.container.appendChild(bar);
  }
  // Sync the switcher's active pill to a mode id (entry defaults to White).
  _set360SwitchActive(id) {
    if (!this._bgSwitcher) return;
    this._bgSwitcher.querySelectorAll('[data-bg]').forEach(x => x.classList.toggle('active', x.dataset.bg === id));
  }
  _unmount360BgSwitcher() {
    if (this._bgSwitcher && this._bgSwitcher.parentElement) this._bgSwitcher.parentElement.removeChild(this._bgSwitcher);
    this._hide360Loading();
    // The backdrop is GLOBAL + session-sticky now, not a property of 360 mode, so
    // leaving 360 must NOT reset it — the user keeps whatever they chose. (This
    // used to force 'studio' on exit and re-arm 'white' for the next entry.)
  }

  // ============================================================
  // CAR LIGHTS — lamp lenses only. The "turn-on" glow modes, underglow strips,
  // and exhaust vapor were removed per spec (the lit look wasn't liked). The
  // head/tail/exhaust lenses keep their milky frosted resting look (set in the
  // loader); we just give the lower tail lamps a soft resting red so they don't
  // read dead-dark when the car is parked in the studio.
  // ============================================================
  _buildCarLights() {
    if (this._taillightMat) this._taillightMat.emissiveIntensity = 0.6;
    this.requestRender && this.requestRender(300);
  }


  // ON-DEMAND RENDER LOOP — only draws when something is actually moving or a
  // render was requested. When the car is idle the GPU sleeps, so the page stops
  // cooking the machine (was rendering the heavy scene every frame, forever).
  _animate() {
    requestAnimationFrame(() => this._animate());
    const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    const dt = Math.min(0.05, ((now - (this._lastT || now)) / 1000)); this._lastT = now;
    let needs = now < (this._renderUntil || 0);
    // Car rotation easing — render only while it's still moving.
    const d = this.targetRotY - this.currentRotY;
    if (Math.abs(d) > 0.0004) { this.currentRotY += d * 0.12; this.carGroup.rotation.y = this.currentRotY; needs = true; }
    else if (this.currentRotY !== this.targetRotY) { this.currentRotY = this.targetRotY; this.carGroup.rotation.y = this.currentRotY; needs = true; }
    // (Idle cinematic turntable spin removed 2026-06-29 per user request.)
    // Keep the split plane glued to the car as it turns.
    if (this._bodySplit && this._bodySplit.on) this._updateBodySplitUniforms();
    if (this._carbonSplitOn) this._updateCarbonSplitUniforms();
    if (needs) this.renderer.render(this.scene, this.camera);
  }

  // Ask for rendering for the next `ms` (after any interaction / scene change).
  requestRender(ms = 600) {
    const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    this._renderUntil = Math.max(this._renderUntil || 0, now + ms);
    this.lastInteractTime = now;   // any scene change / camera move counts as activity → defers the idle spin
  }
}

// ============================================================
// MAIN APP
// ============================================================
const root = document.querySelector('.fr-configurator');
if (!root) {
  console.error('[FLAMERACER] No .fr-configurator container found');
}

const $ = sel => root.querySelector(`[data-${sel}]`);
const $$ = sel => root.querySelectorAll(`[data-${sel}]`);

// Generate or restore build ID from URL
function getBuildId() {
  const urlMatch = window.location.pathname.match(/\/build\/([a-z0-9]+)/i);
  if (urlMatch) return urlMatch[1].toUpperCase();
  return Math.random().toString(36).substring(2, 8).toUpperCase();
}

// ---- Shareable build link ----------------------------------------------------
// Encode the WHOLE spec into a URL-safe string so a shared link rebuilds the exact
// configuration with NO backend. Stays valid as new parts are added (it stores ids).
function encodeBuild() {
  try {
    const json = JSON.stringify(captureCurrentBuild());
    return btoa(unescape(encodeURIComponent(json))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  } catch (e) { return ''; }
}
function decodeBuild(s) {
  if (!s) return null;
  try { return JSON.parse(decodeURIComponent(escape(atob(s.replace(/-/g, '+').replace(/_/g, '/'))))); }
  catch (e) { console.warn('[FLR] bad shared build link', e); return null; }
}
function buildShareUrl() {
  const enc = encodeBuild();
  const base = `${window.location.origin}${window.location.pathname}`;
  return enc ? `${base}?b=${enc}` : base;
}

// ── MY GARAGE — browser-local saved builds (no backend). Each entry stores the
// encoded full-build spec + a thumbnail + name + timestamp. Powers both the Garage
// panel (Review step) and the Loadouts quick-swap chips (Base step).
const FRGarage = {
  KEY: 'fr:garage',
  list() { try { return JSON.parse(localStorage.getItem(this.KEY) || '[]'); } catch (e) { return []; } },
  _save(a) { try { localStorage.setItem(this.KEY, JSON.stringify(a.slice(0, 24))); } catch (e) {} },
  add(name, enc, thumb) { const a = this.list(); const id = 'g' + Math.random().toString(36).slice(2, 8); a.unshift({ id, name: name || 'My Build', enc, thumb: thumb || '', ts: Date.now() }); this._save(a); return id; },
  remove(id) { this._save(this.list().filter(b => b.id !== id)); },
  rename(id, name) { const a = this.list(); const b = a.find(x => x.id === id); if (b) { b.name = name; this._save(a); } },
  get(id) { return this.list().find(b => b.id === id); },
};
function frGarageDate(ts) { try { return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); } catch (e) { return ''; } }
function frLoadGarageBuild(id) {
  const g = FRGarage.get(id); if (!g) return;
  const bld = decodeBuild(g.enc); if (bld) applyFullBuild(bld);
  render();
}

// Read pre-config from URL params (when arriving from product page)
const _params = new URLSearchParams(window.location.search);
const _prePaint = _params.get('paint');
const _sharedBuild = decodeBuild(_params.get('b')); // a shared "view my build" link
// Pre-made configs were removed from the configurator — every build starts as a
// blank "Custom Build" (no base parts, no base price). We deliberately IGNORE any
// ?config the product page passes, so it never says "Furia" / applies a preset.
const _initialBase  = { id: 'custom', name: 'Custom Build', price: 0, parts: [] };
const _initialPaint = (_prePaint   && window.FR_PAINTS.find(p => p.id === _prePaint))         || window.FR_PAINTS[0];

const state = {
  step: 0,
  buildId: getBuildId(),
  build: {
    base:          _initialBase,
    paint:         _initialPaint,
    wheel:         window.FR_WHEELS[0],
    wheelFinish:   null, // FR_WHEEL_FINISHES entry (set when a non-stock wheel is picked)
    tirePrint:     null, // FR_TIRE_PRINTS entry (sidewall lettering; null = blackwall)
    tirePrintColor: null, // single color override for the Signature print (null = its default multi-color set)
    tirePinstripe: null, // FR_TIRE_PINSTRIPE_COLORS entry (coloured sidewall ring; null = none)
    tireFinish:    'matte', // tyre surface finish: 'matte' (default) | 'gloss'
    clearRoof:     false, // Clear Translucent Lid — roof becomes plexiglass (+$29)
    clearCap:      false, // Clear Translucent Cap — made outside the configurator, no 3D change (+$19 PROVISIONAL)
    caliper:       window.FR_WHEEL_FINISHES.find(f => f.id === 'wf-gloss-black') || window.FR_CALIPERS[0], // shares the wheel finish set
    diskColor:     window.FR_WHEEL_FINISHES.find(f => f.id === 'wf-silver') || window.FR_DISK_COLORS[0],     // brake disk/rotor finish (default silver)
    centerCap:     null,
    customWheel:   { finish: null, caliper: null, disk: null, cap: null }, // colour-wheel custom hexes ($10 shared)
    aggressiveStance: false, // free "aggressive stance" selector — pushes the wheels out a touch
    parts:         new Map(),
    decals:        [], // placed pack designs: { packId, packName, page, file, label, panel, mirror, mirrored }
    permits:       [], // placed CUSTOM racing permits: { id, custom, position, design, finish, price }
    banners:       [], // placed custom sun banners: { id, position, design, finish, mirror, price }
    wingDecal:     { on: false, style: 'cyber', text: 'FLAMERACER', color: null, finish: null }, // authored rear-wing decal: { on, style, text, color, finish }
    authored: { // FR Signature stickers (Sticker Lab placements, purposes assigned by owner)
      doorBadge: { on: false, number: '07', name: 'FLAMERACER', sub: 'RACING DIVISION', style: 'classic', accent: null, finish: null }, // mirrored pair on the doors
      roofPanel: { on: false, text: 'FLAMERACER', number: '07', pattern: 'triangles', color: '#16f5d0', finish: null },
      deckPanel: { on: false, text: 'NO STEP',    number: '13', pattern: 'hazard',    color: '#f5d000', finish: null },
    },    decalFinish:   'brushed-aluminum', // GLOBAL finish id for all decals + permits
    decalPack:     'octane', // last-viewed pack tab in the decals step
    licensePlate:  null, // { ...plate, finish }
    plateMount:    null, // FR_PLATE_MOUNTS entry (only set when plate selected)
    bracketColor:  null, // FR_PLATE_BRACKET_COLORS id (null/carbon = default)
    custom:        { on: false, color: null, finish: 'gloss', pearl: false }, // Atelier custom 1-of-1 body colour (+$100; pearl +$99)
    split:         { on: false, colorA: null, colorB: '#0a0a0c', parts: [], pos: 0, tilt: 14, blend: 0.12, flip: false, pearlA: false, pearlB: false, grazeA: null, grazeB: null }, // body two-tone split: TWO chosen colours ($99); pearlA/B + grazeA/B = optional pearlescent finish per side; parts = body-part ids painted into the split (toggled in the Body step)
    carbon:        { on: false, tint: null, shift: false, split: false, mirror: true, matte: false }, // Atelier carbon-fibre weave finish (+$199): tint null = raw carbon; mirror = book-matched centreline weave; matte = no-gloss weave
    trimColor:     null, // whole-trim colour hex (null = factory gloss black)
    trimAreas:     {},   // per-area trim/glass colours { windshield, doors, mirrors, spoiler, diffuser, bumper, roof }
    trimSplit:     [],   // trim areas folded into the body two-tone split (follow the paint shift instead of a solid trim colour)
    wheelSplit:    [],   // wheel components (rim/caliper/disk/cap) folded into the body two-tone split / carbon (follow the paint shift instead of their own finish)
  },
};

const sceneContainer = $('fr-scene');
// ── WebGL availability guard ───────────────────────────────────────────────
// `new FlameracerScene()` builds a THREE.WebGLRenderer, which THROWS on devices
// without WebGL (or with it blacklisted) — which would kill the configurator with
// a blank stage and a stuck loader, and the shopper couldn't build OR buy. Detect
// first and show a graceful, on-brand fallback with a retry instead.
(function frRequireWebGL() {
  let ok = false;
  try { const c = document.createElement('canvas'); ok = !!(window.WebGLRenderingContext && (c.getContext('webgl') || c.getContext('experimental-webgl'))); } catch (e) { ok = false; }
  if (ok) return;
  const loader = root && root.querySelector('[data-fr-loading]'); if (loader) loader.classList.add('hidden');
  if (sceneContainer) sceneContainer.innerHTML =
    '<div class="fr-stage-fallback" role="alert"><div class="fr-stage-fallback-card">' +
    '<div class="fr-stage-fallback-title">3D builder unavailable</div>' +
    '<p>Your browser or device can’t run the live 3D builder — WebGL is turned off or unsupported. ' +
    'Try the latest Chrome, Safari or Edge, or open this page on a newer device.</p>' +
    '<button type="button" class="fr-stage-fallback-btn" onclick="location.reload()">Retry</button>' +
    '</div></div>';
  throw new Error('[FLAMERACER] WebGL unavailable — showed fallback, halted init.');
})();
const scene = new FlameracerScene(sceneContainer);
window.__frApp = scene; // debug/tooling handle (thumbnail baking, console tuning)
scene.registerSlots(window.FR_BODY_PARTS);
scene.setPaint(state.build.paint);
scene.setWheel(state.build.wheel);
scene.setCaliperFinish(state.build.caliper);
scene.setDiskFinish(state.build.diskColor);

// Expose the live body material so envMapIntensity / clearcoat etc. can be
// tuned from the console (e.g. __frBodyMaterial.envMapIntensity = 1.5).
window.__frBodyMaterial = scene.bodyMaterial;

// Expose the live controller instance (for tuning / forcing renders during
// visual review, e.g. __frCtl.requestRender(60000) to keep drawing).
window.__frCtl = scene;
// Hub-bolt live tuner — __frBoltTune({ axial: 0.0, scale: 1.0 }) nudges depth+size.
window.__frBoltTune = (o) => scene.__boltTune(o || {});

// ── DECAL SPIKE (temporary) ──────────────────────────────────────────────
// Validates the native→world transform + conforming DecalGeometry projection.
// Places the Octane under-hood slot (page 07) on Body_1 with a bright debug
// material. Call window.__frDecalSpike() from the console on the dev preview.
window.__frDecalSpike = function (slot) {
  const s = slot || { panel: 'Body_1', point: [0.0001, 0.03, -0.0191], normal: [0.002, 1, 0.027], size: 0.054, rotDeg: 180 };
  const mat = new THREE.MeshStandardMaterial({ color: 0xff1493, emissive: 0xff1493, emissiveIntensity: 0.6, metalness: 0.2, roughness: 0.4, transparent: true, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -6, polygonOffsetUnits: -6 });
  const m = scene._buildConformingDecal(s, mat, 1);
  scene.requestRender(8000);
  return m ? 'spike decal placed' : 'spike FAILED (no target)';
};

// ============================================================
// RENDER FUNCTIONS — same patterns as wireframe, but reading
// from window.FR_* and writing to data-fr-* hooks
// ============================================================
// Per-category cost breakdown (ideation pick #1) — one source of truth that powers
// BOTH the total and the Review-step cost pills. Buckets every charge into
// Foundation / Wheels / Body / Decals / Plate so cost transparency is exact.
function calculateTotalBreakdown() {
  const b = state.build;
  const _finAdd = id => (window.FR_DECAL_FINISHES.find(f => f.id === id) || {}).priceAdd || 0;
  // Foundation — base spec + paint. A SOLID custom colour or a full carbon wrap
  // COVERS the whole body, so the selected base/limited paint is invisible — don't
  // stack its premium on top of the $99 custom / carbon (charge only the base-paint
  // floor). A TWO-TONE SPLIT keeps the base paint as its front tone, so it stays
  // fully priced. (2026-06-30, user: "the paint is stacking".)
  const _carbonOn = !!(b.carbon && b.carbon.on);
  const _customSolid = !!(b.custom && b.custom.on) && !(b.split && b.split.on);
  const _paintCovered = _customSolid || _carbonOn;
  const _stdPaints = (window.FR_PAINTS || []).filter(p => p.tier === 'standard');
  const _paintFloor = _stdPaints.length ? Math.min(..._stdPaints.map(p => p.price || 0)) : 129;
  let foundation = (b.base.price || 0) + (_paintCovered ? _paintFloor : (b.paint.price || 0));
  // Wheels — wheel + finish + tyres + the flat custom-colour unlock. (Caps are free.)
  let wheels = (b.wheel.price || 0);
  if (b.wheelFinish) wheels += b.wheelFinish.priceAdd || 0;
  if (b.tirePrint) wheels += b.tirePrint.priceAdd || 0;
  if (b.tirePrintColor) wheels += b.tirePrintColor.priceAdd || 0;
  const cw = b.customWheel || {};
  if (cw.finish || cw.caliper || cw.disk || cw.cap) wheels += 10;
  // Body — aero parts (+ a flat rare-finish premium per part wearing a rare-tier
  // effect; standard finishes are free) + the clear translucent lid.
  const _rareFin = (window.FR_RARE_FINISH_PRICE != null ? window.FR_RARE_FINISH_PRICE : 12);
  let body = 0; b.parts.forEach(p => body += (p.part.price || 0) + (p.finish?.tier === 'rare' ? _rareFin : 0));
  if (b.clearRoof) body += 29;
  if (b.clearCap)  body += 19; // Translucent Cap — physical add-on, price PROVISIONAL
  // Atelier — ONE charge for the custom paint job (custom colour and/or split, pearl included).
  // (_carbonOn + the paint-coverage logic are defined above with the foundation.)
  // Custom paint = $99 for a solid custom colour and/or a two-tone split — BUT when carbon
  // is on the two-tone split is part of the carbon job (no separate $99).
  if ((b.custom && b.custom.on) || (b.split && b.split.on && !_carbonOn)) body += (window.FR_CUSTOM_PAINT_PRICE || 99);
  // Carbon-fibre weave — one flat extreme-labour charge (covers raw/tinted + its two-tone).
  if (_carbonOn) body += (window.FR_CARBON_WEAVE_PRICE || 199);
  // Custom trim — STANDALONE flat $100; but only $25 per painted area if the custom
  // paint job is already selected (the car's already torn down + in the booth).
  const _trimUnits = (b.trimColor ? 1 : 0) + (b.trimAreas ? Object.values(b.trimAreas).filter(Boolean).length : 0);
  const _trimWithPaint = (b.custom && b.custom.on) || (b.split && b.split.on);
  if (_trimUnits > 0) body += _trimWithPaint ? _trimUnits * (window.FR_TRIM_AREA_PRICE || 25) : (window.FR_TRIM_STANDALONE_PRICE || 100);

  // Decals — permits + banners + each sticker's own finish premium, plus a flat
  // per-PACK base charge ($8) for each distinct designer pack the customer pulls
  // designs from (finish premiums stack on top).
  let decals = 0;
  b.permits.forEach(p => decals += (p.price || 0) + _finAdd(p.finish || b.decalFinish));
  (b.banners || []).forEach(x => decals += (x.price || 0) + _finAdd(x.finish || b.decalFinish));
  b.decals.forEach(d => decals += _finAdd(d.finish || b.decalFinish));
  // Rear wing decal — flat charge (+ its own finish premium) when enabled.
  if (b.wingDecal && b.wingDecal.on) decals += (window.FR_WING_DECAL_PRICE || 6.99) + _finAdd(b.wingDecal.finish || b.decalFinish);
  // FR Signature stickers: door badge (mirrored pair = one charge) + roof/deck panels.
  const _au = b.authored || {};
  if (_au.doorBadge && _au.doorBadge.on) decals += (window.FR_DOOR_BADGE_PRICE || 11.99) + _finAdd(_au.doorBadge.finish || b.decalFinish);
  if (_au.roofPanel && _au.roofPanel.on) decals += (window.FR_PANEL_STICKER_PRICE || 8.99) + _finAdd(_au.roofPanel.finish || b.decalFinish);
  if (_au.deckPanel && _au.deckPanel.on) decals += (window.FR_PANEL_STICKER_PRICE || 8.99) + _finAdd(_au.deckPanel.finish || b.decalFinish);  const _packPrice = (window.FR_DECAL_PACK_PRICE != null ? window.FR_DECAL_PACK_PRICE : 8);
  const _usedPacks = new Set((b.decals || []).map(d => d.packId).filter(Boolean));
  decals += _usedPacks.size * _packPrice;
  // Plate — design + finish + mount adjustment.
  let plate = 0;
  if (b.licensePlate) plate += (b.licensePlate.price || 0) + (b.licensePlate.finish?.priceAdd || 0) + (b.plateMount?.priceAdjust || 0);
  const total = foundation + wheels + body + decals + plate;
  return { foundation, wheels, body, decals, plate, total };
}
function calculateTotal() { return calculateTotalBreakdown().total; }

// Readable accent colour derived from a paint — uses the flip/shift colour for
// chameleon paints, and lifts very dark paints so the text stays visible on the
// dark UI. Used for the viewer sub-label + the --fr-paint-accent CSS var.
function paintAccentColor(p) {
  let hex = (p.flip ? p.flip[1] : p.hex) || '#f5d000';
  const n = parseInt(hex.replace('#', ''), 16);
  const lum = 0.2126 * (n >> 16) + 0.7152 * (n >> 8 & 0xFF) + 0.0722 * (n & 0xFF);
  if (lum < 95) hex = shade(hex, 48); // lighten dark paints (black, midnight) for legibility
  return hex;
}

function shade(hex, percent) {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt, G = (num >> 8 & 0xFF) + amt, B = (num & 0xFF) + amt;
  return '#' + (0x1000000 + (R<255?R<1?0:R:255)*0x10000 + (G<255?G<1?0:G:255)*0x100 + (B<255?B<1?0:B:255)).toString(16).slice(1);
}

let _lastStep = -1;
// Lazily fill part/config thumbnails: render one ONLY when its card scrolls into
// view, and defer the actual GPU render to idle so the page never blocks. This is
// what keeps the heavy v2 model from freezing the panel — nothing renders 35 parts
// (or the whole car ×5 for configs) up front; they fill in as you browse.
let _thumbIO = null;
function scheduleThumbnails() {
  if (typeof scene === 'undefined' || !scene._combinedLoaded) return;
  const panel = $('fr-panel-content'); if (!panel) return;
  if (!_thumbIO) {
    _thumbIO = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (!e.isIntersecting) return;
        const img = e.target; _thumbIO.unobserve(img);
        const idle = window.requestIdleCallback || ((f) => setTimeout(f, 80));
        idle(() => {
          // PRE-BAKED THUMBNAILS (2026-07-30): thumbs are static .webp assets baked
          // from the 3D generator (fr-thumb-<id> / fr-thumb-wheel-<id> /
          // fr-thumb-config-<id>). Try the asset first — no GPU work, no VRAM —
          // and only fall back to the live 3D render for parts not yet baked.
          const renderLive = () => {
            let url = null;
            try {
              if (img.dataset.thumbPart) url = scene.makePartThumbnail(img.dataset.thumbPart);
              else if (img.dataset.thumbConfig) url = scene.makeConfigThumbnail((window.FR_BASE_CONFIGS || []).find(c => c.id === img.dataset.thumbConfig));
              else if (img.dataset.thumbWheel) url = scene.makeWheelThumbnail(img.dataset.thumbWheel) || scene.wheelThumbURL((window.FR_WHEELS || []).find(w => w.id === img.dataset.thumbWheel));
            } catch (e2) {}
            if (img.parentElement) img.parentElement.classList.remove('fr-thumb-pending');
            if (url) { img.src = url; img.style.opacity = '1'; }
            else img.style.display = 'none';
          };
          const baked = img.dataset.thumbPart ? img.dataset.thumbPart
            : img.dataset.thumbConfig ? 'config-' + img.dataset.thumbConfig
            : img.dataset.thumbWheel ? 'wheel-' + img.dataset.thumbWheel : null;
          if (baked && window.FR_ASSET_BASE) {
            img.onerror = () => { img.onerror = null; renderLive(); };
            img.onload = () => { if (img.parentElement) img.parentElement.classList.remove('fr-thumb-pending'); img.style.opacity = '1'; };
            img.src = window.FR_ASSET_BASE + 'fr-thumb-' + baked + '.webp';
          } else renderLive();
        }, { timeout: 1000 });
      });
    }, { root: panel, rootMargin: '160px' });
  }
  panel.querySelectorAll('img[data-thumb-part]:not([src]), img[data-thumb-config]:not([src]), img[data-thumb-wheel]:not([src])').forEach(el => _thumbIO.observe(el));
}

// Organised rainbow selector — 14 evenly-spaced vivid hues (2 rows of 7).
const FR_RAINBOW_PALETTE = ['#ff2d2d', '#ff7a1a', '#ffb800', '#ffe600', '#b6ff1a', '#46e84a', '#1adf8e', '#16d9d9', '#1aa6ff', '#2d6bff', '#6a3dff', '#b23dff', '#ff3df0', '#ff2d9e'];

// VIEW ANGLE PRESETS — cinematic hypercar framing (defined BEFORE render() so
// the step-change dispatch can call setView() on the very first render without a
// temporal-dead-zone error). Hero = close-up low 3/4; side/rear sit low on Y.
// Camera presets — captured by the user in the Camera Tuning Station
// (?camtune=1), framed against the real car at its home position. {pos,look,rotY}.
const viewPresets = {
  hero:  { pos: [3.199,  0.485,  2.615], look: [0.265,  -0.265, -0.167], rotY:  0.0 },
  front: { pos: [4.795,  0.587, -0.04 ], look: [0.267,  -0.265,  0.073], rotY:  0.0 },
  side:  { pos: [0.352, -0.043,  4.392], look: [-0.052, -0.105,  0.104], rotY: -0.032 },
  rear:  { pos: [-5.893, 0.521, -0.189], look: [-0.052, -0.105, -0.136], rotY: -0.032 },
  top:   { pos: [5.194,  4.282,  3.274], look: [-0.052, -0.105, -0.136], rotY: -0.032 },
};

function render() {
  const step = window.FR_STEPS[state.step];

  // Header
  $('fr-step-counter').innerHTML = `<strong>${step.number}</strong> <span style="opacity:0.5">/ ${String(window.FR_STEPS.length).padStart(2, '0')}</span> <span class="fr-step-title">${step.title}</span>`;
  $('fr-build-id').textContent = `BUILD ID — ${state.buildId}`;

  // Viewer overlay
  $('fr-viewer-label').textContent = state.build.base.name.toUpperCase();
  const sub = $('fr-viewer-sublabel');
  sub.textContent = state.build.paint.name.toUpperCase();
  const paintAccent = paintAccentColor(state.build.paint);
  sub.style.color = paintAccent;
  // Expose as a CSS var so on-top accents can follow the selected paint colour.
  document.documentElement.style.setProperty('--fr-paint-accent', paintAccent);

  // Top stats bar — live spec readout
  const totalNow = calculateTotal();
  const editionNum = String(parseInt(state.buildId.slice(-4), 36) % 9999 + 1).padStart(4, '0');
  const baseCode = state.build.base.id.slice(0, 3).toUpperCase();
  const paintCode = state.build.paint.id.split('-').map(s => s[0]).join('').toUpperCase();
  const partsCount = state.build.parts.size;
  // Config code + ship estimate scale DYNAMICALLY with parts added (and stay valid
  // as ~25 more parts get added later). Hand-assembly ≈ 1 day per part, floored at a
  // 7-day minimum; limited paint adds a week.
  const configCode = `${baseCode}-${paintCode}-${String(partsCount).padStart(2, '0')}`;
  const limitedAdd = state.build.paint.tier === 'limited' ? 7 : 0;
  const shipLo = Math.max(7, 7 + partsCount) + limitedAdd;
  const shipDays = `${shipLo}-${shipLo + 7} DAYS`;
  const editionLabel = state.build.paint.tier === 'limited' ? `LIMITED BUILD #${editionNum}` : `BUILD #${editionNum}`;

  const setStat = (key, val) => {
    const el = document.querySelector(`[data-fr-stat-${key}]`);
    if (el) el.textContent = val;
  };
  setStat('price', `$${totalNow.toFixed(0)}`);
  setStat('edition', editionLabel);
  setStat('config', configCode);
  setStat('ship', shipDays);
  // Price delta ticker (ideation quick-win) — flash the price + float a +$X / −$X
  // badge whenever the total changes, so cost reads as live feedback, not a surprise.
  (function () {
    const prev = state._lastTotal; state._lastTotal = totalNow;
    if (prev == null || Math.abs(totalNow - prev) < 0.005) return;
    const d = totalNow - prev, priceEl = document.querySelector('[data-fr-stat-price]');
    if (!priceEl) return;
    const item = priceEl.closest('.fr-stat-item') || priceEl.parentElement;
    if (item && getComputedStyle(item).position === 'static') item.style.position = 'relative';
    priceEl.classList.remove('fr-price-flash'); void priceEl.offsetWidth; priceEl.classList.add('fr-price-flash');
    const badge = document.createElement('span');
    badge.className = 'fr-price-pop ' + (d > 0 ? 'up' : 'down');
    badge.textContent = (d > 0 ? '+$' : '−$') + Math.abs(d).toFixed(0);
    (item || priceEl).appendChild(badge);
    setTimeout(() => { try { badge.remove(); } catch (e) {} }, 1500);
  })();

  // Step dots
  $('fr-step-dots').innerHTML = window.FR_STEPS.map((s, i) =>
    `<div class="fr-step-dot ${i === state.step ? 'active' : ''} ${i < state.step ? 'done' : ''}"></div>`
  ).join('');

  // Tabs
  $('fr-panel-tabs').innerHTML = window.FR_STEPS.map((s, i) =>
    `<button class="fr-panel-tab ${i === state.step ? 'active' : ''} ${i < state.step ? 'done' : ''}" data-step-jump="${i}">
      <span class="fr-tab-num">${s.number}</span>
      <span class="fr-tab-label">${s.label}</span>
    </button>`
  ).join('');
  $$('step-jump').forEach(t => t.addEventListener('click', () => { state.step = parseInt(t.dataset.stepJump); render(); }));

  // Panel content. Preserve the scroll position across re-renders (replacing
  // innerHTML resets scrollTop → the panel jumped on every swatch click). Only
  // reset to top when the STEP actually changes.
  const panel = $('fr-panel-content');
  const _prevScroll = panel ? panel.scrollTop : 0;
  const _sameStep = panel && panel.dataset.step === step.id;
  if (step.id === 'base')     panel.innerHTML = renderBaseStep();
  if (step.id === 'wheels')   panel.innerHTML = renderWheelsStep();
  if (step.id === 'body')     panel.innerHTML = renderBodyStep();
  if (step.id === 'decals')   panel.innerHTML = renderDecalsStep();
  if (step.id === 'plates')   panel.innerHTML = renderPlatesStep();
  if (step.id === 'finish')   panel.innerHTML = renderFinishStep();
  if (panel) {
    panel.dataset.step = step.id;
    panel.scrollTop = _sameStep ? _prevScroll : 0;
    // Step change → one quiet fade/rise on the incoming panel (CSS keyframe,
    // ~200ms, no fill so the resting state is untouched). Re-renders within a
    // step stay perfectly still.
    if (!_sameStep) { panel.classList.remove('fr-step-in'); void panel.offsetWidth; panel.classList.add('fr-step-in'); }
  }

  // Footer
  $('fr-total-price').textContent = `$${calculateTotal().toFixed(2)}`;
  $('fr-build-summary').textContent = `${state.build.parts.size} part${state.build.parts.size !== 1 ? 's' : ''} · ${state.build.decals.length + state.build.permits.length} decal${(state.build.decals.length + state.build.permits.length) !== 1 ? 's' : ''}`;
  $('fr-btn-prev').style.visibility = state.step > 0 ? 'visible' : 'hidden';
  $('fr-btn-next').textContent = state.step < window.FR_STEPS.length - 1 ? 'Next →' : 'Add to Cart →';

  attachHandlers();
  scheduleThumbnails();
  if (typeof scene !== 'undefined' && scene.requestRender) scene.requestRender(700); // any UI change → draw a few frames

  // Auto-frame the right view the first time the user lands on a step
  if (_lastStep !== state.step) {
    _lastStep = state.step;
    const _sid = window.FR_STEPS[state.step].id;
    // Leaving a step while a permit fly was active: this block does its own framing
    // for the new step, so just DROP the stale pre-permit capture (don't animate a
    // restore that would fight the step view). Also close any open permit editor.
    if (_sid !== 'decals' && typeof scene !== 'undefined' && scene && scene._prePermitView) {
      scene._prePermitView = null;
      if (state.ui) state.ui.editPermit = null;
    }
    if (_sid === 'wheels')      focusWheelView('wheels');
    else if (_sid === 'body')   focusBodyZone('front');
    else if (_sid === 'plates') focusPlateView('front');
    else if (_sid === 'decals') setView('hero');   // 3/4 so decals read as they land
    else if (_sid === 'base' || _sid === 'finish') setView('hero');
  }
}

// === Render helpers — (same as wireframe, condensed) ===
function renderBaseStep() {
  const iconSvg = {
    stack:  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>',
    road:   '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M3 3l5 18M21 3l-5 18M12 3v3M12 10v3M12 17v3"/></svg>',
    flag:   '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4 21V4M4 4h12l-2 4 2 4H4"/></svg>',
    gauge:  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="9"/><path d="M12 12l4-4"/><circle cx="12" cy="12" r="1.5" fill="currentColor"/></svg>',
    helmet: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4 14a8 8 0 0116 0v3a2 2 0 01-2 2H6a2 2 0 01-2-2v-3z"/><path d="M9 19v2M15 19v2M8 11h8"/></svg>',
  };

  const configRow = c => `
    <button class="fr-config-row ${c.id === state.build.base.id ? 'selected' : ''}" data-base="${c.id}">
      <div class="fr-config-icon">${iconSvg[c.icon] || iconSvg.stack}</div>
      <div class="fr-config-text">
        <div class="fr-config-name">${c.name}</div>
        <div class="fr-config-desc">${c.description}</div>
      </div>
      <div class="fr-config-thumb" style="display:flex;align-items:center;justify-content:center;color:var(--fr-text-tertiary);opacity:0.5;">
        <div style="width:34px;height:34px;">${iconSvg[c.icon] || iconSvg.stack}</div>
      </div>
      <div class="fr-config-price-block">
        <div class="fr-config-price">${c.price === 0 ? 'Included' : '+ $' + c.price}</div>
        <div class="fr-config-cta">
          ${c.id === state.build.base.id
            ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M8 12l3 3 5-6"/></svg>'
            : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><path d="M12 8v8M8 12h8"/></svg>'}
        </div>
      </div>
    </button>
  `;

  // SQUARE colour swatch (PDP-style) — no rendered preview, just the colour as the
  // chip background (gradient for the chameleon/flip paint), a yellow ring + check
  // when active, an LTD corner tag for limited editions. Name/price live in the
  // section header (selected paint) so the grid stays clean and scannable.
  const swatchBg = p => p.gradient ? `linear-gradient(135deg, ${p.gradient[0]} 0%, ${p.gradient[1]} 100%)`
                      : p.flip ? `linear-gradient(135deg, ${p.flip[0]} 0%, ${p.flip[1]} 100%)`
                      : p.hex;
  const paintCard = p => `
    <button class="fr-paint-sw ${p.id === state.build.paint.id && !_solidCustomCover ? 'selected' : ''}" data-paint="${p.id}" title="${p.name} · $${p.price}" style="background:${swatchBg(p)};">
      ${p.tier === 'limited' ? '<span class="fr-paint-sw-tag">LTD</span>' : ''}
      <span class="fr-paint-sw-check"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M5 12.5l4.5 4.5L19 7"/></svg></span>
    </button>
  `;

  const standard = window.FR_PAINTS.filter(p => p.tier === 'standard');
  const limited = window.FR_PAINTS.filter(p => p.tier === 'limited');
  const sel = state.build.paint || {};
  // A SOLID custom Atelier colour (or carbon) covers the whole body → the base/limited
  // paint is overridden, so don't show it as the active selection (and its premium
  // isn't charged — see calculateTotalBreakdown). A two-tone split keeps the base paint.
  const _solidCustomCover = (!!(state.build.custom && state.build.custom.on) || !!(state.build.carbon && state.build.carbon.on)) && !(state.build.split && state.build.split.on);

  const infoOpen = !!(state.ui && state.ui.paintInfo);
  const configs = window.FR_BASE_CONFIGS || [];
  return `
    ${renderLoadoutChips()}
    <button class="fr-cta-secondary" data-surprise style="width:100%;margin-bottom:14px;">Surprise Me — random 1-of-1 spec</button>
    <div class="fr-foundation-head">
      <div>
        <div class="fr-eyebrow">→ Paint · Step 01</div>
        <div class="fr-section-title">Foundation</div>
        <div class="fr-foundation-current">${_solidCustomCover ? (state.build.carbon && state.build.carbon.on ? 'Carbon Weave · +$' + (window.FR_CARBON_WEAVE_PRICE || 149) : 'Custom Colour · +$' + (window.FR_CUSTOM_PAINT_PRICE || 99)) : (sel.name || '') + (sel.price != null ? ' · $' + sel.price : '')}</div>
      </div>
      <button class="fr-paint-info-btn ${infoOpen ? 'open' : ''}" data-paint-info>
        Paint Info
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="12" cy="12" r="9"/><path d="M12 16v-4M12 8h.01"/></svg>
      </button>
    </div>
    ${infoOpen ? `
      <div class="fr-paint-info-panel">
        <p>Every FlameRacer body is hand-sprayed to order and sealed under a ceramic clear coat.</p>
        <p><strong>Standard</strong> paints are our core gloss finishes, ready in 14–21 days. <strong>Limited Edition</strong> paints are small-batch colours — including the chameleon flip finishes — and ship in 21–28 days.</p>
      </div>` : ''}
    <div class="fr-paint-section">
      <div class="fr-paint-section-header">
        <div class="fr-paint-section-title">Standard</div>
        <div class="fr-paint-section-count">${standard.length} colours</div>
      </div>
      <div class="fr-swatch-grid">${standard.map(paintCard).join('')}</div>
    </div>
    <div class="fr-paint-section">
      <div class="fr-paint-section-header">
        <div class="fr-paint-section-title">Limited Edition</div>
        <div class="fr-paint-section-count">${limited.length} colours</div>
      </div>
      <div class="fr-swatch-grid">${limited.map(paintCard).join('')}</div>
    </div>
    ${(() => {
      const cu = state.build.custom || {};
      const sp = state.build.split || {};
      const cb = state.build.carbon || {};
      const cPrice = window.FR_CUSTOM_COLOR_PRICE || 100, pPrice = window.FR_PEARL_PRICE || 99, sPrice = window.FR_CUSTOM_PAINT_PRICE || 99, tPrice = window.FR_TRIM_PRICE || 99, kPrice = window.FR_CARBON_WEAVE_PRICE || 199, tArea = window.FR_TRIM_AREA_PRICE || 25, tStd = window.FR_TRIM_STANDALONE_PRICE || 100;
      const gloss = window.FR_CUSTOM_COLORS || [], satin = window.FR_SATIN_COLORS || [], pearl = window.FR_PEARL_COLORS || [];
      const allCols = gloss.concat(satin, pearl);
      const eq = (a, b) => (a || '').toLowerCase() === (b || '').toLowerCase();
      const hsl2hex = (h, s, l) => { s /= 100; l /= 100; const a = s * Math.min(l, 1 - l), f = n => { const k = (n + h / 30) % 12; return l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1)); }, to = x => Math.round(255 * x).toString(16).padStart(2, '0'); return '#' + to(f(0)) + to(f(8)) + to(f(4)); };
      const rainbow = []; for (let h = 0; h < 360; h += 15) rainbow.push({ id: 'rb' + h, name: 'Spectrum ' + h + '°', hex: hsl2hex(h, 82, 52) });
      const sw = (c, attr, on) => `<button class="fr-paint-sw ${on ? 'selected' : ''}" ${attr}="${c.hex}" data-fin="${c.satin ? 'satin' : c.pearl ? 'pearl' : (c.metallic || c.chrome) ? 'metallic' : 'gloss'}"${(c.metallic || c.chrome) ? ` data-metalness="${c.metalness != null ? c.metalness : (c.chrome ? 1.0 : 0.9)}" data-roughness="${c.roughness != null ? c.roughness : (c.chrome ? 0.06 : 0.2)}"` : ''}${c.flip ? ` data-flip="${c.flip.join(',')}"` : ''} title="${c.name}" style="background:${c.flip ? `linear-gradient(135deg,${c.flip[0]},${c.flip[1]})` : c.hex};"><span class="fr-paint-sw-check"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M5 12.5l4.5 4.5L19 7"/></svg></span></button>`;
      const info = !!(state.ui && state.ui.splitInfo);
      const colA = sp.colorA || (sel.flip ? sel.flip[0] : sel.hex) || '#ffffff';
      return `
    <div class="fr-paint-section fr-atelier">
      <div class="fr-atelier-head">
        <div class="fr-atelier-title">Atelier <span class="fr-atelier-badge">1-of-1</span></div>
        <button class="fr-paint-info-btn ${info ? 'open' : ''}" data-split-info>About <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="12" cy="12" r="9"/><path d="M12 16v-4M12 8h.01"/></svg></button>
      </div>
      <div class="fr-atelier-sub">Hand-built custom finishes — the car is pulled from its mould, hand-sprayed &amp; wet-sanded. Intense labour, no two alike.</div>
      ${info ? `<div class="fr-paint-info-panel"><p>Atelier work is a true 1-of-1: partial teardown, hand-spray, then wet-sand &amp; polish every panel. Adds ~3–4 weeks. Pearlescent &amp; interference finishes are extra labour again.</p></div>` : ''}

      <button class="fr-atelier-toggle ${(cu.on || sp.on || (state.ui && state.ui.customPaintOpen)) ? 'on' : ''}" data-custompaint-toggle><span class="fr-atelier-dot"></span>${(cu.on || sp.on) ? 'Custom Paint ON · +$' + sPrice : 'Custom Paint · +$' + sPrice}</button>
      ${(state.ui && state.ui.customPaintOpen) ? `
        <div class="fr-atelier-body">
          <div class="fr-split-modes">
            <button class="fr-split-mode ${!sp.on ? 'on' : ''}" data-split-mode="solid">● One colour</button>
            <button class="fr-split-mode ${sp.on ? 'on' : ''}" data-split-mode="split">◫ Two-Tone Split</button>
          </div>
          ${sp.on ? `
            <div class="fr-atelier-duo" style="margin-top:12px;">
              <div><div class="fr-atelier-label">Colour A <span>· front</span></div><div class="fr-swatch-grid">${allCols.map(c => sw(c, 'data-split-a', eq(colA, c.hex))).join('')}</div></div>
              <div><div class="fr-atelier-label">Colour B <span>· rear</span></div><div class="fr-swatch-grid">${allCols.map(c => sw(c, 'data-split-b', eq(sp.colorB, c.hex))).join('')}</div></div>
            </div>
            <div class="fr-atelier-sliders">
              <label>POSITION<input type="range" min="-1" max="1" step="0.02" value="${sp.pos}" data-split-pos></label>
              <label>TILT<input type="range" min="-60" max="60" step="1" value="${sp.tilt}" data-split-tilt></label>
              <label>BLEND<input type="range" min="0" max="1" step="0.02" value="${sp.blend}" data-split-blend></label>
            </div>
            <button class="fr-atelier-mini" data-split-flip>⇄ Flip A / B</button>
            <div class="fr-atelier-hint">Fold individual body parts into the split in the <strong>Body</strong> step — each part is painted by where it sits on the car.</div>
          ` : `
            <div class="fr-atelier-label" style="margin-top:12px;">Custom colour <span>· any 1-of-1 colour · gloss, satin or pearl</span></div>
            <div class="fr-swatch-grid">${rainbow.map(c => sw(c, 'data-custom-color', eq(cu.color, c.hex))).join('')}</div>
            <div class="fr-atelier-label" style="margin-top:8px;">Gloss &amp; metallic</div><div class="fr-swatch-grid">${gloss.map(c => sw(c, 'data-custom-color', eq(cu.color, c.hex))).join('')}</div>
            <div class="fr-atelier-label" style="margin-top:8px;">Satin · really flat</div><div class="fr-swatch-grid">${satin.map(c => sw(c, 'data-custom-color', eq(cu.color, c.hex))).join('')}</div>
            <div class="fr-atelier-label" style="margin-top:8px;">Pearl &amp; interference</div><div class="fr-swatch-grid">${pearl.map(c => sw(c, 'data-custom-color', eq(cu.color, c.hex))).join('')}</div>
            <div class="fr-atelier-label" style="margin-top:8px;">Full-body <span>· any standard colour</span></div><div class="fr-swatch-grid">${(window.FR_PAINTS || []).map(c => sw(c, 'data-custom-color', eq(cu.color, c.hex))).join('')}</div>
          `}
        </div>` : ''}

      <button class="fr-atelier-toggle ${(state.ui && state.ui.trimOpen) ? 'on' : ''}" data-trim-toggle style="margin-top:14px;"><span class="fr-atelier-dot"></span>${(state.build.trimColor || Object.values(state.build.trimAreas || {}).some(Boolean)) ? 'Custom Trim ON' : 'Custom Trim'} · ${(cu.on || sp.on) ? '+$' + tArea + '/area' : '+$' + tStd}</button>
      ${(state.ui && state.ui.trimOpen) ? (() => {
        const TA = [['all', 'All Trim'], ['doors', 'Doors'], ['mirrors', 'Mirrors'], ['spoiler', 'Spoiler'], ['bumper', 'Bumper']];
        const area = (state.ui && state.ui.trimArea) || 'all';
        const cur = area === 'all' ? state.build.trimColor : ((state.build.trimAreas || {})[area] || null);
        const std = (window.FR_PAINTS || []).filter(p => p.tier === 'standard');
        const ltd = (window.FR_PAINTS || []).filter(p => p.tier === 'limited');
        const none = `<button class="fr-paint-sw ${!cur ? 'selected' : ''}" data-trim-color="" title="Factory black" style="background:#0a0a0c;"><span class="fr-paint-sw-check"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M5 12.5l4.5 4.5L19 7"/></svg></span></button>`;
        return `
        <div class="fr-atelier-body">
          <div class="fr-atelier-hint">${(cu.on || sp.on) ? `Custom Paint is on — trim is just <strong>$${tArea}/area</strong>.` : `Trim on its own is a flat <strong>$${tStd}</strong>. Add Custom Paint and it drops to <strong>$${tArea}/area</strong>.`}</div>
          <div class="fr-atelier-label" style="margin-top:10px;">Area to paint</div>
          <div class="fr-atelier-parts">${TA.map(([id, lbl]) => `<button class="fr-atelier-chip ${area === id ? 'on' : ''}" data-trim-area="${id}">${lbl}</button>`).join('')}</div>
          <div class="fr-atelier-label" style="margin-top:12px;">Colour <span>· ${(TA.find(t => t[0] === area) || [, 'whole trim'])[1]}</span></div>
          <div class="fr-swatch-grid">${none}${std.map(p => sw(p, 'data-trim-color', eq(cur, p.hex))).join('')}${ltd.map(p => sw(p, 'data-trim-color', eq(cur, p.hex))).join('')}</div>
          <div class="fr-swatch-grid" style="margin-top:6px;">${rainbow.map(c => sw(c, 'data-trim-color', eq(cur, c.hex))).join('')}</div>
          <div class="fr-swatch-grid" style="margin-top:6px;">${allCols.map(c => sw(c, 'data-trim-color', eq(cur, c.hex))).join('')}</div>
          ${sp.on ? (() => {
            const _ts = state.build.trimSplit || [];
            const _real = (scene.trimAreas ? scene.trimAreas() : []).filter(a => a !== 'windshield');
            const linked = area === 'all' ? (_real.length > 0 && _real.every(a => _ts.includes(a))) : _ts.includes(area);
            return `<div class="fr-atelier-label" style="margin-top:12px;">Paint shift</div>
            <button class="fr-atelier-chip fr-split-mode ${linked ? 'on' : ''}" data-trim-split="${area}" title="Follow the body's two-tone split / carbon instead of a solid trim colour">↔ ${linked ? 'Connected to paint shift' : 'Connect to paint shift'}</button>
            <div class="fr-atelier-hint" style="margin-top:6px;">When on, <strong>${(TA.find(t => t[0] === area) || [, 'this trim'])[1]}</strong> follows the body's two-tone split (and two-tone carbon) instead of a solid colour.</div>`;
          })() : `<div class="fr-atelier-hint" style="margin-top:12px;">Turn on <strong>Two-Tone Split</strong> in Paint to let trim follow the paint shift.</div>`}
        </div>`;
      })() : ''}

      <button class="fr-atelier-toggle ${(cb.on || (state.ui && state.ui.carbonOpen)) ? 'on' : ''}" data-carbon-toggle style="margin-top:14px;"><span class="fr-atelier-dot"></span>${cb.on ? 'Carbon Weave ON · +$' + kPrice : 'Carbon Fibre Weave · +$' + kPrice}</button>
      ${(state.ui && state.ui.carbonOpen) ? `
        <div class="fr-atelier-body">
          <div class="fr-atelier-hint">Real film carbon, hydro-dripped onto the body with a fine intelligent weave. Run it raw, wash any colour over the weave, or make the weave colour-shift. Extreme labour — 1-of-1.</div>
          <button class="fr-atelier-mini ${cb.on ? 'on' : ''}" data-carbon-on style="margin-top:10px;width:100%;">${cb.on ? '✓ Carbon weave applied' : '◫ Apply carbon weave'}</button>
          ${cb.on ? `
            <div class="fr-atelier-label" style="margin-top:12px;">Tint <span>· raw carbon, or wash a candy colour over the weave</span></div>
            <div class="fr-swatch-grid">
              <button class="fr-paint-sw ${!cb.tint ? 'selected' : ''}" data-carbon-tint="" title="Raw carbon" style="background:repeating-linear-gradient(45deg,#15151a 0 4px,#34343c 4px 8px);"><span class="fr-paint-sw-check"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M5 12.5l4.5 4.5L19 7"/></svg></span></button>
              ${[{ name: 'Deep Red', hex: '#8f0e18' }, { name: 'Deep Green', hex: '#064d22' }, { name: 'Deep Purple', hex: '#4d0e82' }, { name: 'Deep Blue', hex: '#0d2f8c' }, { name: 'Deep Teal', hex: '#0c5e63' }].map(c => sw(c, 'data-carbon-tint', eq(cb.tint, c.hex))).join('')}
            </div>
            <div class="fr-atelier-label" style="margin-top:14px;">Paint shift <span>· same two-tone split as the paint</span></div>
            <div class="fr-split-modes">
              <button class="fr-split-mode ${!sp.on ? 'on' : ''}" data-split-mode="solid">● Solid carbon</button>
              <button class="fr-split-mode ${sp.on ? 'on' : ''}" data-split-mode="split">◫ Two-Tone Split</button>
            </div>
            ${sp.on ? `
              <div class="fr-atelier-duo" style="margin-top:10px;">
                <div><div class="fr-atelier-label">Tone A <span>· front</span></div><div class="fr-swatch-grid">${allCols.map(c => sw(c, 'data-split-a', eq(colA, c.hex))).join('')}</div></div>
                <div><div class="fr-atelier-label">Tone B <span>· rear</span></div><div class="fr-swatch-grid">${allCols.map(c => sw(c, 'data-split-b', eq(sp.colorB, c.hex))).join('')}</div></div>
              </div>
              <div class="fr-atelier-sliders">
                <label>POSITION<input type="range" min="-1" max="1" step="0.02" value="${sp.pos}" data-split-pos></label>
                <label>TILT<input type="range" min="-60" max="60" step="1" value="${sp.tilt}" data-split-tilt></label>
                <label>BLEND<input type="range" min="0" max="1" step="0.02" value="${sp.blend}" data-split-blend></label>
              </div>
              <button class="fr-atelier-mini" data-split-flip>⇄ Flip A / B</button>
            ` : ''}
            <div class="fr-atelier-label" style="margin-top:14px;">Weave <span>· normal, or book-matched at the centreline</span></div>
            <div class="fr-split-modes">
              <button class="fr-split-mode ${cb.mirror === false ? 'on' : ''}" data-carbon-weave="normal">▟ Normal weave</button>
              <button class="fr-split-mode ${cb.mirror !== false ? 'on' : ''}" data-carbon-weave="mirror">▶◀ Mirrored</button>
            </div>
            ${cb.mirror !== false ? `<div class="fr-atelier-hint" style="margin-top:8px;">Cut down the centreline &amp; flipped — the tows meet in a chevron front-to-rear, continuing onto the hood.</div>` : ''}
            <div class="fr-atelier-label" style="margin-top:14px;">Finish <span>· glossy lacquer or matte</span></div>
            <div class="fr-split-modes">
              <button class="fr-split-mode ${!cb.matte ? 'on' : ''}" data-carbon-finish="gloss">✦ Glossy</button>
              <button class="fr-split-mode ${cb.matte ? 'on' : ''}" data-carbon-finish="matte">▦ Matte</button>
            </div>
          ` : ''}
        </div>` : ''}
    </div>`;
    })()}
  `;
}

// ── WHEELS STEP · collapsible sections ────────────────────────────────────
// Same fold pattern as the Body zones: view state lives module-level (outlives
// render()), headers toggle IN PLACE via class flip, and the ◎ view affordance
// moves the camera without re-rendering. Default: Wheel Set + Finish open, the
// accent sections folded — their headers carry the current selection so nothing
// chosen ever hides.
let _wheelSecOpen = null;
function initWheelSecOpen() {
  if (_wheelSecOpen) return;
  _wheelSecOpen = { set: true, finish: true, caliper: false, disk: false, caps: false, tire: false };
}
function setWheelSecOpen(id, open) {
  if (!_wheelSecOpen) return;
  _wheelSecOpen[id] = !!open;
  const sec = root.querySelector(`.fr-zone-acc[data-wsec="${id}"]`);
  if (sec) {
    sec.classList.toggle('open', !!open);
    const hdr = sec.querySelector('[data-wsec-toggle]');
    if (hdr) hdr.setAttribute('aria-expanded', String(!!open));
  }
  if (open) scheduleThumbnails();   // wheel tiles hidden in a fold still owe a thumb
}

function renderWheelsStep() {
  initWheelSecOpen();
  const show = state.build.wheel.id !== 'stock';
  const prod = window.FR_WHEELS.filter(w => w.production);
  const test = window.FR_WHEELS.filter(w => w.testPrint);
  // Stock is shown on its own (included), the paid upgrades in an even 2×2 grid
  // below — avoids an orphaned 5th tile alone on a row.
  const stockWheel = prod.find(w => w.id === 'stock');
  const upgrades = prod.filter(w => w.id !== 'stock');

  // Compact wheel TILE/box — rim graphic on top, name + price below (clear
  // hierarchy, no picture, not a text button).
  const wheelCard = w => {
    const hex = '#' + w.color.toString(16).padStart(6, '0');
    const selected = w.id === state.build.wheel.id;
    const isStock = w.id === 'stock';
    return `
      <button class="fr-wheel-tile ${selected ? 'selected' : ''} ${isStock ? 'fr-wheel-tile-stock' : ''}" data-wheel="${w.id}">
        ${w.testPrint ? '<span class="fr-wheel-tile-tag">TEST</span>' : ''}
        ${isStock ? '<span class="fr-wheel-tile-tag" style="background:rgba(63,174,90,0.2);color:#7fe6a0;border-color:rgba(63,174,90,0.5);">INCLUDED</span>' : ''}
        ${selected ? '<span class="fr-wheel-tile-check">✓</span>' : ''}
        <span class="fr-wheel-tile-rim"><img data-thumb-wheel="${w.id}" alt="${w.name} rim" style="opacity:0;transition:opacity .3s;" onerror="this.style.display='none'" /></span>
        <span class="fr-wheel-tile-name">${w.name}${isStock ? ' <span style="opacity:.6;font-weight:400;">(Standard)</span>' : ''}</span>
        <span class="fr-wheel-tile-price">${w.price === 0 ? 'No extra cost' : '+ $' + w.price.toFixed(2)}</span>
      </button>
    `;
  };

  // COMPACT swatch strips (2026-08-05 owner feedback: the 50px tiles ate the
  // panel). Small squares, names as tooltips; the current pick is named in the
  // folded-section header meta, so nothing is lost.
  // `showPrice`: only the RIM finish is actually charged (calculateTotalBreakdown
  // adds b.wheelFinish.priceAdd alone) — caliper/disk/cap picks are free accents,
  // so their tooltips never show a price.
  const swx = (f, attr, selId, showPrice) => `<button class="fr-swx ${selId === f.id ? 'on' : ''}" data-${attr}="${f.id}" title="${f.name}${showPrice && f.priceAdd ? ` · +$${f.priceAdd}` : ''}" style="background:${f.hex};"></button>`;
  const wheelFinishCard = f => swx(f, 'wheel-finish', state.build.wheelFinish && state.build.wheelFinish.id, true);
  const caliperFinishCard = f => swx(f, 'caliper-finish', state.build.caliper && state.build.caliper.id, false);
  const diskFinishCard = f => swx(f, 'disk-finish', state.build.diskColor && state.build.diskColor.id, false);
  const capFinishCard = f => swx(f, 'cap-finish', state.build.centerCap && state.build.centerCap.id, false);

  // "↔ Connect to paint shift" chip for a wheel component (rim/caliper/disk/cap).
  // Shown only when a body Two-Tone Split OR Carbon is active; otherwise a hint. When
  // connected, that component borrows the body material so it follows the paint shift.
  const splitActive = !!((state.build.split && state.build.split.on) || (state.build.carbon && state.build.carbon.on));
  const wheelSplitChip = (which, label) => {
    const linked = (state.build.wheelSplit || []).includes(which);
    if (!splitActive) {
      return `<div class="fr-atelier-hint" style="margin-top:10px;">↔ Turn on a <strong>Two-Tone Split</strong> or <strong>Carbon</strong> in Paint to let the ${label} follow the paint shift.</div>`;
    }
    return `<button class="fr-atelier-chip fr-split-mode ${linked ? 'on' : ''}" data-wheel-split="${which}" title="Follow the body's two-tone split / carbon instead of this part's own finish" style="margin-top:10px;">↔ ${linked ? 'Connected to paint shift' : 'Connect to paint shift'}</button>`;
  };

  // Custom colour = an organised RAINBOW SELECTOR (not a colour wheel). The
  // "Custom" chip opens an even rainbow swatch row for that option. ONE flat +$10
  // unlocks custom on ALL of finish/caliper/disk/caps (see calculateTotal).
  const cw = state.build.customWheel || {};
  const anyCustom = !!(cw.finish || cw.caliper || cw.disk || cw.cap);
  const customDot = (which) => {
    const cur = cw[which] || null;
    const open = state.ui && state.ui.cwOpen === which;
    const face = cur ? cur : 'conic-gradient(from 90deg,#ff2d2d,#ffb800,#46e84a,#16d9d9,#2d6bff,#b23dff,#ff3df0,#ff2d2d)';
    return `<button class="fr-swx ${cur ? 'on' : ''} ${open ? 'open' : ''}" data-cw-open="${which}" title="Custom colour${cur || anyCustom ? '' : ' · +$10'}" style="background:${face};"></button>`;
  };
  // The rainbow swatch row, revealed under a section when its Custom chip is open.
  const customRainbowRow = (which) => {
    if (!(state.ui && state.ui.cwOpen === which)) return '';
    const cur = cw[which];
    return `<div class="fr-swx-row" style="margin-top:8px;padding:8px;background:rgba(255,255,255,0.03);border:1px solid var(--fr-border-subtle);border-radius:2px;">${FR_RAINBOW_PALETTE.map(hex => `
      <button class="fr-swx ${cur && cur.toLowerCase() === hex ? 'on' : ''}" data-cw-pick="${which}:${hex}" title="${hex}" style="background:${hex};"></button>`).join('')}</div>`;
  };

  // Tire print card — a sidewall preview chip (curved wordmark hint) + name.
  // Colors come from print.colors; tier badge marks Single/Dual/Signature.
  const tireSwatch = p => {
    const box = inner => `<span style="display:flex;flex-direction:column;gap:5px;align-items:center;justify-content:center;width:50px;height:50px;border-radius:2px;background:#0a0a0a;border:2px solid rgba(255,255,255,0.18);padding:9px;box-shadow:0 4px 12px rgba(0,0,0,0.4);">${inner}</span>`;
    if (p.kind === 'pinstripe') {
      const line = `<span style="display:block;width:100%;height:4px;border-radius:2px;background:#e8e9ee;"></span>`;
      return box(p.stripes === 2 ? line + line : line);
    }
    if (!p.text) return `<span style="display:block;width:50px;height:50px;border-radius:2px;background:#0a0a0a;border:2px solid rgba(255,255,255,0.18);box-shadow:0 4px 12px rgba(0,0,0,0.4);"></span>`;
    // Wordmark hint — clean dashes, no glow.
    return box(`<span style="display:block;width:72%;height:3px;border-radius:2px;background:#e8e9ee;"></span><span style="display:block;width:92%;height:3px;border-radius:2px;background:#e8e9ee;"></span><span style="display:block;width:56%;height:3px;border-radius:2px;background:#e8e9ee;"></span>`);
  };
  const tirePrintCard = p => {
    const sel = (state.build.tirePrint && p.id === state.build.tirePrint.id) || (!state.build.tirePrint && p.id === 'tp-none');
    const tierLabel = p.tier === 'Signature' ? 'Signature · recolorable' : p.tier;
    return `
      <button class="fr-color-dot ${sel ? 'selected' : ''}" data-tire-print="${p.id}" style="padding:14px 8px;gap:10px;">
        ${tireSwatch(p)}
        <div class="fr-color-dot-name" style="font-size:11px;">${p.name}${p.priceAdd ? ` · +$${p.priceAdd}` : ''}</div>
        ${p.tier && p.tier !== 'Stock' ? `<div style="font-family:var(--fr-mono,monospace);font-size:8px;letter-spacing:.14em;color:var(--fr-accent-yellow,#f5d000);text-transform:uppercase;">${tierLabel}</div>` : ''}
      </button>
    `;
  };

  // Wordmark colour picker for ANY selected tire print. A "Default" chip keeps
  // the print's own colours; the rest set state.build.tirePrintColor (a colour
  // object) so the whole wordmark renders in that solid colour or brushed metal.
  const tireColorCards = () => {
    const cur = state.build.tirePrintColor; // colour object | null
    const curId = cur && cur.id;
    const defaultChip = `<button class="fr-swx ${!cur ? 'on' : ''}" data-tire-color="__default__" title="Default · the print's own colours" style="background:linear-gradient(135deg,#f2f2f2,#8e8e8e);"></button>`;
    const chips = (window.FR_TIRE_TEXT_COLORS || []).map(c => {
      const bg = c.metallic === 'gold' ? 'linear-gradient(135deg,#f7e9a8,#d4af37,#9a7b25)'
               : c.metallic === 'alu'  ? 'linear-gradient(135deg,#f0f2f6,#c2c6cc,#8a8d96)'
               : c.hex;
      return `<button class="fr-swx ${curId === c.id ? 'on' : ''}" data-tire-color="${c.id}" title="${c.name}${c.priceAdd ? ' · +$' + c.priceAdd : ''}" style="background:${bg};"></button>`;
    }).join('');
    return defaultChip + chips;
  };

  // Sidewall pinstripe picker — a "None" chip + coloured ring chips. The swatch
  // shows the ring colour as an actual ring so it previews the result.
  const tirePinstripeCards = () => {
    const cur = state.build.tirePinstripe;
    const none = `
      <button class="fr-color-dot ${!cur ? 'selected' : ''}" data-tire-pinstripe="__none__" style="padding:14px 8px;gap:10px;">
        <span style="display:block;width:50px;height:50px;border-radius:2px;background:#0a0a0a;border:2px solid rgba(255,255,255,0.18);"></span>
        <div class="fr-color-dot-name" style="font-size:11px;">None</div>
      </button>`;
    const chips = (window.FR_TIRE_PINSTRIPE_COLORS || []).map(c => `
      <button class="fr-color-dot ${cur && cur.id === c.id ? 'selected' : ''}" data-tire-pinstripe="${c.id}" style="padding:14px 8px;gap:10px;">
        <span style="display:block;width:50px;height:50px;border-radius:2px;background:#0a0a0a;border:2px solid rgba(255,255,255,0.18);position:relative;box-shadow:0 0 8px ${c.hex};">
          <span style="position:absolute;inset:8px;border-radius:50%;border:3px solid ${c.hex};"></span>
        </span>
        <div class="fr-color-dot-name" style="font-size:11px;">${c.name}</div>
      </button>`).join('');
    return none + chips;
  };

  // Collapsible section shell — clones the Body-zone accordion language so the
  // whole configurator folds the same way. `focus` puts the ◎ view affordance in
  // the header (camera only — stopPropagation keeps it from toggling the fold).
  const wsec = (id, label, meta, focus, bodyHtml) => {
    const open = !!_wheelSecOpen[id];
    return `
    <div class="fr-zone-section fr-zone-acc ${open ? 'open' : ''}" data-wsec="${id}">
      <div class="fr-zone-header" data-wsec-toggle="${id}" role="button" tabindex="0" aria-expanded="${open}" style="cursor:pointer;" title="${open ? 'Collapse' : 'Expand'} this section">
        <div class="fr-zone-label"><span class="fr-zone-acc-chev" aria-hidden="true">›</span>${label}</div>
        <div class="fr-zone-meta">
          <span>${meta}</span>
          ${focus ? `<span class="fr-zone-acc-view" data-wheel-focus="${focus}" role="button" title="View this area">◎ view</span>` : ''}
        </div>
      </div>
      <div class="fr-zone-acc-body">${bodyHtml}</div>
    </div>`;
  };

  const stdFin = window.FR_WHEEL_FINISHES.filter(f => f.tier === 'standard');
  const selFin = window.FR_WHEEL_FINISHES.filter(f => f.tier === 'select');  // FR Select Series — the middle tier
  const rareFin = window.FR_WHEEL_FINISHES.filter(f => f.tier === 'rare');
  const lockText = 'Unlocks with non-stock wheels';
  const lockedCard = msg => `<div class="fr-info-card"><div class="fr-info-card-body">${msg}</div></div>`;
  const grp = (label, extra) => `<div class="fr-finish-group-label" style="margin:14px 0 8px;">${label}${extra ? ` <span style="color:var(--fr-accent-yellow);">${extra}</span>` : ''}</div>`;

  // Current-selection readouts — folded sections still tell you what's on.
  const nm = o => (o && o.name) || null;
  const setMeta = `${prod.length + test.length} wheels · ${state.build.wheel.name}`;
  const finMeta = show ? `${nm(state.build.wheelFinish) || 'Standard'} · Free` : lockText;
  const calMeta = show ? `${nm(state.build.caliper) || 'Stock'} · Free` : lockText;
  const diskMeta = show ? `${nm(state.build.diskColor) || 'Stock'} · Free` : lockText;
  const capMeta = `${nm(state.build.centerCap) || 'None'} · Set of 4 · Free`;
  const tireMeta = `${state.build.tirePrint ? state.build.tirePrint.name : 'None'}`;

  const setBody = `
    ${stockWheel ? `<div class="fr-paint-grid" style="grid-template-columns:1fr;margin-bottom:8px;">${wheelCard(stockWheel)}</div>` : ''}
    <div class="fr-paint-grid" style="grid-template-columns:1fr 1fr;">${upgrades.map(wheelCard).join('')}</div>
    ${test.length ? `${grp(`Test Print Series · Limited · ${test.length}`)}<div class="fr-paint-grid" style="grid-template-columns:1fr 1fr;">${test.map(wheelCard).join('')}</div>` : ''}`;

  const finBody = show
    ? `${grp(`Standard · ${stdFin.length} colours · Free`)}
       <div class="fr-swx-row">${stdFin.map(wheelFinishCard).join('')}${customDot('finish')}</div>
       ${customRainbowRow('finish')}
       ${grp(`FR Select Series · ${selFin.length} colourways`, '+ $10')}
       <div class="fr-swx-row">${selFin.map(wheelFinishCard).join('')}</div>
       ${grp(`Rare Effects · color-shift &amp; translucent · ${rareFin.length}`, '+ cost')}
       <div class="fr-swx-row">${rareFin.map(wheelFinishCard).join('')}</div>
       ${wheelSplitChip('rim', 'rim')}`
    : lockedCard('Wheel finishes unlock once you pick a non-stock wheel above.');

  const calBody = show
    ? `${grp(`Standard · ${stdFin.length} colours · Free`)}
       <div class="fr-swx-row">${stdFin.map(caliperFinishCard).join('')}${customDot('caliper')}</div>
       ${customRainbowRow('caliper')}
       ${grp(`FR Select Series · ${selFin.length} colourways`)}
       <div class="fr-swx-row">${selFin.map(caliperFinishCard).join('')}</div>
       ${grp(`Rare Effects · color-shift &amp; translucent · ${rareFin.length}`)}
       <div class="fr-swx-row">${rareFin.map(caliperFinishCard).join('')}</div>
       ${wheelSplitChip('caliper', 'caliper')}`
    : lockedCard('Caliper colors unlock with any non-stock wheel choice above.');

  const diskBody = show
    ? `${grp(`Standard · ${stdFin.length} colours · Free`)}
       <div class="fr-swx-row">${stdFin.map(diskFinishCard).join('')}${customDot('disk')}</div>
       ${customRainbowRow('disk')}
       ${grp(`FR Select Series · ${selFin.length} colourways`)}
       <div class="fr-swx-row">${selFin.map(diskFinishCard).join('')}</div>
       ${grp(`Rare Effects · color-shift &amp; translucent · ${rareFin.length}`)}
       <div class="fr-swx-row">${rareFin.map(diskFinishCard).join('')}</div>
       ${wheelSplitChip('disk', 'disk')}`
    : lockedCard('The brake disk color unlocks with any non-stock wheel choice above.');

  const capBody = `
    <div class="fr-swx-row">
      <button class="fr-swx fr-swx-none ${!state.build.centerCap ? 'on' : ''}" data-cap-finish="__none__" title="None" style="background:#0a0a0a;"></button>
      ${stdFin.map(capFinishCard).join('')}${customDot('cap')}
    </div>
    ${customRainbowRow('cap')}
    ${grp(`FR Select Series · ${selFin.length} colourways`)}
    <div class="fr-swx-row">${selFin.map(capFinishCard).join('')}</div>
    ${grp(`Rare Effects · color-shift &amp; translucent · ${rareFin.length}`)}
    <div class="fr-swx-row">${rareFin.map(capFinishCard).join('')}</div>
    ${wheelSplitChip('cap', 'centre cap')}`;

  const tireBody = `
    <div class="fr-info-card" style="margin-bottom:12px;">
      <div class="fr-info-card-body">One sidewall treatment per tyre — a wordmark print (tuner / motorsport style) OR a pinstripe ring. Pick one, the camera drops to the tyres, then choose its colour below.</div>
    </div>
    <div class="fr-color-dot-grid">${window.FR_TIRE_PRINTS.map(tirePrintCard).join('')}</div>
    ${state.build.tirePrint ? `
      <div class="fr-zone-section" style="margin-top:12px;background:rgba(255,255,255,0.03);border:1px solid rgba(255,255,255,0.08);border-radius:8px;padding:12px;">
        <div class="fr-zone-label" style="margin-bottom:4px;">${state.build.tirePrint.kind === 'pinstripe' ? 'Stripe Color' : 'Print Color'}</div>
        <div class="fr-zone-meta" style="margin-bottom:10px;">Recolor it${state.build.tirePrintColor ? ` · ${state.build.tirePrintColor.name}` : ' · Default'}</div>
        <div class="fr-swx-row">${tireColorCards()}</div>
      </div>` : ''}`;

  return `
    <div class="fr-section-title">STANCE</div>
    <div class="fr-section-subtitle">Wheels, Finishes & Calipers · Step 02</div>
    <div class="fr-info-card">
      <div class="fr-info-card-label">How it works</div>
      <div class="fr-info-card-body">Tap a wheel and the camera drops to a stance view so you can see it sitting on the car. Each section below folds — its header always shows what you've picked. ◎ view flies the camera to that area.</div>
    </div>
    ${wsec('set', 'Wheel Set', setMeta, 'wheels', setBody)}
    ${wsec('finish', 'Wheel Finish', finMeta, show ? 'wheels' : null, finBody)}
    ${wsec('caliper', 'Caliper Color', calMeta, show ? 'caliper' : null, calBody)}
    ${wsec('disk', 'Disk Color', diskMeta, show ? 'caliper' : null, diskBody)}
    ${show ? wsec('caps', 'Center Caps', capMeta, state.build.centerCap ? 'caps' : null, capBody) : ''}
    ${wsec('tire', 'Tire Sidewall', tireMeta, 'tire', tireBody)}
  `;
}

// ── BODY STEP · zone accordions ───────────────────────────────────────────
// 59 parts across four always-open zones was one long scroll, so each zone now
// collapses. This lives module-level (not on `state`) because it's pure view
// state — but it HAS to outlive render(), which rebuilds the whole panel on
// every part toggle. null = not initialised; the first paint picks the zone.
let _bodyZoneOpen = null;

// Plate brackets belong to the plate step, so they never show in a zone list.
const bodyZoneParts = zoneId => (window.FR_BODY_PARTS || []).filter(p => p.zone === zoneId && !p.plateBracket);

// First paint: open the first zone that already has a part on — a shared or
// restored build lands on its own work — otherwise Front.
function initBodyZoneOpen() {
  if (_bodyZoneOpen) return;
  const zones = window.FR_BODY_ZONES || [];
  const withPart = zones.find(z => bodyZoneParts(z.id).some(p => state.build.parts.has(p.id)));
  const openId = withPart ? withPart.id : 'front';
  _bodyZoneOpen = {};
  zones.forEach(z => { _bodyZoneOpen[z.id] = z.id === openId; });
}

// Open/close a zone IN PLACE (class flip, no render()) — re-rendering here would
// bin the in-flight thumbnails and fight the panel's scroll restore.
function setBodyZoneOpen(zoneId, open) {
  if (!_bodyZoneOpen) return;
  _bodyZoneOpen[zoneId] = !!open;
  const sec = root.querySelector(`.fr-zone-acc[data-zone="${zoneId}"]`);
  if (sec) {
    sec.classList.toggle('open', !!open);
    const hdr = sec.querySelector('[data-zone-toggle]');
    if (hdr) hdr.setAttribute('aria-expanded', String(!!open));
  }
  const chip = root.querySelector(`[data-zone-jump="${zoneId}"]`);
  if (chip) chip.classList.toggle('active', !!open);
  if (open) scheduleThumbnails();   // cards that were hidden still owe a 3D thumb
}

// Scroll the panel to an element. scrollIntoView() would tuck the target under
// the sticky zone bar, so subtract the bar's height.
function scrollPanelToEl(el) {
  const panel = $('fr-panel-content');
  if (!panel || !el) return;
  const bar = panel.querySelector('.fr-zone-jump');
  const off = (bar ? bar.offsetHeight : 0) + 10;
  const top = panel.scrollTop + el.getBoundingClientRect().top - panel.getBoundingClientRect().top - off;
  panel.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
}

function renderBodyStep() {
  initBodyZoneOpen();
  const partCard = part => {
    const active = state.build.parts.get(part.id);
    const enabled = !!active;
    const options = part.hasColor ? window.FR_TOW_HOOK_COLORS : window.FR_FINISHES;
    const currentId = active ? (active.finish?.id || active.color?.id) : null;
    return `
      <div class="fr-part-card ${enabled ? 'enabled' : ''}" data-part-id="${part.id}">
        <button class="fr-part-card-toggle" data-toggle-part="${part.id}">
          <div class="fr-part-thumb fr-thumb-pending" style="aspect-ratio:auto;height:112px;">
            <img data-thumb-part="${part.id}" alt="${part.code}" style="opacity:0;transition:opacity .3s;" onerror="this.style.display='none';" />
            <div class="fr-part-thumb-code">${part.code}</div>
            <div class="fr-part-thumb-toggle ${enabled ? 'on' : ''}"></div>
          </div>
          <div class="fr-part-card-body" style="padding:8px 10px 10px;">
            <div class="fr-part-card-name">${part.name}</div>
            <div class="fr-part-card-desc" title="${part.description}">${part.description}</div>
            <div class="fr-part-card-price">+ $${part.price.toFixed(2)}</div>
          </div>
        </button>
        ${enabled && (!part.removeSpoiler || part.glbNode) ? (() => {
          // removeSpoiler parts with a mesh (the flush deck panel) ARE colorable —
          // the panel is a real painted piece. 2026-07-30 per user.
          // Square finish tiles, grouped by tier: Standard set + Rare Effects
          // (chameleon flips, iridescent ramps, translucent plastics).
          const standard = options.filter(f => f.tier !== 'rare');
          const rare = options.filter(f => f.tier === 'rare');
          const _rarePrice = (window.FR_RARE_FINISH_PRICE != null ? window.FR_RARE_FINISH_PRICE : 12);
          // COMPACT swatch strip (2026-08-05 owner feedback: the old big tiles
          // ate the panel). Small squares, names as tooltips, the CURRENT pick
          // named in the header line so nothing is lost.
          const curFin = options.find(f => f.id === currentId);
          const chip = f => `
            <button class="fr-swx ${currentId === f.id ? 'on' : ''}" data-part-finish="${part.id}" data-finish-id="${f.id}" title="${f.name}${f.tier === 'rare' ? ` · +$${_rarePrice}` : ''}" style="background:${f.gradient ? `linear-gradient(135deg, ${f.gradient.join(',')})` : f.hex};"></button>`;
          return `
          <div class="fr-part-finishes-inline">
            <div class="fr-finish-group">
              <div class="fr-finish-group-label">Colour · ${curFin ? curFin.name : 'Matte Black'}${curFin && curFin.tier === 'rare' ? ` · +$${_rarePrice}` : ''}</div>
              <div class="fr-swx-row">${standard.map(chip).join('')}</div>
            </div>
            ${rare.length ? `
            <div class="fr-finish-group">
              <div class="fr-finish-group-label">Rare Effects · ${rare.length} · +$${_rarePrice}</div>
              <div class="fr-swx-row">${rare.map(chip).join('')}</div>
            </div>` : ''}
          </div>`;
        })() : ''}
        ${enabled && state.build.split && state.build.split.on ? `
          <div style="padding:0 10px 10px;">
            <button class="fr-atelier-chip ${(state.build.split.parts || []).includes(part.id) ? 'on' : ''}" data-split-part="${part.id}" style="width:100%;">${(state.build.split.parts || []).includes(part.id) ? '✓ Painted into the split' : '◫ Match paint split'}</button>
          </div>` : ''}
      </div>
    `;
  };

  // Group parts by zone — collapsible, so 59 parts don't read as one long scroll.
  // Header taps toggle; the ◎ view button (inside the header) only flies the camera.
  // Collapsed zones keep their selected parts visible as a one-line chip strip.
  const zonesHtml = window.FR_BODY_ZONES.map(zone => {
    // Deterministic, STABLE order: price ascending, then name. Deliberately NOT
    // "equipped first" — live-reordering on equip made the tapped card jump away
    // from under the finger (owner complaint 2026-08-05). Equipped state reads
    // from the card highlight, the header count, and the folded picked-chips.
    const partsInZone = bodyZoneParts(zone.id).slice().sort((a, b) => {
      if ((a.price || 0) !== (b.price || 0)) return (a.price || 0) - (b.price || 0);
      return (a.name || '').localeCompare(b.name || '');
    });
    const activeParts = partsInZone.filter(p => state.build.parts.has(p.id));
    const open = !!_bodyZoneOpen[zone.id];
    return `
      <div class="fr-zone-section fr-zone-acc ${open ? 'open' : ''}" data-zone="${zone.id}">
        <div class="fr-zone-header" data-zone-toggle="${zone.id}" role="button" tabindex="0" aria-expanded="${open}" style="cursor:pointer;" title="${open ? 'Collapse' : 'Expand'} this zone">
          <div class="fr-zone-label"><span class="fr-zone-acc-chev" aria-hidden="true">›</span>${zone.label}</div>
          <div class="fr-zone-meta">
            <span>${partsInZone.length} parts${activeParts.length ? ` · <span class="fr-zone-acc-on">${activeParts.length} on</span>` : ''}</span>
            <span class="fr-zone-acc-view" data-body-focus="${zone.id}" role="button" title="View this area">◎ view</span>
          </div>
        </div>
        ${activeParts.length ? `
        <div class="fr-zone-picked">
          ${activeParts.map(p => `<button class="fr-zone-picked-chip" data-picked-part="${p.id}" title="${p.name} — open and jump to this part">${p.name}</button>`).join('')}
        </div>` : ''}
        <div class="fr-part-grid fr-zone-acc-body">${partsInZone.map(partCard).join('')}</div>
        ${zone.id === 'roof' ? `
        <div class="fr-zone-acc-body fr-roof-extras">
          <button class="fr-toggle-row ${state.build.clearRoof ? 'enabled' : ''}" data-clear-roof>
            <div>
              <div class="fr-toggle-name">Clear Translucent Lid</div>
              <div class="fr-toggle-desc">Swaps the roof for see-through plexiglass — view the vapor chamber inside</div>
            </div>
            <div class="fr-toggle-state">${state.build.clearRoof ? 'ON · $29' : 'OFF'}</div>
          </button>
          <button class="fr-toggle-row ${state.build.clearCap ? 'enabled' : ''}" data-clear-cap>
            <div>
              <div class="fr-toggle-name">Clear Translucent Cap</div>
              <div class="fr-toggle-desc">Hand-made translucent tank cap — fitted to your car outside the configurator</div>
            </div>
            <div class="fr-toggle-state">${state.build.clearCap ? 'ON · $19' : 'OFF'}</div>
          </button>
        </div>` : ''}
      </div>
    `;
  }).join('');

  // Sticky zone-jump nav — opens the zone, scrolls the panel to it AND flies the
  // camera. Badge = parts you already have on there, so nothing hides in a
  // collapsed zone. Stays on screen so no zone is ever more than one tap away.
  const zoneNav = `
    <div class="fr-zone-jump">
      <div class="fr-zone-jump-track">
        ${window.FR_BODY_ZONES.map(zone => {
          const activeCount = bodyZoneParts(zone.id).filter(p => state.build.parts.has(p.id)).length;
          return `<button class="fr-zone-jump-chip ${_bodyZoneOpen[zone.id] ? 'active' : ''}" data-zone-jump="${zone.id}">
            <span class="fr-zone-jump-label">${zone.label}</span>
            ${activeCount ? `<span class="fr-zone-jump-badge">${activeCount}</span>` : ''}
          </button>`;
        }).join('')}
      </div>
    </div>
  `;

  // License plates moved to their own step

  return `
    <div class="fr-section-title">AERO & PARTS</div>
    <div class="fr-section-subtitle">${window.FR_BODY_PARTS.length} parts · ${window.FR_BODY_ZONES.length} zones · Step 03</div>
    ${zoneNav}
    <div class="fr-info-card">
      <div class="fr-info-card-label">How it works</div>
      <div class="fr-info-card-body">Jump to a zone with the bar above — the camera flies to that area of the car. Tap a zone header to open or fold it; anything you've picked stays listed on a folded zone. Toggle parts on, then pick a finish. Tapping any part also moves the camera to its zone.</div>
    </div>
    <details class="fr-process-note">
      <summary><span class="fr-process-note-i" aria-hidden="true">i</span><span>How your parts are made &amp; fitted</span><span class="fr-process-note-chev" aria-hidden="true">›</span></summary>
      <div class="fr-process-note-body">Every accessory is hand-bonded to the body with structural resin, then sanded, primed, hand-painted and clear-coated through the same long, multi-stage detailing process as the car itself — built up and refined by hand at every step. Your FlameRacer arrives with all selected parts already mounted, finished and inspected: fully assembled and display-ready, straight out of the box.</div>
    </details>
    ${zonesHtml}
  `;
  // (2026-07-30: standalone Roof section folded into the Roof zone accordion;
  // "Parts Missing / Coming Soon" section removed per user — FR_MISSING_PARTS
  // still tracks pending files in flameracer-data.js, it just no longer renders.)
}

// Group a pack's slots into DESIGN cards: singles stay 1:1, mirror pairs collapse
// into ONE card (the right-side slot is the primary) with a free mirror toggle.
function packDesigns(pack) {
  const seen = new Set(); const out = [];
  for (const s of pack.slots) {
    if (seen.has(s.page)) continue;
    seen.add(s.page);
    if (s.mirror) {
      const partner = pack.slots.find(o => o.page === s.mirror);
      if (partner) seen.add(partner.page);
      const primary = (s.point[0] >= 0) ? s : (partner || s);
      out.push({ kind: 'pair', primary, page: primary.page, mirror: primary.mirror });
    } else {
      out.push({ kind: 'single', primary: s, page: s.page, mirror: null });
    }
  }
  return out;
}

// Per-sticker finish picker: each placed decal / permit carries its OWN finish —
// ONE clean labelled row on its card. `attr` is the data-attribute (e.g.
// 'decal-finish' / 'permit-finish'), `key` the item key the handler splits back
// out, `cur` the currently-selected finish id. Module-level so the custom-permit
// cards can reuse it too.
const FR_FIN_SHORT = { 'brushed-aluminum': 'Alu', 'holographic': 'Holo', 'brushed-gold': 'Gold' };
function decalFinishDots(attr, key, cur) {
  return `
    <div style="margin-top:9px;padding-top:9px;border-top:1px solid var(--fr-border-subtle);">
      <div style="font-family:var(--fr-mono-font);font-size:9px;letter-spacing:0.14em;text-transform:uppercase;color:var(--fr-text-secondary);margin-bottom:6px;">Finish</div>
      <div style="display:flex;gap:6px;">${window.FR_DECAL_FINISHES.map(f => {
        const on = cur === f.id;
        return `<button data-${attr}="${key}:${f.id}" title="${f.name}${f.priceAdd ? ` · +$${f.priceAdd}` : ''}"
          style="flex:1;display:flex;flex-direction:column;align-items:center;gap:5px;padding:6px 4px;border-radius:7px;cursor:pointer;background:var(--fr-bg-panel-row);border:1px solid ${on ? 'var(--fr-accent-yellow)' : 'var(--fr-border-subtle)'};${on ? 'box-shadow:0 0 0 1px var(--fr-accent-yellow);' : ''}">
          <span style="width:100%;height:15px;border-radius:4px;background:${f.gradient ? `linear-gradient(120deg,${f.gradient.join(',')})` : f.hex};box-shadow:inset 0 0 0 1px rgba(255,255,255,0.14);"></span>
          <span style="font-family:var(--fr-mono-font);font-size:8px;letter-spacing:0.05em;text-transform:uppercase;color:${on ? 'var(--fr-accent-yellow)' : 'var(--fr-text-secondary)'};line-height:1;">${FR_FIN_SHORT[f.id] || f.name}${f.priceAdd ? ` +$${f.priceAdd}` : ''}</span>
        </button>`;
      }).join('')}</div>
    </div>`;
}

// Live 2D preview / print PNG of a custom racing permit — same trick as
// platePreviewURL but for a permit `design` object (reuses _paintCustomPlate).
function permitPreviewURL(design) {
  try {
    const W = 512, H = 256, cv = document.createElement('canvas'); cv.width = W; cv.height = H;
    scene._paintCustomPermit(cv.getContext('2d'), W, H, design || {});
    return cv.toDataURL('image/png');
  } catch (e) { return ''; }
}
// Cached mini swatch of each permit card BASE material (for the base picker).
function permitBaseThumb(base) {
  window.__frPermitBaseThumbs = window.__frPermitBaseThumbs || {};
  if (window.__frPermitBaseThumbs[base]) return window.__frPermitBaseThumbs[base];
  const w = 96, h = 48, cv = document.createElement('canvas'); cv.width = w; cv.height = h;
  try { scene._permitBase(cv.getContext('2d'), w, h, { base }); } catch (e) {}
  const u = cv.toDataURL('image/png'); window.__frPermitBaseThumbs[base] = u; return u;
}

// The full design editor for ONE custom racing permit — mirrors the custom-plate
// editor (preview + text + presets + bg + emoji + region + frame + serial…) plus a
// 3-way POSITION picker. Bound to the permit currently being edited
// (state.ui.editPermit). All controls use `permit-*` data-attrs so they never
// collide with the plate editor's `plate-*` handlers.
function renderPermitEditor(p) {
  const POSITIONS = window.FR_PERMIT_POSITIONS || [];
  const takenByOther = new Set(state.build.permits.filter(x => x.custom && x.id !== p.id).map(x => x.position));
  const d = p.design || {};
  const LAYOUTS = [['authorization', 'Authorization'], ['pitpass', 'Pit Pass'], ['license', 'License']];
  const STATUSES = ['APPROVED', 'VALID', 'ACTIVE', 'ALL ACCESS', 'VIP', 'CLEARED', 'PADDOCK', 'PASSED'];
  const CLASSES = ['', 'GT', 'PRO', 'AM', 'DRIFT', 'TIME ATTACK', 'OPEN', 'A', 'S'];
  const SEASONS = ['2024', '2025', '2026', '2027'];
  const ACCENTS = ['#f5d000', '#e01f2d', '#16c5e8', '#39ff14', '#ff6a1a', '#b06bff', '#ffffff', '#ff1f8c'];
  // TASK 2: cyber / tactical / bloodtech card materials added to the permit picker.
  const BASES = [['carbon', 'Carbon'], ['noir', 'Noir'], ['navy', 'Navy'], ['blood', 'Blood'], ['steel', 'Steel'], ['security', 'Security'], ['cyber', 'Cyber'], ['tactical', 'Tactical'], ['bloodtech', 'Bloodtech']];
  const curLayout = d.layout || 'authorization', curStatus = d.status || 'APPROVED', curCls = d.cls || '', curSeason = d.season || '2026', curAccent = d.accent || '#f5d000', curBase = d.base || 'carbon';
  const seal = d.stamp !== false, holo = !!d.holo, barcode = d.barcode !== false, checker = d.checker !== false;
  // TASK 3: shared .fr-editor / .fr-editor-head scaffold (see permit CSS block).
  const lbl = t => `<div class="fr-editor-head">${t}</div>`;
  const sq = (color, attr, val, cur) => `<button class="fr-sq ${cur === val ? 'on' : ''}" data-${attr}="${val}" title="${val}" style="background:${color};"></button>`;
  const chip = (attr, val, label, cur) => `<button class="fr-chip ${cur === val ? 'on' : ''}" data-${attr}="${val}">${label}</button>`;
  return `
    <div class="fr-editor">
      <div class="fr-editor-preview">
        <img data-permit-preview src="${permitPreviewURL(d)}" alt="Permit preview" onerror="this.style.visibility='hidden'" style="width:100%;max-width:300px;aspect-ratio:2 / 1;border-radius:6px;box-shadow:0 6px 18px rgba(0,0,0,0.45);display:block;background:#0d0d10;" />
        <button data-permit-download class="fr-btn-dl">Download PNG</button>
      </div>
      ${lbl('Position on car')}
      <div style="display:grid;grid-template-columns:repeat(2,1fr);gap:8px;">${POSITIONS.map((pos, i) => {
        const on = p.position === i, blocked = takenByOther.has(i);
        return `<button class="fr-btn-pos ${on ? 'on' : ''}" ${blocked ? 'disabled title="Used by another permit"' : `data-permit-position="${i}"`}>${pos.label}</button>`;
      }).join('')}</div>
      ${lbl('Starter Permits · one tap')}
      <div style="display:grid;grid-template-columns:repeat(auto-fill, minmax(80px, 1fr));gap:7px;">
        ${(window.FR_PERMIT_PRESETS || []).map((pr, i) => `<button data-permit-preset="${i}" class="fr-chip fr-chip-cell">${pr.label}</button>`).join('')}
      </div>
      <button data-permit-surprise class="fr-btn-solid">Surprise Me</button>
      ${lbl('Permit Style')}
      <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:7px;">${LAYOUTS.map(([v, l]) => `
        <button data-permit-layout="${v}" title="${l}" style="display:flex;flex-direction:column;align-items:center;gap:5px;padding:6px;border-radius:2px;background:${curLayout === v ? 'rgba(209,15,47,0.14)' : 'rgba(255,255,255,0.04)'};border:1px solid ${curLayout === v ? 'var(--fr-accent-yellow,#f5d000)' : 'rgba(255,255,255,0.14)'};cursor:pointer;">
          <img src="${permitPreviewURL(Object.assign({}, d, { layout: v }))}" alt="${l}" style="width:100%;aspect-ratio:2 / 1;border-radius:3px;display:block;object-fit:cover;" />
          <span style="font-size:9.5px;color:${curLayout === v ? 'var(--fr-accent-yellow,#f5d000)' : 'var(--fr-text-secondary)'};">${l}</span>
        </button>`).join('')}</div>
      ${lbl('Title <span style="opacity:.5;">(header line)</span>')}
      <input data-permit-title-input type="text" maxlength="22" value="${(d.title || '').replace(/"/g, '&quot;')}" placeholder="RACING PERMIT" autocomplete="off"
        style="width:100%;background:#0d0d10;border:1px solid rgba(255,255,255,0.16);border-radius:6px;color:#fff;font-family:var(--fr-display-font,sans-serif);font-size:16px;letter-spacing:0.1em;text-transform:uppercase;padding:11px 13px;outline:none;" />
      ${lbl('Status <span style="opacity:.5;">(the big stamp)</span>')}
      <input data-permit-status-input type="text" maxlength="12" value="${(d.status || '').replace(/"/g, '&quot;')}" placeholder="APPROVED" autocomplete="off"
        style="width:100%;background:#0d0d10;border:1px solid rgba(255,255,255,0.16);border-radius:6px;color:#fff;font-family:var(--fr-display-font,sans-serif);font-size:18px;letter-spacing:0.12em;text-transform:uppercase;padding:11px 13px;outline:none;margin-bottom:8px;" />
      <div style="display:flex;flex-wrap:wrap;gap:6px;">${STATUSES.map(s => chip('permit-status', s, s, curStatus)).join('')}</div>
      ${lbl('Class / Grade')}
      <div style="display:flex;flex-wrap:wrap;gap:6px;">${CLASSES.map(c => chip('permit-class', c, c || 'None', curCls)).join('')}</div>
      ${lbl('Season')}
      <div style="display:flex;flex-wrap:wrap;gap:6px;">${SEASONS.map(s => chip('permit-season', s, s, curSeason)).join('')}</div>
      ${lbl('Accent Colour')}
      <div style="display:flex;flex-wrap:wrap;gap:8px;">${ACCENTS.map(c => sq(c, 'permit-accent', c, curAccent)).join('')}</div>
      ${lbl('Card Material')}
      <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:7px;">${BASES.map(([v, l]) => `
        <button data-permit-base="${v}" title="${l}" style="display:flex;flex-direction:column;align-items:center;gap:5px;padding:6px;border-radius:2px;background:${curBase === v ? 'rgba(209,15,47,0.14)' : 'rgba(255,255,255,0.04)'};border:1px solid ${curBase === v ? 'var(--fr-accent-yellow,#f5d000)' : 'rgba(255,255,255,0.14)'};cursor:pointer;">
          <img src="${permitBaseThumb(v)}" alt="${l}" style="width:100%;aspect-ratio:2 / 1;border-radius:3px;display:block;object-fit:cover;" />
          <span style="font-size:9.5px;color:${curBase === v ? 'var(--fr-accent-yellow,#f5d000)' : 'var(--fr-text-secondary)'};">${l}</span>
        </button>`).join('')}</div>
      ${lbl('Issuing Authority')}
      <input data-permit-authority-input type="text" maxlength="28" value="${(d.authority || '').replace(/"/g, '&quot;')}" placeholder="FLAMERACER MOTORSPORT" autocomplete="off"
        style="width:100%;background:#0d0d10;border:1px solid rgba(255,255,255,0.16);border-radius:6px;color:#fff;font-family:var(--fr-mono-font,monospace);font-size:12px;letter-spacing:0.08em;text-transform:uppercase;padding:10px 12px;outline:none;" />
      ${lbl('Security Features')}
      <div style="display:flex;gap:6px;">
        <button data-permit-toggle="stamp" style="flex:1;padding:9px 5px;border-radius:2px;background:${seal ? 'var(--fr-accent-yellow,#f5d000)' : 'rgba(255,255,255,0.05)'};color:${seal ? '#fff' : '#fff'};border:1px solid ${seal ? 'var(--fr-accent-yellow,#f5d000)' : 'rgba(255,255,255,0.15)'};font-size:10.5px;letter-spacing:0.03em;text-transform:uppercase;cursor:pointer;">◉ Seal</button>
        <button data-permit-toggle="holo" style="flex:1;padding:9px 5px;border-radius:2px;background:${holo ? 'var(--fr-accent-yellow,#f5d000)' : 'rgba(255,255,255,0.05)'};color:${holo ? '#fff' : '#fff'};border:1px solid ${holo ? 'var(--fr-accent-yellow,#f5d000)' : 'rgba(255,255,255,0.15)'};font-size:10.5px;letter-spacing:0.03em;text-transform:uppercase;cursor:pointer;">▦ Holo</button>
        <button data-permit-toggle="barcode" style="flex:1;padding:9px 5px;border-radius:2px;background:${barcode ? 'var(--fr-accent-yellow,#f5d000)' : 'rgba(255,255,255,0.05)'};color:${barcode ? '#fff' : '#fff'};border:1px solid ${barcode ? 'var(--fr-accent-yellow,#f5d000)' : 'rgba(255,255,255,0.15)'};font-size:10.5px;letter-spacing:0.03em;text-transform:uppercase;cursor:pointer;">||| Barcode</button>
        <button data-permit-toggle="checker" style="flex:1;padding:9px 5px;border-radius:2px;background:${checker ? 'var(--fr-accent-yellow,#f5d000)' : 'rgba(255,255,255,0.05)'};color:${checker ? '#fff' : '#fff'};border:1px solid ${checker ? 'var(--fr-accent-yellow,#f5d000)' : 'rgba(255,255,255,0.15)'};font-size:10.5px;letter-spacing:0.03em;text-transform:uppercase;cursor:pointer;">▣ Checker</button>
      </div>
    </div>`;
}

// The whole "Custom Racing Permits" section for the decals step: a compact card per
// added custom permit + an inline editor for the active one + a "design new" button.
function renderCustomPermitsSection() {
  const PRICE = window.FR_CUSTOM_PERMIT_PRICE || 4.99;
  const POSITIONS = window.FR_PERMIT_POSITIONS || [];
  const finishId = state.build.decalFinish || 'brushed-aluminum';
  const customs = state.build.permits.filter(p => p.custom);
  const editingId = (state.ui && state.ui.editPermit) || null;
  const editing = customs.find(p => p.id === editingId) || null;
  const allUsed = customs.length >= POSITIONS.length;

  const card = p => {
    const pos = POSITIONS[p.position] || POSITIONS[0];
    const isEd = p.id === editingId;
    return `
      <div class="fr-decal-card ${isEd ? 'selected' : ''}">
        <div class="fr-sticker-thumb" style="background:#0d0d10;"><img src="${permitPreviewURL(p.design)}" alt="custom permit" style="object-fit:contain;" />${isEd ? '<div class="fr-decal-check">✎</div>' : ''}</div>
        <div class="fr-sticker-meta">
          <div class="fr-sticker-name">${((p.design && (p.design.status || p.design.title)) || 'PERMIT')} · ${pos ? pos.label : ''}</div>
          <div class="fr-sticker-price" style="color:var(--fr-accent-yellow);">${isEd ? 'Editing · ' : 'Added · '}+ $${PRICE.toFixed(2)}</div>
        </div>
        <div style="display:flex;gap:6px;margin-top:7px;">
          <button class="fr-btn-edit ${isEd ? 'on' : ''}" data-permit-edit="${p.id}">${isEd ? '✎ Close Editor' : '✎ Edit Design'}</button>
          <button class="fr-btn-x" data-permit-custom-remove="${p.id}" title="Remove">✕</button>
        </div>
        ${decalFinishDots('permit-finish', p.id, p.finish || finishId)}
      </div>`;
  };

  const listHtml = customs.length ? `<div class="fr-decal-grid">${customs.map(card).join('')}</div>` : '';
  const addBtn = allUsed ? `<div class="fr-mount-note" style="margin-top:10px;">All ${POSITIONS.length} positions are in use. Remove one to design another.</div>`
    : `<button class="fr-btn-add" data-permit-new style="margin-top:${customs.length ? '10px' : '4px'};">✎ Design a Custom Permit · +$${PRICE.toFixed(2)}</button>`;

  return `
    <div class="fr-zone-section" style="margin-top:18px;">
      <div class="fr-zone-header"><div class="fr-zone-label">Custom Racing Permits</div><div class="fr-zone-meta">Design your own · $${PRICE.toFixed(2)} each · up to ${POSITIONS.length}</div></div>
      <div class="fr-info-card" style="margin-bottom:12px;"><div class="fr-info-card-body">Design a permit like a mini license plate — your text, background, emoji, region &amp; frame — then place it on the fender, door, or quarter panel. Conforms to the bodywork like the other decals. Get a print-ready PNG with Download.</div></div>
      ${listHtml}
      ${addBtn}
      ${editing ? renderPermitEditor(editing) : ''}
    </div>`;
}

// ════════════════════ CUSTOM SUN BANNER — UI ════════════════════
function bannerPreviewURL(design) {
  try { const W = 1024, H = 288, cv = document.createElement('canvas'); cv.width = W; cv.height = H; scene._paintCustomBanner(cv.getContext('2d'), W, H, design || {}); return cv.toDataURL('image/png'); } catch (e) { return ''; }
}
// Preview PNG for the rear-wing decal editor. The sticker itself is transparent
// (text only), so draw a dark carbon-ish strip behind it so the preview reads.
function wingPreviewURL(design) {
  try {
    const W = 1024, H = 160, cv = document.createElement('canvas'); cv.width = W; cv.height = H;
    const ctx = cv.getContext('2d');
    const g = ctx.createLinearGradient(0, 0, 0, H); g.addColorStop(0, '#15161b'); g.addColorStop(1, '#0a0a0d');
    ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
    scene._paintWingDecal(ctx, W, H, design || {});
    return cv.toDataURL('image/png');
  } catch (e) { return ''; }
}
function bannerBgThumb(bg, accent) {
  const key = bg + '|' + (accent || '');
  window.__frBannerBgThumbs = window.__frBannerBgThumbs || {};
  if (window.__frBannerBgThumbs[key]) return window.__frBannerBgThumbs[key];
  const w = 160, h = 46, cv = document.createElement('canvas'); cv.width = w; cv.height = h;
  try { scene._paintCustomBanner(cv.getContext('2d'), w, h, { bg, accent: accent || '#ff2052', text: '', sub: '', motif: 'none' }); } catch (e) {}
  const u = cv.toDataURL('image/png'); window.__frBannerBgThumbs[key] = u; return u;
}
// Full editor for ONE custom sun banner — retro-JDM. All `banner-*` data-attrs.
function renderBannerEditor(b) {
  const POSITIONS = window.FR_BANNER_POSITIONS || [];
  const takenByOther = new Set(state.build.banners.filter(x => x.id !== b.id).map(x => x.position));
  const d = b.design || {};
  // TASK 2: cyber / tactical / bloodtech backgrounds added to the banner picker.
  const BGS = [['synthwave', 'Synthwave'], ['risingsun', 'Rising Sun'], ['grid', 'Neon Grid'], ['vapor', 'Vaporwave'], ['sakura', 'Sakura'], ['wave', 'Great Wave'], ['city', 'Neon City'], ['mountains', 'Mt Fuji'], ['noir', 'Noir'], ['cyber', 'Cyber'], ['tactical', 'Tactical'], ['bloodtech', 'Bloodtech'], ['solid', 'Solid']];
  const MOTIFS = [['none', 'None'], ['dragon', '🐉 Dragon'], ['sun', '☀ Rays'], ['sakura', '🌸 Sakura'], ['oni', '👹 Oni'], ['torii', '⛩ Torii']];
  const ACCENTS = ['#ff2052', '#ff3b30', '#16c5e8', '#39ff14', '#b06bff', '#ff6ab5', '#ffd23b', '#ffffff'];
  const TXTS = ['#ffffff', '#0a0a0c', '#ff2052', '#16c5e8', '#39ff14', '#ffd23b'];
  const FONTS = [['Display', '"Bebas Neue", Impact, sans-serif'], ['Block', '"Arial Black", Arial'], ['Impact', 'Impact, "Arial Narrow Bold", sans-serif'], ['Condensed', '"Barlow Condensed", sans-serif'], ['Mono', '"JetBrains Mono", monospace']];
  const curBg = d.bg || 'synthwave', curMotif = d.motif || 'none', curAccent = d.accent || '#ff2052', curTxt = d.textColor || '#ffffff', curFont = d.font || '"Bebas Neue", Impact, sans-serif';
  const outline = d.outline !== false, italic = !!d.italic;
  const curPos = POSITIONS[b.position] || POSITIONS[0];
  // TASK 3: section header now uses the shared .fr-editor-head class so the
  // editor card's own `gap` owns vertical rhythm (even, aligned rows).
  const lbl = t => `<div class="fr-editor-head">${t}</div>`;
  const sq = (color, attr, val, cur) => `<button class="fr-sq ${cur === val ? 'on' : ''}" data-${attr}="${val}" title="${val}" style="background:${color};"></button>`;
  const chip = (attr, val, label, cur) => `<button class="fr-chip ${cur === val ? 'on' : ''}" data-${attr}="${val}">${label}</button>`;
  return `
    <div class="fr-editor">
      <div class="fr-editor-preview">
        <img data-banner-preview src="${bannerPreviewURL(d)}" alt="Banner preview" onerror="this.style.visibility='hidden'" style="width:100%;aspect-ratio:1024 / 288;border-radius:6px;box-shadow:0 6px 18px rgba(0,0,0,0.45);display:block;background:#0d0d10;" />
        <button data-banner-download class="fr-btn-dl">Download PNG</button>
      </div>
      ${lbl('Position')}
      <div style="display:grid;grid-template-columns:repeat(2,1fr);gap:8px;">${POSITIONS.map((pos, i) => {
        const on = b.position === i, blocked = takenByOther.has(i);
        return `<button class="fr-btn-pos ${on ? 'on' : ''}" ${blocked ? 'disabled title="Used by another banner"' : `data-banner-position="${i}"`}>${pos.label}</button>`;
      }).join('')}</div>
      ${curPos && curPos.mirrorable ? `<button data-banner-toggle="mirror" style="width:100%;margin-top:8px;padding:9px;border-radius:2px;background:${b.mirror ? 'var(--fr-accent-yellow,#f5d000)' : 'rgba(255,255,255,0.05)'};color:${b.mirror ? '#fff' : '#fff'};border:1px solid ${b.mirror ? 'var(--fr-accent-yellow,#f5d000)' : 'rgba(255,255,255,0.15)'};font-size:11px;letter-spacing:0.04em;text-transform:uppercase;cursor:pointer;">⇄ Mirror to both sides${b.mirror ? ' ✓' : ' · free'}</button>` : ''}
      ${lbl('Starter Banners · one tap')}
      <div style="display:grid;grid-template-columns:repeat(auto-fill, minmax(74px, 1fr));gap:7px;">
        ${(window.FR_BANNER_PRESETS || []).map((pr, i) => `<button data-banner-preset="${i}" class="fr-chip fr-chip-cell">${pr.label}</button>`).join('')}
      </div>
      <button data-banner-surprise class="fr-btn-solid">Surprise Me</button>
      ${lbl('Background · retro Japan')}
      <div style="display:grid;grid-template-columns:repeat(auto-fill, minmax(96px, 1fr));gap:7px;">${BGS.map(([v, l]) => `
        <button data-banner-bg="${v}" title="${l}" style="display:flex;flex-direction:column;align-items:center;gap:5px;padding:6px;border-radius:2px;background:${curBg === v ? 'rgba(209,15,47,0.14)' : 'rgba(255,255,255,0.04)'};border:1px solid ${curBg === v ? 'var(--fr-accent-yellow,#f5d000)' : 'rgba(255,255,255,0.14)'};cursor:pointer;">
          <img src="${bannerBgThumb(v, curAccent)}" alt="${l}" style="width:100%;aspect-ratio:1024 / 288;border-radius:3px;display:block;object-fit:cover;" />
          <span style="font-size:9.5px;color:${curBg === v ? 'var(--fr-accent-yellow,#f5d000)' : 'var(--fr-text-secondary)'};">${l}</span>
        </button>`).join('')}</div>
      ${lbl('Motif · flanking icon')}
      <div style="display:flex;flex-wrap:wrap;gap:6px;">${MOTIFS.map(([v, l]) => chip('banner-motif', v, l, curMotif)).join('')}</div>
      ${lbl('Big Text')}
      <input data-banner-text-input type="text" maxlength="14" value="${(d.text || '').replace(/"/g, '&quot;')}" placeholder="TOUGE" autocomplete="off"
        style="width:100%;background:#0d0d10;border:1px solid rgba(255,255,255,0.16);border-radius:6px;color:#fff;font-family:var(--fr-display-font,sans-serif);font-size:20px;letter-spacing:0.14em;text-transform:uppercase;padding:12px 14px;outline:none;" />
      ${lbl('Sub Text <span style="opacity:.5;">(kanji / tagline)</span>')}
      <input data-banner-sub-input type="text" maxlength="16" value="${(d.sub || '').replace(/"/g, '&quot;')}" placeholder="走り屋" autocomplete="off"
        style="width:100%;background:#0d0d10;border:1px solid rgba(255,255,255,0.16);border-radius:6px;color:#fff;font-family:var(--fr-display-font,sans-serif);font-size:15px;letter-spacing:0.08em;padding:10px 12px;outline:none;" />
      ${lbl('Accent Colour')}
      <div style="display:flex;flex-wrap:wrap;gap:8px;">${ACCENTS.map(c => sq(c, 'banner-accent', c, curAccent)).join('')}</div>
      ${lbl('Text Colour')}
      <div style="display:flex;flex-wrap:wrap;gap:8px;">${TXTS.map(c => sq(c, 'banner-textcolor', c, curTxt)).join('')}</div>
      ${lbl('Font')}
      <div style="display:flex;flex-wrap:wrap;gap:8px;">${FONTS.map(([label, f]) => `<button data-banner-font="${f.replace(/"/g, '&quot;')}" style="padding:7px 12px;border-radius:2px;background:${curFont === f ? 'var(--fr-accent-yellow,#f5d000)' : 'rgba(255,255,255,0.05)'};color:${curFont === f ? '#fff' : '#fff'};border:1px solid rgba(255,255,255,0.15);font-family:${f};font-size:13px;cursor:pointer;">${label}</button>`).join('')}</div>
      ${lbl('Style')}
      <div style="display:flex;gap:6px;">
        <button data-banner-toggle="outline" style="flex:1;padding:9px 5px;border-radius:2px;background:${outline ? 'var(--fr-accent-yellow,#f5d000)' : 'rgba(255,255,255,0.05)'};color:${outline ? '#fff' : '#fff'};border:1px solid ${outline ? 'var(--fr-accent-yellow,#f5d000)' : 'rgba(255,255,255,0.15)'};font-size:11px;letter-spacing:0.03em;text-transform:uppercase;cursor:pointer;">Outline</button>
        <button data-banner-toggle="italic" style="flex:1;padding:9px 5px;border-radius:2px;background:${italic ? 'var(--fr-accent-yellow,#f5d000)' : 'rgba(255,255,255,0.05)'};color:${italic ? '#fff' : '#fff'};border:1px solid ${italic ? 'var(--fr-accent-yellow,#f5d000)' : 'rgba(255,255,255,0.15)'};font-size:11px;letter-spacing:0.03em;text-transform:uppercase;cursor:pointer;">Italic</button>
      </div>
      ${decalFinishDots('banner-finish', b.id, b.finish || (state.build.decalFinish || 'brushed-aluminum'))}
    </div>`;
}
// The "Custom Sun Banners" section for the decals step.
function renderCustomBannersSection() {
  const PRICE = window.FR_BANNER_PRICE || 6.99;
  const POSITIONS = window.FR_BANNER_POSITIONS || [];
  const finishId = state.build.decalFinish || 'brushed-aluminum';
  const banners = state.build.banners || [];
  const editingId = (state.ui && state.ui.editBanner) || null;
  const editing = banners.find(b => b.id === editingId) || null;
  const allUsed = banners.length >= POSITIONS.length;
  const card = b => {
    const pos = POSITIONS[b.position] || POSITIONS[0];
    const isEd = b.id === editingId;
    return `
      <div class="fr-decal-card ${isEd ? 'selected' : ''}">
        <div class="fr-sticker-thumb" style="background:#0d0d10;"><img src="${bannerPreviewURL(b.design)}" alt="custom banner" style="object-fit:contain;" />${isEd ? '<div class="fr-decal-check">✎</div>' : ''}</div>
        <div class="fr-sticker-meta">
          <div class="fr-sticker-name">${((b.design && b.design.text) || 'BANNER')} · ${pos ? pos.label : ''}${b.mirror ? ' ⇄' : ''}</div>
          <div class="fr-sticker-price" style="color:var(--fr-accent-yellow);">${isEd ? 'Editing · ' : 'Added · '}+ $${PRICE.toFixed(2)}</div>
        </div>
        <div style="display:flex;gap:6px;margin-top:7px;">
          <button class="fr-btn-edit ${isEd ? 'on' : ''}" data-banner-edit="${b.id}">${isEd ? '✎ Close Editor' : '✎ Edit Design'}</button>
          <button class="fr-btn-x" data-banner-custom-remove="${b.id}" title="Remove">✕</button>
        </div>
        ${decalFinishDots('banner-finish', b.id, b.finish || finishId)}
      </div>`;
  };
  const listHtml = banners.length ? `<div class="fr-decal-grid">${banners.map(card).join('')}</div>` : '';
  const addBtn = allUsed ? `<div class="fr-mount-note" style="margin-top:10px;">All ${POSITIONS.length} banner positions are in use.</div>`
    : `<button class="fr-btn-add" data-banner-new style="margin-top:${banners.length ? '10px' : '4px'};">✎ Design a Sun Banner · +$${PRICE.toFixed(2)}</button>`;
  return `
    <div class="fr-zone-section" style="margin-top:18px;">
      <div class="fr-zone-header"><div class="fr-zone-label">Custom Sun Banners</div><div class="fr-zone-meta">Retro-JDM · $${PRICE.toFixed(2)} each · up to ${POSITIONS.length}</div></div>
      <div class="fr-info-card" style="margin-bottom:12px;"><div class="fr-info-card-body">The classic JDM windshield sun strip — or a mirrored side stripe. Pick a retro-Japan background (synthwave, rising sun, Great Wave, sakura…), a flanking motif, your text &amp; kanji. Conforms to the glass / bodywork. Download for the print file.</div></div>
      ${listHtml}
      ${addBtn}
      ${editing ? renderBannerEditor(editing) : ''}
    </div>`;
}

// The "Rear Wing Decal" section for the decals step — a single authored placement
// (the wing's rear text band). Mirrors the banner UI: a toggle, style + text +
// colour controls, live preview. State lives in state.build.wingDecal.
function renderWingDecalSection() {
  const PRICE = window.FR_WING_DECAL_PRICE || 6.99;
  const STYLES = window.FR_WING_DECAL_STYLES || [];
  const finishId = state.build.decalFinish || 'brushed-aluminum';
  const w = state.build.wingDecal || { on: false, style: 'cyber', text: 'FLAMERACER', color: null };
  const on = !!w.on;
  const design = { style: w.style, text: w.text, color: w.color };
  const COLORS = ['#16f5d0', '#ff1f8c', '#39ff14', '#1f9cff', '#b06bff', '#ff3b46', '#f5d000', '#ffffff'];
  const curCol = w.color || null;
  const sq = (color, val, cur) => `<button class="fr-sq ${cur === val ? 'on' : ''}" data-wing-color="${val}" title="${val}" style="background:${color};"></button>`;
  const toggleBtn = `<button class="fr-btn-add ${on ? 'on' : ''}" data-wing-toggle>${on ? '✓ Rear Wing Decal · Added' : '✎ Add Rear Wing Decal · +$' + PRICE.toFixed(2)}</button>`;
  const editor = on ? `
    <div class="fr-editor" style="margin-top:12px;">
      <div class="fr-editor-preview">
        <img data-wing-preview src="${wingPreviewURL(design)}" alt="Wing decal preview" onerror="this.style.visibility='hidden'" style="width:100%;aspect-ratio:1024 / 160;border-radius:6px;box-shadow:0 6px 18px rgba(0,0,0,0.45);display:block;background:#0d0d10;" />
        <button data-wing-download class="fr-btn-dl">Download PNG</button>
      </div>
      <div class="fr-editor-head">Style</div>
      <div class="fr-chip-row">${STYLES.map(s => `<button data-wing-style="${s.id}" class="fr-chip ${w.style === s.id ? 'on' : ''}">${s.label}</button>`).join('')}</div>
      <div class="fr-editor-head">Text <span style="opacity:.5;">(short)</span></div>
      <input data-wing-text-input type="text" maxlength="18" value="${(w.text || '').replace(/"/g, '&quot;')}" placeholder="FLAMERACER" autocomplete="off"
        style="width:100%;background:#0d0d10;border:1px solid rgba(255,255,255,0.16);border-radius:6px;color:#fff;font-family:var(--fr-display-font,sans-serif);font-size:18px;letter-spacing:0.14em;text-transform:uppercase;padding:11px 14px;outline:none;" />
      <div class="fr-editor-head">Colour</div>
      <div style="display:flex;flex-wrap:wrap;gap:8px;"><button data-wing-color="" title="Style default" style="height:30px;padding:0 10px;border-radius:2px;background:rgba(255,255,255,0.05);border:2px solid ${!curCol ? 'var(--fr-accent-yellow,#f5d000)' : 'rgba(255,255,255,0.2)'};color:#fff;font-size:10px;letter-spacing:0.05em;text-transform:uppercase;cursor:pointer;">Default</button>${COLORS.map(c => sq(c, c, curCol)).join('')}</div>
      ${decalFinishDots('wing-finish', 'wing', w.finish || finishId)}
    </div>` : '';
  return `
    <div class="fr-zone-section" style="margin-top:18px;">
      <div class="fr-zone-header"><div class="fr-zone-label">Rear Wing Decal</div><div class="fr-zone-meta">Tactical / cyber · $${PRICE.toFixed(2)}</div></div>
      <div class="fr-info-card" style="margin-bottom:12px;"><div class="fr-info-card-body">A bold text band across the REAR face of the wing. Pick a cyber/tactical style, your (short) text and a colour — it conforms to the wing like real vinyl. Download for the print file.</div></div>
      ${toggleBtn}
      ${editor}
    </div>`;
}

// ════════════════════ FR SIGNATURE STICKERS — UI ════════════════════
// Door number badge + roof/deck futuristic panels: owner-authored placements with
// the SAME editing depth as the custom plate/permit (the proven design systems).
// Approved footprint per authored placement (aspect = h/w from the Sticker Lab bake).
function authoredAspect(key) {
  const id = { doorBadge: 'lower-door', roofPanel: 'roof-panel', deckPanel: 'rear-deck' }[key];
  const s = (window.FR_AUTHORED_STICKER_SLOTS || []).find(x => x.id === id);
  return (s && s.slot && s.slot.aspect) || 0.5;
}
function doorBadgePreviewURL(design) {
  try { const W = 554, H = 144, cv = document.createElement('canvas'); cv.width = W; cv.height = H; scene._paintDoorBadge(cv.getContext('2d'), W, H, design || {}); return cv.toDataURL('image/png'); } catch (e) { return ''; }
}
function panelPreviewURL(design, key) {
  try { const W = 512, H = Math.round(W * authoredAspect(key || 'roofPanel')), cv = document.createElement('canvas'); cv.width = W; cv.height = H; scene._paintPanelSticker(cv.getContext('2d'), W, H, design || {}); return cv.toDataURL('image/png'); } catch (e) { return ''; }
}
// cached mini thumb of each door-badge STYLE (for the style picker) — wide, same ratio
function doorBadgeStyleThumb(styleId) {
  window.__frBadgeStyleThumbs = window.__frBadgeStyleThumbs || {};
  if (window.__frBadgeStyleThumbs[styleId]) return window.__frBadgeStyleThumbs[styleId];
  const u = (() => { try { const W = 208, H = 54, cv = document.createElement('canvas'); cv.width = W; cv.height = H; scene._paintDoorBadge(cv.getContext('2d'), W, H, { style: styleId, number: '07', name: 'FLAMERACER', sub: '' }); return cv.toDataURL('image/png'); } catch (e) { return ''; } })();
  window.__frBadgeStyleThumbs[styleId] = u; return u;
}

function renderDoorBadgeEditor(a) {
  const lbl = t => `<div class="fr-editor-head">${t}</div>`;
  const sq = (color, attr, val, cur) => `<button class="fr-sq ${cur === val ? 'on' : ''}" data-${attr}="${val}" title="${val || 'Style default'}" style="background:${color};"></button>`;
  const STYLES = window.FR_DOOR_BADGE_STYLES || [];
  const ACCENTS = ['#f5d000', '#e01f2d', '#16f5d0', '#39ff14', '#ff1f8c', '#b06bff', '#ffffff', '#101010'];
  const design = { number: a.number, name: a.name, sub: a.sub, style: a.style, accent: a.accent };
  return `
    <div class="fr-editor">
      <div class="fr-editor-preview">
        <img data-badge-preview src="${doorBadgePreviewURL(design)}" alt="Door badge preview" onerror="this.style.visibility='hidden'" style="width:100%;max-width:340px;aspect-ratio:1108/288;border-radius:8px;box-shadow:0 6px 18px rgba(0,0,0,0.45);display:block;background:#0d0d10;" />
        <button data-badge-download class="fr-btn-dl">Download PNG</button>
      </div>
      ${lbl('Starter Badges · one tap')}
      <div style="display:grid;grid-template-columns:repeat(auto-fill, minmax(80px, 1fr));gap:7px;">
        ${(window.FR_DOOR_BADGE_PRESETS || []).map((pr, i) => `<button data-badge-preset="${i}" class="fr-chip fr-chip-cell">${pr.label}</button>`).join('')}
      </div>
      <button data-badge-surprise class="fr-btn-solid">Surprise Me</button>
      ${lbl('Race Number <span style="opacity:.5;">(1–3 characters)</span>')}
      <input data-badge-number-input type="text" maxlength="3" value="${(a.number || '').replace(/"/g, '&quot;')}" placeholder="07" autocomplete="off"
        style="width:100%;background:#0d0d10;border:1px solid rgba(255,255,255,0.16);border-radius:6px;color:#fff;font-family:var(--fr-display-font,sans-serif);font-size:26px;letter-spacing:0.2em;text-transform:uppercase;text-align:center;padding:12px 14px;outline:none;" />
      ${lbl('Top Text <span style="opacity:.5;">(team / division)</span>')}
      <input data-badge-name-input type="text" maxlength="16" value="${(a.name || '').replace(/"/g, '&quot;')}" placeholder="FLAMERACER" autocomplete="off"
        style="width:100%;background:#0d0d10;border:1px solid rgba(255,255,255,0.16);border-radius:6px;color:#fff;font-family:var(--fr-display-font,sans-serif);font-size:15px;letter-spacing:0.1em;text-transform:uppercase;padding:10px 13px;outline:none;" />
      ${lbl('Bottom Text <span style="opacity:.5;">(optional)</span>')}
      <input data-badge-sub-input type="text" maxlength="20" value="${(a.sub || '').replace(/"/g, '&quot;')}" placeholder="RACING DIVISION" autocomplete="off"
        style="width:100%;background:#0d0d10;border:1px solid rgba(255,255,255,0.16);border-radius:6px;color:#fff;font-family:var(--fr-mono-font,monospace);font-size:12px;letter-spacing:0.08em;text-transform:uppercase;padding:10px 12px;outline:none;" />
      ${lbl('Badge Material')}
      <div style="display:grid;grid-template-columns:repeat(2,1fr);gap:7px;">${STYLES.map(s => `
        <button data-badge-style="${s.id}" title="${s.label}" style="display:flex;flex-direction:column;align-items:center;gap:5px;padding:6px;border-radius:2px;background:${a.style === s.id ? 'rgba(209,15,47,0.14)' : 'rgba(255,255,255,0.04)'};border:1px solid ${a.style === s.id ? 'var(--fr-accent-yellow,#f5d000)' : 'rgba(255,255,255,0.14)'};cursor:pointer;">
          <img src="${doorBadgeStyleThumb(s.id)}" alt="${s.label}" style="width:100%;aspect-ratio:1108/288;border-radius:4px;display:block;object-fit:cover;" />
          <span style="font-size:9px;color:${a.style === s.id ? 'var(--fr-accent-yellow,#f5d000)' : 'var(--fr-text-secondary)'};white-space:nowrap;">${s.label}</span>
        </button>`).join('')}</div>
      ${lbl('Number Colour')}
      <div style="display:flex;flex-wrap:wrap;gap:8px;"><button data-badge-accent="" title="Style default" style="height:30px;padding:0 10px;border-radius:2px;background:rgba(255,255,255,0.05);border:2px solid ${!a.accent ? 'var(--fr-accent-yellow,#f5d000)' : 'rgba(255,255,255,0.2)'};color:#fff;font-size:10px;letter-spacing:0.05em;text-transform:uppercase;cursor:pointer;">Default</button>${ACCENTS.map(c => sq(c, 'badge-accent', c, a.accent)).join('')}</div>
      ${decalFinishDots('authored-finish', 'doorBadge', a.finish || (state.build.decalFinish || 'brushed-aluminum'))}
    </div>`;
}

function renderPanelEditor(key, a) {
  const lbl = t => `<div class="fr-editor-head">${t}</div>`;
  const sq = (color, val, cur) => `<button class="fr-sq ${cur === val ? 'on' : ''}" data-panel-color="${key}:${val}" title="${val}" style="background:${color};"></button>`;
  const PATTERNS = window.FR_PANEL_PATTERNS || [];
  const COLORS = ['#16f5d0', '#f5d000', '#ff1f8c', '#39ff14', '#b06bff', '#ff3b46', '#c7cba0', '#e8e9ee'];
  const design = { text: a.text, number: a.number, pattern: a.pattern, color: a.color };
  return `
    <div class="fr-editor">
      <div class="fr-editor-preview">
        <img data-panel-preview="${key}" src="${panelPreviewURL(design, key)}" alt="Panel preview" onerror="this.style.visibility='hidden'" style="width:100%;max-width:280px;aspect-ratio:${(1 / authoredAspect(key)).toFixed(3)}/1;border-radius:8px;box-shadow:0 6px 18px rgba(0,0,0,0.45);display:block;background:#0d0d10;" />
        <button data-panel-download="${key}" class="fr-btn-dl">Download PNG</button>
      </div>
      ${lbl('Starter Panels · one tap')}
      <div style="display:grid;grid-template-columns:repeat(auto-fill, minmax(80px, 1fr));gap:7px;">
        ${(window.FR_PANEL_PRESETS || []).map((pr, i) => `<button data-panel-preset="${key}:${i}" class="fr-chip fr-chip-cell">${pr.label}</button>`).join('')}
      </div>
      <button data-panel-surprise="${key}" class="fr-btn-solid">Surprise Me</button>
      ${lbl('Panel Text')}
      <input data-panel-text-input="${key}" type="text" maxlength="14" value="${(a.text || '').replace(/"/g, '&quot;')}" placeholder="FLAMERACER" autocomplete="off"
        style="width:100%;background:#0d0d10;border:1px solid rgba(255,255,255,0.16);border-radius:6px;color:#fff;font-family:var(--fr-display-font,sans-serif);font-size:18px;letter-spacing:0.14em;text-transform:uppercase;padding:11px 14px;outline:none;" />
      ${lbl('Unit Number <span style="opacity:.5;">(the corner box)</span>')}
      <input data-panel-number-input="${key}" type="text" maxlength="3" value="${(a.number || '').replace(/"/g, '&quot;')}" placeholder="07" autocomplete="off"
        style="width:100%;background:#0d0d10;border:1px solid rgba(255,255,255,0.16);border-radius:6px;color:#fff;font-family:var(--fr-display-font,sans-serif);font-size:20px;letter-spacing:0.2em;text-transform:uppercase;text-align:center;padding:10px 13px;outline:none;" />
      ${lbl('Pattern Band')}
      <div class="fr-chip-row">${PATTERNS.map(p => `<button data-panel-pattern="${key}:${p.id}" class="fr-chip ${a.pattern === p.id ? 'on' : ''}">${p.label}</button>`).join('')}</div>
      ${lbl('Colour')}
      <div style="display:flex;flex-wrap:wrap;gap:8px;">${COLORS.map(c => sq(c, c, a.color)).join('')}</div>
      ${decalFinishDots('authored-finish', key, a.finish || (state.build.decalFinish || 'brushed-aluminum'))}
    </div>`;
}

function renderSignatureSection() {
  const au = state.build.authored || {};
  const openKey = (state.ui && state.ui.editAuthored) || null;
  const ITEMS = [
    { key: 'doorBadge', label: 'Door Number Badge', where: 'Both doors ⇄', price: window.FR_DOOR_BADGE_PRICE || 11.99, icon: '◉',
      preview: a => doorBadgePreviewURL({ number: a.number, name: a.name, sub: a.sub, style: a.style, accent: a.accent }),
      editor: a => renderDoorBadgeEditor(a) },
    { key: 'roofPanel', label: 'Roof Tech Panel', where: 'Roof centre', price: window.FR_PANEL_STICKER_PRICE || 8.99, icon: '▣',
      preview: a => panelPreviewURL({ text: a.text, number: a.number, pattern: a.pattern, color: a.color }, 'roofPanel'),
      editor: a => renderPanelEditor('roofPanel', a) },
    { key: 'deckPanel', label: 'Rear Deck Panel', where: 'Rear deck', price: window.FR_PANEL_STICKER_PRICE || 8.99, icon: '▤',
      preview: a => panelPreviewURL({ text: a.text, number: a.number, pattern: a.pattern, color: a.color }, 'deckPanel'),
      editor: a => renderPanelEditor('deckPanel', a) },
  ];
  const item = it => {
    const a = au[it.key] || {}; const on = !!a.on; const isOpen = openKey === it.key;
    if (!on) return `
      <button class="fr-btn-add fr-btn-add-row" data-authored-toggle="${it.key}">
        <span>Add ${it.label}</span><span style="opacity:0.8;">${it.where} · +$${it.price.toFixed(2)}</span>
      </button>`;
    return `
      <div class="fr-decal-card selected" style="width:100%;">
        <div style="display:flex;gap:10px;align-items:center;">
          <img src="${it.preview(a)}" alt="${it.label}" style="width:96px;aspect-ratio:${(1 / authoredAspect(it.key)).toFixed(3)}/1;height:auto;border-radius:6px;object-fit:cover;background:#0d0d10;flex:none;" />
          <div style="flex:1;min-width:0;">
            <div class="fr-sticker-name">${it.label}</div>
            <div class="fr-sticker-price" style="color:var(--fr-accent-yellow);">${it.where} · +$${it.price.toFixed(2)}</div>
          </div>
          <button class="fr-btn-edit ${isOpen ? 'on' : ''}" data-authored-edit="${it.key}" style="flex:none;padding:8px 12px;">${isOpen ? '✎ Close' : '✎ Edit'}</button>
          <button class="fr-btn-x" data-authored-toggle="${it.key}" title="Remove" style="height:34px;">✕</button>
        </div>
        ${isOpen ? it.editor(a) : ''}
      </div>`;
  };
  return `
    <div class="fr-zone-section" style="margin-top:14px;">
      <div class="fr-zone-header"><div class="fr-zone-label">FR Signature Stickers</div><div class="fr-zone-meta">Owner-placed spots · fully customisable</div></div>
      <div class="fr-info-card" style="margin-bottom:12px;"><div class="fr-info-card-body">Three hand-placed sticker spots, each as customisable as the custom plate &amp; permits: the <b>door number badge</b> (a number plate for your doors, mirrored to both sides), and two <b>futuristic tech panels</b> (roof + rear deck) with your text, unit number and a pattern band. Download gives the print file.</div></div>
      <div style="display:flex;flex-direction:column;gap:10px;">${ITEMS.map(item).join('')}</div>
    </div>`;
}

// ── DECALS STEP · collapsible flow sections ───────────────────────────────
// The decals step is five big design systems stacked; folded sections with a
// sticky jump bar make it navigable. Same in-place class-flip pattern as the
// Body zones / Wheels sections (module-level state, no re-render on toggle).
let _decalSecOpen = null;
function initDecalSecOpen() {
  if (_decalSecOpen) return;
  _decalSecOpen = { finish: true, packs: true, signature: false, permits: false, banners: false };
}
function setDecalSecOpen(id, open) {
  if (!_decalSecOpen) return;
  _decalSecOpen[id] = !!open;
  const sec = root.querySelector(`.fr-flow-sec[data-flow-sec="${id}"]`);
  if (sec) {
    sec.classList.toggle('open', !!open);
    const hdr = sec.querySelector('[data-flow-toggle]');
    if (hdr) hdr.setAttribute('aria-expanded', String(!!open));
  }
  const chip = root.querySelector(`[data-flow-jump="${id}"]`);
  if (chip) chip.classList.toggle('active', !!open);
  if (open) scheduleThumbnails();
}

function renderDecalsStep() {
  initDecalSecOpen();
  const PACKS = window.FR_DECAL_PACKS || {};
  const packIds = Object.keys(PACKS);
  const activePackId = PACKS[state.build.decalPack] ? state.build.decalPack : packIds[0];
  const pack = PACKS[activePackId];
  const base = scene._decalAssetBase();
  const finishId = state.build.decalFinish || 'brushed-aluminum';
  const placedCount = state.build.decals.length + state.build.permits.length;

  // GLOBAL finish selector — one finish for every decal on the car.
  const finishBar = `
    <div class="fr-subsection-label">Finish · sets all (override each sticker below)</div>
    <div style="display:flex;gap:8px;margin:6px 0 14px;">
      ${window.FR_DECAL_FINISHES.map(f => `
        <button class="fr-finish-chip ${finishId === f.id ? 'selected' : ''}" data-global-finish="${f.id}"
          style="flex:1;display:flex;flex-direction:column;align-items:center;gap:6px;padding:10px 6px;background:var(--fr-bg-panel-row);border:1px solid ${finishId === f.id ? 'var(--fr-accent-yellow)' : 'var(--fr-border-subtle)'};cursor:pointer;border-radius:8px;">
          <div style="width:34px;height:34px;border-radius:2px;background:${f.gradient ? `linear-gradient(120deg, ${f.gradient.join(',')})` : f.hex};box-shadow:inset 0 0 0 1px rgba(255,255,255,0.15);"></div>
          <div style="font-family:var(--fr-mono-font);font-size:10px;letter-spacing:0.05em;text-transform:uppercase;color:var(--fr-text-secondary);text-align:center;line-height:1.2;">${f.name}${f.priceAdd > 0 ? `<br><span style="color:var(--fr-accent-yellow)">+$${f.priceAdd}</span>` : ''}</div>
        </button>`).join('')}
    </div>`;

  // PACK tabs — standard chips (natural width, no stretch), on-car count each.
  const packTabs = `
    <div class="fr-subsection-label">Sticker Pack</div>
    <div class="fr-chip-row" style="margin:6px 0 12px;">
      ${packIds.map(pid => {
        const p = PACKS[pid];
        const n = state.build.decals.filter(d => d.packId === pid).length;
        const on = pid === activePackId;
        return `<button class="fr-chip ${on ? 'on' : ''}" data-decal-pack="${pid}">${p.name}${n ? `<span class="fr-chip-count">${n}</span>` : ''}</button>`;
      }).join('')}
    </div>`;

  // DESIGN tiles for the active pack — uniform neutral cells, real names.
  // Caption = the design's NAME (FR_DECAL_NAMES, read off the actual art); the
  // designer-PDF № survives only as a small index on the status line. Selected
  // tile = crimson frame + count badge (×2 when the free mirror doubles it).
  const nameOf = (pid, page) => ((window.FR_DECAL_NAMES || {})[pid] || {})[page] || `${(PACKS[pid] || {}).name || pid} · ${page}`;
  const designs = packDesigns(pack);
  const packOnCount = state.build.decals.filter(x => x.packId === activePackId).length;
  const designCard = d => {
    const placed = state.build.decals.find(x => x.packId === activePackId && x.page === d.page);
    const isSel = !!placed;
    const thumb = base + d.primary.file;
    const hasMirror = d.kind === 'pair';
    const onCount = isSel ? ((placed.mirrored && hasMirror) ? 2 : 1) : 0;
    const name = nameOf(activePackId, d.page);
    return `
      <div class="fr-decal-card ${isSel ? 'selected' : ''}">
        <button class="fr-decal-card-toggle" data-decal-add="${activePackId}:${d.page}" title="${name} · ${isSel ? 'tap to remove' : 'tap to place'}${hasMirror ? ' · mirrors to both sides' : ''}">
          <div class="fr-sticker-thumb"><img src="${thumb}" alt="${name}" loading="lazy" />${isSel ? `<div class="fr-decal-check">${onCount > 1 ? '×2' : '✓'}</div>` : ''}</div>
          <div class="fr-sticker-meta">
            <div class="fr-sticker-name">${name}</div>
            <div class="fr-sticker-price">${isSel ? `<span class="fr-tile-oncar">On car${onCount > 1 ? ' ×2' : ''}</span>` : 'Included'} · №${d.page}${hasMirror ? ' ⇄' : ''}</div>
          </div>
        </button>
        ${isSel && hasMirror ? `
          <button class="fr-chip ${placed.mirrored ? 'on' : ''}" data-decal-mirror="${activePackId}:${d.page}" style="margin-top:6px;width:100%;text-align:center;">
            ⇄ Mirror${placed.mirrored ? ' ✓' : ' · free'}
          </button>` : ''}
        ${isSel ? decalFinishDots('decal-finish', `${activePackId}:${d.page}`, placed.finish || finishId) : ''}
      </div>`;
  };
  const designGrid = `
    <div class="fr-subsection-label">${pack.name} — ${designs.length} designs<span class="fr-subsection-meta">${packOnCount ? `${packOnCount} on car` : 'included with the pack'}</span></div>
    <div class="fr-decal-grid">${designs.map(designCard).join('')}</div>`;

  // RACING PERMITS — separate $2.99-each option.
  const permitCard = p => {
    const sel = state.build.permits.find(x => x.id === p.id);
    const isSel = !!sel;
    return `
      <div class="fr-decal-card ${isSel ? 'selected' : ''}">
        <button class="fr-decal-card-toggle" data-permit="${p.id}" title="${p.name} · ${isSel ? 'tap to remove' : 'tap to place'}">
          <div class="fr-sticker-thumb"><img src="${base + p.file}" alt="${p.name}" loading="lazy" />${isSel ? '<div class="fr-decal-check">✓</div>' : ''}</div>
          <div class="fr-sticker-meta">
            <div class="fr-sticker-name">${p.name}</div>
            <div class="fr-sticker-price">${isSel ? '<span class="fr-tile-oncar">On car</span> · ' : ''}+ $${p.price.toFixed(2)}</div>
          </div>
        </button>
        ${isSel ? decalFinishDots('permit-finish', p.id, sel.finish || finishId) : ''}
      </div>`;
  };
  const preMadePermitCount = state.build.permits.filter(p => !p.custom).length;
  const permitsHtml = `
    <div class="fr-zone-section" style="margin-top:18px;">
      <div class="fr-zone-header"><div class="fr-zone-label">Racing Permits</div><div class="fr-zone-meta">${preMadePermitCount ? `${preMadePermitCount} on car · ` : ''}$2.99 each · optional</div></div>
      <div class="fr-decal-grid">${(window.FR_RACING_PERMITS || []).map(permitCard).join('')}</div>
    </div>`;

  // An open editor must never hide inside a folded section — force its section
  // open (and remember it) before painting.
  if (state.ui) {
    if (state.ui.editAuthored) _decalSecOpen.signature = true;
    if (state.ui.editPermit) _decalSecOpen.permits = true;
    if (state.ui.editBanner) _decalSecOpen.banners = true;
  }

  // Section registry — id · number · title · header meta · jump label · badge count.
  const au = state.build.authored || {};
  const sigCount = ['doorBadge', 'roofPanel', 'deckPanel'].filter(k => au[k] && au[k].on).length + ((state.build.wingDecal && state.build.wingDecal.on) ? 1 : 0);
  const _cnt = n => n ? ` · ${n} on car` : '';
  const _finName = (window.FR_DECAL_FINISHES.find(f => f.id === finishId) || {}).name || '';
  const SECS = [
    { id: 'finish', num: '01', title: 'Finish', meta: _finName || 'applies to every sticker', nav: 'Finish', count: 0 },
    { id: 'packs', num: '02', title: 'Sticker Packs', meta: `${Object.keys(PACKS).length} packs${_cnt(state.build.decals.length)}`, nav: 'Packs', count: state.build.decals.length },
    { id: 'signature', num: '03', title: 'FR Signature Stickers', meta: `4 spots${_cnt(sigCount)}`, nav: 'Custom', count: sigCount },
    { id: 'permits', num: '04', title: 'Racing Permits', meta: `pre-made + custom${_cnt(state.build.permits.length)}`, nav: 'Permits', count: state.build.permits.length },
    { id: 'banners', num: '05', title: 'Sun Banners', meta: `windshield + sides${_cnt((state.build.banners || []).length)}`, nav: 'Banners', count: (state.build.banners || []).length },
  ];

  // Numbered flow header — now also the fold toggle, so the eye walks 01→05 AND
  // any section is one tap from open/closed. Chevron mirrors the zone accordions.
  const flowSec = (s, bodyHtml) => {
    const open = !!_decalSecOpen[s.id];
    return `
    <section class="fr-flow-sec ${open ? 'open' : ''}" data-flow-sec="${s.id}">
      <button class="fr-flow-head" data-flow-toggle="${s.id}" aria-expanded="${open}" title="${open ? 'Collapse' : 'Expand'} this section">
        <span class="fr-flow-num">${s.num}</span>
        <span class="fr-flow-title">${s.title}</span>
        <span class="fr-flow-rule"></span>
        <span class="fr-flow-meta">${s.meta || ''}</span>
        <span class="fr-flow-chev" aria-hidden="true">›</span>
      </button>
      <div class="fr-flow-body">${bodyHtml}</div>
    </section>`;
  };

  // Sticky jump bar — same segmented instrument strip as the Body step, so the
  // two long steps navigate identically. Badge = items already on the car there.
  const flowNav = `
    <div class="fr-zone-jump">
      <div class="fr-zone-jump-track">
        ${SECS.map(s => `<button class="fr-zone-jump-chip ${_decalSecOpen[s.id] ? 'active' : ''}" data-flow-jump="${s.id}">
          <span class="fr-zone-jump-label">${s.nav}</span>
          ${s.count ? `<span class="fr-zone-jump-badge">${s.count}</span>` : ''}
        </button>`).join('')}
      </div>
    </div>`;

  const bodyOf = {
    finish: finishBar,
    packs: packTabs + designGrid,
    signature: renderSignatureSection() + renderWingDecalSection(),
    permits: permitsHtml + renderCustomPermitsSection(),
    banners: renderCustomBannersSection(),
  };

  return `
    <div class="fr-section-title">STICKERS &amp; GRAPHICS</div>
    <div class="fr-section-subtitle">${placedCount} on car · packs · signature spots · permits · banners</div>
    ${flowNav}
    <div class="fr-info-card">
      <div class="fr-info-card-label">How it works</div>
      <div class="fr-info-card-body">Work top to bottom: pick a <b>finish</b>, lay down <b>pack designs</b>, then personalise the <b>signature spots</b>, <b>permits</b> and <b>banners</b> — everything conforms to the bodywork like real vinyl, and every custom design has a print-file download. Tap a numbered header to fold or open its section.</div>
    </div>
    ${SECS.map(s => flowSec(s, bodyOf[s.id])).join('')}
  `;
}

// Cached mini thumbnail of each background style for the style picker (light:
// generated once per style+colour, reused). Drawn with the scene's _drawPlateBg.
function plateBgThumb(styleId, bgColor) {
  const key = styleId + '|' + (bgColor || '');
  window.__frPlateThumbs = window.__frPlateThumbs || {};
  if (window.__frPlateThumbs[key]) return window.__frPlateThumbs[key];
  const w = 110, h = 54, cv = document.createElement('canvas'); cv.width = w; cv.height = h;
  const ctx = cv.getContext('2d');
  try { scene._drawPlateBg(ctx, w, h, { bgStyle: styleId, bgColor: bgColor || '#f2f2f2' }); }
  catch (e) { ctx.fillStyle = bgColor || '#222'; ctx.fillRect(0, 0, w, h); }
  const url = cv.toDataURL('image/png');
  window.__frPlateThumbs[key] = url;
  return url;
}

// Live 2D preview of the custom plate for the editor. ONE cheap 512x256 draw per
// EDIT (not per frame) → no lag. Reuses scene._paintCustomPlate so it matches the
// real 3D plate exactly.
function platePreviewURL(plate) {
  try {
    const W = 512, H = 256, cv = document.createElement('canvas'); cv.width = W; cv.height = H;
    scene._paintCustomPlate(cv.getContext('2d'), W, H, plate);
    return cv.toDataURL('image/png');
  } catch (e) { return ''; }
}

// (The preset-plate thumbnail cache lived here. It only ever served the FR Studio
// `rendered:true` cards, which were retired from the browser on 2026-08-06 — the
// owner stocks the photo series only — so the cache had nothing left to memoise.
// The plate ENGINE itself is untouched: platePreviewURL / _paintCustomPlate still
// drive the Custom plate builder and still paint restored studio plates.)

// ── PLATES STEP · collapsible sections ────────────────────────────────────
// Same fold component as the Wheels/Body steps so every step's sections read
// as one system. Module-level view state; in-place class flips, no re-render.
let _plateSecOpen = null;
function initPlateSecOpen() {
  if (_plateSecOpen) return;
  _plateSecOpen = { presets: true, custom: true, mount: true };
}
function setPlateSecOpen(id, open) {
  if (!_plateSecOpen) return;
  _plateSecOpen[id] = !!open;
  const sec = root.querySelector(`.fr-zone-acc[data-psec="${id}"]`);
  if (sec) {
    sec.classList.toggle('open', !!open);
    const hdr = sec.querySelector('[data-psec-toggle]');
    if (hdr) hdr.setAttribute('aria-expanded', String(!!open));
  }
}

function renderPlatesStep() {
  initPlateSecOpen();
  // THE BROWSABLE CATALOG = the PHOTO SERIES only. The FR Studio `rendered:true`
  // designs were retired from the browser (owner 2026-08-06 — only the photo
  // plates are actually stocked). They remain in FR_LICENSE_PLATES so a saved or
  // shared build that carries one still resolves and still paints, but a fresh
  // build never sees them. Every count + grid below reads THIS list, not the raw
  // catalog, so the numbers can't drift back out of sync.
  const catalog = (window.FR_LICENSE_PLATES || []).filter(p => !p.rendered);
  const selected = state.build.licensePlate;
  const currentFinish = selected?.finish || window.FR_DECAL_FINISHES[0];
  // Fresh-build default = FRONT ONLY (owner decision 2026-08-05); the rear
  // bracket is opt-in via "+ Add rear plate" below.
  const currentMount = state.build.plateMount || window.FR_PLATE_MOUNTS.find(m => m.id === 'front') || window.FR_PLATE_MOUNTS[0];
  const customSelected = !!selected?.custom;
  const customCard = `
    <button class="fr-plate-card ${customSelected ? 'selected' : ''}" data-plate="__custom__">
      <div class="fr-plate-thumb" style="display:flex;align-items:center;justify-content:center;position:relative;overflow:hidden;background:conic-gradient(from 210deg at 30% 30%, #f5d000, #ff6a1a, #e91e8c, #6a2c91, #16c5e8, #0a7a4a, #f5d000);">
        <div style="position:absolute;inset:0;background:rgba(8,8,12,0.62);"></div>
        <div style="position:relative;text-align:center;color:#fff;">
          <div style="font-size:26px;line-height:1;">✎</div>
          <div style="font-size:11px;font-weight:800;letter-spacing:0.1em;margin-top:5px;">DESIGN YOUR OWN</div>
          <div style="font-size:8px;letter-spacing:0.08em;opacity:0.82;margin-top:3px;">TEXT · BG · EMOJI · REGION</div>
        </div>
        ${customSelected ? `<div style="position:absolute;top:6px;right:6px;width:22px;height:22px;border-radius:50%;background:var(--fr-accent-yellow);color:#fff;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:700;z-index:3;">&check;</div>` : ''}
      </div>
      <div class="fr-plate-price"${customSelected ? ' style="background:var(--fr-accent-yellow);color:#fff;font-weight:700;"' : ''}>${customSelected ? '✓ SELECTED' : 'CUSTOM · $12.99'}</div>
    </button>`;

  const plateCard = plate => {
    const isSelected = selected?.id === plate.id;
    const thumbSrc = window.FR_IMG(plate.image);
    // Fallback chain for the OG image plates: Shopify Files URL → THEME ASSETS URL
    // (same base the decal PNGs load from — works locally AND on the live store) →
    // bare relative → hide. The old onerror compared by substring, so the failing
    // CDN URL (which CONTAINS the filename) skipped the fallback and hid the image.
    const alts = [scene._decalAssetBase() + plate.image, plate.image].join('|');
    return `
      <button class="fr-plate-card ${isSelected ? 'selected' : ''}" data-plate="${plate.id}">
        <div class="fr-plate-thumb">
          <img src="${thumbSrc}" data-alts="${alts}" data-tried="0" alt="${plate.name}" onerror="(function(im){var alts=(im.dataset.alts||'').split('|').filter(Boolean);var i=+im.dataset.tried;if(i<alts.length){im.dataset.tried=i+1;im.src=alts[i];}else{im.parentElement.classList.add('fr-thumb-missing');im.style.display='none';}})(this)" />
          <div class="fr-plate-thumb-name">${plate.name}</div>
          ${isSelected ? `<div style="position:absolute;top:6px;right:6px;width:22px;height:22px;border-radius:50%;background:var(--fr-accent-yellow);color:#fff;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:700;z-index:3;box-shadow:0 2px 8px rgba(0,0,0,0.45);">&check;</div>` : ''}
        </div>
        <div class="fr-plate-price"${isSelected ? ' style="background:var(--fr-accent-yellow);color:#fff;font-weight:700;letter-spacing:0.1em;"' : ''}>${isSelected ? '✓ SELECTED' : '+ $' + plate.price.toFixed(2)}</div>
      </button>
    `;
  };

  // The big rear diffusers block the rear plate — front-only (diffuser stays on).
  const diffuserOn = state.build.parts.has('flr-16') || state.build.parts.has('flr-18');
  const diffuserName = state.build.parts.has('flr-16') ? 'Extended Profile Diffuser' : 'Aggressive Strut Diffuser';
  const finishPicker = selected ? `
    <div class="fr-subsection-label" style="margin-top:24px">Plate Finish</div>
    <div class="fr-finish-row">
      ${window.FR_DECAL_FINISHES.map(f => `
        <button class="fr-finish-chip ${currentFinish.id === f.id ? 'selected' : ''} ${f.tier === 'rare' ? 'rare' : ''} ${f.tier === 'premium' ? 'premium' : ''}" data-plate-finish="${f.id}">
          <div class="fr-finish-chip-swatch" style="background:${f.gradient ? `linear-gradient(90deg, ${f.gradient.join(',')})` : f.hex}"></div>
          <div class="fr-finish-chip-name">${f.name}${f.priceAdd > 0 ? ` +$${f.priceAdd}` : ''}</div>
        </button>
      `).join('')}
    </div>
    <div class="fr-mount-note"${diffuserOn ? ' style="border:1px solid rgba(209,15,47,0.45);color:var(--fr-accent-yellow,#f5d000);"' : ''}>${diffuserOn
      ? `⚠ Your <strong>${diffuserName}</strong> occupies the rear, so the plate mounts <strong>front&nbsp;only</strong> — the rear options are unavailable. (Your diffuser stays on.)`
      : `Mounted <strong>front only</strong> by default. Want the rear too? <strong>+ Add rear plate</strong> (+$5) — or run rear-only. Tapping a mount flies the camera to that end.`}</div>
    <div class="fr-subsection-label" style="margin-top:20px">Mounting</div>
    <div class="fr-mount-row">
      ${(() => {
        // FRONT ONLY leads (the default); the rear bracket is a clear opt-in.
        // Prices display as DELTAS from the front-only default; the absolute
        // math (plate.price + mount.priceAdjust) is unchanged.
        const M = window.FR_PLATE_MOUNTS || [];
        const byId = id => M.find(m => m.id === id);
        const ordered = [byId('front'), byId('both'), byId('rear')].filter(Boolean);
        const frontAdj = (byId('front') || {}).priceAdjust || 0;
        const NAME = { front: 'Front Only', both: '+ Add Rear Plate', rear: 'Rear Only' };
        const COPY = { front: 'Default · single front bracket', both: 'Front + rear, fully mounted', rear: 'Single rear bracket' };
        return ordered.map(m => {
          const blocked = diffuserOn && m.id !== 'front';
          const sel = blocked ? false : (diffuserOn ? m.id === 'front' : currentMount.id === m.id);
          const rel = (m.priceAdjust || 0) - frontAdj;
          return `
        <button class="fr-mount-option ${sel ? 'selected' : ''}" ${blocked ? 'disabled title="Unavailable with your diffuser" style="opacity:0.36;cursor:not-allowed;"' : `data-plate-mount="${m.id}"`}>
          ${sel ? `<div style="position:absolute;top:6px;right:6px;width:20px;height:20px;border-radius:50%;background:var(--fr-accent-yellow,#f5d000);color:#fff;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;z-index:2;box-shadow:0 2px 6px rgba(0,0,0,0.4);">✓</div>` : ''}
          <div class="fr-mount-name">${NAME[m.id] || m.name}</div>
          <div class="fr-mount-desc">${blocked ? 'Unavailable — diffuser is on' : (COPY[m.id] || m.description)}</div>
          ${rel !== 0 && !blocked ? `<div class="fr-mount-adjust">${rel > 0 ? '+' : ''}$${rel}</div>` : ''}
        </button>`;
        }).join('');
      })()}
    </div>
    <div class="fr-subsection-label" style="margin-top:20px">Bracket Colour</div>
    <div style="display:flex;flex-wrap:wrap;gap:10px;">
      ${(window.FR_PLATE_BRACKET_COLORS || []).map(c => {
        const on = (state.build.bracketColor || 'carbon') === c.id;
        const swatch = c.hex || 'repeating-linear-gradient(45deg,#16181c,#16181c 4px,#23262c 4px,#23262c 8px)';
        return `<button data-bracket-color="${c.id}" title="${c.name}" style="display:flex;flex-direction:column;align-items:center;gap:5px;cursor:pointer;background:none;border:none;padding:0;">
          <span style="width:34px;height:34px;border-radius:2px;background:${swatch};border:2px solid ${on ? '#fff' : 'rgba(255,255,255,0.18)'};${on ? 'box-shadow:0 0 0 1.5px var(--fr-accent-yellow,#d10f2f);' : ''}"></span>
          <span style="font-size:9px;letter-spacing:0.03em;color:${on ? 'var(--fr-accent-yellow,#f5d000)' : 'var(--fr-text-secondary)'};">${c.name}</span>
        </button>`;
      }).join('')}
    </div>
    <button class="fr-plate-remove" data-plate-remove>Remove plate</button>
  ` : '';

  return `
    <div class="fr-section-title">LICENSE PLATES</div>
    <div class="fr-section-subtitle">${catalog.length} designs · Step 05</div>
    <div class="fr-info-card">
      <div class="fr-info-card-label">How it works</div>
      <div class="fr-info-card-body">Pick a design, then a finish. Plates mount <strong>front only</strong> by default — add the rear bracket below if you want both ends. Selecting a plate flies the camera to the front bumper; the mount buttons jump it front or rear.</div>
    </div>
    ${`
    <div class="fr-zone-section fr-zone-acc ${_plateSecOpen.presets ? 'open' : ''}" data-psec="presets">
      <div class="fr-zone-header" data-psec-toggle="presets" role="button" tabindex="0" aria-expanded="${_plateSecOpen.presets}" style="cursor:pointer;" title="${_plateSecOpen.presets ? 'Collapse' : 'Expand'} this section">
        <div class="fr-zone-label"><span class="fr-zone-acc-chev" aria-hidden="true">›</span>Preset Designs</div>
        <div class="fr-zone-meta">
          <span>${catalog.length} designs${selected && !customSelected ? ` · ${selected.name}` : ''}</span>
          <span class="fr-zone-acc-view" data-plate-focus="front" role="button" title="View front plate">◎ view</span>
        </div>
      </div>
      <div class="fr-zone-acc-body">
        <div class="fr-plate-grid">${catalog.map(plateCard).join('')}</div>
      </div>
    </div>`}
    ${customSelected ? (() => { _plateSecOpen.custom = true; return ''; })() : ''}
    <div class="fr-zone-section fr-zone-acc ${_plateSecOpen.custom ? 'open' : ''}" data-psec="custom">
      <div class="fr-zone-header" data-psec-toggle="custom" role="button" tabindex="0" aria-expanded="${_plateSecOpen.custom}" style="cursor:pointer;" title="${_plateSecOpen.custom ? 'Collapse' : 'Expand'} this section">
        <div class="fr-zone-label"><span class="fr-zone-acc-chev" aria-hidden="true">›</span>Custom Plate</div>
        <div class="fr-zone-meta"><span>Your own text${customSelected ? ' · selected' : ' · +$12.99'}</span></div>
      </div>
      <div class="fr-zone-acc-body">
      <div class="fr-plate-grid fr-plate-grid-custom">${customCard}</div>
      ${customSelected ? (() => {
        const BGS = ['#f2f2f2', '#c0c2c8', '#0e1116', '#1340c8', '#0a2a6b', '#e01f2d', '#0a7a4a', '#16c5e8', '#f5d000', '#ff6a1a', '#6a2c91', '#e91e8c'];
        const TXTS = ['#111114', '#ffffff', '#f5d000', '#e01f2d', '#1340c8', '#0a7a4a'];
        // TASK 2: cyber / tactical / bloodtech / noir added to the plate style picker.
        const STYLES = [['solid', 'Solid'], ['gradient', 'Fade'], ['stripes', 'Racing'], ['carbon', 'Carbon'], ['sunset', 'Sunset'], ['skyline', 'City'], ['neon', 'Neon'], ['galaxy', 'Galaxy'], ['aurora', 'Aurora'], ['circuit', 'Circuit'], ['cyber', 'Cyber'], ['tactical', 'Tactical'], ['bloodtech', 'Bloodtech'], ['noir', 'Noir']];
        const FONTS = [['Block', '"Arial Black", Arial'], ['Impact', 'Impact, "Arial Narrow Bold", sans-serif'], ['Clean', 'Arial, sans-serif'], ['Display', '"Bebas Neue", Impact, sans-serif'], ['Condensed', '"Barlow Condensed", sans-serif'], ['Mono', '"JetBrains Mono", monospace'], ['Type', '"Courier New", monospace'], ['Serif', 'Georgia, serif']];
        const EMOJIS = ['🔥', '💀', '⚡', '🏁', '🐉', '👑', '💸', '🏎️', '😈', '☠️', '🦅', '🚀', '🤘', '😎', '🔱', '👹', '🌪️', '💢'];
        const curBg = selected.bgColor || '#f2f2f2', curTxt = selected.textColor || '#111114', curFont = selected.font || '"Arial Black", Arial', curStyle = selected.bgStyle || 'solid', curEmoji = selected.emoji || '';
        const curFrame = selected.frame || 'thin', curLetter = selected.lettering || 'flat', curRegion = selected.region || 'none', curSerial = selected.serial !== false, curSig = !!selected.signature;
        // TASK 3: shared .fr-editor / .fr-editor-head scaffold (see editor CSS block).
        const lbl = t => `<div class="fr-editor-head">${t}</div>`;
        const sq = (color, attr, val, cur) => `<button class="fr-sq ${cur === val ? 'on' : ''}" data-${attr}="${val}" title="${val}" style="background:${color};"></button>`;
        const chip = (attr, val, label, cur) => `<button class="fr-chip ${cur === val ? 'on' : ''}" data-${attr}="${val}">${label}</button>`;
        return `
        <div class="fr-editor">
          <div class="fr-editor-preview">
            <img data-plate-preview src="${platePreviewURL(selected)}" alt="Plate preview" onerror="this.style.visibility='hidden'" style="width:100%;max-width:300px;aspect-ratio:2 / 1;border-radius:6px;box-shadow:0 6px 18px rgba(0,0,0,0.45);display:block;" />
            <button data-plate-download class="fr-btn-dl">Download PNG</button>
          </div>
          <div class="fr-editor-head">Your Text</div>
          <input id="fr-custom-plate-input" data-custom-plate-input type="text" maxlength="8" value="${(selected.text || '').replace(/"/g, '&quot;')}" placeholder="YOUR TEXT" autocomplete="off"
            style="width:100%;background:#0d0d10;border:1px solid var(--fr-border-emphasis,rgba(255,255,255,0.16));border-radius:6px;color:#fff;font-family:var(--fr-display-font,sans-serif);font-size:20px;letter-spacing:0.16em;text-transform:uppercase;padding:12px 14px;outline:none;" />
          ${lbl('Mini Text <span style="opacity:.5;">(optional)</span>')}
          <input data-custom-plate-mini type="text" maxlength="16" value="${(selected.miniText || '').replace(/"/g, '&quot;')}" placeholder="REGION · SUBTITLE" autocomplete="off"
            style="width:100%;background:#0d0d10;border:1px solid var(--fr-border-emphasis,rgba(255,255,255,0.16));border-radius:6px;color:#fff;font-family:var(--fr-display-font,sans-serif);font-size:13px;letter-spacing:0.12em;text-transform:uppercase;padding:9px 12px;outline:none;" />
          ${lbl('Starter Plates · one tap')}
          <div style="display:grid;grid-template-columns:repeat(auto-fill, minmax(80px, 1fr));gap:7px;">
            ${(window.FR_PLATE_PRESETS || []).map((p, i) => `<button data-plate-preset="${i}" class="fr-chip fr-chip-cell">${p.label}</button>`).join('')}
          </div>
          <button data-plate-surprise class="fr-btn-solid">Surprise Me</button>
          ${lbl('Background Style')}
          <div style="display:grid;grid-template-columns:repeat(auto-fill, minmax(72px, 1fr));gap:7px;">${STYLES.map(([v, l]) => `
            <button data-plate-bgstyle="${v}" title="${l}" style="display:flex;flex-direction:column;align-items:center;gap:5px;padding:6px;border-radius:2px;background:${curStyle === v ? 'rgba(209,15,47,0.14)' : 'rgba(255,255,255,0.04)'};border:1px solid ${curStyle === v ? 'var(--fr-accent-yellow,#f5d000)' : 'rgba(255,255,255,0.14)'};cursor:pointer;">
              <img src="${plateBgThumb(v, curBg)}" alt="${l}" style="width:100%;aspect-ratio:2 / 1;border-radius:3px;display:block;object-fit:cover;" />
              <span style="font-size:9.5px;letter-spacing:0.03em;color:${curStyle === v ? 'var(--fr-accent-yellow,#f5d000)' : 'var(--fr-text-secondary)'};">${l}</span>
            </button>`).join('')}</div>
          ${lbl('Background Color <span style="opacity:.5;">(Solid / Fade / Racing)</span>')}
          <div style="display:flex;flex-wrap:wrap;gap:8px;">${BGS.map(c => sq(c, 'plate-bg', c, curBg)).join('')}</div>
          ${lbl('Text Color')}
          <div style="display:flex;flex-wrap:wrap;gap:8px;">${TXTS.map(c => sq(c, 'plate-textcolor', c, curTxt)).join('')}</div>
          ${lbl('Emoji')}
          <div style="display:grid;grid-template-columns:repeat(auto-fill, minmax(40px, 1fr));gap:7px;">
            <button data-plate-emoji="__none__" style="height:40px;border-radius:2px;background:${curEmoji === '' ? 'var(--fr-accent-yellow,#f5d000)' : 'rgba(255,255,255,0.05)'};color:${curEmoji === '' ? '#fff' : '#fff'};border:1px solid rgba(255,255,255,0.15);font-size:10px;letter-spacing:0.04em;text-transform:uppercase;cursor:pointer;">None</button>
            ${EMOJIS.map(e => `<button data-plate-emoji="${e}" style="height:40px;border-radius:2px;background:${curEmoji === e ? 'var(--fr-accent-yellow,#f5d000)' : 'rgba(255,255,255,0.05)'};border:1px solid rgba(255,255,255,0.15);font-size:20px;line-height:1;cursor:pointer;">${e}</button>`).join('')}
          </div>
          ${lbl('Font')}
          <div style="display:flex;flex-wrap:wrap;gap:8px;">${FONTS.map(([label, f]) => `<button data-plate-font="${f.replace(/"/g, '&quot;')}" style="padding:7px 12px;border-radius:2px;background:${curFont === f ? 'var(--fr-accent-yellow,#f5d000)' : 'rgba(255,255,255,0.05)'};color:${curFont === f ? '#fff' : '#fff'};border:1px solid rgba(255,255,255,0.15);font-family:${f};font-size:13px;cursor:pointer;">${label}</button>`).join('')}</div>
          ${lbl('Frame + Bolts')}
          <div style="display:flex;flex-wrap:wrap;gap:8px;">${[['thin', 'Thin'], ['none', 'None'], ['black', 'Black'], ['chrome', 'Chrome'], ['carbon', 'Carbon']].map(([v, l]) => chip('plate-frame', v, l, curFrame)).join('')}</div>
          ${lbl('Lettering')}
          <div style="display:flex;flex-wrap:wrap;gap:8px;">${[['flat', 'Flat'], ['raised', 'Raised'], ['stamped', 'Stamped']].map(([v, l]) => chip('plate-lettering', v, l, curLetter)).join('')}</div>
          ${lbl('Region Accent · country vibes')}
          <div style="display:flex;flex-wrap:wrap;gap:8px;">${[['none', 'None'], ['eu', 'EU'], ['germany', 'Germany'], ['france', 'France'], ['italy', 'Italy'], ['uk', 'UK'], ['usa', 'USA'], ['california', 'Cali'], ['japan', 'Japan'], ['dubai', 'Dubai'], ['miami', 'Miami']].map(([v, l]) => chip('plate-region', v, l, curRegion)).join('')}</div>
          ${lbl('Serial')}
          <div style="display:flex;gap:8px;">${chip('plate-serial', 'on', '1 of 1 · On', curSerial ? 'on' : '')}${chip('plate-serial', 'off', 'Off', curSerial ? '' : 'off')}</div>
          ${lbl('Signature')}
          <div style="display:flex;gap:8px;">${chip('plate-signature', 'on', '✒ Signature', curSig ? 'on' : '')}${chip('plate-signature', 'off', 'Off', curSig ? '' : 'off')}</div>
        </div>`;
      })() : ''}
      </div>
    </div>
    ${selected ? `
    <div class="fr-zone-section fr-zone-acc ${_plateSecOpen.mount ? 'open' : ''}" data-psec="mount">
      <div class="fr-zone-header" data-psec-toggle="mount" role="button" tabindex="0" aria-expanded="${_plateSecOpen.mount}" style="cursor:pointer;" title="${_plateSecOpen.mount ? 'Collapse' : 'Expand'} this section">
        <div class="fr-zone-label"><span class="fr-zone-acc-chev" aria-hidden="true">›</span>Finish &amp; Mounting</div>
        <div class="fr-zone-meta"><span>${currentFinish.name} · ${({ front: 'Front only', both: 'Front + rear', rear: 'Rear only' })[currentMount.id] || 'Front only'}</span></div>
      </div>
      <div class="fr-zone-acc-body">${finishPicker}</div>
    </div>` : ''}
  `;
}

function getAtelierInventory(paint) {
  // Legacy function kept for backwards compat — atelier removed
  return 0;
}

function renderFinishStep() {
  const b = state.build;
  const plate = b.licensePlate;
  const money = v => (v != null ? `$${(+v).toFixed(2)}` : '');
  // A summary line. `rm` = a data-attr string that makes a ✕ remove button.
  const row = (label, value, price, rm) => `
    <div class="fr-summary-row" style="align-items:center;">
      <span>${label}</span>
      <span style="display:flex;align-items:center;gap:10px;justify-content:flex-end;text-align:right;">
        <span>${value}${price != null && price !== 0 ? ` · ${money(price)}` : (price === 0 ? ' · Incl.' : '')}</span>
        ${rm ? `<button ${rm} title="Remove" style="flex:none;width:20px;height:20px;border-radius:50%;background:rgba(255,60,60,0.14);border:1px solid rgba(255,90,90,0.45);color:#ff8a8a;cursor:pointer;font-size:11px;line-height:1;display:flex;align-items:center;justify-content:center;">✕</button>` : ''}
      </span>
    </div>`;
  const header = t => `<div class="fr-subsection-label" style="margin-top:18px;">${t}</div>`;

  // Body parts — one removable line each. Rare-tier finishes add a flat premium
  // (FR_RARE_FINISH_PRICE); standard finishes are free, so the line just shows the part price.
  const _rareFinPrice = (window.FR_RARE_FINISH_PRICE != null ? window.FR_RARE_FINISH_PRICE : 12);
  const partRows = [...b.parts.values()].map(({ part, finish }) => {
    const isRare = finish?.tier === 'rare';
    const label = `${finish?.name || 'Matte Black'}${isRare ? ' · Rare effect' : ''}`;
    return row(part.name, label, (part.price || 0) + (isRare ? _rareFinPrice : 0), `data-remove-part="${part.id}"`);
  }).join('') || `<div class="fr-summary-row" style="opacity:0.5;"><span>No parts added</span><span>—</span></div>`;

  // Decals — one removable line per design + permit, each with its OWN finish.
  const _finOf = id => (window.FR_DECAL_FINISHES.find(f => f.id === id) || {});
  // Per-PACK base charge — one $8 line per distinct designer pack used (so the
  // pack fee from calculateTotalBreakdown is itemised, not hidden). Removing all
  // of a pack's decals removes its line automatically.
  const _packPrice = (window.FR_DECAL_PACK_PRICE != null ? window.FR_DECAL_PACK_PRICE : 8);
  const _packRows = [...new Map((b.decals || []).filter(d => d.packId).map(d => [d.packId, d.packName])).entries()]
    .map(([, name]) => row(`${name} Pack`, 'Designer decal set', _packPrice)).join('');
  const decalRows = (_packRows + b.decals.map(d => {
        const fin = _finOf(d.finish || b.decalFinish);
        return row(`${d.packName} · ${d.page}${d.mirrored ? ' ⇄' : ''}`, fin.name || 'Brushed Aluminum', fin.priceAdd || 0, `data-remove-decal="${d.packId}:${d.page}"`);
      }).join('')
    + b.permits.map(p => {
        const fin = _finOf(p.finish || b.decalFinish);
        return row(p.name, `Permit · ${fin.name || 'Brushed Aluminum'}`, (p.price || 0) + (fin.priceAdd || 0), `data-remove-permit="${p.id}"`);
      }).join('')
    + (b.banners || []).map(bn => {
        const fin = _finOf(bn.finish || b.decalFinish);
        return row(bn.name, `Banner · ${fin.name || 'Brushed Aluminum'}${bn.mirror ? ' ⇄' : ''}`, (bn.price || 0) + (fin.priceAdd || 0), `data-remove-banner="${bn.id}"`);
      }).join('')
    + ((b.wingDecal && b.wingDecal.on) ? (() => {
        const fin = _finOf(b.wingDecal.finish || b.decalFinish);
        return row(`Wing: ${(b.wingDecal.text || 'FLAMERACER')}`, `Rear wing · ${fin.name || 'Brushed Aluminum'}`, (window.FR_WING_DECAL_PRICE || 6.99) + (fin.priceAdd || 0), `data-remove-wing="1"`);
      })() : '')
    + ((b.authored && b.authored.doorBadge && b.authored.doorBadge.on) ? (() => {
        const a = b.authored.doorBadge, fin = _finOf(a.finish || b.decalFinish);
        return row(`Door Badge: #${a.number || '07'}`, `Both doors ⇄ · ${fin.name || 'Brushed Aluminum'}`, (window.FR_DOOR_BADGE_PRICE || 11.99) + (fin.priceAdd || 0), `data-remove-authored="doorBadge"`);
      })() : '')
    + ((b.authored && b.authored.roofPanel && b.authored.roofPanel.on) ? (() => {
        const a = b.authored.roofPanel, fin = _finOf(a.finish || b.decalFinish);
        return row(`Roof Panel: ${a.text || 'FLAMERACER'}`, `Roof · ${fin.name || 'Brushed Aluminum'}`, (window.FR_PANEL_STICKER_PRICE || 8.99) + (fin.priceAdd || 0), `data-remove-authored="roofPanel"`);
      })() : '')
    + ((b.authored && b.authored.deckPanel && b.authored.deckPanel.on) ? (() => {
        const a = b.authored.deckPanel, fin = _finOf(a.finish || b.decalFinish);
        return row(`Deck Panel: ${a.text || 'NO STEP'}`, `Rear deck · ${fin.name || 'Brushed Aluminum'}`, (window.FR_PANEL_STICKER_PRICE || 8.99) + (fin.priceAdd || 0), `data-remove-authored="deckPanel"`);
      })() : ''))
    || `<div class="fr-summary-row" style="opacity:0.5;"><span>No decals added</span><span>—</span></div>`;

  // Atelier paint & trim — custom colour / two-tone split / per-area trim, each a
  // real removable line at its real price. (These all roll into the "Body" pill in
  // calculateTotalBreakdown; here they get itemised so nothing is a hidden charge.)
  const _allCC = [].concat(window.FR_CUSTOM_COLORS || [], window.FR_SATIN_COLORS || [], window.FR_PEARL_COLORS || []);
  const _ccName = hex => (_allCC.find(c => c.hex.toLowerCase() === (hex || '').toLowerCase()) || {}).name
    || (b.paint.hex && b.paint.hex.toLowerCase() === (hex || '').toLowerCase() ? b.paint.name : hex) || '—';
  const _cap = s => s.charAt(0).toUpperCase() + s.slice(1);
  const _customPrice = window.FR_CUSTOM_PAINT_PRICE || 99;
  const _trimPrice = window.FR_TRIM_AREA_PRICE || 25;
  const _carbonPrice = window.FR_CARBON_WEAVE_PRICE || 199;
  const cu = b.custom || {}, sp = b.split || {}, cb = b.carbon || {};
  const atelierRows = [];
  if (cb.on) {
    const clabel = `${cb.tint ? _ccName(cb.tint) + ' tint' : 'Raw carbon'}${sp.on ? ` · Two-Tone (${_ccName(sp.colorA)}/${_ccName(sp.colorB)})` : ''}${cb.split ? ' · Centre split' : ''}`;
    atelierRows.push(row('Carbon Fibre Weave', clabel, _carbonPrice, 'data-remove-carbon'));
  }
  if (cu.on || (sp.on && !cb.on)) {
    // ONE $99 charge covers the custom colour and/or split (pearl included).
    const label = sp.on
      ? `${_ccName(sp.colorA)} / ${_ccName(sp.colorB)}${(sp.parts || []).length ? ' · +' + sp.parts.length + ' part' + (sp.parts.length > 1 ? 's' : '') : ''}`
      : `${_ccName(cu.color)}${cu.finish === 'satin' ? ' · Satin' : ''}${cu.pearl ? ' · Pearl' : ''}`;
    atelierRows.push(row(sp.on ? 'Custom Paint · Two-Tone Split' : 'Custom Paint', label, _customPrice, 'data-remove-custompaint'));
  }
  const _trimItems = [
    ...(b.trimColor ? [['all', 'All Trim', b.trimColor]] : []),
    ...Object.entries(b.trimAreas || {}).filter(([, h]) => h).map(([a, h]) => [a, _cap(a), h]),
  ];
  if (_trimItems.length) {
    if (cu.on || sp.on) {
      // custom paint already selected → discounted $25 per area, itemised
      _trimItems.forEach(([area, name, hex]) => atelierRows.push(row('Custom Trim · ' + name, _ccName(hex), _trimPrice, `data-remove-trim="${area}"`)));
    } else {
      // standalone → flat $100 for the whole trim job; list the painted areas
      const names = _trimItems.map(([, name, hex]) => `${name} · ${_ccName(hex)}`).join(', ');
      atelierRows.push(row('Custom Trim', names, (window.FR_TRIM_STANDALONE_PRICE || 100), 'data-remove-trim="__alltrim"'));
    }
  }
  const atelierHtml = atelierRows.join('');

  return `
    <div class="fr-section-title">REVIEW BUILD</div>
    <div class="fr-section-subtitle">Your full spec · tap ✕ to remove · Step 06</div>
    <div class="fr-build-summary">
      ${(() => {
        const bd = calculateTotalBreakdown();
        // Restrained cost palette — one crimson lead + a graphite ramp (the neon
        // categorical set read arcade, not atelier).
        const CATS = [['foundation', 'Foundation', 0, '#d10f2f'], ['wheels', 'Wheels', 1, '#c9ced6'], ['body', 'Body', 2, '#9aa0ab'], ['decals', 'Decals', 3, '#6e7581'], ['plate', 'Plate', 4, '#4a5058']];
        return `
        <div class="fr-subsection-label" style="margin:0 0 9px;">Where your money goes · tap to jump</div>
        <div class="fr-cost-pills">
          ${CATS.map(([k, label, step, acc]) => {
            const v = bd[k] || 0, pct = bd.total > 0 ? Math.round(v / bd.total * 100) : 0;
            return `<button class="fr-cost-pill" data-cost-jump="${step}" style="--pill:${acc};${v <= 0 ? 'opacity:0.45;' : ''}">
              <span class="fr-cost-pill-label">${label}</span>
              <span class="fr-cost-pill-val">$${Math.round(v)}</span>
              <span class="fr-cost-pill-bar"><i style="width:${pct}%;"></i></span>
            </button>`;
          }).join('')}
        </div>`;
      })()}
      <div class="fr-summary-list">
        ${header('Foundation')}
        ${row('Base Spec', b.base.name, b.base.price)}
        ${row('Paint', b.paint.name + (b.paint.tier === 'limited' ? ' · Limited' : ''), b.paint.price)}
        ${atelierHtml ? header('Atelier Paint &amp; Trim') + atelierHtml : ''}

        ${header('Wheels & Stance')}
        ${row('Wheel Set', b.wheel.name, b.wheel.price)}
        ${b.wheel.id !== 'stock' && b.wheelFinish ? row('Wheel Finish', b.wheelFinish.name, b.wheelFinish.priceAdd || 0, 'data-remove-wheelfinish') : ''}
        ${b.wheel.id !== 'stock' ? row('Calipers', b.caliper.name, 0) : ''}
        ${b.wheel.id !== 'stock' && b.diskColor ? row('Disk', b.diskColor.name, 0) : ''}
        ${b.centerCap ? row('Center Caps (set of 4)', b.centerCap.name, 0, 'data-remove-caps') : ''}
        ${b.tirePrint ? row(b.tirePrint.kind === 'pinstripe' ? 'Tire Pinstripe' : 'Tire Print', b.tirePrint.name + (b.tirePrintColor ? ' · ' + b.tirePrintColor.name : ''), (b.tirePrint.priceAdd || 0) + (b.tirePrintColor?.priceAdd || 0), 'data-remove-tireprint') : ''}

        ${header(`Body Parts (${b.parts.size})`)}
        ${partRows}
        ${b.clearRoof ? row('Clear Roof Panel', 'Translucent', 29, 'data-remove-roof') : ''}
        ${b.clearCap ? row('Clear Tank Cap', 'Translucent', 19, 'data-remove-cap') : ''}

        ${header(`Metal Decals (${b.decals.length + b.permits.length + (b.banners || []).length})`)}
        ${decalRows}

        ${plate ? header('License Plate') : ''}
        ${plate ? row(plate.custom ? `Custom: "${plate.text || ''}"` : plate.name, `${plate.finish?.name || 'Aluminum'} · ${({ front: 'Front only', both: 'Front + rear', rear: 'Rear only' })[b.plateMount?.id] || 'Front only'}`, (plate.price || 0) + (plate.finish?.priceAdd || 0) + (b.plateMount?.priceAdjust || 0), 'data-remove-plate-item') : ''}


        <div class="fr-summary-row total" style="margin-top:18px;"><span>Total</span><span>$${calculateTotal().toFixed(2)}</span></div>
      </div>
      <div class="fr-share-row">
        <button class="fr-cta-secondary" data-share-build>Share Build</button>
        <button class="fr-cta-secondary" data-save-build>Save &amp; Email</button>
      </div>
      <div class="fr-share-row" style="margin-top:8px;">
        <button class="fr-cta-secondary" data-export-gif>Spin GIF</button>
        <button class="fr-cta-secondary" data-enter-ar id="fr-ar-btn" style="display:none;">View in My Space</button>
      </div>
      ${renderGaragePanel()}
    </div>
  `;
}

// Garage panel (Review step) — save the current build + manage saved builds.
function renderGaragePanel() {
  const builds = FRGarage.list();
  const cards = builds.map(b => `
    <div class="fr-garage-card">
      <div class="fr-garage-thumb">${b.thumb ? `<img src="${b.thumb}" alt="${(b.name || '').replace(/"/g, '&quot;')}" />` : '<span class="fr-garage-noimg">no preview</span>'}</div>
      <div class="fr-garage-meta">
        <div class="fr-garage-name" title="${(b.name || '').replace(/"/g, '&quot;')}">${b.name || 'My Build'}</div>
        <div class="fr-garage-date">${frGarageDate(b.ts)}</div>
      </div>
      <div class="fr-garage-actions">
        <button data-garage-load="${b.id}">Load</button>
        <button data-garage-rename="${b.id}" title="Rename">✎</button>
        <button data-garage-delete="${b.id}" title="Delete" class="del">✕</button>
      </div>
    </div>`).join('');
  return `
    <div class="fr-zone-section" style="margin-top:16px;">
      <div class="fr-zone-header"><div class="fr-zone-label">My Garage</div><div class="fr-zone-meta">${builds.length} saved · this browser</div></div>
      <button class="fr-cta-secondary" data-garage-save style="width:100%;margin-bottom:10px;">🅖 Save this build to Garage</button>
      ${builds.length ? `<div class="fr-garage-grid">${cards}</div>` : `<div class="fr-mount-note">No saved builds yet — save one and reload it anytime (stored in this browser).</div>`}
    </div>`;
}
// Loadouts quick-swap chips (Base step) — one-tap apply a saved build.
function renderLoadoutChips() {
  const builds = FRGarage.list();
  if (!builds.length) return '';
  return `
    <div class="fr-loadouts" style="margin-bottom:18px;">
      <div class="fr-eyebrow">→ Loadouts · your saved builds</div>
      <div class="fr-loadout-row">
        ${builds.slice(0, 8).map(b => `<button class="fr-loadout-chip" data-loadout="${b.id}" title="${(b.name || '').replace(/"/g, '&quot;')}">${b.thumb ? `<img src="${b.thumb}" alt="" />` : ''}<span>${b.name || 'Build'}</span></button>`).join('')}
      </div>
    </div>`;
}

// Apply a base configuration preset: white paint for every config, then clear
// the current body parts and bolt on the config's part list — all in matte
// black. Wheels / plates / decals are left untouched ("just adds the parts").
// ============================================================
// PRE-MADE CONFIGURATIONS — full-build presets
// A preset can carry a complete `build` snapshot (paint, wheels + finish,
// calipers/disk/caps, tyres, aero parts + their finishes, lights, roof, decals,
// plate). captureCurrentBuild() serialises the live build into that shape (used
// by the in-app preset builder, ?makepreset=1); applyFullBuild() restores it.
// Legacy presets that only list `parts` still work (white paint + matte parts).
// ============================================================
function captureCurrentBuild() {
  const b = state.build;
  const id = (o) => (o && o.id) || null;
  const parts = [];
  b.parts.forEach((v, pid) => parts.push({ id: pid, finish: id(v.finish) }));
  return {
    paint: id(b.paint),
    wheel: id(b.wheel),
    wheelFinish: id(b.wheelFinish),
    customWheel: Object.assign({ finish: null, caliper: null, disk: null, cap: null }, b.customWheel || {}),
    aggressiveStance: !!b.aggressiveStance,
    tirePrint: id(b.tirePrint),
    tirePrintColor: id(b.tirePrintColor),
    tirePinstripe: id(b.tirePinstripe),
    tireFinish: b.tireFinish,
    clearRoof: !!b.clearRoof,
    clearCap: !!b.clearCap,
    caliper: id(b.caliper),
    diskColor: id(b.diskColor),
    centerCap: id(b.centerCap),
    parts,
    decals: (b.decals || []).slice(),
    permits: (b.permits || []).slice(),
    banners: (b.banners || []).slice(),
    wingDecal: b.wingDecal ? Object.assign({}, b.wingDecal) : null,
    authored: b.authored ? {
      doorBadge: Object.assign({}, b.authored.doorBadge),
      roofPanel: Object.assign({}, b.authored.roofPanel),
      deckPanel: Object.assign({}, b.authored.deckPanel),
    } : null,    bracketColor: b.bracketColor || null,
    decalFinish: b.decalFinish,
    licensePlate: b.licensePlate ? { id: b.licensePlate.id, custom: !!b.licensePlate.custom, finish: b.licensePlate.finish, text: b.licensePlate.text, miniText: b.licensePlate.miniText, bgColor: b.licensePlate.bgColor, textColor: b.licensePlate.textColor, font: b.licensePlate.font, bgStyle: b.licensePlate.bgStyle, emoji: b.licensePlate.emoji, frame: b.licensePlate.frame, lettering: b.licensePlate.lettering, region: b.licensePlate.region, serial: b.licensePlate.serial, signature: b.licensePlate.signature } : null,
    plateMount: id(b.plateMount),
    custom: b.custom ? Object.assign({}, b.custom) : null,
    split: b.split ? Object.assign({}, b.split, { parts: (b.split.parts || []).slice() }) : null,
    carbon: b.carbon ? Object.assign({}, b.carbon) : null,
    trimColor: b.trimColor || null,
    trimAreas: Object.assign({}, b.trimAreas || {}),
    trimSplit: (b.trimSplit || []).slice(),
    wheelSplit: (b.wheelSplit || []).slice(),
  };
}

// "🎲 Surprise Me" — roll a random, mostly-tasteful 1-of-1 spec and apply it. Reuses
// applyFullBuild so every system (paint/wheels/carbon/parts) stays in sync. The chances
// keep it from clashing too often (favours real wheels, carbon only ~30%, ≤1 aero part).
function frSurprise() {
  const R = arr => (arr && arr.length) ? arr[Math.floor(Math.random() * arr.length)] : null;
  const C = p => Math.random() < p;
  const id = o => o && o.id;
  const wheels = window.FR_WHEELS || [];
  const deepTints = ['#8f0e18', '#064d22', '#4d0e82', '#0d2f8c', '#0c5e63'];
  let wheel = R(wheels);
  if (wheel && wheel.id === 'stock' && C(0.75)) wheel = R(wheels.filter(w => w.id !== 'stock')) || wheel;
  const part = (C(0.4) && (window.FR_BODY_PARTS || []).length) ? R(window.FR_BODY_PARTS) : null;
  const spec = {
    paint: id(R(window.FR_PAINTS)),
    wheel: id(wheel),
    wheelFinish: id(R(window.FR_WHEEL_FINISHES)),
    caliper: id(R(window.FR_CALIPERS)),
    centerCap: C(0.5) ? id(R(window.FR_CENTER_CAPS)) : null,
    aggressiveStance: C(0.4),
    clearRoof: C(0.15),
    carbon: C(0.30) ? { on: true, tint: C(0.6) ? R(deepTints) : null, mirror: true } : { on: false },
    parts: part ? [{ id: part.id, finish: null }] : [],
  };
  applyFullBuild(spec);
  if (typeof setView === 'function') setView('hero');
  render();
}

function applyFullBuild(bld) {
  if (!bld) return;
  const find = (name, id) => (window[name] || []).find(x => x.id === id);
  try {
    // ---- PAINT ----
    const paint = find('FR_PAINTS', bld.paint) || state.build.paint;
    state.build.paint = paint; state.build.atelierPaint = null; scene.setPaint(paint);
    // ---- ATELIER CUSTOM COLOUR (overrides the base paint) ----
    state.build.custom = Object.assign({ on: false, color: null, finish: 'gloss', pearl: false, metallic: false, metalness: null, roughness: null }, bld.custom || {});
    if (state.build.custom.on && state.build.custom.color && scene.setCustomColor) scene.setCustomColor(state.build.custom.color, { satin: state.build.custom.finish === 'satin', pearl: !!state.build.custom.pearl, flip: state.build.custom.flip || null, metallic: !!state.build.custom.metallic, metalness: state.build.custom.metalness, roughness: state.build.custom.roughness });
    // ---- WHEELS + finish (+ custom hex) ----
    state.build.customWheel = Object.assign({ finish: null, caliper: null, disk: null, cap: null }, bld.customWheel || {});
    const wheel = find('FR_WHEELS', bld.wheel) || window.FR_WHEELS[0];
    state.build.wheel = wheel; scene.setWheel(wheel);
    if (wheel.id !== 'stock') {
      const wf = find('FR_WHEEL_FINISHES', bld.wheelFinish);
      if (wf) { state.build.wheelFinish = wf; scene.setWheelFinish(wf); }
      else if (state.build.customWheel.finish) { const cf = { id: 'custom', name: 'Custom', hex: state.build.customWheel.finish }; state.build.wheelFinish = cf; scene.setWheelFinish(cf); }
      else { state.build.wheelFinish = window.FR_WHEEL_FINISHES[0]; scene.setWheelFinish(state.build.wheelFinish); }
    } else { state.build.wheelFinish = null; }
    // ---- CALIPER / DISK / CENTER CAP ----
    const cal = find('FR_WHEEL_FINISHES', bld.caliper) || find('FR_CALIPERS', bld.caliper) || window.FR_WHEEL_FINISHES.find(f => f.id === 'wf-gloss-black');
    state.build.caliper = cal;
    if (state.build.customWheel.caliper) scene.setCaliperFinish({ hex: state.build.customWheel.caliper, metalness: 0.6, roughness: 0.3 });
    else scene.setCaliperFinish(cal);
    const disk = find('FR_WHEEL_FINISHES', bld.diskColor) || find('FR_DISK_COLORS', bld.diskColor) || window.FR_WHEEL_FINISHES.find(f => f.id === 'wf-silver');
    state.build.diskColor = disk;
    if (state.build.customWheel.disk) scene.setDiskFinish({ hex: state.build.customWheel.disk, metalness: 0.9, roughness: 0.35 });
    else scene.setDiskFinish(disk);
    const cap = find('FR_WHEEL_FINISHES', bld.centerCap) || find('FR_CENTER_CAPS', bld.centerCap);
    if (cap && wheel.id !== 'stock') { state.build.centerCap = cap; scene.setCenterCapColor(state.build.customWheel.cap ? { hex: state.build.customWheel.cap, metalness: 0.7, roughness: 0.18 } : cap, true); }
    else { state.build.centerCap = null; scene.setCenterCapColor('#000', false); }
    // ---- AGGRESSIVE STANCE (free wheel poke) ----
    state.build.aggressiveStance = !!bld.aggressiveStance;
    if (scene.setAggressiveStance) scene.setAggressiveStance(state.build.aggressiveStance);
    // ---- TYRES ----
    const tp = find('FR_TIRE_PRINTS', bld.tirePrint);
    state.build.tirePrint = (tp && tp.id !== 'tp-none') ? tp : null;
    state.build.tirePrintColor = find('FR_TIRE_TEXT_COLORS', bld.tirePrintColor) || null;
    scene.setTirePrint(state.build.tirePrint, state.build.tirePrintColor);
    state.build.tirePinstripe = null; // pinstripes are now tire-print entries (handled above)
    state.build.tireFinish = bld.tireFinish || 'matte'; scene.setTireFinish(state.build.tireFinish);
    // ---- ROOF ----
    state.build.clearRoof = !!bld.clearRoof; if (scene.setRoofTranslucent) scene.setRoofTranslucent(state.build.clearRoof);
    state.build.clearCap = !!bld.clearCap; // physical add-on, no scene change
    // ---- SPLIT PAINT + TRIM COLOUR ----
    state.build.split = Object.assign({ on: false, colorA: null, colorB: '#0a0a0c', parts: [], pos: 0, tilt: 14, blend: 0.12, flip: false, pearlA: false, pearlB: false, grazeA: null, grazeB: null }, bld.split || {});
    state.build.split.parts = (state.build.split.parts || []).slice();
    if (scene.setBodySplit) scene.setBodySplit(state.build.split);
    if (state.build.split.on && scene.setPartSplit) state.build.split.parts.forEach(id => scene.setPartSplit(id, true)); // re-fold parts into the split
    // ---- CARBON-FIBRE WEAVE ---- (applied last so it overrides paint/custom/split if on)
    state.build.carbon = Object.assign({ on: false, tint: null, shift: false, split: false, mirror: true, matte: false }, bld.carbon || {});
    if (state.build.carbon.on && scene.setCarbonWeave) scene.setCarbonWeave({ on: true, tint: state.build.carbon.tint || null, split: !!state.build.carbon.split, mirror: state.build.carbon.mirror !== false, matte: !!state.build.carbon.matte });
    else if (scene.setCarbonWeave) scene.setCarbonWeave({ on: false }); // turn carbon OFF if the loaded build has none (else it lingers over the new paint)
    state.build.trimColor = bld.trimColor || null;
    if (scene.setTrimColor) scene.setTrimColor(state.build.trimColor);
    state.build.trimAreas = Object.assign({}, bld.trimAreas || {});
    if (scene.setAreaColor) Object.keys(state.build.trimAreas).forEach(a => scene.setAreaColor(a, state.build.trimAreas[a]));
    state.build.trimSplit = (bld.trimSplit || []).slice();
    if (state.build.split.on && scene.setAreaSplit) state.build.trimSplit.forEach(a => scene.setAreaSplit(a, true)); // re-fold trim areas into the split
    // ---- WHEEL-PART SPLIT ---- (rim/caliper/disk/cap folding into the body split / carbon)
    state.build.wheelSplit = (bld.wheelSplit || []).slice();
    if ((state.build.split.on || (state.build.carbon && state.build.carbon.on)) && scene.setWheelPartSplit) state.build.wheelSplit.forEach(w => scene.setWheelPartSplit(w, true)); // re-fold wheel parts into the split/carbon
    // ---- AERO PARTS (+ per-part finish) ----
    state.build.parts.forEach((_v, pid) => scene.toggleCombinedPart(pid, false));
    state.build.parts.clear();
    const matte = window.FR_FINISHES.find(f => f.id === 'matte-black') || window.FR_FINISHES[0];
    (bld.parts || []).forEach(p => {
      const part = window.FR_BODY_PARTS.find(x => x.id === p.id); if (!part) return;
      let finish = part.hasColor
        ? ((window.FR_TOW_HOOK_COLORS || []).find(f => f.id === p.finish) || window.FR_TOW_HOOK_COLORS[1])
        : (find('FR_FINISHES', p.finish) || matte);
      state.build.parts.set(p.id, { part, finish });
      scene.toggleCombinedPart(p.id, true); scene.setPartFinish(p.id, finish);
    });
    // ---- DECALS / PERMITS / BANNERS / PLATE (best-effort) ----
    state.build.decals = (bld.decals || []).slice();
    state.build.permits = (bld.permits || []).slice();
    state.build.banners = (bld.banners || []).slice();
    state.build.wingDecal = Object.assign({ on: false, style: 'cyber', text: 'FLAMERACER', color: null, finish: null }, bld.wingDecal || {});
    state.build.authored = {
      doorBadge: Object.assign({ on: false, number: '07', name: 'FLAMERACER', sub: 'RACING DIVISION', style: 'classic', accent: null, finish: null }, (bld.authored && bld.authored.doorBadge) || {}),
      roofPanel: Object.assign({ on: false, text: 'FLAMERACER', number: '07', pattern: 'triangles', color: '#16f5d0', finish: null }, (bld.authored && bld.authored.roofPanel) || {}),
      deckPanel: Object.assign({ on: false, text: 'NO STEP', number: '13', pattern: 'hazard', color: '#f5d000', finish: null }, (bld.authored && bld.authored.deckPanel) || {}),
    };
    state.build.decalFinish = bld.decalFinish || state.build.decalFinish;
    const _auOn = Object.values(state.build.authored).some(a => a && a.on);
    if (scene.applyBuildDecals && (state.build.decals.length || state.build.permits.length || state.build.banners.length || (state.build.wingDecal && state.build.wingDecal.on) || _auOn)) {
      scene.applyBuildDecals(state.build.decals, state.build.permits, state.build.decalFinish, state.build.banners, state.build.wingDecal, state.build.authored);
    }
    if (bld.licensePlate) {
      const lp = bld.licensePlate;
      let plate = null;
      if (lp.custom) {
        plate = { id: 'plate-custom', name: 'Custom', price: 12.99, custom: true, finish: lp.finish || window.FR_DECAL_FINISHES[0], text: lp.text || 'CUSTOM', miniText: lp.miniText || '', bgColor: lp.bgColor, textColor: lp.textColor, font: lp.font, bgStyle: lp.bgStyle, emoji: lp.emoji, frame: lp.frame, lettering: lp.lettering, region: lp.region, serial: lp.serial, signature: lp.signature };
      } else {
        const pl = (window.FR_LICENSE_PLATES || []).find(p => p.id === lp.id);
        if (pl) plate = Object.assign({}, pl, { finish: lp.finish || window.FR_DECAL_FINISHES[0], text: lp.text, miniText: lp.miniText });
      }
      if (plate) {
        // Saved builds restore EXACTLY what they carry. Legacy shares from the
        // both-brackets-default era encoded no mount id — restore those as
        // 'both' (their era's meaning); only FRESH selections default to front.
        const mount = (window.FR_PLATE_MOUNTS || []).find(m => m.id === bld.plateMount)
          || (window.FR_PLATE_MOUNTS || []).find(m => m.id === 'both') || null;
        state.build.licensePlate = plate; state.build.plateMount = mount; scene.setLicensePlate(plate, mount);
      }
    } else { state.build.licensePlate = null; state.build.plateMount = null; scene.setLicensePlate && scene.setLicensePlate(null); }
    state.build.bracketColor = bld.bracketColor || null;
    if (scene.setBracketColor) { const bc = (window.FR_PLATE_BRACKET_COLORS || []).find(c => c.id === state.build.bracketColor); scene.setBracketColor(bc ? bc.hex : null); }
  } catch (e) { console.warn('[FLR] applyFullBuild error', e); }
}

function applyBaseConfig(cfg) {
  if (!cfg) return;
  // Full-build preset (authored in the in-app builder) → restore everything.
  if (cfg.build) { applyFullBuild(cfg.build); return; }
  // Legacy preset (only a parts list) → white paint + parts in matte black.
  const white = window.FR_PAINTS.find(p => p.id === 'ghost-white') || window.FR_PAINTS[0];
  state.build.paint = white;
  state.build.atelierPaint = null;
  scene.setPaint(white);

  const matte = window.FR_FINISHES.find(f => f.id === 'matte-black') || window.FR_FINISHES[0];
  // Hide + clear whatever parts are currently on.
  state.build.parts.forEach((_v, id) => scene.toggleCombinedPart(id, false));
  state.build.parts.clear();
  // Bolt on the preset's parts in matte black.
  (cfg.parts || []).forEach(id => {
    const part = window.FR_BODY_PARTS.find(p => p.id === id);
    if (!part) return;
    const finish = part.hasColor ? window.FR_TOW_HOOK_COLORS[1] : matte;
    state.build.parts.set(id, { part, finish });
    scene.toggleCombinedPart(id, true);
    scene.setPartFinish(id, finish);
  });
}

// ============================================================
// HANDLERS
// ============================================================
function attachHandlers() {
  $$('base').forEach(b => b.addEventListener('click', () => {
    const cfg = window.FR_BASE_CONFIGS.find(c => c.id === b.dataset.base);
    state.build.base = cfg;
    applyBaseConfig(cfg);   // white paint + bolt on the preset's parts (matte black)
    cameraAngle();   // very slight sideways angle shift
    render();
  }));
  const surpriseBtn = $('surprise');
  if (surpriseBtn) surpriseBtn.addEventListener('click', () => { if (typeof frSurprise === 'function') frSurprise(); });
  const paintInfoBtn = $('paint-info');
  if (paintInfoBtn) paintInfoBtn.addEventListener('click', () => {
    state.ui = state.ui || {};
    state.ui.paintInfo = !state.ui.paintInfo;
    render();
  });
  $$('paint').forEach(b => b.addEventListener('click', () => {
    const newPaint = window.FR_PAINTS.find(p => p.id === b.dataset.paint);
    state.build.paint = newPaint;          // the base/limited paint is ALWAYS the foundation (always priced)
    state.build.atelierPaint = null;
    const sp = state.build.split;
    if (sp && sp.on) {
      // INDEPENDENT paint shift (user 2026-06-29): a Two-Tone Split is its own +$99
      // layer ON TOP of the base paint — it does NOT replace it. Tapping a base paint
      // makes that colour the FRONT tone (Colour A) and the split stays on, so the car
      // visibly changes while the base paint keeps its foundation price + the split's +$99.
      sp.colorA = newPaint.flip ? newPaint.flip[0] : (newPaint.iridescent ? newPaint.iridescent[0] : newPaint.hex);
      // If the base paint itself shimmers (flip / iridescent), carry that into the front
      // tone so e.g. Midnight keeps its purple shift inside the split instead of going flat.
      sp.pearlA = !!(newPaint.flip || newPaint.iridescent);
      sp.grazeA = newPaint.flip ? newPaint.flip[1] : (newPaint.iridescent ? newPaint.iridescent[2] : null);
      scene.setPaint(newPaint);                  // update the underlying coat (setPaint keeps the split engaged)
      scene.setBodySplit({ colorA: sp.colorA, pearlA: sp.pearlA, grazeA: sp.grazeA }); // re-apply with the new front tone + shimmer
    } else {
      // No split active: tapping a base paint shows it solid, so exit any solid custom
      // colour / carbon override that would otherwise make it look like the paint won't switch.
      if (state.build.custom) state.build.custom.on = false;
      if (state.build.carbon && state.build.carbon.on) { state.build.carbon.on = false; if (scene.setCarbonWeave) scene.setCarbonWeave({ on: false }); }
      scene.setPaint(newPaint);
    }
    cameraTilt();    // very slight vertical crane
    render();
  }));
  // ---- ATELIER: CUSTOM COLOUR ----
  const applyCustomPaint = () => {
    const c = state.build.custom;
    if (c.on && c.color) scene.setCustomColor(c.color, { satin: c.finish === 'satin', pearl: !!c.pearl, flip: c.flip || null, metallic: !!c.metallic, metalness: c.metalness, roughness: c.roughness });
    else scene.setPaint(state.build.paint); // restore the selected base paint
  };
  const customPaintToggle = $('custompaint-toggle');
  if (customPaintToggle) customPaintToggle.addEventListener('click', () => { state.ui = state.ui || {}; state.ui.customPaintOpen = !state.ui.customPaintOpen; render(); });
  $$('custom-color').forEach(b => b.addEventListener('click', () => {
    const c = state.build.custom;
    c.on = true;                               // picking a custom colour turns Custom Paint on
    c.color = b.dataset.customColor;
    c.finish = b.dataset.fin === 'satin' ? 'satin' : 'gloss';
    c.flip = b.dataset.flip ? b.dataset.flip.split(',') : null;
    c.pearl = (b.dataset.fin === 'pearl');     // pearl swatch = pearlescent finish (included in the custom paint price)
    c.metallic = (b.dataset.fin === 'metallic'); // chrome / gold / metallic swatch → high metalness render
    c.metalness = c.metallic && b.dataset.metalness != null ? parseFloat(b.dataset.metalness) : null;
    c.roughness = c.metallic && b.dataset.roughness != null ? parseFloat(b.dataset.roughness) : null;
    if (state.build.split && state.build.split.on) { state.build.split.on = false; if (scene.setBodySplit) scene.setBodySplit({ on: false }); } // a solid custom colour exits split
    if (state.build.carbon && state.build.carbon.on) { state.build.carbon.on = false; if (scene.setCarbonWeave) scene.setCarbonWeave({ on: false }); } // ...and exits carbon
    applyCustomPaint(); render();
  }));
  // ---- ATELIER: CARBON-FIBRE WEAVE ----
  const applyCarbon = () => {
    const k = state.build.carbon;
    if (k && k.on && scene.setCarbonWeave) scene.setCarbonWeave({ on: true, tint: k.tint || null, split: !!k.split, mirror: k.mirror !== false, matte: !!k.matte });
    else if (scene.setCarbonWeave) scene.setCarbonWeave({ on: false });
  };
  const carbonToggle = $('carbon-toggle');
  if (carbonToggle) carbonToggle.addEventListener('click', () => { state.ui = state.ui || {}; state.ui.carbonOpen = !state.ui.carbonOpen; render(); });
  const carbonOn = $('carbon-on');
  if (carbonOn) carbonOn.addEventListener('click', () => {
    const k = state.build.carbon;
    k.on = !k.on;
    if (k.on) {
      if (state.build.custom) state.build.custom.on = false; // carbon replaces a solid custom colour (but the two-tone split CAN ride on top → two-tone carbon)
      applyCarbon();
      if (state.build.split && state.build.split.on && scene.setBodySplit) scene.setBodySplit(state.build.split); // keep the split engaged
    } else {
      if (state.build.split && state.build.split.on) { state.build.split.on = false; if (scene.setBodySplit) scene.setBodySplit({ on: false }); } // its two-tone goes with it
      applyCarbon(); // setCarbonWeave(off) → restores the base paint
    }
    render();
  });
  $$('carbon-tint').forEach(b => b.addEventListener('click', () => {
    const k = state.build.carbon;
    k.on = true; k.tint = b.dataset.carbonTint || null;
    if (state.build.custom) state.build.custom.on = false;
    applyCarbon(); render();
  }));
  // Normal one-way weave vs precise book-matched (mirrored) weave.
  $$('carbon-weave').forEach(b => b.addEventListener('click', () => {
    state.build.carbon.mirror = (b.dataset.carbonWeave === 'mirror');
    applyCarbon(); render();
  }));
  $$('carbon-finish').forEach(b => b.addEventListener('click', () => {
    state.build.carbon.matte = (b.dataset.carbonFinish === 'matte');
    applyCarbon(); render();
  }));
  const carbonSplit = $('carbon-split');
  if (carbonSplit) carbonSplit.addEventListener('click', () => { const k = state.build.carbon; k.on = true; k.split = !k.split; applyCarbon(); render(); });

  // ---- ATELIER: PAINT SPLIT + TRIM COLOUR ----
  const splitInfoBtn = $('split-info');
  if (splitInfoBtn) splitInfoBtn.addEventListener('click', () => { state.ui = state.ui || {}; state.ui.splitInfo = !state.ui.splitInfo; render(); });
  const splitToggle = $('split-toggle');
  if (splitToggle) splitToggle.addEventListener('click', () => {
    const sp = state.build.split;
    sp.on = !sp.on;
    if (sp.on) {
      if (!sp.colorA) { const _p = state.build.paint; sp.colorA = _p.flip ? _p.flip[0] : (_p.iridescent ? _p.iridescent[0] : _p.hex); sp.pearlA = !!(_p.flip || _p.iridescent); sp.grazeA = _p.flip ? _p.flip[1] : (_p.iridescent ? _p.iridescent[2] : null); }
      if (!sp.colorB) sp.colorB = '#0a0a0c';
    }
    scene.setBodySplit(sp);
    if (sp.on) (sp.parts || []).forEach(id => scene.setPartSplit(id, true));   // re-apply included parts
    render();
  });
  // Prominent One-colour / Two-Tone Split segmented control.
  $$('split-mode').forEach(b => b.addEventListener('click', () => {
    const sp = state.build.split;
    const want = b.dataset.splitMode === 'split';
    if (want === sp.on) return;
    sp.on = want;
    const carbonOn = state.build.carbon && state.build.carbon.on;
    if (sp.on) {
      if (!sp.colorA) { const _p = state.build.paint; sp.colorA = _p.flip ? _p.flip[0] : (_p.iridescent ? _p.iridescent[0] : _p.hex); sp.pearlA = !!(_p.flip || _p.iridescent); sp.grazeA = _p.flip ? _p.flip[1] : (_p.iridescent ? _p.iridescent[2] : null); }
      if (!sp.colorB) sp.colorB = '#0a0a0c';
      if (!carbonOn && state.build.custom) state.build.custom.on = false; // a PAINT split is its own mode; a CARBON split rides on the carbon (two-tone carbon)
      scene.setBodySplit(sp);
      (sp.parts || []).forEach(id => scene.setPartSplit(id, true));
      (state.build.trimSplit || []).forEach(a => scene.setAreaSplit && scene.setAreaSplit(a, true)); // re-fold connected trim areas
    } else {
      scene.setBodySplit({ on: false });
      if (carbonOn) applyCarbon(); else scene.setPaint(state.build.paint); // restore carbon look, or the base paint
    }
    render();
  }));
  $$('split-a').forEach(b => b.addEventListener('click', () => {
    const sp = state.build.split;
    sp.colorA = b.dataset.splitA;
    sp.pearlA = b.dataset.fin === 'pearl';                                  // pearl swatch → grazing-angle shift on this side
    sp.grazeA = (sp.pearlA && b.dataset.flip) ? b.dataset.flip.split(',')[1] : null;
    scene.setBodySplit({ colorA: sp.colorA, pearlA: sp.pearlA, grazeA: sp.grazeA });
    render();
  }));
  $$('split-b').forEach(b => b.addEventListener('click', () => {
    const sp = state.build.split;
    sp.colorB = b.dataset.splitB;
    sp.pearlB = b.dataset.fin === 'pearl';
    sp.grazeB = (sp.pearlB && b.dataset.flip) ? b.dataset.flip.split(',')[1] : null;
    scene.setBodySplit({ colorB: sp.colorB, pearlB: sp.pearlB, grazeB: sp.grazeB });
    render();
  }));
  const _splitRange = (sel, key) => { const el = $(sel); if (el) el.addEventListener('input', () => { state.build.split[key] = parseFloat(el.value); scene.setBodySplit({ [key]: state.build.split[key] }); }); };
  _splitRange('split-pos', 'pos'); _splitRange('split-tilt', 'tilt'); _splitRange('split-blend', 'blend');
  const splitFlip = $('split-flip');
  if (splitFlip) splitFlip.addEventListener('click', () => { state.build.split.flip = !state.build.split.flip; scene.setBodySplit({ flip: state.build.split.flip }); });
  // body parts joining the split (painted by position)
  const _setPartInSplit = (id, on) => {
    const sp = state.build.split; sp.parts = sp.parts || [];
    const i = sp.parts.indexOf(id);
    if (on && i < 0) sp.parts.push(id);
    if (!on && i >= 0) sp.parts.splice(i, 1);
    scene.setPartSplit(id, on);
  };
  $$('split-part').forEach(b => b.addEventListener('click', () => { const id = b.dataset.splitPart; _setPartInSplit(id, !(state.build.split.parts || []).includes(id)); render(); }));
  const splitAll = $('split-allparts');
  if (splitAll) splitAll.addEventListener('click', () => {
    const ids = [...state.build.parts.keys()];
    const allIn = ids.length && ids.every(id => (state.build.split.parts || []).includes(id));
    ids.forEach(id => _setPartInSplit(id, !allIn));
    render();
  });
  const trimToggle = $('trim-toggle');
  if (trimToggle) trimToggle.addEventListener('click', () => { state.ui = state.ui || {}; state.ui.trimOpen = !state.ui.trimOpen; render(); });
  $$('trim-area').forEach(b => b.addEventListener('click', () => { state.ui = state.ui || {}; state.ui.trimArea = b.dataset.trimArea; render(); }));
  $$('trim-color').forEach(b => b.addEventListener('click', () => {
    const area = (state.ui && state.ui.trimArea) || 'all';
    const hex = b.dataset.trimColor || null;
    // Picking a solid colour means this area is no longer following the paint shift.
    if (hex && state.build.trimSplit && state.build.trimSplit.length) {
      const _drop = area === 'all' ? state.build.trimSplit.slice() : [area];
      _drop.forEach(a => { const j = state.build.trimSplit.indexOf(a); if (j >= 0) { state.build.trimSplit.splice(j, 1); if (scene.setAreaSplit) scene.setAreaSplit(a, false); } });
    }
    if (area === 'all') { state.build.trimColor = hex; scene.setAreaColor('all', hex); }
    else { state.build.trimAreas = state.build.trimAreas || {}; if (hex) state.build.trimAreas[area] = hex; else delete state.build.trimAreas[area]; scene.setAreaColor(area, hex); }
    render();
  }));
  // Connect a trim area to the body two-tone paint split (follow the shift instead of a solid trim colour).
  // trimSplit always stores REAL bucket ids (never the literal 'all'); 'all' expands to every real trim bucket.
  const _setAreaInSplit = (area, on) => {
    state.build.trimSplit = state.build.trimSplit || [];
    const i = state.build.trimSplit.indexOf(area);
    if (on && i < 0) state.build.trimSplit.push(area);
    if (!on && i >= 0) state.build.trimSplit.splice(i, 1);
    if (scene.setAreaSplit) scene.setAreaSplit(area, on);
  };
  const _realTrimAreas = () => (scene.trimAreas ? scene.trimAreas() : []).filter(a => a !== 'windshield');
  $$('trim-split').forEach(b => b.addEventListener('click', () => {
    const area = b.dataset.trimSplit;
    state.build.trimSplit = state.build.trimSplit || [];
    if (area === 'all') {
      const ids = _realTrimAreas();
      const allIn = ids.length && ids.every(a => state.build.trimSplit.includes(a));
      ids.forEach(a => _setAreaInSplit(a, !allIn));
    } else {
      _setAreaInSplit(area, !state.build.trimSplit.includes(area));
    }
    render();
  }));
  $$('wheel').forEach(b => b.addEventListener('click', () => {
    state.build.wheel = window.FR_WHEELS.find(w => w.id === b.dataset.wheel);
    scene.setWheel(state.build.wheel);
    if (state.build.wheel.id === 'stock') {
      // Stock wheel: no finish recolor, revert to its native look. Stock's only
      // option is tire prints — clear caps/finish so those sections stay hidden.
      state.build.wheelFinish = null;
      if (state.build.centerCap) { state.build.centerCap = null; scene.setCenterCapColor('#000', false); }
    } else {
      // Non-stock: default to the first finish if none chosen yet, then apply
      if (!state.build.wheelFinish) state.build.wheelFinish = window.FR_WHEEL_FINISHES[0];
      scene.setWheelFinish(state.build.wheelFinish);
      // Center caps come FREE and selected by default with any wheel upgrade.
      if (!state.build.centerCap) state.build.centerCap = window.FR_WHEEL_FINISHES.find(f => f.id === 'wf-gloss-black');
      scene.setCenterCapColor(state.build.customWheel.cap ? { hex: state.build.customWheel.cap, metalness: 0.7, roughness: 0.18 } : state.build.centerCap, true);
    }
    focusWheelView('wheels');
    render();
  }));
  $$('wheel-finish').forEach(b => b.addEventListener('click', () => {
    state.build.wheelFinish = window.FR_WHEEL_FINISHES.find(f => f.id === b.dataset.wheelFinish);
    scene.setWheelFinish(state.build.wheelFinish);
    focusWheelView('wheels');
    render();
  }));
  // Connect a wheel component (rim/caliper/disk/cap) to the body two-tone split / carbon.
  $$('wheel-split').forEach(b => b.addEventListener('click', () => {
    const which = b.dataset.wheelSplit;
    state.build.wheelSplit = state.build.wheelSplit || [];
    const i = state.build.wheelSplit.indexOf(which);
    const on = i < 0;
    if (on) state.build.wheelSplit.push(which);
    else state.build.wheelSplit.splice(i, 1);
    if (scene.setWheelPartSplit) scene.setWheelPartSplit(which, on);
    render();
  }));
  const rainbowToggle = $('rainbow-toggle');
  if (rainbowToggle) rainbowToggle.addEventListener('click', () => {
    state.ui = state.ui || {};
    state.ui.wheelRainbow = !state.ui.wheelRainbow;
    render();
  });
  // ---- Review-build ✕ remove buttons ----
  $$('remove-part').forEach(b => b.addEventListener('click', () => {
    const id = b.dataset.removePart;
    state.build.parts.delete(id);
    scene.toggleCombinedPart(id, false);
    render();
  }));
  $$('remove-decal').forEach(b => b.addEventListener('click', () => {
    const [packId, page] = b.dataset.removeDecal.split(':');
    const i = state.build.decals.findIndex(d => d.packId === packId && d.page === page);
    if (i >= 0) state.build.decals.splice(i, 1);
    scene.applyBuildDecals(state.build.decals, state.build.permits, state.build.decalFinish, state.build.banners, state.build.wingDecal, state.build.authored);
    render();
  }));
  $$('remove-permit').forEach(b => b.addEventListener('click', () => {
    const id = b.dataset.removePermit;
    const i = state.build.permits.findIndex(p => p.id === id);
    if (i >= 0) state.build.permits.splice(i, 1);
    scene.applyBuildDecals(state.build.decals, state.build.permits, state.build.decalFinish, state.build.banners, state.build.wingDecal, state.build.authored);
    render();
  }));
  $$('remove-banner').forEach(b => b.addEventListener('click', () => {
    const id = b.dataset.removeBanner;
    const i = state.build.banners.findIndex(x => x.id === id);
    if (i >= 0) state.build.banners.splice(i, 1);
    scene.applyBuildDecals(state.build.decals, state.build.permits, state.build.decalFinish, state.build.banners, state.build.wingDecal, state.build.authored);
    render();
  }));
  const remWing = $('remove-wing');
  if (remWing) remWing.addEventListener('click', () => {
    if (state.build.wingDecal) state.build.wingDecal.on = false;
    scene.applyBuildDecals(state.build.decals, state.build.permits, state.build.decalFinish, state.build.banners, state.build.wingDecal, state.build.authored);
    render();
  });
  $$('remove-authored').forEach(b => b.addEventListener('click', () => {
    const key = b.dataset.removeAuthored;
    if (state.build.authored && state.build.authored[key]) state.build.authored[key].on = false;
    scene.applyBuildDecals(state.build.decals, state.build.permits, state.build.decalFinish, state.build.banners, state.build.wingDecal, state.build.authored);
    render();
  }));
  const remWF = $('remove-wheelfinish');
  if (remWF) remWF.addEventListener('click', () => { state.build.wheelFinish = null; scene.setWheel(state.build.wheel); render(); });
  const remCaps = $('remove-caps');
  if (remCaps) remCaps.addEventListener('click', () => { state.build.centerCap = null; scene.setCenterCapColor('#000', false); render(); });
  const remTP = $('remove-tireprint');
  if (remTP) remTP.addEventListener('click', () => { state.build.tirePrint = null; state.build.tirePrintColor = null; scene.setTirePrint(null); render(); });
  // (Tire finish + separate pinstripe controls removed — pinstripes are now tire-print entries.)
  // Atelier: removing the custom-paint line drops BOTH the solid custom colour and the
  // split (they share the one $99 charge) and restores the selected base paint.
  const remCP = $('remove-custompaint');
  if (remCP) remCP.addEventListener('click', () => {
    if (state.build.custom) state.build.custom.on = false;
    if (state.build.split && state.build.split.on) { state.build.split.on = false; if (scene.setBodySplit) scene.setBodySplit({ on: false }); }
    if (scene.setPaint) scene.setPaint(state.build.paint);
    render();
  });
  const remCarbon = $('remove-carbon');
  if (remCarbon) remCarbon.addEventListener('click', () => {
    if (state.build.carbon) state.build.carbon.on = false;
    if (state.build.split && state.build.split.on) { state.build.split.on = false; if (scene.setBodySplit) scene.setBodySplit({ on: false }); } // its two-tone goes with it
    if (scene.setCarbonWeave) scene.setCarbonWeave({ on: false });
    render();
  });
  // Atelier: remove a single painted trim area (or "all").
  $$('remove-trim').forEach(b => b.addEventListener('click', () => {
    const area = b.dataset.removeTrim;
    if (area === '__alltrim') { // standalone trim line → clear the WHOLE trim job
      state.build.trimColor = null;
      Object.keys(state.build.trimAreas || {}).forEach(a => { if (scene.setAreaColor) scene.setAreaColor(a, null); });
      state.build.trimAreas = {};
      if (scene.setAreaColor) scene.setAreaColor('all', null);
    } else if (area === 'all') { state.build.trimColor = null; if (scene.setAreaColor) scene.setAreaColor('all', null); }
    else if (state.build.trimAreas) { delete state.build.trimAreas[area]; if (scene.setAreaColor) scene.setAreaColor(area, null); }
    render();
  }));
  const remRoof = $('remove-roof');
  if (remRoof) remRoof.addEventListener('click', () => { state.build.clearRoof = false; if (scene.setRoofTranslucent) scene.setRoofTranslucent(false); render(); });
  const remCap = $('remove-cap');
  if (remCap) remCap.addEventListener('click', () => { state.build.clearCap = false; render(); });
  const remPlateItem = $('remove-plate-item');
  if (remPlateItem) remPlateItem.addEventListener('click', () => { state.build.licensePlate = null; state.build.plateMount = null; scene.setLicensePlate(null); render(); });
  $$('tire-print').forEach(b => b.addEventListener('click', () => {
    const p = window.FR_TIRE_PRINTS.find(t => t.id === b.dataset.tirePrint);
    state.build.tirePrint = (p && p.id !== 'tp-none') ? p : null;
    state.build.tirePrintColor = null; // reset any single-color override when the print changes
    scene.setTirePrint(p); // 'tp-none' has empty text → restores blackwall
    focusWheelView('tire'); // camera drops + zooms toward the tires
    render();
  }));
  $$('tire-color').forEach(b => b.addEventListener('click', () => {
    const v = b.dataset.tireColor;
    state.build.tirePrintColor = (v === '__default__') ? null : (window.FR_TIRE_TEXT_COLORS.find(c => c.id === v) || null);
    // re-apply the current print with the chosen wordmark colour / finish
    scene.setTirePrint(state.build.tirePrint, state.build.tirePrintColor);
    focusWheelView('tire');
    render();
  }));
  // (Sidewall-pinstripe handler removed — pinstripes are selected as tire prints now.)
  $$('caliper-finish').forEach(b => b.addEventListener('click', () => {
    const f = window.FR_WHEEL_FINISHES.find(x => x.id === b.dataset.caliperFinish);
    if (!f) return;
    state.build.caliper = f;
    if (state.build.customWheel) state.build.customWheel.caliper = null; // a preset finish clears the custom hex
    scene.setCaliperFinish(f);
    focusWheelView('caliper');
    render();
  }));
  $$('disk-finish').forEach(b => b.addEventListener('click', () => {
    const f = window.FR_WHEEL_FINISHES.find(x => x.id === b.dataset.diskFinish);
    if (!f) return;
    state.build.diskColor = f;
    if (state.build.customWheel) state.build.customWheel.disk = null;
    scene.setDiskFinish(f);
    focusWheelView('caliper');
    render();
  }));
  $$('cap-finish').forEach(b => b.addEventListener('click', () => {
    if (b.dataset.capFinish === '__none__') {       // "None" chip removes the caps (free either way)
      state.build.centerCap = null;
      scene.setCenterCapColor('#000', false);
      render();
      return;
    }
    // Caps reuse the full wheel-finish set (colours + rare effects), and are FREE.
    const f = window.FR_WHEEL_FINISHES.find(x => x.id === b.dataset.capFinish);
    if (!f) return;
    state.build.centerCap = f;
    if (state.build.customWheel) state.build.customWheel.cap = null;
    scene.setCenterCapColor(f, true);
    focusWheelView('caps');
    render();
  }));
  // Custom colour = rainbow selector. The "Custom" chip opens an even rainbow row;
  // picking a swatch sets that option's custom colour. ONE $10 unlocks all.
  $$('cw-open').forEach(b => b.addEventListener('click', () => {
    state.ui = state.ui || {};
    state.ui.cwOpen = state.ui.cwOpen === b.dataset.cwOpen ? null : b.dataset.cwOpen;
    render();
  }));
  $$('cw-pick').forEach(b => b.addEventListener('click', () => {
    const [which, hex] = b.dataset.cwPick.split(':');
    state.build.customWheel = state.build.customWheel || {};
    state.build.customWheel[which] = hex;
    if (which === 'finish') {
      state.build.wheelFinish = { id: 'finish-custom', name: 'Custom', hex, priceAdd: 0, custom: true };
      scene.setWheelFinish(state.build.wheelFinish); focusWheelView('wheels');
    } else if (which === 'caliper') {
      state.build.caliper = { id: 'cal-custom', name: 'Custom', hex };
      scene.setCaliperFinish({ hex, metalness: 0.6, roughness: 0.3 }); focusWheelView('caliper');
    } else if (which === 'disk') {
      state.build.diskColor = { id: 'disk-custom', name: 'Custom', hex, metalness: 0.9, roughness: 0.35, propertyName: 'Disk Color' };
      scene.setDiskFinish(state.build.diskColor); focusWheelView('caliper');
    } else if (which === 'cap') {
      state.build.centerCap = { id: 'cap-custom', name: 'Custom', hex, price: 0, variantId: null };
      scene.setCenterCapColor({ hex, metalness: 0.7, roughness: 0.18 }, true); focusWheelView('caps');
    }
    render();
  }));
  const clearRoofBtn = $('clear-roof');
  if (clearRoofBtn) clearRoofBtn.addEventListener('click', () => {
    state.build.clearRoof = !state.build.clearRoof;
    scene.setRoofTranslucent(state.build.clearRoof);
    render();
  });
  const clearCapBtn = $('clear-cap');
  if (clearCapBtn) clearCapBtn.addEventListener('click', () => {
    state.build.clearCap = !state.build.clearCap; // physical add-on — priced, no scene change
    render();
  });
  $$('toggle-part').forEach(el => el.addEventListener('click', e => {
    if (e.target.closest('[data-finish-id]')) return;
    const id = el.dataset.togglePart;
    const part = window.FR_BODY_PARTS.find(p => p.id === id);
    if (state.build.parts.has(id)) {
      state.build.parts.delete(id);
      scene.togglePart(id, false);
    } else {
      // Incompatibility: turning this part on auto-removes any it conflicts with
      // (only-one groups + "don't work together" pairs all swap out the other).
      ((window.FR_PART_CONFLICTS && window.FR_PART_CONFLICTS[id]) || []).forEach(cid => {
        if (state.build.parts.has(cid)) { state.build.parts.delete(cid); scene.togglePart(cid, false); }
      });
      const opts = part.hasColor ? window.FR_TOW_HOOK_COLORS : window.FR_FINISHES;
      // Default finish BY ID — the palette expansion (2026-08-05) reordered
      // FR_FINISHES, so a positional opts[1] would now land on Ghost White.
      const finish = opts.find(f => f.id === 'matte-black') || opts.find(f => f.id === 'tow-black') || opts[1] || opts[0];
      state.build.parts.set(id, { part, finish });
      scene.togglePart(id, true);
      scene.setPartFinish(id, finish);
    }
    // If a rear-plate-blocking diffuser just toggled, re-apply the plate so it
    // drops to front-only (scene force-fronts while a diffuser is on).
    if ((id === 'flr-16' || id === 'flr-18') && state.build.licensePlate) {
      if (state.build.parts.has(id)) state.build.plateMount = window.FR_PLATE_MOUNTS.find(m => m.id === 'front');
      scene.setLicensePlate(state.build.licensePlate, state.build.plateMount);
    }
    // Fly the camera to the zone this part lives on (3D only — the PANEL must
    // not move: the card stays under the finger so the colour strip that just
    // expanded beneath it is immediately tappable).
    focusBodyZone(part.zone);
    render();
    // Only if the freshly-expanded colour strip is clipped below the fold,
    // nudge the panel by the MINIMAL distance (nearest-style, never to-top).
    if (state.build.parts.has(id)) {
      const panel = $('fr-panel-content');
      const card = root.querySelector(`.fr-part-card[data-part-id="${id}"]`);
      if (panel && card) {
        const pr = panel.getBoundingClientRect(), cr = card.getBoundingClientRect();
        const over = cr.bottom - pr.bottom;
        const headroom = Math.max(0, cr.top - pr.top - 8);   // never push the card's top out of view
        if (over > 0) panel.scrollTo({ top: panel.scrollTop + Math.min(over + 10, headroom), behavior: 'smooth' });
      }
    }
  }));
  $$('part-finish').forEach(el => el.addEventListener('click', () => {
    const id = el.dataset.partFinish, fid = el.dataset.finishId;
    const part = window.FR_BODY_PARTS.find(p => p.id === id);
    const opts = part.hasColor ? window.FR_TOW_HOOK_COLORS : window.FR_FINISHES;
    const finish = opts.find(f => f.id === fid);
    state.build.parts.get(id).finish = finish;
    scene.setPartFinish(id, finish);
    render();
  }));
  $$('plate').forEach(b => b.addEventListener('click', () => {
    const isCustom = b.dataset.plate === '__custom__';
    const p = isCustom
      ? { id: 'plate-custom', name: 'Custom', price: 12.99, custom: true, text: 'CUSTOM' }
      : window.FR_LICENSE_PLATES.find(pl => pl.id === b.dataset.plate);
    if (state.build.licensePlate?.id === p.id) {
      state.build.licensePlate = null;
      state.build.plateMount = null;
    } else {
      // Select plate with default finish (Brushed Aluminum) and FRONT-ONLY mount
      // (2026-08-05 owner decision: the rear plate is opt-in, never default).
      state.build.licensePlate = { ...p, finish: window.FR_DECAL_FINISHES[0] };
      state.build.plateMount = window.FR_PLATE_MOUNTS.find(m => m.id === 'front') || window.FR_PLATE_MOUNTS[0];
      focusPlateView('front');
    }
    scene.setLicensePlate(state.build.licensePlate, state.build.plateMount);
    render();
  }));
  $$('plate-finish').forEach(b => b.addEventListener('click', () => {
    const f = window.FR_DECAL_FINISHES.find(x => x.id === b.dataset.plateFinish);
    if (state.build.licensePlate) {
      state.build.licensePlate = { ...state.build.licensePlate, finish: f };
      scene.setLicensePlate(state.build.licensePlate, state.build.plateMount); // re-tint the 3D plate
      render();
    }
  }));
  // Custom-plate text input — live-updates the plate graphic as you type.
  // Live-update the 2D preview image as the user types (cheap: one draw per keypress).
  const refreshPlatePreview = () => { const pv = root.querySelector('[data-plate-preview]'); if (pv && state.build.licensePlate) { const u = platePreviewURL(state.build.licensePlate); if (u) pv.src = u; } };
  const customInput = $('custom-plate-input');
  if (customInput) {
    const sync = () => {
      if (state.build.licensePlate && state.build.licensePlate.custom) {
        state.build.licensePlate = { ...state.build.licensePlate, text: customInput.value || 'CUSTOM', name: 'Custom: ' + (customInput.value || '') };
        scene.setLicensePlate(state.build.licensePlate, state.build.plateMount);
        refreshPlatePreview();
      }
    };
    customInput.addEventListener('input', sync);
  }
  // Custom-plate MINI text input — optional subtitle line, live-updates.
  const customMini = $('custom-plate-mini');
  if (customMini) {
    customMini.addEventListener('input', () => {
      if (state.build.licensePlate && state.build.licensePlate.custom) {
        state.build.licensePlate = { ...state.build.licensePlate, miniText: customMini.value || '' };
        scene.setLicensePlate(state.build.licensePlate, state.build.plateMount);
        refreshPlatePreview();
      }
    });
  }
  // Custom-plate background / text-colour / font pickers.
  const applyCustom = (patch) => {
    if (!state.build.licensePlate || !state.build.licensePlate.custom) return;
    if (patch.text != null) patch.name = 'Custom: ' + patch.text;
    state.build.licensePlate = { ...state.build.licensePlate, ...patch };
    scene.setLicensePlate(state.build.licensePlate, state.build.plateMount);
    render();
  };
  $$('plate-bg').forEach(b => b.addEventListener('click', () => applyCustom({ bgColor: b.dataset.plateBg })));
  $$('plate-bgstyle').forEach(b => b.addEventListener('click', () => applyCustom({ bgStyle: b.dataset.plateBgstyle })));
  $$('plate-textcolor').forEach(b => b.addEventListener('click', () => applyCustom({ textColor: b.dataset.plateTextcolor })));
  $$('plate-emoji').forEach(b => b.addEventListener('click', () => applyCustom({ emoji: b.dataset.plateEmoji === '__none__' ? '' : b.dataset.plateEmoji })));
  $$('plate-font').forEach(b => b.addEventListener('click', () => applyCustom({ font: b.dataset.plateFont })));
  $$('plate-frame').forEach(b => b.addEventListener('click', () => applyCustom({ frame: b.dataset.plateFrame })));
  $$('plate-lettering').forEach(b => b.addEventListener('click', () => applyCustom({ lettering: b.dataset.plateLettering })));
  $$('plate-region').forEach(b => b.addEventListener('click', () => applyCustom({ region: b.dataset.plateRegion })));
  $$('plate-serial').forEach(b => b.addEventListener('click', () => applyCustom({ serial: b.dataset.plateSerial === 'on' })));
  $$('plate-signature').forEach(b => b.addEventListener('click', () => applyCustom({ signature: b.dataset.plateSignature === 'on' })));
  // Download the custom plate as a print-ready PNG (for the mini sticker printer).
  $$('plate-download').forEach(b => b.addEventListener('click', () => {
    if (!state.build.licensePlate || !state.build.licensePlate.custom) return;
    const url = platePreviewURL(state.build.licensePlate);
    if (!url) return;
    const a = document.createElement('a'); a.href = url; a.download = `flameracer-plate-${state.buildId}.png`;
    document.body.appendChild(a); a.click(); a.remove();
  }));
  $$('plate-preset').forEach(b => b.addEventListener('click', () => { const p = (window.FR_PLATE_PRESETS || [])[+b.dataset.platePreset]; if (p) applyCustom({ ...p.set }); }));
  $$('plate-surprise').forEach(b => b.addEventListener('click', () => {
    const pick = a => a[Math.floor(Math.random() * a.length)];
    applyCustom({
      bgStyle: pick(['gradient', 'stripes', 'carbon', 'sunset', 'skyline', 'neon', 'galaxy', 'aurora', 'circuit', 'cyber', 'tactical', 'bloodtech', 'noir']),
      textColor: pick(['#ffffff', '#f5d000', '#ff1f4b', '#39ff14', '#16c5e8']),
      font: pick(['"Arial Black", Arial', 'Impact, "Arial Narrow Bold", sans-serif', '"Bebas Neue", Impact, sans-serif', '"Barlow Condensed", sans-serif', '"JetBrains Mono", monospace']),
      emoji: pick(['', '🔥', '💀', '⚡', '🏁', '🐉', '👑', '😈', '🚀']),
      text: pick(['TURBO', 'BOOST', 'APEX', 'SENDIT', 'REDLINE', 'NITRO', 'GRIP', 'DRIFT', 'RACE', '1320', 'V8', 'GO']),
      lettering: pick(['flat', 'raised', 'stamped']),
      frame: pick(['thin', 'black', 'chrome', 'carbon']),
    });
  }));
  $$('plate-mount').forEach(b => b.addEventListener('click', () => {
    const m = window.FR_PLATE_MOUNTS.find(x => x.id === b.dataset.plateMount);
    state.build.plateMount = m;
    scene.setLicensePlate(state.build.licensePlate, m);
    focusPlateView(m.id);
    render();
  }));
  $$('bracket-color').forEach(b => b.addEventListener('click', () => {
    const c = (window.FR_PLATE_BRACKET_COLORS || []).find(x => x.id === b.dataset.bracketColor);
    if (!c) return;
    state.build.bracketColor = c.id;
    scene.setBracketColor(c.hex); // null hex → original carbon
    render();
  }));
  const plateRem = $('plate-remove');
  if (plateRem) plateRem.addEventListener('click', () => {
    // Light visual confirmation, then remove.
    plateRem.classList.add('fr-removed');
    plateRem.textContent = '✓ Plate removed';
    setTimeout(() => {
      state.build.licensePlate = null;
      state.build.plateMount = null;
      scene.setLicensePlate(null);
      render();
    }, 600);
  });
  // Re-project all of the build's decals + permits with the global finish.
  const refreshDecals = () => scene.applyBuildDecals(state.build.decals, state.build.permits, state.build.decalFinish, state.build.banners, state.build.wingDecal, state.build.authored);
  // Global finish — applies to every decal + permit on the car.
  $$('global-finish').forEach(b => b.addEventListener('click', () => {
    state.build.decalFinish = b.dataset.globalFinish;
    state.build.decals.forEach(d => d.finish = b.dataset.globalFinish);   // master sets all…
    state.build.permits.forEach(p => p.finish = b.dataset.globalFinish);
    refreshDecals();
    render();
  }));
  // …then each sticker's own finish can override the master, independently.
  $$('decal-finish').forEach(b => b.addEventListener('click', () => {
    const [packId, page, fin] = b.dataset.decalFinish.split(':');
    const d = state.build.decals.find(x => x.packId === packId && x.page === page);
    if (d) { d.finish = fin; refreshDecals(); render(); }
  }));
  $$('permit-finish').forEach(b => b.addEventListener('click', () => {
    const [id, fin] = b.dataset.permitFinish.split(':');
    const p = state.build.permits.find(x => x.id === id);
    if (p) { p.finish = fin; refreshDecals(); render(); }
  }));
  // Switch the active sticker pack tab.
  $$('decal-pack').forEach(b => b.addEventListener('click', () => {
    state.build.decalPack = b.dataset.decalPack;
    render();
  }));
  // Add / remove a pack design (one card = one design; pairs collapse to one).
  $$('decal-add').forEach(b => b.addEventListener('click', () => {
    const [packId, page] = b.dataset.decalAdd.split(':');
    const pack = window.FR_DECAL_PACKS[packId];
    const slot = pack.slots.find(s => s.page === page);
    const i = state.build.decals.findIndex(d => d.packId === packId && d.page === page);
    if (i >= 0) state.build.decals.splice(i, 1);
    else state.build.decals.push({ packId, packName: pack.name, page, file: slot.file, panel: slot.panel, mirror: slot.mirror, mirrored: !!slot.mirror, finish: state.build.decalFinish || 'brushed-aluminum' });
    refreshDecals();
    render();
  }));
  // Toggle the free mirror (renders the design on both sides).
  $$('decal-mirror').forEach(b => b.addEventListener('click', () => {
    const [packId, page] = b.dataset.decalMirror.split(':');
    const d = state.build.decals.find(x => x.packId === packId && x.page === page);
    if (d) { d.mirrored = !d.mirrored; refreshDecals(); render(); }
  }));
  // Add / remove a racing permit ($2.99 each).
  $$('permit').forEach(b => b.addEventListener('click', () => {
    const id = b.dataset.permit;
    const def = (window.FR_RACING_PERMITS || []).find(p => p.id === id);
    const i = state.build.permits.findIndex(p => p.id === id);
    if (i >= 0) state.build.permits.splice(i, 1);
    else if (def) state.build.permits.push({ id: def.id, name: def.name, file: def.file, price: def.price, finish: state.build.decalFinish || 'brushed-aluminum' });
    refreshDecals();
    render();
  }));

  // ── CUSTOM RACING PERMITS — design / edit / position / remove ──────────────
  const PERMIT_PRICE = window.FR_CUSTOM_PERMIT_PRICE || 4.99;
  const editingPermit = () => state.build.permits.find(p => p.custom && p.id === (state.ui && state.ui.editPermit));
  const refreshPermitPreview = () => { const e = editingPermit(); const pv = root.querySelector('[data-permit-preview]'); if (e && pv) { const u = permitPreviewURL(e.design); if (u) pv.src = u; } };
  // Apply a design patch to the permit being edited. `rerender` re-renders the whole
  // panel (for discrete clicks that change selected states); without it we only
  // repaint the cheap 2D preview + the 3D decal (used for live typing via debounce).
  const permitName = dz => 'Permit: ' + ((dz && (dz.status || dz.title)) || 'CUSTOM');
  const applyPermitDesign = (patch, rerender) => {
    const e = editingPermit(); if (!e) return;
    e.design = { ...e.design, ...patch };
    e.name = permitName(e.design);
    if (rerender) { refreshDecals(); render(); } else { refreshPermitPreview(); }
  };
  // Live typing rebuilds the 3D projection (heavy) on a short debounce so the preview
  // stays snappy; the 2D preview updates instantly on every keystroke.
  let _permitTypeTimer = null;
  const debouncedPermitRebuild = () => { if (_permitTypeTimer) clearTimeout(_permitTypeTimer); _permitTypeTimer = setTimeout(() => refreshDecals(), 300); };
  $$('permit-new').forEach(b => b.addEventListener('click', () => {
    const POSITIONS = window.FR_PERMIT_POSITIONS || [];
    const used = new Set(state.build.permits.filter(p => p.custom).map(p => p.position));
    let pos = 0; while (pos < POSITIONS.length && used.has(pos)) pos++;
    if (pos >= POSITIONS.length) return; // all positions taken
    const preset = (window.FR_PERMIT_PRESETS || [])[0];
    const design = preset ? { ...preset.set } : { layout: 'authorization', title: 'RACING PERMIT', status: 'APPROVED', cls: 'GT', authority: 'FLAMERACER MOTORSPORT', season: '2026', accent: '#f5d000', base: 'carbon', stamp: true, barcode: true, checker: true, holo: false };
    const id = 'cust-permit-' + Math.random().toString(36).slice(2, 8);
    state.build.permits.push({ id, custom: true, position: pos, design, name: permitName(design), price: PERMIT_PRICE, finish: state.build.decalFinish || 'brushed-aluminum' });
    state.ui = state.ui || {}; state.ui.editPermit = id;
    refreshDecals(); render();
    focusPermitView(pos); // fly to the new permit's spot so it's visible while designing
  }));
  $$('permit-edit').forEach(b => b.addEventListener('click', () => {
    state.ui = state.ui || {};
    const opening = state.ui.editPermit !== b.dataset.permitEdit;
    state.ui.editPermit = opening ? b.dataset.permitEdit : null;
    render();
    // When OPENING an editor, fly the camera to that permit's spot so the customer
    // sees what they're editing (it may be on the far side of the car). When CLOSING,
    // restore the view we were at before the permit fly-to (not hero).
    if (opening) {
      const ep = state.build.permits.find(p => p.custom && p.id === state.ui.editPermit);
      if (ep) focusPermitView(ep.position);
    } else {
      restorePermitView();
    }
  }));
  $$('permit-custom-remove').forEach(b => b.addEventListener('click', () => {
    const id = b.dataset.permitCustomRemove;
    const i = state.build.permits.findIndex(p => p.id === id);
    if (i >= 0) state.build.permits.splice(i, 1);
    const wasEditing = state.ui && state.ui.editPermit === id;
    if (wasEditing) state.ui.editPermit = null;
    refreshDecals(); render();
    if (wasEditing) restorePermitView(); // removing the permit being edited closes it → restore view
  }));
  $$('permit-position').forEach(b => b.addEventListener('click', () => {
    const e = editingPermit(); if (!e) return;
    const pos = +b.dataset.permitPosition;
    if (state.build.permits.some(p => p.custom && p.id !== e.id && p.position === pos)) return; // taken
    e.position = pos; refreshDecals(); render();
    focusPermitView(pos); // fly to the newly chosen position so it's in view
  }));
  // Live text inputs (title / status / authority) → cheap 2D preview now, debounced 3D.
  const liveInput = (sel, key) => { const el = $(sel); if (!el) return; el.addEventListener('input', () => { const e = editingPermit(); if (!e) return; e.design = { ...e.design, [key]: el.value }; e.name = permitName(e.design); refreshPermitPreview(); debouncedPermitRebuild(); }); };
  liveInput('permit-title-input', 'title');
  liveInput('permit-status-input', 'status');
  liveInput('permit-authority-input', 'authority');
  $$('permit-layout').forEach(b => b.addEventListener('click', () => applyPermitDesign({ layout: b.dataset.permitLayout }, true)));
  $$('permit-status').forEach(b => b.addEventListener('click', () => applyPermitDesign({ status: b.dataset.permitStatus }, true)));
  $$('permit-class').forEach(b => b.addEventListener('click', () => applyPermitDesign({ cls: b.dataset.permitClass }, true)));
  $$('permit-season').forEach(b => b.addEventListener('click', () => applyPermitDesign({ season: b.dataset.permitSeason }, true)));
  $$('permit-accent').forEach(b => b.addEventListener('click', () => applyPermitDesign({ accent: b.dataset.permitAccent }, true)));
  $$('permit-base').forEach(b => b.addEventListener('click', () => applyPermitDesign({ base: b.dataset.permitBase }, true)));
  $$('permit-toggle').forEach(b => b.addEventListener('click', () => {
    const e = editingPermit(); if (!e) return;
    const k = b.dataset.permitToggle, cur = (k === 'holo') ? !!e.design.holo : (e.design[k] !== false);
    applyPermitDesign({ [k]: !cur }, true);
  }));
  $$('permit-preset').forEach(b => b.addEventListener('click', () => { const pr = (window.FR_PERMIT_PRESETS || [])[+b.dataset.permitPreset]; if (pr) applyPermitDesign({ ...pr.set }, true); }));
  $$('permit-surprise').forEach(b => b.addEventListener('click', () => {
    const pick = a => a[Math.floor(Math.random() * a.length)];
    applyPermitDesign({
      layout: pick(['authorization', 'pitpass', 'license']),
      status: pick(['APPROVED', 'VIP', 'ALL ACCESS', 'ACTIVE', 'PADDOCK', 'CLEARED', 'OUTLAW', 'SEND IT']),
      title: pick(['RACING PERMIT', 'PIT PASS', 'TRACK ACCESS', 'PADDOCK CLUB', 'STREET LICENCE', 'SCRUTINEERING']),
      cls: pick(['GT', 'PRO', 'AM', 'DRIFT', 'TIME ATTACK', 'OPEN', 'A', 'S']),
      accent: pick(['#f5d000', '#e01f2d', '#16c5e8', '#39ff14', '#ff6a1a', '#b06bff', '#ff1f8c']),
      base: pick(['carbon', 'noir', 'navy', 'blood', 'steel', 'security', 'cyber', 'tactical', 'bloodtech']),
      season: pick(['2025', '2026', '2027']),
      holo: Math.random() < 0.5,
    }, true);
  }));
  $$('permit-download').forEach(b => b.addEventListener('click', () => {
    const e = editingPermit(); if (!e) return;
    const url = permitPreviewURL(e.design); if (!url) return;
    const a = document.createElement('a'); a.href = url; a.download = `flameracer-permit-${state.buildId}-${e.id}.png`;
    document.body.appendChild(a); a.click(); a.remove();
  }));

  // ── CUSTOM SUN BANNERS — design / edit / position / mirror / remove ──────────
  const BANNER_PRICE = window.FR_BANNER_PRICE || 6.99;
  const editingBanner = () => state.build.banners.find(b => b.id === (state.ui && state.ui.editBanner));
  const bannerName = bd => 'Banner: ' + ((bd && bd.text) || 'CUSTOM');
  const refreshBannerPreview = () => { const e = editingBanner(); const pv = root.querySelector('[data-banner-preview]'); if (e && pv) { const u = bannerPreviewURL(e.design); if (u) pv.src = u; } };
  const applyBannerDesign = (patch, rerender) => {
    const e = editingBanner(); if (!e) return;
    e.design = { ...e.design, ...patch }; e.name = bannerName(e.design);
    if (rerender) { refreshDecals(); render(); } else { refreshBannerPreview(); }
  };
  let _bannerTypeTimer = null;
  const debouncedBannerRebuild = () => { if (_bannerTypeTimer) clearTimeout(_bannerTypeTimer); _bannerTypeTimer = setTimeout(() => refreshDecals(), 300); };
  $$('banner-new').forEach(b => b.addEventListener('click', () => {
    const POSITIONS = window.FR_BANNER_POSITIONS || [];
    const used = new Set(state.build.banners.map(x => x.position));
    let pos = 0; while (pos < POSITIONS.length && used.has(pos)) pos++;
    if (pos >= POSITIONS.length) return;
    const preset = (window.FR_BANNER_PRESETS || [])[0];
    const design = preset ? { ...preset.set } : { text: 'TOUGE', sub: '走り屋', bg: 'synthwave', motif: 'dragon', accent: '#ff2052', textColor: '#ffffff', font: '"Bebas Neue", Impact, sans-serif', outline: true, italic: true };
    const id = 'cust-banner-' + Math.random().toString(36).slice(2, 8);
    state.build.banners.push({ id, position: pos, design, name: bannerName(design), price: BANNER_PRICE, finish: state.build.decalFinish || 'brushed-aluminum', mirror: false });
    state.ui = state.ui || {}; state.ui.editBanner = id;
    refreshDecals(); render();
  }));
  $$('banner-edit').forEach(b => b.addEventListener('click', () => { state.ui = state.ui || {}; state.ui.editBanner = (state.ui.editBanner === b.dataset.bannerEdit) ? null : b.dataset.bannerEdit; render(); }));
  $$('banner-custom-remove').forEach(b => b.addEventListener('click', () => {
    const id = b.dataset.bannerCustomRemove;
    const i = state.build.banners.findIndex(x => x.id === id);
    if (i >= 0) state.build.banners.splice(i, 1);
    if (state.ui && state.ui.editBanner === id) state.ui.editBanner = null;
    refreshDecals(); render();
  }));
  $$('banner-position').forEach(b => b.addEventListener('click', () => {
    const e = editingBanner(); if (!e) return;
    const pos = +b.dataset.bannerPosition;
    if (state.build.banners.some(x => x.id !== e.id && x.position === pos)) return;
    e.position = pos;
    const def = (window.FR_BANNER_POSITIONS || [])[pos];
    if (!def || !def.mirrorable) e.mirror = false;
    refreshDecals(); render();
  }));
  $$('banner-toggle').forEach(b => b.addEventListener('click', () => {
    const e = editingBanner(); if (!e) return;
    const k = b.dataset.bannerToggle;
    if (k === 'mirror') { e.mirror = !e.mirror; refreshDecals(); render(); return; }
    applyBannerDesign({ [k]: !(e.design[k] !== false) }, true);
  }));
  const bannerText = $('banner-text-input');
  if (bannerText) bannerText.addEventListener('input', () => { const e = editingBanner(); if (!e) return; e.design = { ...e.design, text: bannerText.value }; e.name = bannerName(e.design); refreshBannerPreview(); debouncedBannerRebuild(); });
  const bannerSub = $('banner-sub-input');
  if (bannerSub) bannerSub.addEventListener('input', () => { const e = editingBanner(); if (!e) return; e.design = { ...e.design, sub: bannerSub.value }; refreshBannerPreview(); debouncedBannerRebuild(); });
  $$('banner-bg').forEach(b => b.addEventListener('click', () => applyBannerDesign({ bg: b.dataset.bannerBg }, true)));
  $$('banner-motif').forEach(b => b.addEventListener('click', () => applyBannerDesign({ motif: b.dataset.bannerMotif }, true)));
  $$('banner-accent').forEach(b => b.addEventListener('click', () => applyBannerDesign({ accent: b.dataset.bannerAccent }, true)));
  $$('banner-textcolor').forEach(b => b.addEventListener('click', () => applyBannerDesign({ textColor: b.dataset.bannerTextcolor }, true)));
  $$('banner-font').forEach(b => b.addEventListener('click', () => applyBannerDesign({ font: b.dataset.bannerFont }, true)));
  $$('banner-preset').forEach(b => b.addEventListener('click', () => { const pr = (window.FR_BANNER_PRESETS || [])[+b.dataset.bannerPreset]; if (pr) applyBannerDesign({ ...pr.set }, true); }));
  $$('banner-surprise').forEach(b => b.addEventListener('click', () => {
    const pick = a => a[Math.floor(Math.random() * a.length)];
    applyBannerDesign({
      bg: pick(['synthwave', 'risingsun', 'grid', 'vapor', 'sakura', 'wave', 'city', 'mountains', 'cyber', 'tactical', 'bloodtech']),
      motif: pick(['none', 'dragon', 'sun', 'sakura', 'oni', 'torii']),
      accent: pick(['#ff2052', '#ff3b30', '#16c5e8', '#39ff14', '#b06bff', '#ff6ab5']),
      textColor: pick(['#ffffff', '#ffd23b', '#ff2052']),
      text: pick(['TOUGE', 'DRIFT', 'NIPPON', 'ZERO', 'TOKYO', 'KANSEI', 'BOSO', 'NIGHT']),
      sub: pick(['走り屋', '頭文字', '日本', '東京', '零', '夜', '神奈川', '峠']),
      italic: Math.random() < 0.5,
    }, true);
  }));
  $$('banner-download').forEach(b => b.addEventListener('click', () => {
    const e = editingBanner(); if (!e) return;
    const url = bannerPreviewURL(e.design); if (!url) return;
    const a = document.createElement('a'); a.href = url; a.download = `flameracer-banner-${state.buildId}-${e.id}.png`;
    document.body.appendChild(a); a.click(); a.remove();
  }));
  $$('banner-finish').forEach(b => b.addEventListener('click', () => {
    const [id, fin] = b.dataset.bannerFinish.split(':');
    const e = state.build.banners.find(x => x.id === id);
    if (e) { e.finish = fin; refreshDecals(); render(); }
  }));

  // ── REAR WING DECAL controls ──
  const ensureWing = () => (state.build.wingDecal = state.build.wingDecal || { on: false, style: 'cyber', text: 'FLAMERACER', color: null, finish: null });
  const wingDesign = () => { const w = ensureWing(); return { style: w.style, text: w.text, color: w.color }; };
  const refreshWingPreview = () => { const pv = root.querySelector('[data-wing-preview]'); if (pv) { const u = wingPreviewURL(wingDesign()); if (u) pv.src = u; } };
  let _wingTypeTimer = null;
  const debouncedWingRebuild = () => { if (_wingTypeTimer) clearTimeout(_wingTypeTimer); _wingTypeTimer = setTimeout(() => refreshDecals(), 300); };
  const wt = $('wing-toggle');
  if (wt) wt.addEventListener('click', () => {
    const w = ensureWing();
    w.on = !w.on;
    if (w.on && !w.finish) w.finish = state.build.decalFinish || 'brushed-aluminum';
    refreshDecals(); render();
  });
  $$('wing-style').forEach(b => b.addEventListener('click', () => { const w = ensureWing(); w.style = b.dataset.wingStyle; refreshDecals(); render(); }));
  $$('wing-color').forEach(b => b.addEventListener('click', () => { const w = ensureWing(); w.color = b.dataset.wingColor || null; refreshDecals(); render(); }));
  const wingText = $('wing-text-input');
  if (wingText) wingText.addEventListener('input', () => { const w = ensureWing(); w.text = wingText.value; refreshWingPreview(); debouncedWingRebuild(); });
  $$('wing-finish').forEach(b => b.addEventListener('click', () => {
    const w = ensureWing(); const [, fin] = b.dataset.wingFinish.split(':');
    w.finish = fin; refreshDecals(); render();
  }));
  $$('wing-download').forEach(b => b.addEventListener('click', () => {
    const url = wingPreviewURL(wingDesign()); if (!url) return;
    const a = document.createElement('a'); a.href = url; a.download = `flameracer-wing-${state.buildId}.png`;
    document.body.appendChild(a); a.click(); a.remove();
  }));

  // ── FR SIGNATURE STICKERS controls (door badge + roof/deck panels) ──
  const AU_DEFAULTS = {
    doorBadge: { on: false, number: '07', name: 'FLAMERACER', sub: 'RACING DIVISION', style: 'classic', accent: null, finish: null },
    roofPanel: { on: false, text: 'FLAMERACER', number: '07', pattern: 'triangles', color: '#16f5d0', finish: null },
    deckPanel: { on: false, text: 'NO STEP', number: '13', pattern: 'hazard', color: '#f5d000', finish: null },
  };
  const ensureAuthored = (key) => {
    state.build.authored = state.build.authored || {};
    state.build.authored[key] = state.build.authored[key] || Object.assign({}, AU_DEFAULTS[key]);
    return state.build.authored[key];
  };
  const badgeDesign = () => { const a = ensureAuthored('doorBadge'); return { number: a.number, name: a.name, sub: a.sub, style: a.style, accent: a.accent }; };
  const panelDesign = (key) => { const a = ensureAuthored(key); return { text: a.text, number: a.number, pattern: a.pattern, color: a.color }; };
  const refreshBadgePreview = () => { const pv = root.querySelector('[data-badge-preview]'); if (pv) { const u = doorBadgePreviewURL(badgeDesign()); if (u) pv.src = u; } };
  const refreshPanelPreview = (key) => { const pv = root.querySelector(`[data-panel-preview="${key}"]`); if (pv) { const u = panelPreviewURL(panelDesign(key), key); if (u) pv.src = u; } };
  let _auTypeTimer = null;
  const debouncedAuthoredRebuild = () => { if (_auTypeTimer) clearTimeout(_auTypeTimer); _auTypeTimer = setTimeout(() => refreshDecals(), 300); };
  $$('authored-toggle').forEach(b => b.addEventListener('click', () => {
    const key = b.dataset.authoredToggle; const a = ensureAuthored(key);
    a.on = !a.on;
    state.ui = state.ui || {};
    if (a.on) { if (!a.finish) a.finish = state.build.decalFinish || 'brushed-aluminum'; state.ui.editAuthored = key; }
    else if (state.ui.editAuthored === key) state.ui.editAuthored = null;
    refreshDecals(); render();
  }));
  $$('authored-edit').forEach(b => b.addEventListener('click', () => {
    const key = b.dataset.authoredEdit; state.ui = state.ui || {};
    state.ui.editAuthored = state.ui.editAuthored === key ? null : key;
    render();
  }));
  $$('authored-finish').forEach(b => b.addEventListener('click', () => {
    const [key, fin] = b.dataset.authoredFinish.split(':');
    const a = ensureAuthored(key); a.finish = fin; refreshDecals(); render();
  }));
  // door badge fields
  const _wireInput = (sel, fn) => { const el = $(sel); if (el) el.addEventListener('input', fn); };
  _wireInput('badge-number-input', () => { const a = ensureAuthored('doorBadge'); a.number = $('badge-number-input').value; refreshBadgePreview(); debouncedAuthoredRebuild(); });
  _wireInput('badge-name-input',   () => { const a = ensureAuthored('doorBadge'); a.name = $('badge-name-input').value; refreshBadgePreview(); debouncedAuthoredRebuild(); });
  _wireInput('badge-sub-input',    () => { const a = ensureAuthored('doorBadge'); a.sub = $('badge-sub-input').value; refreshBadgePreview(); debouncedAuthoredRebuild(); });
  $$('badge-style').forEach(b => b.addEventListener('click', () => { const a = ensureAuthored('doorBadge'); a.style = b.dataset.badgeStyle; refreshDecals(); render(); }));
  $$('badge-accent').forEach(b => b.addEventListener('click', () => { const a = ensureAuthored('doorBadge'); a.accent = b.dataset.badgeAccent || null; refreshDecals(); render(); }));
  $$('badge-preset').forEach(b => b.addEventListener('click', () => {
    const pr = (window.FR_DOOR_BADGE_PRESETS || [])[+b.dataset.badgePreset]; if (!pr) return;
    Object.assign(ensureAuthored('doorBadge'), pr.set); refreshDecals(); render();
  }));
  // Surprise Me — same one-tap randomiser the permit/banner editors have.
  $$('badge-surprise').forEach(b => b.addEventListener('click', () => {
    const pick = a => a[Math.floor(Math.random() * a.length)];
    Object.assign(ensureAuthored('doorBadge'), {
      number: pick(['07', '13', '42', '69', '00', '01', '99', '404', '77']),
      name: pick(['FLAMERACER', 'NIGHT CITY', 'SHADOW OPS', 'RECON', 'ARMORED DIV', 'GL1TCH', 'DANGER', 'TOUGE CLUB']),
      sub: pick(['RACING DIVISION', 'CLASSIFIED', 'DESERT OPS', 'NETRUNNER', 'KEEP CLEAR', 'SIGNAL LOST', 'GT PROGRAMME', 'FIELD UNIT']),
      style: pick(((window.FR_DOOR_BADGE_STYLES || []).map(s => s.id)).concat('classic')),
      accent: Math.random() < 0.4 ? null : pick(['#f5d000', '#e01f2d', '#16f5d0', '#39ff14', '#ff1f8c', '#b06bff', '#ffffff']),
    });
    refreshDecals(); render();
  }));
  $$('badge-download').forEach(b => b.addEventListener('click', () => {
    const url = doorBadgePreviewURL(badgeDesign()); if (!url) return;
    const a = document.createElement('a'); a.href = url; a.download = `flameracer-door-badge-${state.buildId}.png`;
    document.body.appendChild(a); a.click(); a.remove();
  }));
  // roof/deck panel fields (keyed by data value "roofPanel"/"deckPanel")
  $$('panel-text-input').forEach(el => el.addEventListener('input', () => {
    const key = el.dataset.panelTextInput; const a = ensureAuthored(key); a.text = el.value;
    refreshPanelPreview(key); debouncedAuthoredRebuild();
  }));
  $$('panel-number-input').forEach(el => el.addEventListener('input', () => {
    const key = el.dataset.panelNumberInput; const a = ensureAuthored(key); a.number = el.value;
    refreshPanelPreview(key); debouncedAuthoredRebuild();
  }));
  $$('panel-pattern').forEach(b => b.addEventListener('click', () => {
    const [key, pat] = b.dataset.panelPattern.split(':'); const a = ensureAuthored(key); a.pattern = pat;
    refreshDecals(); render();
  }));
  $$('panel-color').forEach(b => b.addEventListener('click', () => {
    const [key, col] = b.dataset.panelColor.split(':'); const a = ensureAuthored(key); a.color = col;
    refreshDecals(); render();
  }));
  $$('panel-preset').forEach(b => b.addEventListener('click', () => {
    const [key, i] = b.dataset.panelPreset.split(':');
    const pr = (window.FR_PANEL_PRESETS || [])[+i]; if (!pr) return;
    Object.assign(ensureAuthored(key), pr.set); refreshDecals(); render();
  }));
  // Surprise Me — same one-tap randomiser the permit/banner editors have.
  $$('panel-surprise').forEach(b => b.addEventListener('click', () => {
    const pick = a => a[Math.floor(Math.random() * a.length)];
    const key = b.dataset.panelSurprise;
    Object.assign(ensureAuthored(key), {
      text: pick(['FLAMERACER', 'NO STEP', 'NEO TOKYO', 'OVERDRIVE', 'BLACKBOX', 'STRIKE UNIT', 'GHOST', 'VAPOR ONLY']),
      number: pick(['07', '13', '42', '77', '00', '99']),
      pattern: pick((window.FR_PANEL_PATTERNS || []).map(p => p.id)),
      color: pick(['#16f5d0', '#f5d000', '#ff1f8c', '#39ff14', '#b06bff', '#ff3b46', '#c7cba0', '#e8e9ee']),
    });
    refreshDecals(); render();
  }));
  $$('panel-download').forEach(b => b.addEventListener('click', () => {
    const key = b.dataset.panelDownload;
    const url = panelPreviewURL(panelDesign(key), key); if (!url) return;
    const a = document.createElement('a'); a.href = url; a.download = `flameracer-${key === 'roofPanel' ? 'roof' : 'deck'}-panel-${state.buildId}.png`;
    document.body.appendChild(a); a.click(); a.remove();
  }));

  // Tap the ◎ view affordance on a wheels-step header to fly the camera. It sits
  // INSIDE the fold-toggle header, so stop the click before it also folds.
  $$('wheel-focus').forEach(el => el.addEventListener('click', (e) => {
    e.stopPropagation();
    focusWheelView(el.dataset.wheelFocus);
  }));
  // Wheels-step section accordion — fold in place (no render) so the panel never jumps
  $$('wsec-toggle').forEach(el => {
    const toggle = () => {
      const id = el.dataset.wsecToggle;
      const open = !(_wheelSecOpen && _wheelSecOpen[id]);
      setWheelSecOpen(id, open);
      if (open) {   // opening a section = look at what it edits
        const focus = { set: 'wheels', finish: 'wheels', caliper: 'caliper', disk: 'caliper', caps: 'caps', tire: 'tire' }[id];
        const unlocked = state.build.wheel.id !== 'stock' || id === 'set' || id === 'tire';
        if (focus && unlocked) focusWheelView(focus);
      }
    };
    el.addEventListener('click', toggle);
    el.addEventListener('keydown', e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } });
  });
  // Tap the ◎ view button to fly the camera to that area. It sits INSIDE the
  // accordion header, so stop the click before it also folds the zone.
  $$('body-focus').forEach(el => el.addEventListener('click', e => {
    e.stopPropagation();
    focusBodyZone(el.dataset.bodyFocus);
  }));
  // Body-zone accordion — fold in place (no render) so the panel never jumps
  $$('zone-toggle').forEach(el => {
    const toggle = () => {
      const zid = el.dataset.zoneToggle;
      const open = !(_bodyZoneOpen && _bodyZoneOpen[zid]);
      setBodyZoneOpen(zid, open);
      if (open) focusBodyZone(zid);   // opening a zone = look at it
    };
    el.addEventListener('click', toggle);
    el.addEventListener('keydown', e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } });
  });
  // Decals-step flow sections — numbered headers fold in place (no render)
  $$('flow-toggle').forEach(el => el.addEventListener('click', () => {
    const id = el.dataset.flowToggle;
    setDecalSecOpen(id, !(_decalSecOpen && _decalSecOpen[id]));
  }));
  // Decals jump bar — open the section and scroll the panel to it
  $$('flow-jump').forEach(el => el.addEventListener('click', () => {
    const id = el.dataset.flowJump;
    setDecalSecOpen(id, true);
    scrollPanelToEl(root.querySelector(`.fr-flow-sec[data-flow-sec="${id}"]`));
  }));
  // Quick zone-jump nav — open the zone, scroll the panel to it AND fly the camera
  $$('zone-jump').forEach(el => el.addEventListener('click', () => {
    const zid = el.dataset.zoneJump;
    focusBodyZone(zid);
    setBodyZoneOpen(zid, true);
    scrollPanelToEl(root.querySelector(`.fr-zone-section[data-zone="${zid}"]`));
  }));
  // Chip in a collapsed zone's summary strip → open the zone, land on that card
  $$('picked-part').forEach(el => el.addEventListener('click', () => {
    const pid = el.dataset.pickedPart;
    const part = (window.FR_BODY_PARTS || []).find(p => p.id === pid);
    if (!part) return;
    setBodyZoneOpen(part.zone, true);   // before measuring — the strip collapses away
    focusBodyZone(part.zone);
    const card = root.querySelector(`.fr-part-card[data-part-id="${pid}"]`);
    if (!card) return;
    scrollPanelToEl(card);
    card.classList.add('fr-part-card-flash');
    setTimeout(() => card.classList.remove('fr-part-card-flash'), 1200);
  }));
  // Tap the ◎ view affordance on the plate header to fly the camera. It nests
  // inside the fold-toggle header, so stop the click before it also folds.
  $$('plate-focus').forEach(el => el.addEventListener('click', (e) => {
    e.stopPropagation();
    focusPlateView(el.dataset.plateFocus);
  }));
  // Plates-step section accordion — fold in place (no render)
  $$('psec-toggle').forEach(el => {
    const toggle = () => {
      const id = el.dataset.psecToggle;
      setPlateSecOpen(id, !(_plateSecOpen && _plateSecOpen[id]));
    };
    el.addEventListener('click', toggle);
    el.addEventListener('keydown', e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } });
  });
  const share = $('share-build');
  if (share) share.addEventListener('click', async () => {
    const url = buildShareUrl(); // encodes the WHOLE spec — opening the link rebuilds it
    try { await navigator.clipboard.writeText(url); share.textContent = '✓ Link copied'; }
    catch { window.prompt('Copy your build link:', url); }
    setTimeout(render, 2000);
  });
  const saveBuild = $('save-build');
  if (saveBuild) saveBuild.addEventListener('click', () => {
    const url = buildShareUrl();
    const subject = encodeURIComponent(`My Flameracer Build · ${state.buildId}`);
    const body = encodeURIComponent(`Here's my Flameracer build — open this link to view it:\n\n${url}\n`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  });
  // Review-step cost pills → jump to the relevant step.
  $$('cost-jump').forEach(b => b.addEventListener('click', () => { state.step = +b.dataset.costJump; render(); }));

  // ── MY GARAGE + LOADOUTS ──────────────────────────────────────────────────
  const gSave = $('garage-save');
  if (gSave) gSave.addEventListener('click', () => {
    const name = (state.build.paint?.name || 'Build') + ' · ' + (state.build.base?.name || 'Custom');
    FRGarage.add(name, encodeBuild(), scene.captureBuildThumbnail ? scene.captureBuildThumbnail() : '');
    render();
  });
  $$('garage-load').forEach(b => b.addEventListener('click', () => frLoadGarageBuild(b.dataset.garageLoad)));
  $$('loadout').forEach(b => b.addEventListener('click', () => frLoadGarageBuild(b.dataset.loadout)));
  $$('garage-delete').forEach(b => b.addEventListener('click', () => { FRGarage.remove(b.dataset.garageDelete); render(); }));
  $$('garage-rename').forEach(b => b.addEventListener('click', () => {
    const cur = FRGarage.get(b.dataset.garageRename); const name = window.prompt('Rename build:', cur ? cur.name : '');
    if (name != null && name.trim()) { FRGarage.rename(b.dataset.garageRename, name.trim()); render(); }
  }));

  // ── EXPORT: 360° spin GIF ─────────────────────────────────────────────────
  const gifBtn = $('export-gif');
  if (gifBtn) gifBtn.addEventListener('click', async () => {
    if (gifBtn.dataset.busy) return; gifBtn.dataset.busy = '1';
    const orig = gifBtn.textContent; gifBtn.textContent = 'Rendering 0%';
    try {
      const url = await scene.exportSpinGif(p => { gifBtn.textContent = `Rendering ${Math.round(p * 100)}%`; });
      if (url) { const a = document.createElement('a'); a.href = url; a.download = `flameracer-${state.buildId}.gif`; document.body.appendChild(a); a.click(); a.remove(); gifBtn.textContent = 'GIF saved ✓'; }
      else gifBtn.textContent = 'GIF failed';
    } catch (e) { console.warn('[FLR] gif export', e); gifBtn.textContent = 'GIF failed'; }
    setTimeout(() => { gifBtn.textContent = orig; delete gifBtn.dataset.busy; }, 2200);
  });

  // ── AR: reveal the button only if WebXR immersive-ar is supported ─────────
  const arBtn = $('enter-ar');
  if (arBtn && scene.arSupported) {
    scene.arSupported().then(ok => { if (ok) arBtn.style.display = ''; });
    arBtn.addEventListener('click', async () => {
      arBtn.textContent = 'Starting AR…';
      const ok = await scene.enterAR().catch(() => false);
      if (!ok) arBtn.textContent = 'AR unavailable';
      setTimeout(() => { arBtn.textContent = 'View in My Space'; }, 2500);
    });
  }
}

// Step navigation
$('fr-btn-prev').addEventListener('click', () => { if (state.step > 0) { state.step--; render(); } });
$('fr-btn-next').addEventListener('click', async () => {
  if (state.step < window.FR_STEPS.length - 1) { state.step++; render(); return; }
  // Final step → add to cart
  await addBuildToCart();
});

// ============================================================
// SHOPIFY CART INTEGRATION
// ============================================================
async function addBuildToCart() {
  const btn = $('fr-btn-next');
  btn.disabled = true;
  btn.textContent = 'Adding...';

  try {
    const items = buildCartPayload();
    if (!items.length) throw new Error('No items in build');

    // ONE-SKU BILLING GUARD: when the Custom Build variant is wired, verify the
    // amount we're about to charge (quantity × unit price) matches the quoted
    // total to the cent. Block the add rather than silently under-charge.
    // When the variant id is null the owner is still testing — don't block.
    if (window.FR_CUSTOM_BUILD_VARIANT_ID) {
      const _unitCents = window.FR_CUSTOM_BUILD_UNIT_CENTS || 1;
      const _buildItem = items.find(i => i.id === window.FR_CUSTOM_BUILD_VARIANT_ID);
      const _chargedTotal = _buildItem ? (_buildItem.quantity * _unitCents) / 100 : 0;
      const _quotedTotal = calculateTotal();
      if (Math.abs(_chargedTotal - _quotedTotal) > 0.01) {
        throw new Error(`Billing mismatch: about to charge $${_chargedTotal.toFixed(2)} but total is $${_quotedTotal.toFixed(2)}. Add to cart blocked — please refresh and try again.`);
      }
    }

    const response = await fetch(root.dataset.cartUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.description || `Cart add failed (${response.status})`);
    }

    // Success — redirect to cart
    window.location.href = root.dataset.cartPageUrl;
  } catch (err) {
    console.error('[FLAMERACER] Cart add failed:', err);
    alert(`Couldn't add to cart: ${err.message}\n\nThis usually means variant IDs aren't filled in yet. Check flameracer-data.js.`);
    btn.disabled = false;
    btn.textContent = 'Add to Cart →';
  }
}

function buildCartPayload() {
  const items = [];
  // Per-order PRINT FILE path (no backend): when the build has any custom art (a
  // custom plate or a custom permit), attach the full share link to the main car
  // line item. The encoded link rebuilds the EXACT design in the configurator, where
  // the owner clicks Download PNG to get the print file(s) for the sticker printer.
  const _customPermits = state.build.permits.filter(p => p.custom);
  const _customBanners = state.build.banners || [];
  const _wingDecal = (state.build.wingDecal && state.build.wingDecal.on) ? state.build.wingDecal : null;
  const _auth = state.build.authored || {};
  const _dbOn = _auth.doorBadge && _auth.doorBadge.on ? _auth.doorBadge : null;
  const _rpOn = _auth.roofPanel && _auth.roofPanel.on ? _auth.roofPanel : null;
  const _dpOn = _auth.deckPanel && _auth.deckPanel.on ? _auth.deckPanel : null;
  const _hasCustomPlate = !!(state.build.licensePlate && state.build.licensePlate.custom);
  const _hasCustomArt = _hasCustomPlate || _customPermits.length > 0 || _customBanners.length > 0 || !!_wingDecal || !!_dbOn || !!_rpOn || !!_dpOn;
  const _permitPositions = window.FR_PERMIT_POSITIONS || [];
  const _bannerPositions = window.FR_BANNER_POSITIONS || [];
  const _bracket = (window.FR_PLATE_BRACKET_COLORS || []).find(c => c.id === state.build.bracketColor);
  const _allCC = [].concat(window.FR_CUSTOM_COLORS || [], window.FR_SATIN_COLORS || [], window.FR_PEARL_COLORS || []);
  const _ccName = (hex) => (_allCC.find(c => c.hex.toLowerCase() === (hex || '').toLowerCase()) || {}).name || (state.build.paint.hex && state.build.paint.hex.toLowerCase() === (hex || '').toLowerCase() ? state.build.paint.name : hex) || '—';
  const _customPrice = window.FR_CUSTOM_PAINT_PRICE || 99;
  const buildProps = {
    'Build ID': state.buildId,
    'Configuration': state.build.base.name,
    ...(_bracket && _bracket.hex && state.build.licensePlate ? { 'Plate Bracket': _bracket.name } : {}),
    ...(((state.build.custom && state.build.custom.on) || (state.build.split && state.build.split.on)) ? { 'Atelier Custom Paint': ((state.build.split && state.build.split.on) ? `Split: ${_ccName(state.build.split.colorA)} / ${_ccName(state.build.split.colorB)}${(state.build.split.parts || []).length ? ' + ' + state.build.split.parts.length + ' part(s)' : ''}` : `${_ccName(state.build.custom.color)}${state.build.custom.finish === 'satin' ? ' · satin' : ''}${state.build.custom.pearl ? ' · pearlescent' : ''}`) + ` (+$${_customPrice})` } : {}),
    ...((state.build.trimColor || (state.build.trimAreas && Object.keys(state.build.trimAreas).length)) ? (() => { const items = [state.build.trimColor ? 'All:' + _ccName(state.build.trimColor) : '', ...Object.entries(state.build.trimAreas || {}).filter(([, h]) => h).map(([a, h]) => a + ':' + _ccName(h))].filter(Boolean); const _tp = (state.build.custom && state.build.custom.on) || (state.build.split && state.build.split.on); const _cost = _tp ? items.length * (window.FR_TRIM_AREA_PRICE || 25) : (window.FR_TRIM_STANDALONE_PRICE || 100); return { 'Custom Trim': items.join(' · ') + ` (+$${_cost})` }; })() : {}),
    ...((state.build.split && state.build.split.on && state.build.trimSplit && state.build.trimSplit.length) ? { 'Trim Follows Paint Shift': state.build.trimSplit.join(' · ') } : {}),
    ...((state.build.carbon && state.build.carbon.on) ? { 'Atelier Carbon Weave': `${state.build.carbon.tint ? _ccName(state.build.carbon.tint) + ' tint' : 'Raw carbon'}${state.build.carbon.shift ? ' · colour-shift' : ''} (+$${window.FR_CARBON_WEAVE_PRICE || 199})` } : {}),
    ...(_hasCustomArt ? {
      'Custom Design File': buildShareUrl(),
      'Print Instructions': 'Open the Custom Design File link, then click Download PNG in the editor to get the print-ready file(s).',
      ...(_hasCustomPlate ? { 'Custom Plate Text': state.build.licensePlate.text || '' } : {}),
      ...(_customPermits.length ? { 'Custom Permits': _customPermits.map(p => `"${(p.design && (p.design.status || p.design.title)) || ''}" @ ${(_permitPositions[p.position] || {}).label || 'fender'}`).join(' · ') } : {}),
      ...(_customBanners.length ? { 'Custom Banners': _customBanners.map(bn => `"${(bn.design && bn.design.text) || ''}" @ ${(_bannerPositions[bn.position] || {}).label || 'windshield'}${bn.mirror ? ' (mirrored)' : ''}`).join(' · ') } : {}),
      ...(_wingDecal ? { 'Rear Wing Decal': `"${_wingDecal.text || 'FLAMERACER'}" · ${((window.FR_WING_DECAL_STYLES || []).find(s => s.id === _wingDecal.style) || {}).label || _wingDecal.style || 'Cyber Neon'}${_wingDecal.color ? ' · ' + _wingDecal.color : ''} (+$${(window.FR_WING_DECAL_PRICE || 6.99).toFixed(2)})` } : {}),
      ...(_dbOn ? { 'Door Number Badge': `#${_dbOn.number || '07'} "${_dbOn.name || ''}" · ${((window.FR_DOOR_BADGE_STYLES || []).find(s => s.id === _dbOn.style) || {}).label || _dbOn.style} · both doors (+$${(window.FR_DOOR_BADGE_PRICE || 11.99).toFixed(2)})` } : {}),
      ...(_rpOn ? { 'Roof Panel Sticker': `"${_rpOn.text || ''}" #${_rpOn.number || ''} · ${_rpOn.pattern} · ${_rpOn.color} (+$${(window.FR_PANEL_STICKER_PRICE || 8.99).toFixed(2)})` } : {}),
      ...(_dpOn ? { 'Rear Deck Panel Sticker': `"${_dpOn.text || ''}" #${_dpOn.number || ''} · ${_dpOn.pattern} · ${_dpOn.color} (+$${(window.FR_PANEL_STICKER_PRICE || 8.99).toFixed(2)})` } : {}),    } : {}),
  };

  // ── ONE-SKU BILLING (see FR_CUSTOM_BUILD_VARIANT_ID in flameracer-data.js) ──
  // If a Custom Build variant is configured, bill the EXACT quoted total as a
  // single line item (quantity = total-in-cents / unit-cents). All the build
  // spec below (paint, carbon, trim, wheels, decals, plate, permits, banners)
  // still rides as fulfillment properties on that one item — nothing is lost,
  // no per-variant price dependency, no double-charge.
  if (window.FR_CUSTOM_BUILD_VARIANT_ID) {
    const _total = calculateTotal();
    const _unit = window.FR_CUSTOM_BUILD_UNIT_CENTS || 1;
    const _decalFinNameOne = id => (window.FR_DECAL_FINISHES.find(f => f.id === id) || {}).name || 'Brushed Aluminum';
    const _specProps = {
      ...buildProps,
      'Paint': state.build.paint.name,
      // Wheels + tire spec
      ...(state.build.wheel.price > 0 ? {
        'Wheel': state.build.wheel.name,
        'Wheel Finish': state.build.wheelFinish?.name || 'Default',
        'Caliper Color': state.build.caliper.name,
        'Disk Color': state.build.diskColor?.name || 'Silver',
        ...(state.build.tirePrint ? { 'Tire Print': state.build.tirePrint.name + (state.build.tirePrintColor ? ` (${state.build.tirePrintColor.name})` : '') } : {}),
        ...(state.build.tirePinstripe ? { 'Tire Pinstripe': state.build.tirePinstripe.name } : {}),
      } : {}),
      ...(state.build.centerCap ? { 'Cap Color': state.build.centerCap.name + ' (set of 4)' } : {}),
      // Body parts (finishes)
      ...(state.build.parts.length ? { 'Body Parts': state.build.parts.map(({ part, finish }) => `${part.code}: ${finish.name}`).join(' · ') } : {}),
      // Racing permits
      ...(state.build.permits.length ? { 'Racing Permits': state.build.permits.map(p => `${p.name} (${_decalFinNameOne(p.finish || state.build.decalFinish)})`).join(' · ') } : {}),
      // License plate
      ...(state.build.licensePlate ? {
        'Plate Design': state.build.licensePlate.name,
        'Plate Finish': state.build.licensePlate.finish?.name || 'Brushed Aluminum',
        'Mount': state.build.plateMount?.name || 'Front Only',
      } : {}),
      'Build Total': '$' + _total.toFixed(2),
    };
    return [{
      id: window.FR_CUSTOM_BUILD_VARIANT_ID,
      quantity: Math.max(1, Math.round(_total * 100 / _unit)),
      properties: _specProps,
    }];
  }

  // ── FALLBACK (billing NOT wired) — preserve the legacy per-variant items so
  // nothing breaks while unconfigured. These UNDER-charge (see the bug: 70 of 90
  // variantIds are null). Set FR_CUSTOM_BUILD_VARIANT_ID to switch to one-SKU. ──
  console.warn('[FLAMERACER] Checkout billing not wired — set FR_CUSTOM_BUILD_VARIANT_ID. Customers would not be charged the full total.');

  // 1. Base variant (config + paint combination)
  const baseKey = `${state.build.base.id}_${state.build.paint.id}`;
  const baseVariantId = window.FR_BASE_VARIANT_MAP[baseKey];

  if (!baseVariantId) {
    console.warn('[FLAMERACER] Missing base variant ID for', baseKey, '— check FR_BASE_VARIANT_MAP');
  }

  items.push({
    id: baseVariantId,
    quantity: 1,
    properties: {
      ...buildProps,
      'Paint': state.build.paint.name,
    },
  });

  // 2. Wheels (if not stock)
  if (state.build.wheel.price > 0 && state.build.wheel.variantId) {
    items.push({
      id: state.build.wheel.variantId,
      quantity: 1,
      properties: {
        'Build ID': state.buildId,
        'Wheel': state.build.wheel.name,
        'Wheel Finish': state.build.wheelFinish?.name || 'Default',
        'Caliper Color': state.build.caliper.name,
        'Disk Color': state.build.diskColor?.name || 'Silver',
        ...(state.build.tirePrint ? { 'Tire Print': state.build.tirePrint.name + (state.build.tirePrintColor ? ` (${state.build.tirePrintColor.name})` : '') } : {}),
        ...(state.build.tirePinstripe ? { 'Tire Pinstripe': state.build.tirePinstripe.name } : {}),
      },
    });
  }

  // 2b. Wheel finish as its own line item (only if you gave the finish
  // its own Shopify variant — otherwise it rides as a property above).
  if (state.build.wheel.price > 0 && state.build.wheelFinish?.variantId) {
    items.push({
      id: state.build.wheelFinish.variantId,
      quantity: 1,
      properties: {
        'Build ID': state.buildId,
        'Wheel Finish': state.build.wheelFinish.name,
      },
    });
  }

  // 2c. Tire print as its own line item (only once you give the print a
  // Shopify variant). Applies to any wheel set, including stock — so it's
  // independent of the wheel block above. Until variantId is set it rides
  // as the 'Tire Print' property on the wheel line item (2) instead.
  if (state.build.tirePrint?.variantId) {
    items.push({
      id: state.build.tirePrint.variantId,
      quantity: 1,
      properties: {
        'Build ID': state.buildId,
        'Tire Print': state.build.tirePrint.name,
        'Tier': state.build.tirePrint.tier || '',
        ...(state.build.tirePrintColor ? { 'Wordmark Color': state.build.tirePrintColor.name } : {}),
      },
    });
  }

  // 3. Center caps — sold as one set of 4 for the flat $9.99 price.
  // NOTE: the Shopify cap variant must be priced as the SET ($9.99), since we
  // send quantity 1 here (not per-cap).
  if (state.build.centerCap?.variantId) {
    items.push({
      id: state.build.centerCap.variantId,
      quantity: 1,
      properties: {
        'Build ID': state.buildId,
        'Cap Color': state.build.centerCap.name,
        'Set': '4 caps',
      },
    });
  }

  // 4. Body parts
  state.build.parts.forEach(({ part, finish }) => {
    if (!part.variantId) {
      console.warn('[FLAMERACER] Missing variant ID for', part.id);
      return;
    }
    items.push({
      id: part.variantId,
      quantity: 1,
      properties: {
        'Build ID': state.buildId,
        'Part': part.code,
        'Finish': finish.name,
      },
    });
  });

  // 5. Decals — pack designs carry an $8 per-pack base fee (FR_DECAL_PACK_PRICE,
  // shown in the configurator total); racing permits are paid add-ons too. Real
  // Shopify variant IDs are still TODO for packs AND permits, so they only become
  // checkout line items once they have a variantId; until then the chosen finish +
  // design list ride along as properties and the fee shows in the quoted total.
  const _decalFinName = id => (window.FR_DECAL_FINISHES.find(f => f.id === id) || {}).name || 'Brushed Aluminum';
  state.build.permits.forEach(p => {
    if (!p.variantId) return;
    items.push({
      id: p.variantId,
      quantity: 1,
      properties: {
        'Build ID': state.buildId,
        'Racing Permit': p.name,
        'Decal Finish': _decalFinName(p.finish || state.build.decalFinish),
      },
    });
  });

  // 6. License plate (with finish + mount)
  if (state.build.licensePlate?.variantId) {
    items.push({
      id: state.build.licensePlate.variantId,
      quantity: 1,
      properties: {
        'Build ID': state.buildId,
        'Plate Design': state.build.licensePlate.name,
        'Plate Finish': state.build.licensePlate.finish?.name || 'Brushed Aluminum',
        'Mount': state.build.plateMount?.name || 'Front Only',
      },
    });
  }

  return items.filter(i => i.id);
}

// ============================================================
// BOOT
// ============================================================
// Loader with a real 1–100% readout. Driven by the GLB download progress
// (see the boot loop below); stays up until the car is in the scene, with a
// hard fallback so it can never get permanently stuck.
const _loaderEl = $('fr-loading');
const _loaderText = document.querySelector('.fr-loading-text');
const _loaderBar = document.querySelector('.fr-loading-bar');
let _loaderFill = null;
if (_loaderBar) {
  _loaderBar.classList.add('is-determinate');
  _loaderFill = document.createElement('div');
  _loaderFill.className = 'fr-load-fill';
  _loaderBar.appendChild(_loaderFill);
}
window.__frSetLoadPct = (pct) => {
  pct = Math.max(0, Math.min(100, Math.round(pct)));
  if (_loaderText) _loaderText.textContent = (pct >= 100 ? 'PREPARING STUDIO' : 'LOADING ' + pct + '%');
  if (_loaderFill) _loaderFill.style.width = pct + '%';
};
window.__frHideLoader = () => { if (_loaderEl) _loaderEl.classList.add('hidden'); };
window.__frSetLoadPct(0);
// Fallback: never leave the loader up longer than 25s even if a load hangs.
const _loaderFallback = setTimeout(() => window.__frHideLoader(), 45000); // generous: covers slow GLB + load retries
window.__frClearLoaderFallback = () => clearTimeout(_loaderFallback);
// Declared BEFORE this initial render(): the first render() calls setView('hero')
// → animateCameraTo(), which reads _camAnimRAF. If the `let` lived only below (next
// to animateCameraTo), this call would hit its temporal-dead-zone → ReferenceError
// that ABORTS the whole module, so the car-boot loader further down never runs and
// the placeholder car is shown after the loader times out. Initializing it here is
// the fix. (animateCameraTo itself is a hoisted function decl, so it's callable.)
let _camAnimRAF = null; // in-flight camera animation — cancelled before a new one
render();

// ============================================================
// CAMERA — reusable eased transition
// Both the view-angle buttons and the decal panel fly-to use this.
// ============================================================
// _camAnimRAF is declared above the initial render() call (TDZ-safe — see note there).
function animateCameraTo(posArr, lookArr, rotY, duration = 820) {
  const cam = window.__frCamera;
  if (!cam) return;
  // Cancel any in-flight transition so two never fight (the #1 cause of a preset
  // not landing exactly). Presets are authoritative: this always finishes on the
  // exact pos/look/rotY requested.
  if (_camAnimRAF) cancelAnimationFrame(_camAnimRAF);
  if (typeof scene !== 'undefined' && scene.requestRender) scene.requestRender(duration + 200);

  const startPos = { x: cam.position.x, y: cam.position.y, z: cam.position.z };
  const endPos   = { x: posArr[0],  y: posArr[1],  z: posArr[2]  };
  const startLook = cam.userData._lookAt || { x: 0, y: -0.1, z: 0 };
  const endLook   = { x: lookArr[0], y: lookArr[1], z: lookArr[2] };

  // Rotate the car group to match (keeps user-drag continuity).
  // ⚠️ Use the scene's REAL carGroup — NOT children.find(Group !== garage), which
  // returns the first non-garage group = the 'hexlight' rig, so the car's rotation
  // never actually reset (the 360-drag "memory" bug). scene.carGroup is the car.
  const carGroup = (typeof scene !== 'undefined' && scene.carGroup)
    || window.__frScene?.children.find(c => c.type === 'Group' && c.name !== 'garage' && c.name !== 'hexlight');
  const startRot = carGroup?.rotation.y || 0;
  // SHORTEST-PATH rotation: after the user scrolls/drags around, rotation.y can be
  // many turns from the preset. Rotate the SHORT way to the equivalent angle
  // instead of unwinding the whole spin — so "back to hero" snaps cleanly.
  let endRot = startRot;
  if (rotY !== undefined && rotY !== null) {
    const TWO_PI = Math.PI * 2;
    let delta = ((rotY - startRot) % TWO_PI + TWO_PI) % TWO_PI; // 0..2π
    if (delta > Math.PI) delta -= TWO_PI;                       // -π..π (short way)
    endRot = startRot + delta;
  }
  // Re-centre the customizing ±30° drag clamp on the view we're easing to.
  if (typeof scene !== 'undefined') scene._baseRotY = endRot;
  // Any camera transition clears the wheel-finish asymmetric-clamp flag by default;
  // focusWheelView('wheels') re-asserts it right after this call. (TASK 1)
  if (typeof scene !== 'undefined') scene._wheelFinishClamp = false;

  const t0 = performance.now();

  function animate(now) {
    const t = Math.min(1, (now - t0) / duration);
    // Ease in/out cubic
    const e = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

    cam.position.x = startPos.x + (endPos.x - startPos.x) * e;
    cam.position.y = startPos.y + (endPos.y - startPos.y) * e;
    cam.position.z = startPos.z + (endPos.z - startPos.z) * e;
    const lookX = startLook.x + (endLook.x - startLook.x) * e;
    const lookY = startLook.y + (endLook.y - startLook.y) * e;
    const lookZ = startLook.z + (endLook.z - startLook.z) * e;
    cam.lookAt(lookX, lookY, lookZ);
    cam.userData._lookAt = { x: lookX, y: lookY, z: lookZ };

    if (carGroup) {
      carGroup.rotation.y = startRot + (endRot - startRot) * e;
      // Sync scene targetRotY so user dragging picks up from new angle — and so
      // the _animate easing loop never tugs the car away from the preset.
      if (typeof scene !== 'undefined' && scene.targetRotY !== undefined) {
        scene.targetRotY = carGroup.rotation.y;
        scene.currentRotY = carGroup.rotation.y;
      }
    }

    if (t < 1) { _camAnimRAF = requestAnimationFrame(animate); }
    else { _camAnimRAF = null; } // settled exactly on the preset
  }
  _camAnimRAF = requestAnimationFrame(animate);
}

// Fly the camera to a decal panel's preset (defined on the zone in data.js)
function focusStickerZone(zoneId) {
  const zone = (window.FR_STICKER_ZONES || []).find(z => z.id === zoneId);
  if (!zone || !zone.view) return;
  animateCameraTo(zone.view.pos, zone.view.look, zone.view.rotY);
  // We're now framing a panel — drop the active state on the manual view buttons
  document.querySelectorAll('[data-fr-view]').forEach(b => b.classList.remove('active'));
}

// Fly the camera to a wheels-step preset (defined in FR_WHEEL_VIEWS in data.js)
// key: 'wheels' (stance) · 'caliper' (tight on wheel) · 'caps' (tight on hub)
function focusWheelView(key) {
  const v = (window.FR_WHEEL_VIEWS || {})[key];
  if (!v) return;
  if (typeof scene !== 'undefined' && scene) scene._presetView = false;   // zoomed view → no idle spin
  animateCameraTo(v.pos, v.look, v.rotY);
  // WHEEL-FINISH VIEW asymmetric drag clamp (TASK 1). Only the 'wheels' preset — the
  // wheel-finish focus view — locks the RIGHT side to the base angle while leaving LEFT
  // free. animateCameraTo() above already cleared this flag for every other transition,
  // and it captures scene._baseRotY = endRot, so the clamp pivots on the exact entry
  // angle. NOTE: preview tab runs hidden so the tween/clamp can't be visually checked
  // here — verified by logic + module-check. Tunable via window.__frWheelClamp* hooks.
  if (typeof scene !== 'undefined' && scene) scene._wheelFinishClamp = (key === 'wheels' || key === 'caliper' || key === 'caps'); // 2026-06-30 (user): clamp wheel-finish + caliper + disk (disk uses the 'caliper' view) + center caps
  document.querySelectorAll('[data-fr-view]').forEach(b => b.classList.remove('active'));
}

// Fly the camera to a body-part zone's preset (view lives on FR_BODY_ZONES)
function focusBodyZone(zoneId) {
  const zone = (window.FR_BODY_ZONES || []).find(z => z.id === zoneId);
  if (!zone || !zone.view) return;
  if (typeof scene !== 'undefined' && scene) scene._presetView = false;   // zoomed view → no idle spin
  animateCameraTo(zone.view.pos, zone.view.look, zone.view.rotY);
  document.querySelectorAll('[data-fr-view]').forEach(b => b.classList.remove('active'));
}

// Fly the camera to the front or rear plate (FR_PLATE_VIEWS in data.js)
// mountId: 'both' | 'front' -> front view · 'rear' -> rear view
function focusPlateView(mountId) {
  const key = mountId === 'rear' ? 'rear' : 'front';
  const v = (window.FR_PLATE_VIEWS || {})[key];
  if (!v) return;
  if (typeof scene !== 'undefined' && scene) scene._presetView = false;   // zoomed view → no idle spin
  animateCameraTo(v.pos, v.look, v.rotY);
  document.querySelectorAll('[data-fr-view]').forEach(b => b.classList.remove('active'));
}

// Fly the camera to a CUSTOM PERMIT's slot on the bodywork so the customer can
// actually see the credential they're designing/placing — it's otherwise often
// projected onto the far/hidden side of the car and invisible while editing.
// `positionIndex` is an index into FR_PERMIT_POSITIONS (a permit's `.position`).
// Mirrors focusWheelView but computes the frame dynamically: it samples the slot's
// live WORLD position + surface normal via scene._decalNativeToWorld(slot), then
// places the camera out along that normal (gentle 3/4 yaw + slight crane) so the
// permit fills a good chunk of the view on the CORRECT side. Because the camera
// offset is derived from the slot's own normal, it always lands on whichever side
// the permit is on — no per-slot tuning needed.
function focusPermitView(positionIndex) {
  if (typeof scene === 'undefined' || !scene) return;
  scene._presetView = false;   // zoomed view → no idle spin
  const positions = window.FR_PERMIT_POSITIONS || [];
  const slot = (positions[positionIndex] || positions[0] || {}).slot;
  if (!slot || typeof scene._decalNativeToWorld !== 'function' || !scene.combinedModel) return;

  // Capture the view we're flying AWAY from so exiting the permit editor restores it
  // (instead of snapping to hero). Mirror setFullscreen's _preFsView pattern: only
  // capture on the FIRST permit fly — re-opening another permit while already in a
  // permit fly must NOT overwrite the original pre-permit view (no stacking).
  if (!scene._prePermitView) {
    const cam = scene.camera || window.__frCamera;
    const L = cam && cam.userData ? cam.userData._lookAt : null;
    if (cam) scene._prePermitView = {
      pos: cam.position.toArray(),
      look: L ? [L.x, L.y, L.z] : null,
      rotY: scene.carGroup ? scene.carGroup.rotation.y : 0,
    };
  }

  // animateCameraTo rotates the car back to the rotY we pass, so sample the slot
  // with the car AT that rotation → pos/look stay consistent with the final frame.
  // Keep the car at its authored base orientation (same side the wheel/body presets
  // use); the camera offset below is what guarantees we end up facing the permit.
  const carGroup = scene.carGroup;
  const rotY = (scene._baseRotY !== undefined ? scene._baseRotY : (carGroup ? carGroup.rotation.y : 0));
  let prevRot;
  if (carGroup) { prevRot = carGroup.rotation.y; carGroup.rotation.y = rotY; carGroup.updateWorldMatrix(true, true); }

  const { pos, normal } = scene._decalNativeToWorld(slot);

  if (carGroup) { carGroup.rotation.y = prevRot; carGroup.updateWorldMatrix(true, true); }

  // Pull the camera OUT along the surface normal (so it sits off the panel facing
  // the permit), but add a gentle 3/4 by yawing that outward direction ~22° around
  // Y, and lift it a touch for a flattering down-angle. Distance is tied to the
  // model fit so the permit fills the frame on any model scale.
  const out = normal.clone(); out.y *= 0.35; // flatten the normal's vertical tilt
  if (out.lengthSq() < 1e-6) out.set(1, 0, 0);
  out.normalize();
  const yaw = -0.38; // ~22° three-quarter swing
  const cy = Math.cos(yaw), sy = Math.sin(yaw);
  const ox = out.x * cy - out.z * sy;
  const oz = out.x * sy + out.z * cy;
  const dist = 0.95; // world units off the panel — tight enough to read the credential
  const camPos = [
    pos.x + ox * dist,
    pos.y + 0.32,            // slight crane for a 3/4 down-angle
    pos.z + oz * dist,
  ];
  const look = [pos.x, pos.y, pos.z];
  animateCameraTo(camPos, look, rotY, 760);
  document.querySelectorAll('[data-fr-view]').forEach(b => b.classList.remove('active'));
}
// Restore the view captured before the FIRST permit fly-to (set by focusPermitView).
// Called when the permit editor CLOSES or the user leaves the Decals step, so the
// camera returns to where the user was — not hero. Clears the capture so the next
// open re-captures cleanly. No-op if we never entered a permit fly.
function restorePermitView() {
  if (typeof scene === 'undefined' || !scene) return;
  const v = scene._prePermitView;
  if (!v) return;
  scene._prePermitView = null;
  if (typeof animateCameraTo === 'function') {
    animateCameraTo(v.pos, v.look || [0, -0.1, 0], v.rotY, 620);
  }
}
// Expose on the scene instance too, matching the focusPermitView(positionIndex)
// naming used elsewhere / in the spec (the body of the helper lives in this closure
// alongside animateCameraTo, so we delegate to it rather than duplicate the math).
if (typeof scene !== 'undefined' && scene) { scene.focusPermitView = focusPermitView; scene.restorePermitView = restorePermitView; }

// ============================================================
// FOUNDATION-STEP MICRO-MOTION
// Subtle camera reactions when picking a config or paint. Camera-only
// (no car spin, no zoom): each eases out and settles back exactly.
//   cameraTilt(rise, dur)  → very slight vertical crane  (paint)
//   cameraAngle(deg, dur)  → very slight sideways angle   (config)
// Tune the first arg in each call below (in the base/paint handlers).
// ============================================================
let _tilting = false;
function cameraTilt(rise = 0.35, dur = 540) {
  const cam = window.__frCamera;
  if (!cam || _tilting) return;
  if (typeof scene !== 'undefined' && scene.requestRender) scene.requestRender(dur + 120);
  _tilting = true;
  const look = cam.userData._lookAt || { x: 0, y: -0.1, z: 0 };
  const y0 = cam.position.y;
  const t0 = performance.now();
  function f(now) {
    const t = Math.min(1, (now - t0) / dur);
    const e = Math.sin(t * Math.PI); // 0 → 1 → 0
    cam.position.y = y0 + rise * e;
    cam.lookAt(look.x, look.y, look.z);
    if (t < 1) requestAnimationFrame(f);
    else { cam.position.y = y0; cam.lookAt(look.x, look.y, look.z); _tilting = false; }
  }
  requestAnimationFrame(f);
}

let _angling = false;
function cameraAngle(deg = 5, dur = 540) {
  const cam = window.__frCamera;
  if (!cam || _angling) return;
  if (typeof scene !== 'undefined' && scene.requestRender) scene.requestRender(dur + 120);
  _angling = true;
  const look = cam.userData._lookAt || { x: 0, y: -0.1, z: 0 };
  const ax = cam.position.x - look.x, az = cam.position.z - look.z;
  const r  = Math.hypot(ax, az);
  const a0 = Math.atan2(az, ax);
  const t0 = performance.now();
  function f(now) {
    const t = Math.min(1, (now - t0) / dur);
    const e = Math.sin(t * Math.PI);
    const a = a0 + (deg * Math.PI / 180) * e;
    cam.position.x = look.x + Math.cos(a) * r;
    cam.position.z = look.z + Math.sin(a) * r;
    cam.lookAt(look.x, look.y, look.z);
    if (t < 1) requestAnimationFrame(f);
    else { cam.position.x = look.x + Math.cos(a0) * r; cam.position.z = look.z + Math.sin(a0) * r; cam.lookAt(look.x, look.y, look.z); _angling = false; }
  }
  requestAnimationFrame(f);
}


function setView(viewId) {
  const preset = viewPresets[viewId];
  if (!preset) return;
  // Hide the hex panels in TOP view so they don't cover the car
  if (typeof scene !== 'undefined' && scene.setTopView) scene.setTopView(viewId === 'top');
  if (typeof scene !== 'undefined' && scene) scene._presetView = true;   // idle spin allowed at the 5 preset angles
  animateCameraTo(preset.pos, preset.look, preset.rotY, 780);
}

// SIDE SWEEP — drops the car to a side profile and gently rocks it left↔right
// (smooth, via the scene's targetRotY lerp). Toggle on/off.
let _sweepRAF = null;
function toggleSideSweep(btn) {
  if (_sweepRAF) { cancelAnimationFrame(_sweepRAF); _sweepRAF = null; if (btn) btn.classList.remove('active'); return; }
  if (typeof scene !== 'undefined' && scene.setTopView) scene.setTopView(false);
  setView('side');                       // ease the camera to a clean side profile
  if (btn) btn.classList.add('active');
  const t0 = performance.now();
  const loop = (now) => {
    const tt = (now - t0) / 1000;
    // let setView's 0.6s camera move settle, then take over the car rotation
    if (tt > 0.55 && typeof scene !== 'undefined') scene.targetRotY = Math.sin((tt - 0.55) * 0.7) * 0.62;
    _sweepRAF = requestAnimationFrame(loop);
  };
  _sweepRAF = requestAnimationFrame(loop);
}

// Wire view buttons
document.querySelectorAll('[data-fr-view]').forEach(btn => {
  btn.addEventListener('click', () => {
    document.querySelectorAll('[data-fr-view]').forEach(b => b.classList.remove('active'));
    btn.classList.add('active');
    setView(btn.dataset.frView);
  });
});


// ============================================================
// REAL CAR GLB — Car_Parts.glb (combined body + wheels + tires + wordmark)
// The file lives in Shopify admin → Content → Files (served from the
// /cdn/shop/files/ CDN, same as the configurator images). The loader tries
// several URL candidates in order and uses the first that loads, so it works
// whether the file is in Content → Files OR theme assets/.
//
// To pin an exact URL (recommended), set this in the theme before the script:
//   window.FR_CAR_GLB = "{{ 'flameracer-car.glb' | file_url }}";   // Content → Files
//   (or)  window.FR_CAR_GLB = "{{ 'flameracer-car.glb' | asset_url }}"; // Assets
// ============================================================
(function loadRealCar() {
  if (typeof scene === 'undefined' || !scene.loadFullCar) return;

  // Candidate URLs, in priority order. First to load wins.
  const candidates = [
    window.FR_CAR_GLB,                                   // explicit override if set
    (window.FR_IMG ? window.FR_IMG('flameracer-full.glb') : null),
    (window.FR_IMG ? window.FR_IMG('flameracer-car.glb') : null),
    '/cdn/shop/files/flameracer-full.glb',
    (window.Shopify?.theme ? '/assets/flameracer-full.glb' : null),
    'flameracer-full.glb',
  ].filter(Boolean);

  (async () => {
    const onProgress = (e) => {
      if (e && e.lengthComputable && e.total) {
        // Reserve the last 8% for parse/decode (no events fire during that).
        window.__frSetLoadPct && window.__frSetLoadPct((e.loaded / e.total) * 92);
      }
    };
    // Try a URL up to `attempts` times — a transient (slow CDN, DRACO decoder
    // still warming, brief 502 after a publish) shouldn't permanently blank the
    // car. Retrying the WORKING url before falling through to alternates fixes
    // the intermittent "car never loads" on first paint.
    const tryUrl = async (url, attempts = 3) => {
      for (let i = 0; i < attempts; i++) {
        try { await scene.loadCombinedCar(url, onProgress); return true; }
        catch (err) {
          console.warn('[FLAMERACER] car load attempt', i + 1, 'of', attempts, 'failed for', url, '—', err?.message || err);
          if (i < attempts - 1) await new Promise(r => setTimeout(r, 1400));
        }
      }
      return false;
    };
    for (const url of candidates) {
      if (await tryUrl(url)) {
        scene._realCarLoaded = true;
        window.__frSetLoadPct && window.__frSetLoadPct(100);
        window.__frClearLoaderFallback && window.__frClearLoaderFallback();
        setTimeout(() => window.__frHideLoader && window.__frHideLoader(), 350);
        console.log('[FLAMERACER] combined car loaded from', url);
        // A shared "view my build" link (?b=...) rebuilds the exact spec now that
        // the car mesh exists.
        if (typeof _sharedBuild !== 'undefined' && _sharedBuild) {
          try { applyFullBuild(_sharedBuild); } catch (e) { console.warn('[FLR] shared build apply failed', e); }
        }
        // Re-render so steps that show rendered thumbnails (config cards, part
        // pics) regenerate now that the car exists.
        if (typeof render === 'function') { try { render(); } catch (e) {} }
        return;
      }
    }
    // Everything failed — drop the loader so the placeholder car is usable, and
    // schedule ONE more full retry of the primary URL a few seconds later (covers
    // a publish still propagating assets).
    window.__frHideLoader && window.__frHideLoader();
    console.warn('[FLAMERACER] no car GLB loaded yet — retrying the primary URL in 5s.');
    setTimeout(async () => {
      if (scene._combinedLoaded) return;
      if (await tryUrl(candidates[0], 2)) {
        scene._realCarLoaded = true;
        console.log('[FLAMERACER] combined car loaded on delayed retry');
        if (typeof render === 'function') { try { render(); } catch (e) {} }
      } else {
        console.warn('[FLAMERACER] car still not loading — check the .glb asset uploaded + the DRACO decoder CDN is reachable.');
      }
    }, 5000);
  })();
})();

// Console: reload combined car from any URL
window.__frLoadCar = (u) => scene.loadCombinedCar(u).then(() => console.log('[FLR] reloaded car from', u)).catch(e => console.warn('[FLR] load failed:', e?.message || e));

// Console: move/mirror/scale a part to dial in its position, then tell me the numbers
//   __frPartMove('flr-17', { dy: 0.05 })        // nudge up
//   __frPartMove('flr-03', { mirror: true })    // mirror to other side
//   __frPartMove('flr-23', { x:0, y:1.05, z:0, scale:1 })
window.__frPartMove = (id, o) => scene.movePart(id, o);
// Console: show a single part to inspect it
window.__frPartShow = (id) => { scene.toggleCombinedPart(id, true); console.log('[FLR] showing', id); };
window.__frPartHide = (id) => { scene.toggleCombinedPart(id, false); };
// Console: list which part ids matched a node in the GLB
window.__frPartsMatched = () => { const m=[...(scene.partNodeById?.keys()||[])]; console.log('[FLR] matched parts ('+m.length+'):', m.join(', ')); return m; };
// Console: live-tune the license-plate graphic placement, e.g.
//   __frPlateTune({ scale:1.1, aspect:2.0, push:0.04, dy:0.0, hScale:0.85 })
window.__frPlateTune = (t) => { scene._plateTune = Object.assign(scene._plateTune || {}, t || {}); scene.setLicensePlate(scene._currentPlate, scene._currentPlateMount); console.log('[FLR] plateTune', scene._plateTune); return scene._plateTune; };
// Console: live-tune a CUSTOM-PERMIT position's slot, then copy the JSON back into
// FR_PERMIT_POSITIONS in flameracer-data.js. i = 0 fender / 1 door / 2 quarter.
//   __frPermitTune(0, { size:0.03, rotDeg:170, point:[0.062,-0.03,0.031], normal:[0.99,-0.15,0.05] })
// Add a temporary custom permit first so there's something projected to see.
window.__frPermitTune = (i, patch) => {
  const POS = window.FR_PERMIT_POSITIONS || [];
  const p = POS[i | 0]; if (!p) return '[FLR] no permit position ' + i;
  Object.assign(p.slot, patch || {});
  scene.applyBuildDecals(state.build.decals, state.build.permits, state.build.decalFinish, state.build.banners, state.build.wingDecal, state.build.authored);
  console.log('[FLR] permitTune', i, p.label, JSON.stringify(p.slot));
  return p.slot;
};
// Console: live-tune the REAR WING DECAL placement, then copy the JSON back into
// FR_WING_DECAL_POS in flameracer-data.js. Enables the decal so there's something
// to see. e.g. __frWingTune({ point:[-0.0018,0.0162,-0.1696], normal:[0,-0.57,-0.82], size:0.045, aspectH:0.133, depth:0.03, rotDeg:180 })
window.__frWingTune = (patch) => {
  const P = window.FR_WING_DECAL_POS; if (!P) return '[FLR] no FR_WING_DECAL_POS';
  Object.assign(P, patch || {});
  P.aspect = (P.aspectH != null) ? P.aspectH : P.aspect; // keep `aspect` (used by the projector) in sync with aspectH
  state.build.wingDecal = Object.assign({ on: true, style: 'cyber', text: 'FLAMERACER', color: null, finish: null }, state.build.wingDecal || {}, { on: true });
  scene.applyBuildDecals(state.build.decals, state.build.permits, state.build.decalFinish, state.build.banners, state.build.wingDecal, state.build.authored);
  console.log('[FLR] wingTune', JSON.stringify(P));
  return P;
};
// Console: tune Wide Stance push as a FRACTION of the half-track, e.g.
//   __frWideStance(0.20)  = push each wheel out 20%; re-applies immediately.
window.__frWideStance = (v) => { scene._wideStancePush = v; if (scene._wideStanceOn) scene.setWideStance(true); console.log('[FLR] wide-stance push = ' + (v * 100).toFixed(0) + '% of half-track'); return v; };
// __frAggrStance(0.05) = the free "Aggressive Stance" poke as a % of half-track.
window.__frAggrStance = (v) => { scene._aggrStancePush = v; scene._refreshWheelPush(); console.log('[FLR] aggressive-stance push = ' + (v * 100).toFixed(1) + '% of half-track'); return v; };
// __frWheelRaise(0.04) = the constant up-raise on ALL wheels, as a % of wheel height.
window.__frWheelRaise = (v) => { scene._wheelRaiseFrac = v; scene._refreshWheelPush(); console.log('[FLR] wheel up-raise = ' + (v * 100).toFixed(1) + '% of wheel height'); return v; };
// Console: print size+center of every sub-model node (to diagnose scale/position)
window.__frBounds = () => {
  const model = scene.combinedModel; if (!model) return console.warn('[FLR] combined car not loaded');
  console.log('--- SUB-MODEL BOUNDS (local) ---');
  model.children.forEach(n => {
    const box = new THREE.Box3().setFromObject(n);
    const s = new THREE.Vector3(); box.getSize(s);
    const c = new THREE.Vector3(); box.getCenter(c);
    console.log((n.name||'(unnamed)').slice(0,46).padEnd(48),
      'size[', s.toArray().map(v=>v.toFixed(2)).join(','), '] ctr[', c.toArray().map(v=>v.toFixed(2)).join(','), '] vis:', n.visible);
  });
};
// Console: list every node name in the combined GLB
window.__frNodes = () => { const ns=[]; scene.combinedModel?.children.forEach(n=>ns.push(n.name||'(unnamed)')); console.log('[FLR] nodes ('+ns.length+'):\n'+ns.map((n,i)=>i+'. '+n).join('\n')); return ns; };
// Console: show/hide any node by exact name (for wheels/extras)
window.__frNodeShow = (name, v=true) => { const n=scene.combinedModel?.children.find(x=>x.name===name); if(n){n.visible=v;console.log('[FLR]',v?'show':'hide',name);}else console.warn('no node',name); };
// Console: toggle the floor mirror puddle (off by default in combined mode)
window.__frMirrorToggle = (v) => { if(window.__frMirror) window.__frMirror.visible = !!v; if(scene.floor) scene.floor.visible = !!v; console.log('[FLR] mirror/floor', v?'on':'off'); };
// Console: emergency — strip EVERYTHING except the real combined GLB car
window.__frCleanScene = () => {
  const cg = scene.carGroup; const fit = cg.getObjectByName('realcar-fit');
  [...cg.children].forEach(ch => { if (ch !== fit) cg.remove(ch); });
  if (window.__frMirror) window.__frMirror.visible = false;
  if (scene.floor) scene.floor.visible = false;
  scene.combinedModel?.children.forEach(n => { if (!/^base_car$/i.test(n.name) && !/^car$/i.test(n.name)) n.visible = false; });
  console.log('[FLR] scene cleaned — base car only');
};
// Console: find every BoxGeometry mesh that is NOT inside the real car GLB
window.__frFindBoxes = () => {
  const out = [];
  scene.scene.traverse(o => {
    if (!o.isMesh || o.geometry?.type !== 'BoxGeometry') return;
    let n=o.parent, inGLB=false; while(n){ if(n.name==='realcar-fit'){inGLB=true;break;} n=n.parent; }
    if (inGLB) return;
    const box=new THREE.Box3().setFromObject(o); const s=new THREE.Vector3(); box.getSize(s); const c=new THREE.Vector3(); box.getCenter(c);
    out.push({name:o.name||'(none)', parent:o.parent?.name||o.parent?.type||'(none)', color:o.material?.color?.getHexString?.(), size:s.toArray().map(v=>+v.toFixed(2)), ctr:c.toArray().map(v=>+v.toFixed(2)), vis:o.visible});
  });
  console.log('[FLR] non-GLB boxes ('+out.length+'):'); out.forEach(b=>console.log('  ', JSON.stringify(b)));
  return out;
};
// Console: REMOVE every BoxGeometry mesh outside the GLB (kills red/white placeholders)
window.__frKillBoxes = () => {
  const kill = [];
  scene.scene.traverse(o => {
    if (!o.isMesh || o.geometry?.type !== 'BoxGeometry') return;
    let n=o.parent, inGLB=false; while(n){ if(n.name==='realcar-fit'){inGLB=true;break;} n=n.parent; }
    if (!inGLB) kill.push(o);
  });
  kill.forEach(o => { try { o.parent && o.parent.remove(o); } catch(e){} });
  console.log('[FLR] removed', kill.length, 'placeholder boxes');
};

// Live nudge helpers for orientation/scale after you see it:
//   __frCarTweak({ rotY: Math.PI/2, length: 3.4, y: -0.4 })
window.__frCarTweak = (o = {}) => {
  const fit = scene.carGroup.getObjectByName('realcar-fit');
  if (!fit) return console.warn('[FLR] real car not loaded');
  const inner = fit.children[0];
  if (o.rotY !== undefined && inner) inner.rotation.y = o.rotY;
  if (o.y !== undefined) fit.position.y = o.y;
  if (o.length !== undefined) {
    const box = new THREE.Box3().setFromObject(inner);
    const size = new THREE.Vector3(); box.getSize(size);
    const maxDim = Math.max(size.x, size.y, size.z) || 1;
    fit.scale.setScalar(o.length / maxDim);
  }
  console.log('[FLR] tweaked', o);
};

// ============================================================
// CAMERA TUNING STATION  (load the page with ?camtune=1)
// ------------------------------------------------------------
// An in-app OrbitControls overlay so the camera presets are framed in the
// REAL production scene (same fit-scale, car position, lighting) instead of
// guessed coords. The user orbits/pans/zooms to frame each shot, picks the
// slot it belongs to, and hits Capture — we record camera.position (pos),
// controls.target (look) and carGroup.rotation.y (rotY), exactly the three
// values animateCameraTo() consumes. Captures stream to a copyable JSON box
// and (if reachable) POST to cam_saver.mjs → _glb_work/camera_presets.json.
// ============================================================
(function initCamTune() {
  if (!new URLSearchParams(location.search).has('camtune')) return;

  const SLOTS = [
    { group: 'Main view',  id: 'view.hero',     label: 'Hero (3/4)' },
    { group: 'Main view',  id: 'view.front',    label: 'Front' },
    { group: 'Main view',  id: 'view.side',     label: 'Side' },
    { group: 'Main view',  id: 'view.rear',     label: 'Rear' },
    { group: 'Main view',  id: 'view.top',      label: 'Top' },
    { group: 'Wheel',      id: 'wheel.wheels',  label: 'Wheels / stance' },
    { group: 'Wheel',      id: 'wheel.caliper', label: 'Caliper + rim' },
    { group: 'Wheel',      id: 'wheel.caps',    label: 'Centre cap' },
    { group: 'Wheel',      id: 'wheel.tire',    label: 'Tire sidewall' },
    { group: 'Body zone',  id: 'body.front',    label: 'Body — front' },
    { group: 'Body zone',  id: 'body.hood', label: 'Body — hood' },
    { group: 'Body zone',  id: 'body.roof', label: 'Body — roof' },
    { group: 'Body zone',  id: 'body.side',     label: 'Body — side' },
    { group: 'Body zone',  id: 'body.rear',     label: 'Body — rear' },
    { group: 'Plate',      id: 'plate.front',   label: 'Plate — front' },
    { group: 'Plate',      id: 'plate.rear',    label: 'Plate — rear' },
  ];

  // Read a slot's CURRENT preset (so the user can start from the existing
  // framing and just nudge). Sources mirror where each preset lives.
  function currentPreset(id) {
    const [kind, name] = id.split('.');
    if (kind === 'view')  return viewPresets[name];
    if (kind === 'wheel') return (window.FR_WHEEL_VIEWS || {})[name];
    if (kind === 'plate') return (window.FR_PLATE_VIEWS || {})[name];
    if (kind === 'body')  return ((window.FR_BODY_ZONES || []).find(z => z.id === name) || {}).view;
    return null;
  }

  const r3 = n => Math.round(n * 1000) / 1000;
  const captured = {}; // id -> {pos,look,rotY}
  // Seed with the live presets so a partial session still emits a full set.
  SLOTS.forEach(s => { const p = currentPreset(s.id); if (p) captured[s.id] = { pos: p.pos.slice(), look: p.look.slice(), rotY: p.rotY || 0 }; });

  let controls = null, carGroup = null, pinRotY = 0, raf = null;
  // Global car placement (carGroup.position). Side-to-side = x, depth = z,
  // lift = y. Spinning stays clean because the car still rotates about its own
  // centre (carGroup local origin); we're only moving where that origin sits.
  const carPos = { x: 0, y: -0.4, z: 0 };

  function buildExportObject() {
    const out = { viewPresets: {}, FR_WHEEL_VIEWS: {}, FR_BODY_ZONES: {}, FR_PLATE_VIEWS: {},
                  carOffset: { x: r3(carPos.x), y: r3(carPos.y), z: r3(carPos.z) },
                  boltDepth: r3(window.__frCtl?._boltDepth ?? 0), boltScale: r3(window.__frCtl?._boltScale ?? 0.13),
                  boltSpin: r3(window.__frCtl?._boltSpin ?? 0), boltU: r3(window.__frCtl?._boltU ?? 0), boltV: r3(window.__frCtl?._boltV ?? 0) };
    Object.entries(captured).forEach(([id, v]) => {
      const [kind, name] = id.split('.');
      const rec = { pos: v.pos.map(r3), look: v.look.map(r3), rotY: r3(v.rotY) };
      if (kind === 'view')  out.viewPresets[name] = rec;
      if (kind === 'wheel') out.FR_WHEEL_VIEWS[name] = rec;
      if (kind === 'body')  out.FR_BODY_ZONES[name] = rec;
      if (kind === 'plate') out.FR_PLATE_VIEWS[name] = rec;
    });
    return out;
  }

  // Apply the global car placement live + re-ground floor/shadow/mirror so the
  // car keeps sitting ON the floor wherever it's moved.
  function applyCarPlacement() {
    const ctl = window.__frCtl, THREE = window.__frTHREE;
    ctl.carGroup.position.set(carPos.x, carPos.y, carPos.z);
    ctl.carGroup.updateWorldMatrix(true, true);
    const b = new THREE.Box3().setFromObject(ctl.carGroup);
    const gy = b.min.y;
    if (ctl.floor)       ctl.floor.position.y = gy;
    if (ctl.shadowFloor) ctl.shadowFloor.position.set(carPos.x, gy + 0.006, carPos.z);
    if (ctl.mirror)      ctl.mirror.position.set(carPos.x, gy + 0.001, carPos.z);
    if (window.__frGroundGlow) window.__frGroundGlow.position.set(carPos.x, gy + 0.008, carPos.z);
    ctl.requestRender && ctl.requestRender(300);
  }

  // Camera nudge — pan (right/up) + dolly (forward) in screen space, so "move
  // the framing a touch left/right/up/down" is a button, not a drag.
  function nudgeCam(dx, dy, dz) {
    const cam = window.__frCamera, THREE = window.__frTHREE;
    const fwd = new THREE.Vector3(); cam.getWorldDirection(fwd);
    const right = new THREE.Vector3().crossVectors(fwd, new THREE.Vector3(0, 1, 0)).normalize();
    const move = new THREE.Vector3()
      .addScaledVector(right, dx)
      .addScaledVector(new THREE.Vector3(0, 1, 0), dy);
    cam.position.add(move); controls.target.add(move);   // pan
    cam.position.addScaledVector(fwd, dz);                // dolly
    controls.update();
  }

  function refreshJSON() {
    const ta = document.getElementById('fr-ct-json');
    if (ta) ta.value = JSON.stringify(buildExportObject(), null, 2);
  }

  function postSave() {
    const status = document.getElementById('fr-ct-status');
    fetch('http://127.0.0.1:8127/save', { method: 'POST', body: JSON.stringify(buildExportObject()) })
      .then(() => { if (status) status.textContent = 'saved → camera_presets.json ✓'; })
      .catch(() => { if (status) status.textContent = 'saver offline — copy the JSON box instead'; });
  }

  async function start() {
    const cam = window.__frCamera, renderer = window.__frRenderer, scene3 = window.__frScene, ctl = window.__frCtl;
    carGroup = ctl.carGroup;
    const THREE = window.__frTHREE;

    const { OrbitControls } = await import('three/addons/controls/OrbitControls.js');
    controls = new OrbitControls(cam, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.minDistance = 0.4;
    controls.maxDistance = 40;
    // Aim at the car's current centre to start.
    const box = new THREE.Box3().setFromObject(carGroup);
    const c = box.getCenter(new THREE.Vector3());
    controls.target.copy(c);
    pinRotY = carGroup.rotation.y;
    carPos.x = carGroup.position.x; carPos.y = carGroup.position.y; carPos.z = carGroup.position.z;
    controls.update();

    // Drive a continuous render + pin the car heading so the app's drag-spin
    // can't fight the camera orbit (we overwrite targetRotY every frame).
    (function loop() {
      raf = requestAnimationFrame(loop);
      controls.update();
      carGroup.rotation.y = pinRotY;
      if (ctl.targetRotY !== undefined) { ctl.targetRotY = pinRotY; ctl.currentRotY = pinRotY; }
      ctl.requestRender && ctl.requestRender(120);
    })();

    buildHUD();
    refreshJSON();
    console.log('[CAMTUNE] ready — orbit to frame, pick a slot, Capture.');
  }

  function loadSlot(id) {
    const p = captured[id] || currentPreset(id);
    if (!p) return;
    const cam = window.__frCamera;
    cam.position.set(p.pos[0], p.pos[1], p.pos[2]);
    controls.target.set(p.look[0], p.look[1], p.look[2]);
    pinRotY = p.rotY || 0;
    const slider = document.getElementById('fr-ct-roty');
    const out = document.getElementById('fr-ct-rotyval');
    if (slider) slider.value = pinRotY;
    if (out) out.textContent = pinRotY.toFixed(2);
    controls.update();
  }

  function captureSlot(id) {
    const cam = window.__frCamera;
    captured[id] = {
      pos:  [cam.position.x, cam.position.y, cam.position.z],
      look: [controls.target.x, controls.target.y, controls.target.z],
      rotY: pinRotY,
    };
    refreshJSON();
    postSave();
    const status = document.getElementById('fr-ct-status');
    if (status) status.textContent = `captured ${id}`;
    // mark the slot button done
    const btn = document.querySelector(`[data-ct-slot="${id}"]`);
    if (btn) btn.classList.add('done');
  }

  function buildHUD() {
    const el = document.createElement('div');
    el.id = 'fr-camtune';
    let groups = '';
    let lastG = '';
    SLOTS.forEach(s => {
      if (s.group !== lastG) { groups += `<div class="ct-g">${s.group}</div>`; lastG = s.group; }
      groups += `<div class="ct-row">
        <button class="ct-load" data-ct-load="${s.id}" title="fly to current">${s.label}</button>
        <button class="ct-cap"  data-ct-slot="${s.id}">Capture</button>
      </div>`;
    });
    el.innerHTML = `
      <style>
        #fr-camtune{position:fixed;top:10px;left:10px;z-index:99999;width:262px;
          background:rgba(14,15,18,.92);color:#e8eaee;font:12px/1.4 -apple-system,system-ui,sans-serif;
          border:1px solid #2a2d36;border-radius:10px;box-shadow:0 8px 40px rgba(0,0,0,.55);backdrop-filter:blur(6px);}
        #fr-camtune .ct-head{display:flex;align-items:center;justify-content:space-between;padding:9px 11px;cursor:grab;user-select:none;border-bottom:1px solid #23262e;}
        #fr-camtune .ct-head:active{cursor:grabbing;}
        #fr-camtune .ct-title{font-size:12.5px;letter-spacing:.3px;color:#ffd24a;font-weight:600;}
        #fr-camtune .ct-mini{background:#23262e;border:none;color:#cfd3db;border-radius:5px;width:24px;height:22px;cursor:pointer;font-size:13px;line-height:1;}
        #fr-camtune .ct-body{padding:10px 11px 12px;max-height:78vh;overflow:auto;}
        #fr-camtune.ct-collapsed .ct-body{display:none;}
        #fr-camtune.ct-collapsed{width:auto;}
        #fr-camtune .ct-hint{opacity:.6;font-size:10.5px;margin-bottom:8px;}
        #fr-camtune .ct-g{margin:10px 0 4px;font-size:10px;letter-spacing:1px;text-transform:uppercase;color:#7f8694;}
        #fr-camtune .ct-row{display:flex;gap:6px;margin-bottom:4px;}
        #fr-camtune .ct-load{flex:1;text-align:left;background:#1c1f27;border:1px solid #2c303a;color:#cfd3db;border-radius:6px;padding:5px 8px;cursor:pointer;}
        #fr-camtune .ct-load:hover{background:#252934;}
        #fr-camtune .ct-load.done{border-color:#3fae5a;color:#9fe6b0;}
        #fr-camtune .ct-cap{background:#2a2310;border:1px solid #5a4a18;color:#ffd24a;border-radius:6px;padding:5px 8px;cursor:pointer;font-weight:600;}
        #fr-camtune .ct-cap:hover{background:#3a3014;}
        #fr-camtune .ct-pad{display:grid;grid-template-columns:repeat(3,1fr);gap:5px;margin:4px 0;}
        #fr-camtune .ct-pad button{background:#1c1f27;border:1px solid #2c303a;color:#cfd3db;border-radius:6px;padding:7px 0;cursor:pointer;font-size:13px;}
        #fr-camtune .ct-pad button:hover{background:#252934;}
        #fr-camtune .ct-pad .sp{visibility:hidden;}
        #fr-camtune .ct-slide{display:flex;align-items:center;gap:7px;margin:5px 0;}
        #fr-camtune .ct-slide label{flex:0 0 78px;font-size:10.5px;color:#aab0bb;}
        #fr-camtune .ct-slide input{flex:1;}
        #fr-camtune .ct-slide .v{flex:0 0 38px;text-align:right;font:10px ui-monospace,monospace;color:#9fe6b0;}
        #fr-camtune textarea{width:100%;height:104px;margin-top:8px;background:#0c0d10;color:#9fe6b0;
          border:1px solid #2c303a;border-radius:6px;font:10px/1.3 ui-monospace,monospace;padding:6px;resize:vertical;box-sizing:border-box;}
        #fr-camtune .ct-actions{display:flex;gap:6px;margin-top:6px;}
        #fr-camtune .ct-actions button{flex:1;background:#1c1f27;border:1px solid #2c303a;color:#cfd3db;border-radius:6px;padding:6px;cursor:pointer;}
        #fr-camtune .ct-reset{margin-top:4px;width:100%;background:#241418;border:1px solid #4a2630;color:#f0a9b4;border-radius:6px;padding:5px;cursor:pointer;}
        #fr-camtune .ct-status{margin-top:6px;font-size:10.5px;color:#7fd6ff;min-height:14px;}
      </style>
      <div class="ct-head" id="fr-ct-head">
        <span class="ct-title">📷 Camera Tuning</span>
        <button class="ct-mini" id="fr-ct-mini" title="collapse / expand">▁</button>
      </div>
      <div class="ct-body">
        <div class="ct-hint">Drag canvas = orbit · right-drag = pan · scroll = zoom. Drag this header to move the panel; ▁ collapses it so it never covers the car.</div>

        <div class="ct-g">Capture a shot</div>
        ${groups}

        <div class="ct-g">Camera nudge</div>
        <div class="ct-pad">
          <button class="sp"></button><button data-nudge="0,0.08,0" title="up">▲</button><button class="sp"></button>
          <button data-nudge="-0.08,0,0" title="left">◀</button><button data-nudge="0,-0.08,0" title="down">▼</button><button data-nudge="0.08,0,0" title="right">▶</button>
          <button data-nudge="0,0,0.18" title="zoom in">＋</button><button class="sp"></button><button data-nudge="0,0,-0.18" title="zoom out">－</button>
        </div>

        <div class="ct-g">Car placement (all shots)</div>
        <div class="ct-slide"><label>Side ◀▶ (x)</label><input id="fr-ct-cx" type="range" min="-3" max="3" step="0.02"><span class="v" id="fr-ct-cxv">0</span></div>
        <div class="ct-slide"><label>Depth (z)</label><input id="fr-ct-cz" type="range" min="-3" max="3" step="0.02"><span class="v" id="fr-ct-czv">0</span></div>
        <div class="ct-slide"><label>Lift (y)</label><input id="fr-ct-cy" type="range" min="-1.9" max="0.8" step="0.02"><span class="v" id="fr-ct-cyv">0</span></div>
        <div class="ct-slide"><label>Turn (rotY)</label><input id="fr-ct-roty" type="range" min="-3.14159" max="3.14159" step="0.01" value="0"><span class="v" id="fr-ct-rotyval">0</span></div>
        <button class="ct-reset" id="fr-ct-reset">reset car to centre</button>
        <div class="ct-hint">Side/Depth/Lift move the WHOLE car (its home spot for every shot — still spins clean). Turn rotates it for the current shot only.</div>

        <div class="ct-g">Hub bolt (pick a cool rim first)</div>
        <div class="ct-slide"><label>Depth (in◀▶out)</label><input id="fr-ct-bolt-axial" type="range" min="-0.6" max="0.6" step="0.01" value="0"><span class="v" id="fr-ct-bolt-axialv">0</span></div>
        <div class="ct-slide"><label>Size</label><input id="fr-ct-bolt-scale" type="range" min="0.04" max="0.35" step="0.005" value="0.13"><span class="v" id="fr-ct-bolt-scalev">0.13</span></div>
        <div class="ct-hint">Tunes all 4 hub bolts together — but the dedicated <b>Hub Bolt Placer</b> (hub_viewer.html) is the better tool. Values print below as <code>boltDepth</code> / <code>boltScale</code>.</div>

        <textarea id="fr-ct-json" readonly></textarea>
        <div class="ct-actions">
          <button id="fr-ct-copy">Copy JSON</button>
          <button id="fr-ct-save">Save to server</button>
        </div>
        <div class="ct-status" id="fr-ct-status"></div>
      </div>
    `;
    document.body.appendChild(el);

    el.querySelectorAll('[data-ct-load]').forEach(b => b.addEventListener('click', () => loadSlot(b.dataset.ctLoad)));
    el.querySelectorAll('[data-ct-slot]').forEach(b => b.addEventListener('click', () => captureSlot(b.dataset.ctSlot)));
    el.querySelectorAll('[data-nudge]').forEach(b => b.addEventListener('click', () => {
      const [dx, dy, dz] = b.dataset.nudge.split(',').map(Number); nudgeCam(dx, dy, dz);
    }));

    // Car-placement sliders.
    const cx = el.querySelector('#fr-ct-cx'), cz = el.querySelector('#fr-ct-cz'), cy = el.querySelector('#fr-ct-cy');
    const cxv = el.querySelector('#fr-ct-cxv'), czv = el.querySelector('#fr-ct-czv'), cyv = el.querySelector('#fr-ct-cyv');
    cx.value = carPos.x; cz.value = carPos.z; cy.value = carPos.y;
    cxv.textContent = carPos.x.toFixed(2); czv.textContent = carPos.z.toFixed(2); cyv.textContent = carPos.y.toFixed(2);
    cx.addEventListener('input', () => { carPos.x = parseFloat(cx.value); cxv.textContent = carPos.x.toFixed(2); applyCarPlacement(); refreshJSON(); });
    cz.addEventListener('input', () => { carPos.z = parseFloat(cz.value); czv.textContent = carPos.z.toFixed(2); applyCarPlacement(); refreshJSON(); });
    cy.addEventListener('input', () => { carPos.y = parseFloat(cy.value); cyv.textContent = carPos.y.toFixed(2); applyCarPlacement(); refreshJSON(); });
    el.querySelector('#fr-ct-reset').addEventListener('click', () => {
      carPos.x = 0; carPos.y = -0.4; carPos.z = 0;
      cx.value = 0; cz.value = 0; cy.value = -0.4;
      cxv.textContent = '0.00'; czv.textContent = '0.00'; cyv.textContent = '-0.40';
      applyCarPlacement(); refreshJSON();
    });

    // Hub-bolt sliders → live rebuild via the scene tuner.
    const bAx = el.querySelector('#fr-ct-bolt-axial'), bSc = el.querySelector('#fr-ct-bolt-scale');
    const bAxv = el.querySelector('#fr-ct-bolt-axialv'), bScv = el.querySelector('#fr-ct-bolt-scalev');
    if (window.__frCtl) { bAx.value = window.__frCtl._boltDepth ?? 0; bSc.value = window.__frCtl._boltScale ?? 0.13; bAxv.textContent = (+bAx.value).toFixed(3); bScv.textContent = (+bSc.value).toFixed(3); }
    const applyBolt = () => { bAxv.textContent = (+bAx.value).toFixed(3); bScv.textContent = (+bSc.value).toFixed(3); window.__frCtl?.__boltTune({ depth: parseFloat(bAx.value), scale: parseFloat(bSc.value) }); refreshJSON(); };
    bAx.addEventListener('input', applyBolt); bSc.addEventListener('input', applyBolt);

    const slider = el.querySelector('#fr-ct-roty');
    const out = el.querySelector('#fr-ct-rotyval');
    slider.addEventListener('input', () => { pinRotY = parseFloat(slider.value); out.textContent = pinRotY.toFixed(2); });
    el.querySelector('#fr-ct-copy').addEventListener('click', () => {
      const ta = el.querySelector('#fr-ct-json'); ta.select(); navigator.clipboard?.writeText(ta.value);
      el.querySelector('#fr-ct-status').textContent = 'JSON copied to clipboard';
    });
    el.querySelector('#fr-ct-save').addEventListener('click', postSave);

    // Collapse toggle.
    el.querySelector('#fr-ct-mini').addEventListener('click', () => el.classList.toggle('ct-collapsed'));

    // Drag the panel by its header so it never blocks the car.
    const head = el.querySelector('#fr-ct-head');
    let dragging = false, ox = 0, oy = 0;
    head.addEventListener('pointerdown', e => {
      if (e.target.id === 'fr-ct-mini') return;
      dragging = true; const r = el.getBoundingClientRect();
      ox = e.clientX - r.left; oy = e.clientY - r.top;
      el.style.right = 'auto'; head.setPointerCapture(e.pointerId);
    });
    head.addEventListener('pointermove', e => {
      if (!dragging) return;
      el.style.left = Math.max(0, e.clientX - ox) + 'px';
      el.style.top  = Math.max(0, e.clientY - oy) + 'px';
    });
    head.addEventListener('pointerup', e => { dragging = false; try { head.releasePointerCapture(e.pointerId); } catch (_) {} });
  }

  // Wait for the real car to finish loading before attaching.
  let tries = 0;
  const wait = setInterval(() => {
    tries++;
    const ready = window.__frCamera && window.__frRenderer && window.__frCtl &&
                  window.__frCtl.carGroup && window.__frCtl.carGroup.getObjectByName('realcar-fit');
    if (ready) { clearInterval(wait); start(); }
    else if (tries > 120) { clearInterval(wait); console.warn('[CAMTUNE] car never loaded — aborting'); }
  }, 500);

  window.__frCamTune = {
    capture: captureSlot, load: loadSlot, export: buildExportObject, nudge: nudgeCam,
    setCar: (x, y, z) => { if (x !== undefined) carPos.x = x; if (y !== undefined) carPos.y = y; if (z !== undefined) carPos.z = z; applyCarPlacement(); refreshJSON(); },
  };
})();

// ============================================================
// PERMIT PLACEMENT VIEWER  (load the page with ?permittune)
// A light click-to-place tool for the 3 CUSTOM-RACING-PERMIT positions. Spin/zoom
// the car with the normal drag/scroll controls, pick a slot (1/2/3) in the HUD, then
// CLICK a spot on the bodywork to drop that permit's rectangle there (raycast →
// GLB-native point + normal). Sliders set its size + rotation; all 3 rectangles
// project live + conform to the panels. Copy the JSON box back into
// FR_PERMIT_POSITIONS in flameracer-data.js to bake the placements. Customer-safe:
// the whole tool is gated behind ?permittune so it never loads in a normal session.
// ============================================================
(function initPermitTune() {
  if (!new URLSearchParams(location.search).has('permittune')) return;
  const r4 = n => Math.round(n * 10000) / 10000;
  const r3 = n => Math.round(n * 1000) / 1000;
  const T = () => window.__frTHREE;
  let active = 0;
  const status = m => { const s = document.getElementById('fr-pt-status'); if (s) s.textContent = m; };

  // A bright test rectangle for slot `num` (1-based); the active one is highlighted
  // and has a TOP marker bar so "which way is up" (rotDeg) is obvious.
  function tuneTex(num, isActive) {
    const TH = T(), W = 512, H = 256, cv = document.createElement('canvas'); cv.width = W; cv.height = H;
    const c = cv.getContext('2d');
    c.fillStyle = isActive ? 'rgba(245,208,0,0.42)' : 'rgba(0,200,255,0.24)'; c.fillRect(0, 0, W, H);
    c.strokeStyle = isActive ? '#f5d000' : '#15b6e6'; c.lineWidth = 18; c.strokeRect(9, 9, W - 18, H - 18);
    c.fillStyle = isActive ? '#f5d000' : '#15b6e6'; c.fillRect(W / 2 - 46, 18, 92, 24); // top = "up"
    c.fillStyle = '#0a0a0c'; c.textAlign = 'center'; c.textBaseline = 'middle'; c.font = '900 150px "Arial Black",Arial';
    c.fillText(String(num), W / 2, H / 2 + 12);
    const tex = new TH.CanvasTexture(cv); tex.colorSpace = TH.SRGBColorSpace; return tex;
  }

  let _raf = false;
  function scheduleProject() { if (_raf) return; _raf = true; requestAnimationFrame(() => { _raf = false; project(); }); }
  function project() {
    const ctl = window.__frCtl, TH = T(); if (!ctl || !ctl.combinedModel) return;
    (ctl._permitTuneMeshes || []).forEach(m => { try { if (m.parent) m.parent.remove(m); m.material.map && m.material.map.dispose(); m.material.dispose(); m.geometry.dispose(); } catch (e) {} });
    ctl._permitTuneMeshes = [];
    (window.FR_PERMIT_POSITIONS || []).forEach((p, i) => {
      const mat = new TH.MeshBasicMaterial({ map: tuneTex(i + 1, i === active), transparent: true, opacity: i === active ? 1 : 0.8, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -8, polygonOffsetUnits: -8, side: TH.DoubleSide });
      const mesh = ctl._buildConformingDecal({ ...p.slot }, mat, 0.5);
      if (mesh) ctl._permitTuneMeshes.push(mesh);
    });
    ctl.requestRender && ctl.requestRender(500);
    refreshJSON();
  }

  // Raycast a screen point against the car body → GLB-native {panel, point, normal}.
  // Inverse of _decalNativeToWorld: world point → model.worldToLocal; world normal →
  // transformDirection by inverse(matrixWorld). Rotation-independent (native = model-
  // local), so it works at any car heading.
  function pickAt(clientX, clientY) {
    const ctl = window.__frCtl, TH = T(), cam = window.__frCamera, renderer = window.__frRenderer;
    const model = ctl.combinedModel; if (!model) return null;
    const rect = renderer.domElement.getBoundingClientRect();
    const ndc = new TH.Vector2(((clientX - rect.left) / rect.width) * 2 - 1, -((clientY - rect.top) / rect.height) * 2 + 1);
    const ray = new TH.Raycaster(); ray.setFromCamera(ndc, cam);
    const meshes = []; model.traverse(o => { if (o.isMesh && o.visible) meshes.push(o); });
    const hits = ray.intersectObjects(meshes, false);
    if (!hits.length) return null;
    const hit = hits[0];
    model.updateWorldMatrix(true, true);
    const np = model.worldToLocal(hit.point.clone());
    let wn = hit.face ? hit.face.normal.clone().transformDirection(hit.object.matrixWorld).normalize()
                      : cam.getWorldDirection(new TH.Vector3()).negate();
    const nn = wn.clone().transformDirection(model.matrixWorld.clone().invert()).normalize();
    return { panel: hit.object.name || 'Body_1', point: [r4(np.x), r4(np.y), r4(np.z)], normal: [r4(nn.x), r4(nn.y), r4(nn.z)] };
  }

  function buildBlock() {
    const lines = (window.FR_PERMIT_POSITIONS || []).map(p => {
      const s = p.slot;
      return `  { id: '${p.id}', label: '${p.label}', slot: { panel: '${s.panel}', point: [${s.point.map(r4).join(', ')}], normal: [${s.normal.map(r4).join(', ')}], size: ${r3(s.size)}, rotDeg: ${Math.round(s.rotDeg)}, mirror: null } },`;
    }).join('\n');
    return `window.FR_PERMIT_POSITIONS = [\n${lines}\n];`;
  }
  function refreshJSON() { const ta = document.getElementById('fr-pt-json'); if (ta) ta.value = buildBlock(); }

  function syncSliders() {
    const s = (window.FR_PERMIT_POSITIONS[active] || {}).slot || {};
    const sz = document.getElementById('fr-pt-size'), szv = document.getElementById('fr-pt-sizeval');
    const rt = document.getElementById('fr-pt-rot'), rtv = document.getElementById('fr-pt-rotval');
    if (sz) { sz.value = s.size; szv.textContent = (+s.size).toFixed(3); }
    if (rt) { rt.value = s.rotDeg; rtv.textContent = Math.round(s.rotDeg) + '°'; }
    document.querySelectorAll('[data-pt-slot]').forEach(b => b.classList.toggle('on', +b.dataset.ptSlot === active));
  }

  function wireCanvas() {
    const el = window.__frRenderer.domElement;
    let sx = 0, sy = 0, moved = false;
    el.addEventListener('pointerdown', e => { sx = e.clientX; sy = e.clientY; moved = false; }, true);
    el.addEventListener('pointermove', e => { if (Math.abs(e.clientX - sx) > 5 || Math.abs(e.clientY - sy) > 5) moved = true; }, true);
    el.addEventListener('pointerup', e => {
      if (moved) return; // was a drag-spin, not a place-tap
      const pick = pickAt(e.clientX, e.clientY);
      if (!pick) { status('no body under cursor — click on the car'); return; }
      const slot = (window.FR_PERMIT_POSITIONS[active] || {}).slot; if (!slot) return;
      slot.panel = pick.panel; slot.point = pick.point; slot.normal = pick.normal;
      project(); status(`placed #${active + 1} on ${pick.panel}`);
    }, true);
  }

  function buildHUD() {
    const css = document.createElement('style');
    css.textContent = `
      #fr-pt{position:fixed;top:10px;right:10px;z-index:99999;width:268px;background:#14161c;
        border:1px solid #262a33;border-radius:10px;color:#cfd3db;font:13px/1.4 system-ui,sans-serif;box-shadow:0 10px 40px rgba(0,0,0,.55);}
      #fr-pt .pt-head{display:flex;align-items:center;justify-content:space-between;padding:9px 11px;cursor:grab;user-select:none;border-bottom:1px solid #23262e;}
      #fr-pt .pt-title{font-size:12.5px;letter-spacing:.3px;color:#ffd24a;font-weight:600;}
      #fr-pt .pt-mini{background:#23262e;border:none;color:#cfd3db;border-radius:5px;width:24px;height:22px;cursor:pointer;}
      #fr-pt.pt-collapsed .pt-body{display:none;}
      #fr-pt .pt-body{padding:11px;}
      #fr-pt .pt-hint{opacity:.65;font-size:11px;margin-bottom:9px;}
      #fr-pt .pt-slots{display:grid;grid-template-columns:repeat(3,1fr);gap:6px;margin-bottom:10px;}
      #fr-pt .pt-slots button{background:#1c1f27;border:1px solid #2c303a;color:#cfd3db;border-radius:7px;padding:8px 4px;cursor:pointer;font-size:11px;}
      #fr-pt .pt-slots button.on{background:#f5d000;border-color:#f5d000;color:#0a0a0c;font-weight:700;}
      #fr-pt .pt-slide{display:flex;align-items:center;gap:8px;margin:7px 0;}
      #fr-pt .pt-slide label{flex:0 0 56px;font-size:11px;color:#aab0bb;}
      #fr-pt .pt-slide input{flex:1;}
      #fr-pt .pt-slide span{flex:0 0 42px;text-align:right;font-size:11px;color:#ffd24a;}
      #fr-pt textarea{width:100%;height:118px;margin-top:9px;background:#0c0e12;border:1px solid #262a33;border-radius:7px;color:#9fe6b0;font:11px/1.35 ui-monospace,monospace;padding:8px;resize:vertical;}
      #fr-pt .pt-row{display:flex;gap:6px;margin-top:7px;}
      #fr-pt .pt-row button{flex:1;background:#2a2310;border:1px solid #5a4a18;color:#ffd24a;border-radius:7px;padding:7px;cursor:pointer;font-weight:600;font-size:11.5px;}
      #fr-pt .pt-status{margin-top:8px;font-size:10.5px;color:#7f8694;min-height:13px;}`;
    document.head.appendChild(css);

    const el = document.createElement('div'); el.id = 'fr-pt';
    const POS = window.FR_PERMIT_POSITIONS || [];
    el.innerHTML = `
      <div class="pt-head" id="fr-pt-head"><span class="pt-title">PERMIT PLACEMENT</span><button class="pt-mini" id="fr-pt-collapse">–</button></div>
      <div class="pt-body">
        <div class="pt-hint">Spin / zoom the car, pick a slot, then <b>click the body</b> to drop it. Marker bar = top edge.</div>
        <div class="pt-slots">${POS.map((p, i) => `<button data-pt-slot="${i}" class="${i === active ? 'on' : ''}">${i + 1} · ${p.label.split(' ')[0]}</button>`).join('')}</div>
        <div class="pt-slide"><label>Size</label><input id="fr-pt-size" type="range" min="0.01" max="0.06" step="0.001"><span id="fr-pt-sizeval"></span></div>
        <div class="pt-slide"><label>Rotate</label><input id="fr-pt-rot" type="range" min="0" max="360" step="1"><span id="fr-pt-rotval"></span></div>
        <textarea id="fr-pt-json" readonly spellcheck="false"></textarea>
        <div class="pt-row"><button id="fr-pt-copy">⧉ Copy JSON</button><button id="fr-pt-reset">↺ Re-project</button></div>
        <div class="pt-status" id="fr-pt-status">ready — pick a slot + click the car</div>
      </div>`;
    document.body.appendChild(el);

    el.querySelectorAll('[data-pt-slot]').forEach(b => b.addEventListener('click', () => { active = +b.dataset.ptSlot; syncSliders(); project(); }));
    const sz = el.querySelector('#fr-pt-size'), szv = el.querySelector('#fr-pt-sizeval');
    sz.addEventListener('input', () => { const s = window.FR_PERMIT_POSITIONS[active].slot; s.size = parseFloat(sz.value); szv.textContent = s.size.toFixed(3); scheduleProject(); });
    const rt = el.querySelector('#fr-pt-rot'), rtv = el.querySelector('#fr-pt-rotval');
    rt.addEventListener('input', () => { const s = window.FR_PERMIT_POSITIONS[active].slot; s.rotDeg = parseInt(rt.value); rtv.textContent = s.rotDeg + '°'; scheduleProject(); });
    el.querySelector('#fr-pt-copy').addEventListener('click', () => { const ta = el.querySelector('#fr-pt-json'); ta.select(); navigator.clipboard && navigator.clipboard.writeText(ta.value); status('JSON copied — paste into flameracer-data.js'); });
    el.querySelector('#fr-pt-reset').addEventListener('click', project);
    el.querySelector('#fr-pt-collapse').addEventListener('click', () => el.classList.toggle('pt-collapsed'));

    // Drag the panel by its header.
    const head = el.querySelector('#fr-pt-head'); let dragging = false, ox = 0, oy = 0;
    head.addEventListener('pointerdown', e => { if (e.target.id === 'fr-pt-collapse') return; dragging = true; const r = el.getBoundingClientRect(); ox = e.clientX - r.left; oy = e.clientY - r.top; el.style.right = 'auto'; head.setPointerCapture(e.pointerId); });
    head.addEventListener('pointermove', e => { if (!dragging) return; el.style.left = Math.max(0, e.clientX - ox) + 'px'; el.style.top = Math.max(0, e.clientY - oy) + 'px'; });
    head.addEventListener('pointerup', e => { dragging = false; try { head.releasePointerCapture(e.pointerId); } catch (_) {} });

    syncSliders();
  }

  // Wait for the real car to load, then attach the HUD + canvas picker + first paint.
  let tries = 0;
  const wait = setInterval(() => {
    tries++;
    const ctl = window.__frCtl;
    const ready = window.__frCamera && window.__frRenderer && ctl && ctl.combinedModel && ctl.carGroup && ctl.carGroup.getObjectByName('realcar-fit');
    if (ready) { clearInterval(wait); try { buildHUD(); wireCanvas(); project(); console.log('[PERMITTUNE] ready — pick a slot, click the car, copy the JSON.'); } catch (e) { console.warn('[PERMITTUNE] init failed', e); } }
    else if (tries > 120) { clearInterval(wait); console.warn('[PERMITTUNE] car never loaded — aborting'); }
  }, 500);
})();

// ============================================================
// FULLSCREEN 360 SHOWCASE — a toggle button on the viewer stage. Desktop = free
// 360 orbit (scene.setFullscreen wires OrbitControls); phone = big canvas + the
// lighting dock (no free orbit). The dock + view rail live on the stage so they
// expand with it.
// ============================================================
(function initFullscreen() {
  const css = document.createElement('style');
  css.textContent = `
    .fr-fullscreen { position: fixed !important; inset: 0 !important; width: 100vw !important; height: 100vh !important; z-index: 9998 !important; background:#000 !important; }
    html.fr-fs-active, html.fr-fs-active body { overflow: hidden !important; }
    .fr-fs-toggle { position:absolute; bottom:14px; right:14px; z-index:26;
      background:rgba(16,17,21,.72); border:1px solid rgba(255,255,255,.2); color:#e8eaee;
      border-radius:9px; padding:9px 13px; cursor:pointer; font:600 12px system-ui,sans-serif;
      backdrop-filter:blur(6px); display:flex; gap:7px; align-items:center; box-shadow:0 4px 18px rgba(0,0,0,.4); }
    .fr-fs-toggle:hover { background:rgba(44,48,58,.92); }
    @media(max-width:760px){ .fr-fs-toggle{ bottom:10px; right:10px; padding:7px 10px; font-size:11px; } } /* bottom-right clears the top-right spec label */

    /* ===== 360 backdrop switcher (top-centre of the fullscreen stage) ===== */
    .fr-bg-switch { position:absolute; top:16px; left:50%; transform:translateX(-50%); z-index:30;
      display:flex; align-items:center; gap:4px; padding:5px 6px;
      background:rgba(16,17,21,.66); border:1px solid rgba(255,255,255,.16); border-radius:11px;
      backdrop-filter:blur(8px); box-shadow:0 6px 22px rgba(0,0,0,.45); }
    .fr-bg-switch-label { font:600 9px var(--fr-mono,monospace); letter-spacing:.16em; text-transform:uppercase;
      color:rgba(255,255,255,.45); padding:0 8px 0 4px; }
    .fr-bg-switch button { border:none; cursor:pointer; background:transparent; color:rgba(255,255,255,.62);
      font:600 12px system-ui,sans-serif; padding:7px 14px; border-radius:8px; transition:all .18s ease; }
    .fr-bg-switch button:hover { color:#fff; background:rgba(255,255,255,.08); }
    .fr-bg-switch button.active { color:#fff; background:var(--fr-accent-yellow,#d10f2f); box-shadow:none; }
    @media(max-width:760px){ .fr-bg-switch{ top:10px; gap:2px; padding:4px; } .fr-bg-switch button{ padding:6px 10px; font-size:11px; } .fr-bg-switch-label{ display:none; } }

    /* ===== 360 "drag to orbit" hint (base styles were missing — the JS toggles
       .hide and restarts an entrance animation that never existed) ===== */
    .fr-360-hint { position:absolute; bottom:64px; left:50%; transform:translateX(-50%); z-index:28;
      display:flex; align-items:center; gap:9px; padding:9px 16px;
      background:rgba(16,17,21,.72); border:1px solid rgba(255,255,255,.16); border-radius:2px;
      color:#e8eaee; font:600 11px var(--fr-mono,monospace); letter-spacing:.1em; text-transform:uppercase;
      backdrop-filter:blur(6px); pointer-events:none; transition:opacity .4s ease;
      animation:fr360hintIn .5s ease; }
    .fr-360-hint.hide { opacity:0; }
    .fr-360-hint-ico svg { width:18px; height:18px; display:block; animation:fr360nudge 1.6s ease-in-out infinite; }
    @keyframes fr360hintIn { from { opacity:0; transform:translate(-50%,8px); } to { opacity:1; transform:translate(-50%,0); } }
    @keyframes fr360nudge { 0%,100% { transform:translateX(0); } 50% { transform:translateX(4px); } }
    @media (prefers-reduced-motion: reduce) { .fr-360-hint, .fr-360-hint-ico svg { animation:none; } }

    /* ===== LIGHT-STUDIO CHROME — moved out (2026-08-06) =====================
       Every .fr-360-light override now lives in ONE place: the "STAGE CONTROL
       CHROME" layer at the end of flameracer-configurator.css. It was split
       across both files, and because this <style> is injected into <head> at
       runtime it always won ties against the stylesheet — so the stylesheet's
       rules silently lost and the light-mode controls fell back to the near-solid
       white slabs the owner flagged. Keeping the whole system in the stylesheet
       removes the source-order trap. Don't re-add light-mode rules here.
       ====================================================================== */

    /* ===== 360 backdrop loading screen (covers the stage while an env loads) ===== */
    .fr-360-loading { position:absolute; inset:0; z-index:40; display:flex; flex-direction:column;
      align-items:center; justify-content:center; gap:18px;
      background:rgba(8,8,10,.82); backdrop-filter:blur(3px);
      opacity:0; pointer-events:none; transition:opacity .25s ease; }
    .fr-360-loading.show { opacity:1; pointer-events:auto; }
    .fr-360-spin { width:46px; height:46px; border-radius:50%;
      border:3px solid rgba(255,255,255,.16); border-top-color:var(--fr-accent-yellow,#f5d000);
      animation:fr360spin .8s linear infinite; }
    @keyframes fr360spin { to { transform:rotate(360deg); } }
    .fr-360-loading-text { font:600 12px var(--fr-mono,monospace); letter-spacing:.18em; text-transform:uppercase;
      color:rgba(255,255,255,.7); }
  `;
  document.head.appendChild(css);

  let tries = 0;
  const wait = setInterval(() => {
    tries++;
    const ctl = window.__frCtl;
    const stage = ctl && ctl.renderer && ctl.renderer.domElement.parentElement;
    if (!stage) { if (tries > 120) clearInterval(wait); return; }
    clearInterval(wait);
    if (getComputedStyle(stage).position === 'static') stage.style.position = 'relative';
    if (stage.querySelector('.fr-fs-toggle')) return;
    const btn = document.createElement('button');
    btn.className = 'fr-fs-toggle';
    // .is-active is the ONLY thing that may give this button a filled look, and it
    // tracks whether 360 is actually engaged. Previously light mode painted it a
    // near-solid white pill at rest, which read as a stuck highlight.
    const setLabel = on => {
      btn.innerHTML = on ? '✕ <span>Exit</span>' : '⛶ <span>360° View</span>';
      btn.classList.toggle('is-active', !!on);
      btn.setAttribute('aria-pressed', String(!!on));
    };
    setLabel(false);
    stage.appendChild(btn);
    const toggle = () => { const on = !ctl._fullscreen; ctl.setFullscreen(on); setLabel(on); };
    btn.addEventListener('click', toggle);
    document.addEventListener('keydown', e => { if (e.key === 'Escape' && ctl._fullscreen) { ctl.setFullscreen(false); setLabel(false); } });

    // ── STUDIO BACKDROP TOGGLE (Dark ⇄ Light) ────────────────────────────────
    // 2026-08-06 owner spec. Sits just above the 360° button so it's in reach
    // WHENEVER the viewer is interactive — not only inside 360, which is where
    // the white room used to be locked away (and forced on).
    //
    // Dark is the default on every fresh load and is the untouched original look:
    // nothing calls setView360Env until the user asks for it, so the scene boots
    // exactly as it always did, and the first switch to white is what captures the
    // dark state to restore later. State is session-sticky in memory only (it
    // rides ctl._bgMode) — deliberately NOT persisted to storage, because the
    // owner wants every fresh load to start black.
    //
    // Stacked ABOVE the 360 button rather than beside it: that button's width
    // changes with its label ("360° View" ⇄ "Exit"), so a side-by-side offset
    // would drift.
    if (!stage.querySelector('.fr-studio-toggle')) {
      const sb = document.createElement('button');
      sb.className = 'fr-studio-toggle';
      sb.type = 'button';
      const paintStudio = () => {
        const light = ctl._bgMode === 'white';
        sb.innerHTML = '<span class="fr-studio-dot" aria-hidden="true"></span><span>Studio: ' + (light ? 'Light' : 'Dark') + '</span>';
        sb.setAttribute('aria-pressed', String(light));
        sb.title = light ? 'Switch to the dark studio backdrop' : 'Switch to the light studio backdrop';
      };
      paintStudio();
      stage.appendChild(sb);
      sb.addEventListener('click', () => ctl.setView360Env(ctl._bgMode === 'white' ? 'studio' : 'white'));
      // setView360Env calls this after every change, so the label stays honest no
      // matter which control drove it (this button, or the in-360 switcher).
      ctl._syncStudioToggle = paintStudio;
      // Live console tuning for the light-studio art, same idiom as __frFloorTune:
      //   __frLightStudio.floorCentre = '#ffffff'; __frCtl.setView360Env('white');
      window.__frLightStudio = ctl.constructor.LIGHT_STUDIO;
    }
  }, 400);
})();

// ============================================================
// PRE-MADE CONFIGURATION BUILDER — owner tool, gated behind ?makepreset=1 so
// customers never see it. Build a car normally with the real configurator, then
// type a Name + Price here and hit "Capture + Save": it serialises the FULL live
// build (captureCurrentBuild) and POSTs it to the local preset_saver (port 8129,
// Desktop "5-PRESET-BUILDER-flameracer.bat") which appends it to
// _glb_work/premade_configs.json. Claude then bakes those into FR_BASE_CONFIGS.
// A copy of the JSON is shown in the box as a fallback if the saver is offline.
// Placed at the very end of the module so any error here can NEVER abort the
// car-boot loader above.
// ============================================================
(function initPresetBuilder() {
  try {
    const qs = new URLSearchParams(location.search);
    if (!qs.has('makepreset') && !qs.has('makepresets')) return;

    const css = document.createElement('style');
    css.textContent = `
      .fr-mkpreset { position:fixed; left:14px; bottom:14px; z-index:10000; width:300px;
        background:rgba(16,17,21,.95); border:1px solid rgba(245,208,0,.4); border-radius:14px;
        padding:14px 14px 12px; box-shadow:0 18px 50px rgba(0,0,0,.6); color:#e8eaee;
        font-family:system-ui,sans-serif; -webkit-backdrop-filter:blur(14px); backdrop-filter:blur(14px); }
      .fr-mkpreset h4 { margin:0 0 3px; font:700 13px var(--fr-mono,monospace); letter-spacing:.12em;
        text-transform:uppercase; color:var(--fr-accent-yellow,#f5d000); }
      .fr-mkpreset p.hint { margin:0 0 11px; font-size:11px; color:rgba(255,255,255,.5); line-height:1.4; }
      .fr-mkpreset label { display:block; font:600 9px var(--fr-mono,monospace); letter-spacing:.12em;
        text-transform:uppercase; color:rgba(255,255,255,.5); margin:9px 0 4px; }
      .fr-mkpreset input, .fr-mkpreset textarea { width:100%; box-sizing:border-box; border-radius:8px;
        border:1px solid rgba(255,255,255,.16); background:rgba(255,255,255,.05); color:#fff;
        font:500 13px system-ui,sans-serif; padding:8px 10px; }
      .fr-mkpreset .row2 { display:flex; gap:8px; }
      .fr-mkpreset .row2 > div { flex:1; }
      .fr-mkpreset textarea { font:500 10px ui-monospace,monospace; height:64px; resize:vertical; margin-top:8px; color:rgba(255,255,255,.6); }
      .fr-mkpreset .acts { display:flex; gap:8px; margin-top:12px; }
      .fr-mkpreset button { flex:1; cursor:pointer; border:none; border-radius:9px; padding:10px;
        font:700 12px system-ui,sans-serif; }
      .fr-mkpreset .save { background:var(--fr-accent-yellow,#f5d000); color:#0b0b0d; }
      .fr-mkpreset .save:hover { filter:brightness(1.08); }
      .fr-mkpreset .copy { background:rgba(255,255,255,.1); color:#fff; }
      .fr-mkpreset .status { margin-top:9px; font:600 11px var(--fr-mono,monospace); min-height:14px; color:rgba(255,255,255,.55); }
      .fr-mkpreset .status.ok { color:#39ff14; } .fr-mkpreset .status.err { color:#ff5a5a; }
      .fr-mkpreset .min { position:absolute; top:10px; right:12px; background:none; border:none; color:rgba(255,255,255,.5); cursor:pointer; font-size:15px; padding:0; flex:none; width:auto; }
      .fr-mkpreset.collapsed .body { display:none; }
    `;
    document.head.appendChild(css);

    const slug = (s) => (s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || ('preset-' + Math.floor(Date.now() / 1000));

    const build = () => {
      const panel = document.createElement('div');
      panel.className = 'fr-mkpreset';
      panel.innerHTML = `
        <button class="min" data-min title="Collapse">▁</button>
        <h4>Save Pre-Made Config</h4>
        <div class="body">
          <p class="hint">Build the car how you want, then name it &amp; save. Price can be tweaked later too.</p>
          <label>Name</label>
          <input data-name type="text" placeholder="e.g. Furia" autocomplete="off" />
          <div class="row2">
            <div><label>Price ($)</label><input data-price type="number" min="0" step="1" placeholder="0" /></div>
            <div><label>Tagline</label><input data-desc type="text" placeholder="Apex predator" autocomplete="off" /></div>
          </div>
          <div class="acts">
            <button class="save" data-save>Capture + Save</button>
            <button class="copy" data-copy>Copy JSON</button>
          </div>
          <div class="status" data-status></div>
          <textarea data-json readonly placeholder="captured JSON appears here…"></textarea>
        </div>`;
      document.body.appendChild(panel);

      const q = (s) => panel.querySelector(s);
      const status = q('[data-status]');
      const setStatus = (msg, kind) => { status.textContent = msg; status.className = 'status' + (kind ? ' ' + kind : ''); };

      const makePreset = () => {
        const name = (q('[data-name]').value || '').trim() || 'New Config';
        const preset = {
          id: slug(name),
          name,
          price: parseFloat(q('[data-price]').value) || 0,
          description: (q('[data-desc]').value || '').trim() || 'Custom build',
          icon: 'helmet',
          build: captureCurrentBuild(),
        };
        q('[data-json]').value = JSON.stringify(preset, null, 2);
        return preset;
      };

      q('[data-save]').addEventListener('click', async () => {
        const preset = makePreset();
        setStatus('Saving "' + preset.id + '"…');
        try {
          const r = await fetch('http://127.0.0.1:8129/save-preset', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(preset) });
          const t = await r.text();
          if (r.ok) setStatus('✓ ' + t, 'ok');
          else setStatus('✗ ' + t, 'err');
        } catch (e) {
          setStatus('Saver offline — copy the JSON instead', 'err');
        }
      });
      q('[data-copy]').addEventListener('click', () => {
        makePreset();
        q('[data-json]').select();
        try { navigator.clipboard.writeText(q('[data-json]').value); setStatus('Copied JSON to clipboard', 'ok'); }
        catch (e) { setStatus('Select-all + Ctrl+C the box below', ''); }
      });
      q('[data-min]').addEventListener('click', () => panel.classList.toggle('collapsed'));
    };

    // Wait until the configurator + capture fn are ready.
    let tries = 0;
    const wait = setInterval(() => {
      tries++;
      if (typeof captureCurrentBuild === 'function' && window.__frCtl) { clearInterval(wait); build(); }
      else if (tries > 120) clearInterval(wait);
    }, 300);
  } catch (e) { console.warn('[FLR] preset builder init failed', e); }
})();
